/**
 * SHARED API CONTRACT
 * ---------------------------------------------------------------------------
 * Single source of truth for the postMessage bridge between the headless
 * viewer iframe and the playground host. This file is intentionally free of
 * any THREE.js / DOM / viewer-internal imports so it can be copied VERBATIM
 * into the playground repo.
 *
 * IMPORTANT: When you change this file, copy it byte-for-byte into the
 * playground repo (src/shared/api-contract.ts) and bump CONTRACT_VERSION.
 *
 * v9: post-processing surface (SSR / SSAO / bloom / vignette / tonemap) and
 *     the dynamic multi-light system (scene lights, presets, self-shadowing).
 *     The v8 try-on declarations were dropped — they were never implemented
 *     in the viewer nor referenced by the playground UI; they can return in a
 *     future version alongside a real implementation.
 * v10: the post pipeline was rebuilt in-house (webgi-style). PostFXSettings
 *     gains progressive refinement, color grading (contrast/saturation),
 *     debug buffer views and an UNCHARTED2 tonemap; SSAO gains autoRadius
 *     (model-scaled world radius); SSR is re-parameterized in model-relative
 *     scales (maxDistanceScale/thicknessScale replace the absolute
 *     maxDistance/thickness — unknown legacy fields in a patch are ignored).
 *     SceneState/RingApi gain groundReflectionBlur.
 * v11: iJewel-parity pass. The realtime VSM ground shadow was replaced by a
 *     PROGRESSIVE BAKED shadow (GroundShadowBakeSettings + get/set/rebake
 *     RPCs); the old flat shadow fields (shadowRadius, shadowBlurSamples,
 *     shadowMapSize, shadowSpread, the contact-shadow pair and
 *     progressiveSoftness) left SceneState — the legacy setters remain
 *     callable (remapped or no-ops) and old settings exports import
 *     silently. PostFXSettings gains tonemapBackground and tonemapGround
 *     (default false — webgi behavior), ssao.edgeSharpness, ssr power,
 *     maskFrontFactor and lowQualityFrames, and bloom.power. exportGLB
 *     gains { compress } (Draco + material extras round-trip);
 *     setApplySceneMaterials toggles vjson MaterialConfigurator apply.
 * v12: SSR march rebuilt on the three.js SSRPass technique (perspective-
 *     correct screen-space walk) with GGX importance-sampled glossy rays
 *     (0beqz/screen-space-reflections); SSRSettings gains `fresnel`
 *     (grazing-angle falloff, default true).
 * v13: iJewel-parity composite effects + renderer params (engine wave 1).
 *     PostFXSettings gains `dof` (bokeh depth of field), `chromaticAberration`,
 *     `grain` (film grain), `lut` (colour LUT blend) and `clipBackground`;
 *     `vignette` gains `color`; PipelineDebugView gains `coc` (+ reserved
 *     velocity/taa/sscs/ssgi/bevel for later waves). New RPCs: `setDofFocus`,
 *     `loadLutUrl`/`loadLutFile`/`clearLut`/`listLuts`, `setBackgroundImageUrl`/
 *     `setBackgroundImageFile`/`setTransparentBackground`, `loadDiamondEnvFromUrl`/
 *     `loadDiamondEnvFromFile`/`setDiamondEnvRotation`, `getRendererParams`/
 *     `setRendererParams`; `snapshot` gains `format`/`quality`. Engine waves 3
 *     also add (still v13) `sscs` (screen-space contact shadows), `ssgi`
 *     (screen-space GI), `ssbevel` (G-buffer normal rounding) and `ssaoCavity`
 *     (small-scale cavity AO) to PostFXSettings, with debug views
 *     coc/sscs/ssgi/bevel. All additive — v12 hosts ignore the new fields.
 * v14: selection/picking upgrades (engine wave 4). A screen-space Outline
 *     overlay replaces the bbox-wireframe highlight (`OutlineSettings` +
 *     `getOutline`/`setOutline`); multi-select (`setSelection`/`getSelection`,
 *     `selectionChanged` gains `groupIds`); hover (`PickerSettings` +
 *     `getPickerSettings`/`setPickerSettings` + `hoverChanged` event,
 *     shift/ctrl-click additive, autoFocus). Later v14 parts add the scene
 *     tree, transform gizmos and full material editor.
 */

 // v15: TAA (temporal AA) — PostFXSettings.taa + a velocity buffer (active in
 // motion, bypassed on idle); GLTF clip animations, camera views, interaction
 // prompt, turntable; OBJ/FBX/STL/KTX2/meshopt loaders + LoadOptions; tiled
 // snapshot, mp4/webm turntable video, our-schema .vjson export; KHR material
 // variants.
 // v16: scene extras long tail — `generateGeometry` (procedural primitives),
 // `computePose` (PCA auto-orient + drop-to-ground), `setHdriGround`
 // (GroundedSkybox), `addStageModel` (prop excluded from export/auto-fit),
 // `applyTextToGroupMap` (canvas text decal) and `setObjectRotation`. All
 // additive — v15 hosts ignore them.
 // v17: EXACT iJewel field parity (waves A/B). Post-FX/AA/tonemap/outline field
 // gaps (SSR boost/tolerance/objectRadius/autoRadius/maskFrontRays; SSAO+SSGI
 // smoothEnabled; SSGI tolerance/bias/renderWithCamera/ssgiEnabled; SSCS
 // onlySSCSDebug; SSBevel sceneBevel; Bloom debug; Vignette power; Outline
 // highlight modes; LUT lutBackground; Progressive jitter; TAA feedbackMin/Max;
 // gemRefraction/velocityBuffer/renderDepth) + full Camera ControlsParams
 // (min/maxDistance, zoom/azimuth/polar limits, enable toggles, dolly, near/far,
 // position/target). All additive.
 // v18: iJewel Scene-tab parity (wave C). Full Ground panel
 // (getGroundConfig/setGroundConfig — plane props + Randomized Light +
 // Progressive Directional shadow light), HDRi grounded-dome full config
 // (getHdriGroundConfig + setHdriGround tripodHeight/originPosition), live
 // Gradient-SVG background generator (getGradientConfig/generateGradient). All
 // additive; fields our engine lacks are stored and round-trip.
 // v19: iJewel Animations + Picking parity (wave D). GLTF anim extras
 // (timeScale/animateOnScroll/Drag/autoplayOnLoad/syncMaxDuration +
 // setGltfAnimationOptions); CameraViews config + focusNext/Previous +
 // recordAllCameraViews; full InteractionPromptSettings; Pose autoUpdateGround;
 // TransformControls showX/Y/Z; PivotControls + PivotEdit settings (stored);
 // Diamond manager (forceSceneEnvironment/envMapRotation + make diamond/standard
 // + clearDiamondCache); material-library replaceMaterial. All additive.
 // v20: full material editor texture maps (wave E). PhysicalMaterialParams gains
 // bumpScale/legacyBumpScale/normalScaleX-Y/displacementScale/aoMapIntensity/
 // lightMapIntensity/alphaTest/depthTest/depthWrite/colorWrite/polygonOffset(+
 // factor/units). New texture-map surface: MaterialMapSlot (19 slots),
 // TextureTransform, MaterialMapInfo + setGroupMaterialMapUrl/File/
 // clearGroupMaterialMap/setGroupMaterialSampler/listGroupMaterialMaps. Maps
 // apply to the non-gem MeshPhysicalMaterial (gem-guarded). All additive.
 // v21: material extensions (wave F). MaterialExtensionConfig (anisotropy,
 // triplanar, thin-film, fragment-clipping, flat, sparkle-bump, custom-bump,
 // clearcoat-tint, per-material SSBevel) + get/setGroupMaterialExtensions.
 // Native features wire through; FragmentClipping uses real clipping planes;
 // webgi-specific injections stored + round-trip. Gem-guarded. All additive.
 // v22: configurators (wave G). Material Configurator (per-group switchable
 // material variations — snapshot/apply/rename/remove/clear) + Switch Node
 // (switchable geometry, one option visible via setNodeVisible). All additive.
 // v23: modifiers (wave H). Object Rotations (radial duplication), Extrude Tube
 // Shapes, full Simple Text decal, mesh Simplify (three SimplifyModifier, gem-
 // skipped), Parallax mapping/camera + Rhino import options (stored). Additive.
 // v24: export + utility-plugins (wave I). Full GlbExportOptions + full
 // VideoExportOptions (FFmpeg fields) + LoaderConfig (LoadingScreen) + FrameFade
 // + S3Config + getLoaderConfig/get-setFrameFade/enter-exit-toggleFullScreen/
 // get-setS3Config/s3Connect/clearCaches/autoCenterAllGeometries. S3 backend-
 // bound (stores + no-ops). All additive.
 // v25: decorative / niche plugins (wave J). Physics (cannon-es), Snow Fall,
 // Wave Ground, Watch Hands (regex mesh match), CSS3D Renderer, Scrollable
 // Camera (dev preview), xAtlas UV unwrap (built-in box projection), Rainbow
 // Diamond (gem-only color tint). All OFF by default; gem invariant preserved.
 // v26: final parity (exact-layout reconciliation). DiamondManagerSettings +=
 // diamondOrientedEnvMap/env2Url/env3Url; new EnvAdvancedConfig (secondary env
 // slot + fixed-direction + cache) + WidgetsConfig; GlbExportOptions
 // .pluginIncludeList; zoomCamera RPC. Closes the last audit gaps; additive,
 // off/stored where the engine lacks the exact behavior.
 // v27: SSR + SS-Contact-Shadows high-quality rewrite. SSRSettings += denoise/
 // blurKernel/blurSharpness/denoiseIterations/temporal/temporalBlend/
 // probeFallback/probeIntensity; SsContactShadowSettings += softness/denoise/
 // blurKernel/blurSharpness/blurIterations/temporal; new ReflectionProbeSettings
 // (dynamic cubemap probe) on PostFXSettings; PipelineDebugView += ssrDenoise/
 // reflectionProbe. Edge-aware bilateral denoise + velocity temporal reproject
 // make both effects smooth in a single frame and during motion; gems stay
 // bit-identical. All additive (legacy patches ignore unknown fields).
 // v28: band StoneShape widened to an open string id (catalog-driven). The
 // stone-shape catalog now includes the full diamond-with-prong settings
 // library (cut × claw/square/heart/leaf gallery variants) alongside the
 // original bare cuts; new ids look like "set:round_claw". Backward-compatible:
 // the original "round"/"oval"/"cushion"/"princess"/"pear" ids still work and
 // getCatalogs().stoneShapes lists everything available.
 // v29: engagement-ring layer (playground-side; the viewer's band engine has not
 // landed it yet, so this contract does NOT carry the engagement block — the two
 // repos are byte-identical for everything EXCEPT that still-in-flight feature).
 // v30: per-material & per-object reflection control. SSRSettings += wBackground/
 // wMetal/wGem/wGround (how strongly traced metals reflect each *kind* of source,
 // classified from the hit pixel's G-buffer code — diamond→metal etc.).
 // ReflectionProbeSettings += asGemEnv/gemProbeIntensity (opt-in: sample the
 // dynamic probe inside the diamond shader for real diamond→diamond /
 // diamond→metal reflection — recompiles the gem material). GemUniformName +=
 // surfaceReflBoost/internalEnvBoost (overall diamond reflectivity). New
 // ReflectionGroup/ReflectionMatrix types + RingApi reflection-matrix methods
 // (getReflectionGroups/getReflectionMatrix/setReflectionMatrixCell/
 // setReflectionMatrixEnabled/resetReflectionMatrix) for per-object ring↔ring /
 // part↔part control via a group-id prepass. PipelineDebugView += groupId. Every
 // field defaults to identity (weights 1.0, matrix off, asGemEnv off) ⇒ the
 // render — and the gem crop especially — is byte-identical until tuned.
// v31: iJewel field-gap closeouts. RendererParams.mode 'preview'→'custom'
// (canonical; 'preview' still accepted as a deprecated input alias); TonemapMode
// += 'NONE' (webgi constant 0 — no operator AND exposure ignored, distinct from
// LINEAR); GroundConfig += material (plane color/roughness/metalness);
// PhysicalMaterialParams += overrideEnvironment (webgi separateEnvMapIntensity —
// the material's envMapIntensity is absolute, not × scene.environmentIntensity);
// SwitchNodeState += camDistance/updateSceneOnSelectionChange; ExtrudeTubeOptions
// += enabled/extrudeCirceTube; MaterialMapSlot += 'customBumpMap'; snapshot opts
// += frames/crop; DiamondManagerSettings env2/3 become RESIDENT gem-env textures
// with a per-group envMapIndex (0|1|2). New methods: clearMaterialVariations,
// getGroupMaterialInfo, renameSwitchNode, setHoveredGroup, addStageModelFile,
// setStageGroundBelow, setGroupGemEnvSlot, activateDiamondEnvSlot; simplifyMeshes
// gains an errorThreshold/lockBorder opts arg (meshoptimizer path). Gem shader
// stays byte-identical — the new env-slot samplers live behind #ifdef GEM_ENV_SLOTS.
// v32: wave-2 feature surface. MaterialExtensionConfig += a LayeredMaterial cavity
// block (webgi LayeredMaterialPlugin / WEBGI_materials_layered — a screen-space
// curvature "worn crevice" layer, NOT an albedo blend; layeredEnabled/Cutoff/
// CavityColor/CavityRoughness/CavityMetalness/CavityScale/StepSize) driven through
// the existing set/getGroupMaterialExtensions; metal-only, gem-guarded, default OFF
// ⇒ pristine materials (whole 8.glb scene) stay bit-identical. New RandomizedSelfShadow
// (jittered key/self-shadow directional light, the model-shadow analog of the ground
// bake — get/setRandomizedSelfShadow + SceneState.randomizedSelfShadow). Gem
// inclusion material (GemInclusionParams + setGroupGemInclusions — webgi GemRefraction
// inclusions, gem code behind #ifdef GEM_INCLUSIONS). Scene orchestration plugins:
// VariationConfigurator (switchable material/object variations), VirtualCameras
// (named saved poses), ScenePresets (named of-vjson snapshots) and SceneLoop (kiosk
// playlist) with their RPC method sets + two new events (variationProgress,
// sceneLoopStateChanged). ScrollableCameraView live driver (setScrollProgress +
// generateScrollViewsCode). GltfAnimationOptions/GltfPlaybackState += autoIncrementTime/
// dragAxis/dragAnimationDamping/scrollAnimationDamping. PostFXSettings += motion
// (adaptive dynamic-resolution notch floor: enabled + minScale). All additive, every
// feature OFF/identity by default; the gem crop and default look are unchanged.
// v33: engagement-ring configurator mode. New ViewerMode "engagement" (a third route
// mirroring band), the EngagementState schema (center/side stones, head/setting, shank,
// extras, computed spec) and EngagementApi (extends RingApi with coarse partial-patch
// setters that each return the fresh EngagementState, plus metal/gem material editing,
// spec, draft batching and design serialize/load). New "engagementStateChanged" event.
// Additive; ring/band modes and the default gem shader are untouched.
// v34: engagement shank gains band-grade surface (edge/grooves/channel/split
// metals/diamond run) — the band+head merge. ShankConfig += edgeStyle/grooves/
// channel/splitMode/splitPatternId/materials/diamond, each reusing the existing
// band DTO verbatim (no new types), driven through the same setShank partial-
// patch setter (deep-merged). No new RPC methods. Additive; ring/band modes and
// the default gem shader are untouched.
// v35: engagement spec/validator layer + manufacturing export. EngagementApi
// gains `exportManufacturingGLB` (returns the assembled ring as a GLB — later a
// CSG-fused STL; STUB delegates to exportGLB for now). The already-declared
// EngagementSpec fields are now POPULATED: `metalWeightGrams` (per-alloy cast
// weight from part volume × alloy density, refData ALLOY_DENSITY_G_PER_CM3, gold
// presets assumed 18K since PartitionMaterial carries no karat) and `warnings`
// (a manufacturability validator against the Appendix-A floors in refData —
// prong strut / pavé gap / bezel wall / band thickness / prong-count-vs-carat /
// halo airline / flush-stacking). No schema changes. Additive; ring/band modes
// and the default gem shader are untouched.
// v36: "complete the set" — engagement mode can stack a matching wedding band
// beside the engagement ring, reusing the band-mode BandConfigurator engine.
// EngagementApi gains `addCompanionBand` (build + place a companion band flush
// against the shank; its default is profile A, 2 mm wide, the shank's ring size
// and metal), `removeCompanionBand` (full dispose) and `setCompanionBandLayout`
// (slide the band along the finger axis by an offset in mm). No schema changes;
// ring/band modes and the default gem shader are untouched.
// v37: Ring Builder — the wedding-band and engagement-ring builders merge into
// one surface. New `RingState` = BandState + optional `EngagementBlock` (the
// engagement-only parts: centre/side stones, head, extras, style, cathedral,
// fit — the band schema keeps sole ownership of profile/width/size/edge/grooves/
// channel/split/materials/melee, which ShankConfig used to duplicate, so
// ShankConfig is now @deprecated). New `RingBuilderApi` (the union of the BandApi
// and EngagementApi blocks over RingApi, one beginDraft/endDraft pair, every
// setter returning RingState) + `getRingState`/`setEngagementEnabled`/
// `setEngagement`, and a new `ringStateChanged` event. Also declares the ported
// geometry knobs, all OPTIONAL and off by default: BandState.shape (RingShape
// silhouettes), .division (DivisionStyle seam paths), .comfortFitMM;
// GrooveConfig.jointType/seam/edges; DiamondStyle += eye/cross-bezel/
// cross-channel/cross-around/free-scatter; with setRingShape/setDivision/setComfortFit/
// setJointGrooves, and their optional `Catalogs.ringShapes/divisionStyles/
// jointTypes` dropdown data. ADDITIVE ONLY — BandApi, EngagementApi, BandState,
// EngagementState, bandStateChanged, engagementStateChanged and the three-member
// ViewerMode all survive (deprecated) until their consumers are migrated; the
// gem crop and the default look are unchanged.
// v37 (cont.): `EngagementExtras` is now surprise-stones ONLY. Its
// `engravingText`/`engravingFont` were duplicates of BandState.engravingText /
// BandState.engraving.fontFamily, which the merged EngraveTab writes and
// BandConfigurator.rebuildEngraving is the only thing that draws — so on an
// EngagementBlock they had no writer and went stale on export. The two fields
// moved to `LegacyEngagementExtras`, read only by the deprecated
// EngagementApi/EngagementState; a persisted block that still carries a
// non-empty `engravingText` is migrated onto the band half on load.
// v38: the `bandStudio` scene preset + a user-facing quality tier. The Ring
// Builder route (/band) now boots iJewel3D's measured wedding-band look
// (docs/plans/ijewel-phase2-parity-plan.md §1: env_metal_035 at envMapIntensity
// 0.61, pure-white untonemapped stage, ACES + contrast 1.1, SSAO falloff 1.9 /
// edgeSharpness 0.3, SSR intensity 4, key+fill directional lights over the env)
// — pure viewer-side project config, no contract surface of its own. What IS
// contract is the gear-menu switch that sits on top of it: new `QualityTier`
// ("medium" | "high" | "ultra") and `setQualityTier(tier)`, which drives pixel
// ratio, progressive frame budget and SSR/SSAO resolution together instead of
// making the host patch three unrelated settings. It layers over the existing
// `QualityMode` ladder (medium→interactive, high→high, ultra→ultra); "low" stays
// internal and unexposed. `high` reproduces today's desktop defaults exactly, so
// a host that never calls this renders unchanged. Ring mode (/3dviewer) and the
// gem shader are untouched — the diamond env path is not part of the preset.
// v38 (cont.), the geometry half of the same parity wave — seven additive band
// features, every one absent/false by default so a saved design renders exactly
// as it does today (audited by the geometry harness's bit-identity cases):
//   · `RingShape` += "octagon" | "wavy" (the reference's last two silhouettes).
//   · `JointType` += "milgrain" | "milgrain2" — a real ring of swept metal beads
//     instead of a carve. One builder (`band/MilgrainBuilder.ts`) serves all
//     three placements the reference offers: partition-seam grooves, decorative
//     grooves and edge treatments. Beads are METAL (mesh `band_milgrain`), never
//     gems.
//   · `SideEdgeStyle` + `BandState.edgeSides` — independent Left/Right edge
//     treatment. ABSENT ⇒ the global `edgeStyle` drives both sides (today);
//     present ⇒ it overrides the global on both sides.
//   · `BandState.insidePolished` — force a polished finish on the inner wall
//     regardless of the outer surface finish.
//   · `BandState.autoThickness` — drive thickness from width (½ the width,
//     clamped to 1–3 mm), re-applied on every width edit.
//   · `GrooveConfig.seamBoundaries` — per-partition-boundary enables for the
//     seam grooves. Absent/short ⇒ every boundary on, i.e. today's behaviour.
//   · `UK_RING_SIZES` + `setRingSizeUK` — the British letter scale (A–Z with
//     halves). Stateless: it converts to an inner diameter and drives the
//     existing US size, so nothing new is persisted.
// New RPCs: setEdgeSides / setInsidePolished / setAutoThickness / setRingSizeUK
// (gate on caps `ringGeometryV38`). Octagon/Wavy and the milgrain joints need no
// new method — they arrive through `setRingShape` / `setJointGrooves` and are
// simply absent from a v37 viewer's `ringShapes` / `jointTypes` catalogs.
// v39, the DIAMOND half of the iJewel3D phase-2 parity wave (plan §2.4). Every
// field is optional and absent ⇒ exactly today's stone run, so a saved design
// renders bit-identically (audited by the gem harness's legacy cases). Almost
// all of it rides the EXISTING `setDiamond(Partial<DiamondConfig>)` patch — one
// new method name in total.
//   · `DiamondPlacement` + `DiamondConfig.placement` — "side" sets the run into
//     the band's SIDE WALL instead of its outer surface. Geometrically distinct:
//     the seat is a real carve in the side face (so it feeds the same
//     volume/weight spec), the stone's table faces along the finger axis, and
//     the run's radial height is `sideRowPosition` (−1 inner … +1 outer).
//   · `DiamondSides` + `DiamondConfig.sides` — which wall(s) a side run occupies
//     ("both" places two mirrored runs). Absent ⇒ "both".
//   · `SideSetting` + `DiamondConfig.sideSetting` — the side-wall hardware:
//     "bezel" (a collar around each stone), "channel" (ONE milled groove the run
//     drops into), "prong" (bead prongs), "none". Absent ⇒ "bezel".
//   · `DiamondSetting` + `DiamondConfig.setting` — their top-surface "Diamond
//     Setting": Channel · Prongs · Flush · Only ("only" = no metal hardware at
//     all). Absent ⇒ whatever the style itself implies, i.e. today.
//   · `StoneSpacing` + `DiamondConfig.spacing` — the seven named pitches. The
//     first four are multiples of the stone diameter, the last three distribute
//     the run evenly over ½ / ⅓ / all of the ring. Absent ⇒ today's 1.05×.
//   · `DiamondColor` + `DiamondConfig.color` — White · Black · Yellow · Pink ·
//     Ruby. NOT a new material path: each maps onto an existing gem preset plus
//     `gemParams` tint, so gems keep the one ray-trace shader.
//   · `StoneType` + `DiamondConfig.type` — a run can carry gold BEADS instead of
//     gems. A bead is metal (mesh `goldbead_*`, band metal material, never a name
//     the gem classifier matches) and is excluded from carat totals while
//     counting toward metal volume/weight.
//   · `DiamondStyle` += "vertical" | "clamp" | "clamp-angled" — the reference's
//     remaining top Setting Types (Cross/Eye/Free already existed).
//   · `InnerDiamondConfig` + `BandState.innerDiamond` — one stone set into the
//     INSIDE of the band, with its own seat carved in the inner wall.
//   · `BandSpec.stoneCounts` / `beadCount` — per-run counts for the host's live
//     "62 stones on the left side" readout. `stoneCount` counts GEMS only and is
//     now tallied as stones are placed, not from the pre-loop placement list.
// New RPC: setInnerDiamond (gate on caps `ringDiamondsV39`). New catalogs:
// `diamondColors`, `stoneSpacings`, `diamondSettings`.
// v40, the FREE-PLACEMENT diamond editor (plan §2.4 "Setting Type: Free"). The
// reference unrolls the band into a flat 0°→360° strip and lets stones be
// dragged onto it one by one; this is the state + placement half of that. Absent
// `freeStones` ⇒ exactly today's run, and the two existing free styles are
// untouched ("free-stones" is still the even fan, "free-scatter" still the
// golden-angle scatter).
//   · `DiamondStyle` += "free-place" — EXPLICIT per-stone positions, a third
//     thing from the other two frees. Reading `state.freeStones` is gated on
//     this style, so the array can be edited before it is switched on.
//   · `FreeStone` + `BandState.freeStones` — one entry per placed stone:
//     `thetaDeg` around the ring, `u` across the band width (−1 … +1, the same
//     normalised convention as `rowPosition`), plus per-stone `sizeCt` / `type`
//     / `color` / `setting` overrides that each fall back to the run-level
//     `DiamondConfig` value when absent.
//   · `BandState.freeRepeat` — N-fold repetition: stone i is emitted at
//     `thetaDeg + freeRotateDeg + k·(360/N)` for k in 0…N−1. Absent ⇒ 1.
//   · `BandState.freeRotateDeg` — spins the whole arrangement around the band,
//     −180 … +180. Absent ⇒ 0.
//   · `FreePatternId` + the `freePatterns` catalog — six deterministic pattern
//     stamps (line, S-curve, arc, cluster, scatter, rosette) generated by the
//     ENGINE from the live band geometry, so an arrangement scales with the
//     band's circumference and width instead of being hard-coded in the host.
// The stones flow through the one `gemList()` placement source, so seat carving,
// hardware, counts, carats and metal volume behave exactly as they do for a
// parametric run — there is no second path.
// New RPCs: setFreeStones / addFreeStone / updateFreeStone / removeFreeStone /
// applyFreePattern / setFreeRepeat / setFreeRotate (gate on caps
// `ringFreeStonesV40`). New catalog: `freePatterns`.
// v41, the LONG TAIL of the iJewel3D phase-2 parity plan (§3.4, §3.5 and the
// two naming/balance gaps in §2.3) — the last wave. Every field is optional and
// absent ⇒ exactly today's ring, so a saved design renders bit-identically.
//   · `DesignGroove` + `BandState.designGrooves` — their "Design" sub-tab: an
//     arbitrary list of decorative grooves, each with its own Direction
//     (horizontal around the circumference / vertical across the width),
//     position in mm, `JointType`, width (0.25–3.00) and depth (0.10–1.00).
//     They feed the SAME max-depth `applyOuterCarve` pass as the global
//     `grooves` — not a second carve — so overlaps cut to the deeper of the two
//     and every groove removes real metal from `spec.metalVolumeMM3` (adding one
//     lowers the quoted price, as it does in the reference). A bead `jointType`
//     sweeps milgrain through the existing rail builder instead of carving;
//     bead rails are circumferential, so only a HORIZONTAL bead groove beads.
//     `DesignGroove.surface` is stored and round-tripped but does not yet retint
//     the groove floor — see the field's own note.
//   · `PartitionMaterial.label` + `setPartitionLabel` — their "Metal 1 / Metal
//     2 …" naming. The engine fills the positional default on read, so the host
//     can print `label` unconditionally.
//   · `BandState.customRatios` + `setCustomRatios` + `normalizeRatios` — their
//     Custom Balance toggle: arbitrary per-partition ratios replacing the split
//     pattern's five presets. Stored normalised to the live partition count
//     (floored at 1% so a partition can be thin but never vanish) and re-fitted
//     when the partition count changes. `null` restores the preset ratios.
//   · `addRing` / `removeRing` / `getRingCount` — their "+ Add ring" goes past a
//     pair, so the two-ring pair became an ordered LIST and `setActiveRing`
//     takes any index. `setBandLayout` lays out N rings (the two-ring poses are
//     unchanged, and every preset still resets each group to identity before
//     measuring). `addSecondRing`/`removeSecondRing` stay as deprecated shims.
// New RPCs: addDesignGroove / updateDesignGroove / removeDesignGroove /
// setDesignGrooves / setCustomRatios / setPartitionLabel / addRing / removeRing
// / getRingCount (gate on caps `ringLongTailV41`).
// v42, iJewel3D phase-2 parity WAVE F — the last three capability gaps. All
// three are additive and default-off, so an existing design is untouched.
//   · N METAL PARTITIONS. `SplitMode` gained "quad" and "multi", but the mode is
//     now only a LABEL: the geometry has always been driven by
//     `partitionRatios.length`, and `setPartitionCount(1…MAX_PARTITIONS)` drives
//     that count directly (the reference's Single · Duo · Tri · Quad · Multi).
//     `materials`, the seam carve, `grooves.seamBoundaries`, `customRatios` and
//     the milgrain rails all follow it. The catalog carries presets up to five
//     partitions; past that (or when a count has no preset in the wanted seam
//     direction) an even pattern is SYNTHESISED from `evenSplitPatternId` and
//     parsed back by the engine, so the catalog stays a curated list instead of
//     an enumeration. The three original SplitMode members are unchanged.
//   · A LIVE HER/HIS LINK. `setRingsLinked(on)` / `getRingsLinked()`. While on,
//     every band setter applied to the active ring is mirrored to the others
//     ENGINE-side — every host and every RPC path gets it, including the
//     beginDraft/endDraft pair. The exclusion list (ring size, engraving TEXT,
//     the engagement block) is on `setRingsLinked` and is deliberate: those are
//     the wearer's, not the design's.
//   · VIEWER TOOLS. `flipCamera()` swings the camera half a turn about the
//     target to show the other face; `zoomCamera()` (v26) is the zoom-out half
//     of the same cluster. Both are camera-only — no geometry, no re-bake.
// New RPCs: setPartitionCount / setRingsLinked / getRingsLinked / flipCamera
// (gate on caps `ringWaveFV42`).
// Also v42, additive and gate-free: `SplitPatternItem.ratios` — the served
// catalog now carries each pattern's balance, so a host can draw the tile from
// the numbers the geometry uses rather than parsing them out of the label — and
// `ENGRAVING_BASE_FONT_PX`, the 1× reference for `EngravingConfig.fontSizePx`
// (the wire value is unchanged; only the unit a host may show it in).
// The engraving canvas also now LOADS a web family before drawing in it
// (`band/engravingFonts.ts`) — the decal lives in the viewer document, so a
// family the host offers has to be fetched here. No contract surface: the
// family is still just a name on `EngravingConfig`.
// v43, the iJewel3D phase-2 GAP-LIST wave — five measured gaps, all additive.
// Every field is optional and absent ⇒ today's ring, with ONE deliberate
// exception called out below (`EngravingConfig.bumpMap`).
//   · `GrooveConfig.surface` — the Separation panel's own Surface row (Polished ·
//     Matt · Sand) on the groove's cut FLOOR, which is a different control from
//     the partition's 7-option metal Surface. Reuses the existing
//     `GrooveSurface` (v41), so both groove families now speak one type.
//   · ENGRAVING RELIEF on `EngravingConfig`: `bumpMap` · `bumpOnly` ·
//     `bumpScale` (0 … 0.01, their live 0.0020) · `parallax` (off) ·
//     `markRoughness` (0 … 1 MATT..GLOSS, their live 0.50). Their inscription is
//     a carved mark that catches light, ours a flat unlit decal.
//     ⚠️ `bumpMap` absent ⇒ TRUE, matching the reference's default: this is the
//     one v43 field whose absent-value is NOT today's behaviour, so an existing
//     design's inscription gains relief once the engine honours it. "Mark Color"
//     is the pre-existing `EngravingConfig.color` and was not duplicated.
//   · DIAMOND SPECIFICATION at the run level (`DiamondConfig`) and per stone
//     (`FreeStone`): `origin` (the existing `DiamondOrigin`, until now reachable
//     only on the engagement centre stone), `colorGrade` (`GiaColorGrade`,
//     D … K) and `clarity` (`DiamondClarity`, FL … I). Specification and PRICE
//     metadata only — none of the three ever reaches the gem shader, so a design
//     that changes only these renders bit-identically. New catalogs
//     `diamondOrigins` / `gemColorGrades` / `gemClarities`, served from the
//     `DIAMOND_ORIGINS` / `GEM_COLOR_GRADES` / `GEM_CLARITIES` constants so the
//     reference's long clarity labels are not retyped in the host.
//   · FREE PATTERNS, now TEN. `FreePatternId` is the union of the reference's six
//     measured tiles (Scatter · Wave · Cascade · Dipper · Cluster · Halo) and the
//     four generated curves of ours it has no counterpart for (Line · S-Curve ·
//     Arc · Rosette). Every pre-v43 id stays valid. The six reference stamps are
//     no longer invented curves: `buildFreePattern` emits their tile artwork,
//     measured stone-by-stone (`MEASURED_FREE_PATTERNS`) — which is why the R₂
//     scatter that used to draw `scatter`/`cluster` is gone. A stamp is a
//     one-shot action that writes `freeStones`, so no saved design changes.
//   · THE RING MANAGER: `setRingVisible(index, visible)` and
//     `moveRing(from, to)`, plus `BandState.ringType` / `ringName` — pure
//     metadata that survives save/load so the manager can label its rows after a
//     reload. `removeRing`'s "ring 0 is never removed" note is relaxed: any index
//     is removable and removing ring 0 promotes the next ring.
// New RPCs: setRingVisible / moveRing (gate on caps `ringManagerV43`).
//
// v44 — SceneState learns WHICH environment it was authored against.
//   `SceneState` gained optional `envPresetId` / `envUrl`. It already carried
//   `envIntensity` / `envRotation` but nothing identifying the HDRI those were
//   tuned for, so applying a saved scene preset while a different environment
//   was loaded stamped the numbers onto the wrong env — lighting that matched
//   neither the saved look nor the current one. `_applySceneState` now restores
//   the environment first and re-stamps intensity/rotation once it lands.
//   Purely additive and OPTIONAL: a state captured before v44 omits both fields
//   and the environment is left exactly as it is, so no saved document changes
//   meaning. No new RPC names, so no caps flag is required.
// v45 — the editor's scene state survives a reload, and a metal HDR can be
//   un-set. Three optional `SceneState` fields close persistence gaps the
//   editor itself had opened:
//   · `backgroundImageUrl` — an image/gradient backdrop was applied but never
//     recorded, so Save + reload silently dropped it.
//   · `metalEnvUrl` — same for a Metal HDR override. `metalEnvRotation` /
//     `metalEnvMaxBrightness` were already saved and replayed, but onto
//     whichever metal env happened to be loaded; `_applySceneState` now
//     restores the HDR FIRST and re-stamps those two once it lands, exactly as
//     v44 did for the scene env.
//   · `gemInclusions` — per-group webgi inclusion state. `setGroupGemInclusions`
//     was write-only, so a chosen inclusion map lived only in the live material.
//   New RPCs: `clearMetalEnv()` drops an explicitly loaded Metal HDR override so
//   metals fall back to the scene environment (without it the first Metal HDR
//   load was permanent and every later Environment pick silently stopped
//   reaching the metals), and `getGroupGemInclusions(id)` reads the state the
//   inclusion panel used to guess. Both are brand-new method names — gate on
//   caps `v45`. The SceneState fields are optional and need no gate: a snapshot
//   captured before v45 omits them and the matching state is left alone.
// v46 — the background says who owns it, and a file-picked backdrop stops
//   vanishing. `SceneState.backgroundSource` (read-only) names the writer that
//   owns `scene.background`: a host needs it for the Background Blur rows, and
//   `applySceneState` needs it to tell "the scene was transparent" apart from
//   "an image or the env owned the background" — `background: null` means both,
//   and replaying it blindly blanked a restored image scene to TRANSPARENT.
//   `setBackgroundImageFile` now inlines the picked image into
//   `backgroundImageUrl` as a size-capped `data:` URL instead of saving nothing,
//   so a backdrop a user picked from disk genuinely survives a save + reload;
//   an image over the cap still reports null, and restoring that keeps the live
//   background rather than dropping it. No new method names (so no caps flag),
//   and a pre-v46 snapshot simply omits `backgroundSource`.
// v47 — GROUP IDS ARE NOW STABLE ACROSS MODEL LOADS. `GroupInfo.id` (and every
//   id derived from it — `ElementInfo.id`, `SceneState.gemInclusions[].groupId`,
//   the `materials` array of an of-vjson document) used to be the three.js
//   `material.uuid`, which three regenerates on every parse. Anything a host
//   persisted against a group id therefore matched NOTHING after a reload: v45's
//   `gemInclusions` were written into every saved scene and restored into
//   nowhere, and of-vjson's per-group material assignments had the same hole.
//   The id is now derived from model CONTENT (material name/type + the group's
//   first mesh name + the gem/metal sub-key, with a `~n` suffix disambiguating a
//   file that reuses both names), so the same part of the same file gets the same
//   id every session and those restores actually land.
//   No shape and no method names changed, so there is nothing to gate: hosts
//   treat the id as opaque. Two consequences worth knowing:
//     · a document saved BEFORE v47 carries uuid ids that resolve to nothing —
//       it restores exactly as much as it did before (nothing), and the viewer
//       now says so on the console instead of failing silently;
//     · a group id is only meaningful for the model it came from. Applying a
//       saved scene over a DIFFERENT model skips the groups it cannot find and
//       logs which ones, rather than half-applying.
// v48 — the studio-12 close-out wave. Two additions, both about state that
//   existed only implicitly:
//     · `SceneState.metalEnvActive` (read-only) and `SceneState.metalEnvSource`
//       ("none" | "default" | "override"), plus `restoreDefaultMetalEnv()`.
//       "Metal Rotation" and "Metal HDR Brightness Cap" are no-ops without a
//       live HDR, and the FIRST Environment pick disposes the boot one — so
//       both rows sat enabled, reported values, and did nothing, with no way
//       back to the default HDR short of a page reload. `metalEnvUrl: null`
//       also conflated "explicitly cleared" with "still on the boot default",
//       so a scene saved in the first state reloaded into the second.
//     · `SceneState.configurator` — the viewer-side MaterialConfigurator /
//       SwitchNode / KHR-variant selection. `of-vjson/1` captured none of it,
//       so every configurator setup was lost on reload while the Vault's own
//       configurator definition round-tripped fine. Purely additive; a
//       pre-v48 document has the field absent and restores as before.
//   Both are optional and absent-means-old-behaviour, so no caps flag is needed
//   for the SceneState fields. `restoreDefaultMetalEnv` is a NEW METHOD NAME and
//   does need one (RPC has no timeout — calling it on a v47 viewer hangs).
// v50 (Ring Builder gem-tab restructure): `Catalogs.stoneCuts` +
//   `Catalogs.prongVariants` republish the stone catalogue as the two axes it
//   always was — 10 cuts x 5 prong/gallery styles — instead of the 48
//   pre-combined tiles `stoneShapes` flattens them into. Purely additive and
//   state-neutral: `stoneShape` keeps the identical ids and the host composes
//   one from the pair, so no saved design moves. Seven of the fifty cells were
//   never modelled, and `StoneCutItem.variants` names the ones that exist so a
//   host can DISABLE the rest — substituting is not an option, because the
//   engine answers an unknown stone id with the first bare cut (a Round)
//   silently, with no error and no log. `bare` is the prong-less id a Flush or
//   "Only" run needs, `null` for the five cuts whose bare GLB is still pending.
//   No new method NAME, so no caps flag is strictly required — but the host
//   gates on `ringGemMatrixV50` anyway, matching how every other catalog wave
//   here has been gated.
// v51 (milgrain, item 4): `BandState.milgrain` + `setMilgrain` make the bead law
//   adjustable — size, rows (1-3, replacing the "x2" enum), row spacing, how far
//   a bead seats into the metal, and tessellation. Every field optional and
//   absent-means-the-shipped-constant, so a design that never touches it is
//   bit-identical. `setMilgrain` is a NEW METHOD NAME and therefore needs a
//   caps flag (`ringMilgrainV51`) — RPC has no timeout.
// v52 (free stones, items 22 + 23.3): `FREE_PATTERNS[].resample` marks which
//   stamps are CURVES and can therefore honour a stone count — item 22's "I want
//   to increase Number of diamonds in Patterns" — against the arrangements
//   (scatter / cluster / halo / rosette) where re-sampling would just make a
//   different arrangement. `buildFreePattern` gains `spanDeg` for the other half
//   ("in wave it goes extremely left to right"). Both default to the stamp's own
//   values, so no untouched stamp and no saved design moves. `FreeStone.sizeScale`
//   emits a stamp's graduation as a MULTIPLE of the run's stone size instead of a
//   baked carat, so the Stone Size slider drives a stamped arrangement again;
//   `sizeCt` still wins where a caller wants absolute carats. All additive, no
//   new method names.
// v53 (item 22, "our wave looks wrong"): `MEASURED_FREE_PATTERNS` re-measured
//   off the reference's LIVE ring instead of its tile artwork. v43 recovered the
//   right shapes but converted them with two wrong scales — it stretched each
//   tile's 96-unit height to fill the whole ring (Wave 316.5 deg against a
//   measured 95.95, Halo 57.8 against 17.5) and normalised the tiles' absolute
//   0.5-1.8 mm stone diameters to a peak of 1.0. Stamped patterns therefore came
//   out ~3.3x too spread, ~1.25x too shallow across the width, and with every
//   stone at or below the run's nominal size. The stamps are DATA, and stamping
//   is a one-shot action that writes `freeStones`, so no saved design changes
//   meaning and no method name is new — but a host draws its pattern thumbnails
//   from this table, so the version moves to say the artwork it renders changed.
//   Raw numbers: viewer `docs/plans/ijewel-free-pattern-measurements.md`.
// v54 (channel cut controls): the Gems tab could offer only Cut Shape and Cut
//   Depth, because `setDiamond` re-derives `widthMM` / `heightMM` / `position` /
//   `diagonalDeg` / `centerAngleDeg` / `fullThrough` from the style on EVERY
//   call and the tab makes 27 of those calls — any slider over them was reset by
//   the next click. Guarding the whole re-stamp is not available: it breaks
//   `groove-coupling-check`'s "moving the row afterwards takes the trench with
//   it", which exists because the trench is a shadow of the run and not a stored
//   number. So ownership is now PER FIELD: `ChannelParams.manual` lists what the
//   user has taken over and `setDiamond` skips exactly those.
//   `position` and `enabled` are NOT lockable, on purpose — see
//   `ChannelManualField`.
//   A locked value is still CLAMPED on every rebuild (a width locked narrower
//   than a stone later dragged wider would carve through its own rail), so the
//   lock records intent, never an unsafe number.
//   New RPC `setChannelManual` — a brand-new method name, so gate on caps
//   `ringChannelManualV54`. `manual` is optional and absent means "all derived",
//   so no saved design changes meaning.
//   ⚠️ `heightMM`'s arc-limit gate was also corrected in this wave: it compared
//   an arc length in mm against the band's WIDTH across the finger (3.50 mm)
//   when the honest threshold is the CIRCUMFERENCE (65.66 mm on a size-7 1.80 mm
//   band), so every value between them silently carved all 360 deg. No shipping
//   design moves — every non-tension style writes the 100 sentinel — but seven
//   synthetic pins in `ring-geometry-check.mjs` were re-recorded deliberately.
// v55 (cross-channel cut controls): "cross channel like Channel need all edit
//   possible options". `cross-channel` is the one grooved style whose trench is
//   not a `ChannelParams` groove — that one runs AROUND the ring, a cross
//   channel runs ACROSS the band — so it could not borrow those rows and had no
//   rows of its own. Its whole shape was three numbers derived inside
//   `crossChannelCarve`: a centre from `placementDeg`, a half-angle from
//   `stoneSizeMM + 0.4`, and a depth from `min(thickness x 0.45, stone x 0.62)`.
//   No width, no shape, no position, no span, and nothing a user could reach.
//   `CrossChannelParams` gives it seven dimensions in its OWN geometry, with the
//   same per-field ownership `ChannelParams.manual` introduced in v54 —
//   `CrossChannelManualField`. New RPCs `setCrossChannel` and
//   `setCrossChannelManual`, both brand-new names, so gate on caps
//   `ringCrossChannelV55`. `BandState.crossChannel` is optional and absent means
//   "every field derived", which is exactly what shipped before, so no saved
//   design changes meaning.
// v56 — the head gets an understructure, and a V tip.
//   Two new VALUES on existing setters, no new method names: `HeadUnderstructure`
//   gains `"cathedral-basket"` and `ProngTip` gains `"v"`. RPC therefore cannot
//   hang on an older viewer, but the host must not OFFER options the viewer
//   cannot build, so the playground gates both rows on caps `ringHeadV56`.
//   Everything else in that wave — the collet under every head, the girdle-polygon
//   seat solve, the heart's `girdleRadius` convention, the consolidated Band panel
//   and the (already-implemented) bridge-accent row — rides existing methods and
//   changes no types. Saved designs keep their meaning; they re-render, because
//   the metal they describe was in the wrong place. See the wave's own note in
//   `docs/plans/`.
// v57 — the channel's cut DEPTH becomes ownable, so it can be derived.
//   `ChannelManualField` gains `"depthMM"`. The channel's depth was a flat
//   0.6 mm regardless of the stone while the reference scales it (0.299 d), and
//   it could not simply be derived: every field the style derives goes through a
//   helper that honours the user's v54 ownership, and `depthMM` was not in that
//   union — so deriving it by direct assignment would have made the Cut Depth
//   control INERT on the main channel styles.
//   This follows the union's own stated rule rather than excepting it: the
//   docblock above excludes `position`, `enabled` and `fullThrough` because they
//   are a shadow, a style decision and a structural mode selector. `depthMM` is
//   a DIMENSION, exactly like `widthMM` and `heightMM`, and its cross-channel
//   twin has been ownable since v55. Its absence was an omission.
//   No new method name — `setChannelManual` is v54 and already iterates the
//   patch generically — so nothing can hang on an older viewer, which would
//   accept the field and store it but not derive it. The playground gates only
//   the derived/reset affordance, on caps `ringChannelDepthV57`.
// v58 — a scene node becomes inspectable, not just movable.
//   `NodeDetails` + `NodeGeometryInfo`, and five methods: `getNodeDetails`,
//   `setNodeTransform`, `setNodeFlags`, `renameNode`, `getNodeGeometryInfo`.
//   The reference's Picking tab grows a per-mesh section and a Geometry section
//   when something is selected, and we could not build either: there was NO
//   getter for a node's transform in any form. `setObjectRotation` is
//   write-only and rotation-only; position and scale had neither a read nor a
//   write. The one place a full transform crossed the bridge was the
//   `transformChanged` event, which is push-only and fires solely on gizmo
//   drag-end — so a panel opened on a fresh selection had nothing to show.
//   Likewise `castShadow` existed only on `SceneLightState` (a LIGHT), and
//   `receiveShadow` / `frustumCulled` / `renderOrder` were absent entirely.
//   `setNodeTransform` supersedes `setObjectRotation`, which STAYS — the
//   Modifiers object-rotation control writes through it — and now delegates to
//   the same internal path so the two cannot drift.
//   Deliberately NOT included: duplicate, wrap-in-parent and a per-node delete.
//   Cloning a mesh clones its material, and every gem needs its own
//   ShaderMaterial with `updateDiamondUniforms` wired to `onBeforeRender`, so a
//   naive clone renders wrong in motion; a per-node delete has to honour the
//   disposal discipline or it leaks baked gem cubemaps. Both want their own
//   wave. Draw calls are absent for a different reason: `renderer.info` is
//   per-frame and scene-wide, so there is no honest per-node number.
//   FIVE brand-new method names — an older viewer answers `Unknown method` and
//   RPC has no timeout, so the playground MUST gate these on caps
//   `ringNodeInspectorV58`. Purely additive: no existing field changes meaning
//   and no saved design renders differently.
// v59 — depth of field focuses on a POINT, and the click that sets it exists.
//   `DofSettings` gains `enableEdit` (arm the next canvas click) and
//   `focalPoint: Vec3 | null` (the world point to hold in focus).
//   The reference has had a DOF "Enable Edit" mode all along and we could not
//   build it: no viewer event carries a screen or world coordinate —
//   `selectionChanged` and `hoverChanged` carry a groupId and nothing else, and
//   `freeStoneSelectionChanged`'s own docblock says why ("the canvas lives in
//   the iframe, so a click never reaches the host to ask about"). `setDofFocus`
//   existed, but its only caller hardcoded the screen centre.
//   The deeper gap was not the click. We stored a focal DISTANCE, so the focal
//   plane was pinned in front of the camera and whatever you focused on drifted
//   out of focus the moment you orbited. `focalPoint` is re-projected to a
//   distance every frame — which is what the reference does, and why it stores
//   a point.
//   Rejected: a host-side click-catcher over the canvas. It needs no contract
//   change, but the host cannot see a click that lands on a cross-origin
//   iframe, so the overlay has to sit on top — taking orbit away for as long as
//   the mode is armed. The viewer's own pointer handler already separates a
//   click from a drag by 3px, so arming it there costs nothing.
//   No new method name — both fields ride the existing `setPostFX` patch — so
//   nothing can hang on an older viewer and NO caps flag is needed. Purely
//   additive: `enableEdit` absent ⇒ false, `focalPoint` absent ⇒ null, both of
//   which are today's behaviour, so no saved scene changes meaning.
//   Documentation only, same wave: `setDofFocus`'s docblock claimed it
//   "triggers a crossfade" — there is none in the pipeline and never was — and
//   `SimpleTextConfig.boxWidth`/`boxHeight` plus
//   `ParallaxCameraConfig.enableEdit`/`cameraView` are now marked STORED, NEVER
//   READ where they are declared, rather than removed.
// v60 — the viewport splits four ways.
//   `ViewportLayout` ("single" | "quad"), `ViewportPaneId`, `ViewportPaneMode`
//   ("top" | "front" | "right" | "perspective"), `ViewportPaneState` and
//   `ViewportLayoutState`, plus `setViewportLayout` / `getViewportLayout` /
//   `setViewportPaneMode` / `setActiveViewportPane` and a
//   `viewportLayoutChanged` event.
//   Four panes in ONE canvas, scissored. Four iframes was the obvious shape and
//   is not possible: the host creates its `ViewerClient` in an effect keyed on
//   the iframe src and captures the element once, four iframes would be four
//   WebGL contexts and four model loads, and `window.viewerClient` is a single
//   global that the last host to mount wins.
//   The three locked panes are ORTHOGRAPHIC and carry their own cameras. The
//   main `CameraController` cannot serve them: it clamps polar angle away from
//   the poles because OrbitControls' spherical parameterisation is singular
//   there, so a true straight-down Top view is unreachable by construction.
//   Rotation is simply off in those panes, so the singularity never arises.
//   Each pane owns its own `FramePipeline`. One pipeline with a swappable
//   camera would cross-contaminate `accumFrame`, the accumulation ping-pong,
//   TAA history, `VelocityPass._prevViewProj` and the SSR temporal buffer four
//   times per frame. Four quarter-area pipelines hold about the same total
//   render-target pixels as today's one full-canvas pipeline, so the memory is
//   roughly a wash and the temporal separation comes for free.
//   FOUR brand-new method names — an older viewer answers `Unknown method` and
//   RPC has no timeout, so the playground MUST gate these on caps
//   `viewportQuadV60`. Purely additive: the viewer boots in `single`, which is
//   the original render path unchanged, so no saved scene renders differently.
// v61 — an axis-locked pane ORBITS about its axis instead of standing still.
//   `ViewportPaneMode` is now "x" | "y" | "z" | "perspective", replacing
//   "top" | "front" | "right" | "perspective".
//   v60's locked panes were fixed Top / Front / Right views that could pan and
//   zoom but never rotate. A locked pane now rotates about ONE world axis and
//   only that axis: the camera stays on a single ring of orbit and elevation
//   never changes, which is the constraint a CAD user means by "locked to X".
//   The mechanism is OrbitControls' own: its azimuth rotation IS rotation about
//   `camera.up`, so the pane sets `up` to its lock axis and pins polar angle to
//   PI/2 top and bottom. The equator is chosen deliberately — `makeSafe()`
//   clamps phi away from both poles, so a min===max clamp near one would fight
//   it every frame, which is exactly the shape of the v60 shaking.
//   NOT purely additive: the three old values are gone. Nothing persisted them —
//   the host made pane modes session-only — and no saved design carries a pane
//   mode, so no stored state changes meaning. No new method NAME either:
//   `setViewportPaneMode` already exists, only its accepted VALUES change. That
//   is the v56 situation (new values on an existing setter): RPC cannot hang,
//   but an older viewer cannot build them, so the playground gates the rows on
//   caps `viewportAxisLockV61`.
// v62 — gem inclusions become webgi's, not just ours.
//   `GemInclusionParams` gains the whole iJewel "Inclusions" panel: three
//   interior maps (`inclusionMapUrl`, `inclusionNormalMapUrl`,
//   `inclusionRoughnessMapUrl`, tiled by `inclusionScale`, normal strength
//   `incNormalScale`), a crown surface layer (`normalMapUrl` +
//   `surfaceBumpScale`, `roughnessMapUrl` + `surfaceRoughness`, tiled by
//   `surfaceScale`), the cloudiness controls (`blurRadius`, `variableBlur`,
//   `variableParam1`/`variableParam2`, `clampHDR`) and a body colour map
//   (`colorMapUrl` + `colorMapScale`). Ported from webgi 0.17's
//   GemRefractionPlugin, which the 46 inclusion presets of iJewel's library are
//   authored against.
//   Until now a `.dmat` from that library imported with NO inclusions at all:
//   its block lives at `userData._gemInclusions`, its maps are `{uuid}`
//   references into `textures[]`, and the old reader looked for neither — and
//   it read `useInclusion` (false in every one of those files) as the enable
//   flag, where webgi's is `hasInclusions`.
//   The v32 fields keep their meaning, because they are a DIFFERENT layer:
//   `mapUrl` is an ADDITIVE speckle (bright flecks on black — the in-house
//   /gem-inclusions/ maps), where webgi's `inclusionMapUrl` MULTIPLIES the
//   light (white is clear, dark is a flaw). One map through both formulas
//   would turn every saved speckle design black, so they are two fields and
//   both layers can be on at once. Likewise `bumpScale` stays the speckle
//   weight; webgi's `bumpScale` is `surfaceBumpScale` here. `scale` is now an
//   alias of `inclusionScale` (one tiling for every interior map; the getter
//   reports both).
//   `getGroupGemInclusions` now returns every field, defaults filled in, and
//   applying a `.dmat` REPLACES a group's inclusions (a file without any turns
//   them off) the way loading a material in webgi does.
//   No new method names — everything rides `setGroupGemInclusions` /
//   `getGroupGemInclusions`, so no caps flag is needed to avoid a hang; a host
//   that wants to hide the new rows from an older viewer gates on
//   `contractVersion >= 62`. Purely additive for saved scenes: every new field
//   absent is today's render, and with inclusions off the gem shader compiles
//   to exactly the pre-v62 program.
// v63 — On Body: the piece is FITTED to the photograph, not floated over it.
//   `placeOnBody` / `clearOnBody` / `setBodyOccluder` / `getBodyOccluder` /
//   `getModelFit`, the types `OnBodyKind`, `OnBodyAnchor`, `OnBodyParams`,
//   `OnBodyPlacement`, `BodyOccluderConfig` and `ModelFit`, and the reserved
//   node id `MODEL_ROOT_NODE_ID`.
//   Until now picking a hand photo only set `scene.background`. The camera kept
//   its fit-to-everything, so the ring floated at full frame over the hand, the
//   back of the band was drawn across the finger, and the ground shadow and
//   reflection still rendered under it. Now the photo anchor (knuckles, ring
//   centre, finger width) is mapped through the LIVE backdrop framing into the
//   view, the ring's hole axis is laid along the finger at the finger's width
//   x `fit`, and a depth-only finger cylinder hides the far half of the band.
//   The cylinder never writes colour, so the photo shows through it, and the
//   frame pipeline treats its pixels as background end to end: they are absent
//   from the G-buffer (so no AO, SSR or contact shadow reads them), they are
//   background to the composite (so `tonemapBackground: false` keeps the photo
//   exact there) and they carry no velocity.
//   The model is moved through the v58 `setNodeTransform` path, on the model
//   CONTAINER, under the reserved id `MODEL_ROOT_NODE_ID` — so
//   `getNodeDetails(MODEL_ROOT_NODE_ID)` reads back exactly what was placed and
//   the host nudges with a setter it already has. A canvas resize re-maps the
//   placement (a cover fit crops differently at every aspect) and carries the
//   host's nudge and occluder edits along with it; the result is announced on
//   the existing `transformChanged` event.
//   While On Body is on: the camera is a fixed front view (fov 25, controls
//   off), the ground plane, its reflection and the baked shadow are off, and
//   the split viewport is refused (`setViewportLayout("quad")` stays `single`).
//   `clearOnBody` restores every one of those. None of it is persisted:
//   `getSceneState` reports the pre-On-Body camera and ground, and the occluder
//   lives outside the model container, so it is in no export, no material
//   group, no scene tree, no camera fit, no pick and no ground-shadow bake.
//   FIVE brand-new method NAMES, so the playground gates them on caps
//   `onBodyV63` (RPC has no timeout). Purely additive: with On Body off no new
//   object is in the scene and every render path is the pre-v63 one.
// v64 — On Body sits the ring ON the finger: where it goes, which way it turns,
//   and the shadow it casts on the skin.
//   Reported: the ring sat on the knuckle where the finger meets the hand, read
//   wider than the finger, looked like a flat bar laid on top, and placing it by
//   hand made it huge. Two of those were the anchor (host-side: MediaPipe's MCP
//   landmark is INSIDE the hand, so seat 0.5 was the finger's base, and the
//   width was measured there, where the finger merges into the hand); two were
//   the viewer: the head always faced the camera and nothing tied the band to
//   the skin.
//   Additive fields only, no new method names (so no caps flag):
//   - `OnBodyAnchor.along` (0..2 along p0 → p1; iJewel's "Along Finger",
//     default 0.65) — supersedes `OnBodyParams.seat` and `center`'s position
//     along the bone, keeps its sideways offset;
//   - `OnBodyAnchor.axisWorld` — the finger's direction from MediaPipe WORLD
//     landmarks (x right, y down, z away): its pitch out of the picture, as
//     the median of it, `axis3` and zero (the two depth cues often disagree);
//   - `OnBodyAnchor.dorsal` — the back-of-hand normal, same frame: the head
//     turns toward it (perpendicular to the finger), so on a palm photo the
//     head goes behind the finger;
//   - `OnBodyParams.headToCamera` — the v63 "head faces the camera" rule, on
//     request;
//   - `BodyOccluderConfig.contactShadow` (0..1, default 0.5) — a soft, analytic
//     contact shadow on the photo's skin either side of the band, drawn only on
//     the occluder's own pixels. With no occluder nothing new is in the scene,
//     so every plain render is the pre-v64 one, byte for byte.
//   A v63 host sends none of these and gets v63 placement, except that the
//   contact shadow is on by default while an occluder is placed.
//   Also: `AutoAssignInfo`'s docstring stopped claiming name matching (it never
//   matched a name; `count` is the group count).
// v65 — the whole scene in one file, and a ring GLB that carries its scene.
//   Every setting except the ring itself can now be saved to one file and
//   opened in one go. `exportVjson` (of-vjson/1) could not do it: it saved no
//   diamond environment, a scene env from a file as a dead `blob:` link, ten of
//   ~45 metal fields and none of a library metal's textures, nothing of LUTs,
//   the ground panel, the camera controls, animation or the loading screen —
//   and its import only overwrote the fields the file named, returning before
//   its own loads had finished.
//   The new format is `of-scene/2` (`OF_SCENE_SCHEMA`). Its container is a
//   standard GLB: the document sits at the root `extensions.OF_scene`
//   (`OF_SCENE_EXTENSION`, listed in `extensionsUsed` only, so every other glTF
//   tool still opens the file) and each asset's ORIGINAL bytes sit in the BIN
//   chunk — an environment is never re-encoded (RGBE is lossy for EXR). A
//   `.jscene` is an empty glTF scene carrying the block (`kind: "scene"`); a
//   "Ring + scene" `.glb` is the exported ring with the same block
//   (`kind: "ring-scene"`, `GlbExportOptions.embedScene`).
//   FOUR brand-new method NAMES — `exportSceneFile`, `exportSceneJSON`,
//   `importScene`, `inspectSceneFile` — so the playground gates them on caps
//   `sceneFileV65` (RPC has no timeout). They are named `export*` on purpose:
//   the configurator-preview guard already covers that prefix.
//   `importScene` REPLACES by default: a field the file does not name returns to
//   the boot baseline, so the result matches the file exactly, and it resolves
//   only once every load it started has landed. Device-specific values (quality
//   tier, pixel ratio, gem bounce cap) stay the device's own unless asked.
//   Material groups are matched exact id → label/mesh names → kind, and the
//   result reports the map so a host can remap its own per-group settings.
//   Additive fields: `LoadOptions.embeddedScene` (default "apply" when adding a
//   model) and the same option on `swapModel` (default "ignore"),
//   `ModelInfo.embeddedScene` (the per-model status), `GlbExportOptions.
//   embedScene` / `sceneHost`, and the `sceneApplied` event.
//   Group ids became STABLE: the id a mesh is first grouped under is pinned on
//   it and read back by every later grouping, so it no longer changes when a
//   preset replaces a named material, and it survives a GLB round trip (the pin
//   rides as node extras). One deliberate change: a session that used to see a
//   group id drift after a material swap now keeps the first id.
//   `exportVjson`, of-vjson/1, scene presets and Looks keep their old merge
//   behaviour. Also fixed alongside, no contract change: `SceneState.envUrl` no
//   longer reports a dead `blob:` for a picked HDR; `metalEnvMaxBrightness`
//   `null` (JSON's Infinity) restores as "no cap"; `exportMetalJSON` writes the
//   authored env intensity, not the stamped one; a variation restores a gem's
//   `surfaceReflBoost` / `internalEnvBoost`. The `autoAssignAvailable` event
//   docstring stopped claiming name matches (v64 fixed `AutoAssignInfo`'s).
// v66 — CNC program export for the wedding band.
//   THREE brand-new method NAMES on `RingBuilderApi` (not `RingApi`, whose
//   get* methods the scene-file coverage guard enumerates): `exportBandCnc`,
//   `previewBandCnc`, `getCncDefaultProfile` — gate them on caps `ringCncV66`.
//   `exportBandCnc` returns the active ring's program in the shop's madCAM-
//   posted 4-axis rotary dialect (`CncExportResult`: the .NC, its air-cut twin,
//   a setup sheet, the gate's report, what was not machined and why). When
//   EITHER the program's gate or its air cut's gate fails, `ok` is false and
//   BOTH `nc` and `airCut` are absent. `previewBandCnc` draws its toolpaths
//   over the ring (outside the model container, like the On Body occluder)
//   and returns the report. New types in "CNC program export (v66)": the
//   machine profile (`CncMachineProfile` and its parts, plain JSON a host may
//   store and edit), the report (`CncReport`, `CncCheck`, `CncCycleTime`),
//   `CncExportOptions` / `CncExportResult` / `CncPreviewOptions`.
//   Also in v66, all additive:
//   · the `.cam.3dm` — `CncExportOptions.cam3dm` (omit ⇒ true) and
//     `CncExportResult.cam3dm` / `cam3dmFileName`: Rhino reference geometry
//     for madCAM, never a program, issued even when the gate fails (then
//     with no tool-centre path and a visible GATE FAILED marker);
//   · batch sizes — a FOURTH new method name, `exportBandCncBatch`
//     (`CncBatchOptions` → `CncBatchResult` of `CncBatchItem`s, each with
//     optional `notes` for what is not a gate check), gated on the same
//     `ringCncV66` cap since v66 never shipped without it;
//   · path grooves — `GrooveDirection` gains "path", `DesignGroove` gains
//     `path` (`DesignGroovePath`, arc measured from 12 o'clock = C0, with a
//     path-wide `cornerRadiusMM` and an optional per-corner `radiusMM`) and
//     `preset` (`DesignGroovePreset`, `BypassShoulderParams`), the pure
//     `bypassShoulderGrooves()` generator, and `clampDesignGrooves` keeps both
//     (a list without them normalises exactly as before);
//   · shaped seats — `CncSeatStandard.cut` and `.clearanceMM`: a row for a
//     non-round cut (princess, oval, pear …), whose seat is a pocket round
//     the stone's girdle outline cut with the flat end mill `seatToolId`; a
//     row without `cut` is the round row it always was;
//   · `CncExportResult.seatStandards` — the seat rows a program was planned
//     from, derived ones included, so a host can offer a `seat.derived` row
//     to the shop prefilled rather than typed from nothing;
//   · docstrings only, no behaviour change: the withholding rule above, a
//     VERTICAL `DesignGroove.positionMM` (measured from 3 o'clock, as the
//     engine has always carved it — not the 12 o'clock the v41 text claimed),
//     `previewBandCnc`'s resolve value, and `getAutoAssignInfo` /
//     `autoAssignAvailable` (no dismiss call exists; the event is never sent).
//   No existing method, field or default changed.
// v67 — apply a whole design in ONE rebuild (band designs round, W1).
//   TWO brand-new method NAMES on `RingBuilderApi`: `applyRingDesign(patch)`
//   and `loadRingState(state)` — gate both on caps `ringDesignV67`.
//   `applyRingDesign` runs the existing setters, in the host replay's order,
//   inside one engine-side batch (one rebuild, one `ringStateChanged`), so every
//   clamp and derivation still runs. Its patch type `RingDesignPatch` EXCLUDES
//   the wearer fields (`RING_WEARER_FIELDS`: ring size US/EU, engraving text,
//   the engagement block, `ringType`/`ringName`, spec), and the engine drops
//   them at runtime too, because the live link mirrors it by name.
//   `loadRingState` = the design half through `applyRingDesign` (mirrored when
//   linked) + the wearer fields on the active ring only, in the same batch —
//   what undo, `?design=` and a share link restore with. `addRing(state)` now
//   goes through the same path, so a seeded ring is clamped and its channel
//   derived (it was `Object.assign`ed past both).
//   Additive, all optional (absent ⇒ exactly today's ring):
//   · `DiamondSetting` gains "pave" (shared beads at each stone junction) and
//     "shared-prong" (one claw shared by two neighbours);
//   · `DiamondConfig.junctionScale` (0.5 … 1.6, bead/claw size as a multiple
//     of the law's; absent ⇒ 1);
//   · a `DesignGroove`'s width floor is 0.15 mm (was 0.25; the separation
//     groove's too). A list that was legal before normalises exactly as it
//     did. The bypass-shoulder preset keeps its own 0.25 mm floor.
// v67 (cont.) — band designs W2–W5 (seven lanes, merged in one integration).
//   Still NO new method name beyond W1's two: every addition below travels
//   through `applyRingDesign` / `loadRingState` / `setDiamond` /
//   `setEngraving` / `setBandLayout` / `Catalogs`, so the existing
//   `ringDesignV67` cap gates it. All optional; absent ⇒ today's ring.
//   · Stone patterns: `DiamondStyle` gains "pattern"; `BandState.stonePattern`
//     (`StonePattern` = 1–4 `StonePatternLayer`s + an optional bookkeeping
//     `libraryId`, always stored through `clampStonePattern`) is read only by
//     that style. Pure, deterministic generator beside `buildFreePattern`:
//     `buildStonePattern`, `expandStonePattern` (≤ 1000 stones), `pocketOutline`
//     / `pocketPath`, `clampStonePattern(Layer)`, `resolveStonePatternLayer`,
//     `stonePatternIssues`, `stonePatternTightestPair`, `patternOutlineHolds`,
//     `pointInPatternOutline`, `patternSin`, `patternRng`,
//     `surpriseStonePattern(seed, band)`, `stonePatternFromLibrary(id)`; data
//     `STONE_PATTERN_LIBRARY` (349 entries, ids stable forever, golden-pinned),
//     `STONE_PATTERN_CATEGORIES` (27), `STONE_PATTERN_SETTINGS`, and the
//     STONE_PATTERN_* limits. Engine expansion of the style is NOT wired yet.
//   · Parametric section: `PARAM_PROFILE_ID` ("param") + `BandState.paramProfile`
//     (`ParamProfile`: crown, flat top, edge radius crisp→round, 45° bevel,
//     comfort, one-sided `ParamProfileStep`), `PARAM_PROFILE_DEFAULTS`,
//     `clampParamProfile(p, widthMM, thicknessMM)`; kept while another profile
//     is chosen.
//   · `DesignGroove.vAngleDeg` (30…150): a true V of that included angle (the
//     clamp sets jointType "v60" and derives the depth).
//   · `PartialStepParams` / `PARTIAL_STEP_DEFAULTS` / `partialStepGrooves()`:
//     a step that stops part way (one square path groove the engine carves as
//     a POCKET); `DesignGroovePreset` gains kind "partialStep".
//   · `StoneSpacing` gains "close": max(1.03 d, d + 0.062 mm), for pavé and
//     shared claws (listed in `Catalogs.stoneSpacings`).
//   · `EngravingConfig.logo` (`EngravingLogo` | null, `EngravingLogoMode`,
//     `ENGRAVING_LOGO_SVG_MAX_CHARS`): an inside-the-band mark beside the
//     inscription or instead of it — whitelisted path data drawn with Path2D,
//     or a byte-sniffed CORS raster. Personal: never mirrored by the live
//     link. A restore without it clears it. Listed by the CNC as inside
//     engraving (not machined).
//   · `BandLayoutPreset` gains "catalogue" (listed in `Catalogs.bandLayouts`):
//     ring 0 in front tipped to a raised three-quarter camera, the partner
//     behind on the same floor, leaning about the VIEW axis, overlapping in the
//     picture but clear in 3-D; selecting it frames the set.
//   Deliberately non-neutral (all designs are unshipped v67 or are fixes):
//   a horizontal Design groove that runs off a band edge gets the side-wall
//   ramp (the old fold is gone); shared-prong claws stand at the row's edges
//   (never within 0.25 d of its centreline) and their stones sit at the band
//   surface; any layout change now refits the ground and re-bakes its shadow;
//   a stone on a groove/pocket floor gets a CNC seat cut FROM that floor
//   (CNC_GENERATOR_VERSION stays of-cnc/3 — no shipped design's bytes move).
//   Kernel-only (no contract surface): convex filleted polygon pockets
//   (`RingGeometryParams.pockets` / `FloorSource`, `src/band/pocketPolygon.ts`).
// v68 — the photographed pose (band designs round, ref-1 photo match).
//   ONE brand-new method NAME: `RingBuilderApi.getBandLayout()` — gate on caps
//   `ringPoseV68`. Everything else rides existing names; all optional, and
//   every absent field is exactly today's behaviour:
//   · `setBandLayout(preset, pose?)` on `RingBuilderApi` and `BandApi`: an
//     optional `BandLayoutPose` (`BandLayoutRingPose` per visible ring —
//     standing turn/lean/spin, catalogue tilt/yaw/roll/rise, side/depth × D0 —
//     plus `BandLayoutCamera` elevation/azimuth/fov/fill/centre, `clearanceD0`
//     and `mirror`), stored through the pure `clampBandLayoutPose` and
//     re-applied on every rebuild. A call without one clears it (the preset's
//     law, bit-identical to v67). `BandLayoutInfo` is what `getBandLayout`
//     returns.
//   · `DiamondConfig.junctionOptions` (`JunctionOptions` {sides, ends, grow}):
//     which pavé beads / shared claws a run builds. Absent ⇒ both sides, end
//     pieces, beads that grow — today's hardware. A restore without it clears it.
//   · `GrooveSurface` gains "shadow": a polished cut drawn with the dark core a
//     narrow polished V shows in a studio photograph (its own material layer,
//     like matt / sand, keeping a fraction of the env reflection). The CNC
//     treats it as polished. Absent ⇒ "polished", as before.
//   · `StonePattern.seatRimMM` (0 … 0.25, `STONE_PATTERN_SEAT_RIM_MAX_MM`): the
//     outer-face metal a pattern keeps beside each seat. Absent ⇒ 0.25, the
//     engine's `MIN_SEAT_RIM_MM`, i.e. today's placement; lower lets an edge
//     row hug the band edge (ref-1's stones run to the rim), with a spec
//     warning. 0 … 0.25: the seat never leaves the outer face. Down to −0.6
//     (`STONE_PATTERN_SEAT_RIM_MIN_MM`, critic round 2): the row may run OVER
//     the edge — each seat is cut only on the outer face, clipped where it
//     ends, the girdle stands over the rounded edge, and the warning says so.
//     `StonePatternBand.edgeMarginMM` may be negative to match (callers never
//     passed one below 0.15 before, so no generated pattern moves).
//   · `setGroundReflectionBlur(v)` / `SceneState.groundReflectionBlur` accept
//     up to 4 (critic round 3: the catalogue photo's floor ghost is far softer
//     than our fully-blurred mirror): above 1 the mirror is frosted further by
//     extra blur passes. 0 … 1 is untouched (it was clamped to 1 before, so no
//     saved scene holds more).
//   · `LutSettings.lutGround` (optional): false keeps the colour grade off the
//     ground plane, as `lutBackground` keeps it off the background (a grade
//     with an L* shoulder otherwise greys a white floor out to its far edge).
//     Absent ⇒ graded, as before.
//   No existing method, field or default changed; no saved design re-renders.
// v68 is ONE version holding two waves, made in parallel and merged before
// either shipped: the photographed pose above and the HDR editor below. Their
// surfaces do not overlap (caps: `ringPoseV68`, with its aliases `ringLookV68`
// and `ringJunctionV68`; `hdrPickV68`, `hdrEnvPixelsV68`,
// `hdrStonesShareSceneV68`).
// v68 (HDR editor) — TWO new method names, for HDR Studio (`/dev/hdr`).
//   · `RingApi.pickSurface(nx, ny)` → `SurfaceHit | null`: what the view ray
//     through a normalised canvas point (0..1, y down) hits on the loaded
//     model — the world point, the interpolated world normal (facing the
//     camera), the view direction and its mirror reflection, plus `envSlot` /
//     `envDir`: which environment shades that surface ("scene" | "metal" |
//     "diamond") and the reflection expressed in THAT texture's own frame, with
//     every rotation in force removed (scene / metal-HDR `envMapRotation`,
//     three's negated-Euler lookup, the gem shader's `envRotation` uniform and
//     the env-follow-camera offset). A host converts `envDir` to an equirect
//     position with three's convention (u = atan2(z, x)/2π + 0.5,
//     v = asin(y)/π + 0.5) to put a light exactly where the click will show it.
//   · Honours the split viewport (the pane under the point and its camera) and
//     the On Body occluder (a point on the photo's finger hits nothing).
//   · A pure query: no event, no state, no render, nothing selected — gems and
//     their shader are untouched (diamonds stay bit-identical). Inert on older
//     viewers; the host gates the method NAME behind caps `hdrPickV68`.
//   · `RingApi.loadEnvFromPixels(px)` → the scene environment from raw
//     half-float RGBA pixels (`EnvPixels`, top row first) — the same texture
//     an .hdr through `loadEnvFromFile` becomes (HalfFloat, linear, flipY),
//     PMREM'd and installed exactly like it, but with no file: no blob fetch,
//     no decode, no hops. HDR Studio pushes its live map through it (a 1K push
//     measured ~4 async hops + a decode slower through the File path, each hop
//     waiting on a main thread the editor shares during a drag). No source
//     bytes are kept (`SceneState.envUrl` null, like a procedural preset).
//     NEW method name — caps `hdrEnvPixelsV68`.
//   · Fix the host relies on (caps `hdrStonesShareSceneV68`, no new name): with
//     `DiamondManagerSettings.forceSceneEnvironment` on, every scene-env load
//     re-points the stones at the NEW equirect; before, the manager disposed
//     the one they sampled (three re-uploaded it: one leaked texture per load)
//     and they kept showing the old map. Off, nothing changes.
//   · New types `SurfaceHit` and `EnvPixels` (declared just below
//     CONTRACT_VERSION, so this wave's contract text is two contiguous hunks).
export const CONTRACT_VERSION = 68;

/** v68 (HDR editor): what the view ray through a normalised canvas point hits. */
export interface SurfaceHit {
  /** World-space hit point. */
  point: Vec3;
  /** World-space interpolated normal, unit length, facing the camera. */
  normal: Vec3;
  /** Unit view direction, camera → point. */
  viewDir: Vec3;
  /** Unit mirror reflection: viewDir − 2(viewDir·normal)·normal. Where a light
   *  must sit (in world space) for this surface to show its highlight here. */
  reflectDir: Vec3;
  /** Which environment shades this surface, and `reflectDir` expressed in THAT
   *  texture's own frame (every rotation in force — scene / metal / diamond
   *  rotation and the follow-camera offset — removed), three.js equirect
   *  convention: u = atan2(z, x)/2π + 0.5, v = asin(y)/π + 0.5 (v = 1 is the top
   *  row of the image). "scene" also covers the reflection probe (it bakes the
   *  scene env in world space). */
  envSlot: "scene" | "metal" | "diamond";
  envDir: Vec3;
  /** Material group of the hit mesh, when the model has a group index. */
  groupId: string | null;
  kind: "metal" | "gem" | "other";
}

/** v68 (HDR editor): an equirect environment as raw pixels. */
export interface EnvPixels {
  /** 2:1 is expected, not required; each side 1…16384. */
  width: number;
  height: number;
  /** IEEE half-float bits, RGBA interleaved, row 0 = the TOP of the map,
   *  width × height × 4 values. Alpha is ignored. */
  data: Uint16Array;
}

/**
 * Legal band width range, in mm.
 *
 * A wedding band goes to 12 mm, but an engagement shank carrying a head is not
 * manufacturable that wide — the standalone engagement builder capped it at 4 mm
 * and the merged builder keeps that limit whenever the head is attached.
 *
 * Shared so the host slider range and the engine clamp can never disagree:
 * `BandConfigurator` enforces it on both `setWidth` and `setEngagementEnabled`,
 * and the Size tab reads it for the control's max.
 */
export const BAND_WIDTH_MAX_MM = 12;
export const ENGAGEMENT_WIDTH_MAX_MM = 4;

/**
 * Maximum engraving length, in characters.
 *
 * Shared so the host's input limit and the engine's clamp cannot drift: the limit is a real
 * constraint on what fits inside the band, and `BandConfigurator.setEngravingText` enforces it
 * so an RPC caller cannot exceed what the UI allows.
 */
export const ENGRAVING_MAX_CHARS = 100;

/** v67: the most characters of path data `EngravingLogo.svg` may carry (16 KB). */
export const ENGRAVING_LOGO_SVG_MAX_CHARS = 16384;

/**
 * The engraving's 1× font size, in canvas pixels (v42).
 *
 * `EngravingConfig.fontSizePx` stays the wire value — this is only the point it
 * is measured from, so a host can offer the size the way a jeweller states it
 * ("1.25× the standard inscription") instead of as a raw pixel count on a
 * canvas the user never sees. Exported so the default in `DEFAULT_BAND_STATE`
 * and the host's multiplier cannot drift: at 1× the two agree by construction.
 */
export const ENGRAVING_BASE_FONT_PX = 48;

// ─── Primitive DTOs ────────────────────────────────────────────────────────

export interface Vec3 { x: number; y: number; z: number; }

export type MaterialKind = "metal" | "gem";
export type QualityMode = "low" | "interactive" | "high" | "ultra";
/** User-facing render-quality tier (v38) — what the Ring Builder's gear menu
 * offers. Coarser than `QualityMode` on purpose: one tier moves pixel ratio,
 * the progressive frame budget and SSR/SSAO resolution together. */
export type QualityTier = "medium" | "high" | "ultra";
export type CameraPreset = "front" | "back" | "left" | "right" | "top" | "threeQuarter";
export type ViewAngle = "default" | "front" | "side";

/** Opaque, JSON-serializable camera pose - round-tripped through the host. */
export type CameraPose = Record<string, unknown>;

export type AnimationEasing = "linear" | "easeIn" | "easeOut" | "easeInOut";
export type AnimationTarget = "model" | "camera" | "group" | "element";
export type AnimationProperty =
  | "position"
  | "rotation"
  | "scale"
  | "cameraTarget"
  | "groupOffset"
  | "groupRotation"
  | "groupScale"
  | "visibility"
  // Material properties (target "group" applies to all member meshes,
  // target "element" applies to a single mesh). gem* require a gem material,
  // metal* require a metal material.
  | "gemColor"
  | "gemBoost"
  | "gemOpacity"
  | "metalColor"
  | "metalRoughness"
  | "metalMetalness";

export interface AnimationKeyframe {
  time: number;
  value: number[];
  easing?: AnimationEasing;
}

export interface AnimationTrack {
  id: string;
  target: AnimationTarget;
  property: AnimationProperty;
  /** Required for group tracks, ignored otherwise. */
  targetId?: string;
  enabled?: boolean;
  keyframes: AnimationKeyframe[];
}

export interface AnimationTimeline {
  id?: string;
  name?: string;
  duration: number;
  tracks: AnimationTrack[];
}

export type AnimationPresetId =
  | "orbit360"
  | "ringFall"
  | "boxReveal"
  | "ringSplit"
  | "diamondMove"
  | "diamondRain";

export interface AnimationPlaybackState {
  playing: boolean;
  currentTime: number;
  duration: number;
  loop: boolean;
  playbackRate: number;
  timelineId: string | null;
}

// ─── GLTF clip animations (v15) ─────────────────────────────────────────────
// Baked clips that ship inside a .glb/.gltf (AnimationMixer-driven). Distinct
// from the host-authored keyframe timeline above; the two are mutually
// exclusive (loading a model clears the timeline).

export interface GltfAnimationInfo {
  index: number;
  name: string;
  duration: number; // seconds
}

export interface GltfPlaybackState {
  available: boolean; // the current model ships one or more clips
  clipCount: number;
  index: number;      // active clip, -1 if none selected
  name: string | null;
  playing: boolean;
  time: number;       // seconds into the active clip
  duration: number;   // active clip length (0 if none)
  loop: boolean;
  speed: number;
  /** Playback time scale −2..2 (negative = reverse) (v19, iJewel timeScale). */
  timeScale: number;
  /** Drive animation time from page scroll (v19, stored). */
  animateOnScroll: boolean;
  /** Drive animation time from pointer drag (v19, stored). */
  animateOnDrag: boolean;
  /** Auto-play when the model loads (v19). */
  autoplayOnLoad: boolean;
  /** Dev: sync to the longest clip duration (v19, stored). */
  syncMaxDuration: boolean;
  /** Advance clip time every frame even without input (webgi, default true) (v32). */
  autoIncrementTime: boolean;
  /** Which pointer-drag axis scrubs the clip (webgi, default "y") (v32). */
  dragAxis: "x" | "y";
  /** Inertial damping of drag scrubbing [0,1] (webgi, default 0.3) (v32). */
  dragAnimationDamping: number;
  /** Inertial damping of scroll scrubbing [0,1] (webgi, default 0.1) (v32). */
  scrollAnimationDamping: number;
}

/** GLTF playback options patch (v19). */
export interface GltfAnimationOptions {
  timeScale?: number;
  animateOnScroll?: boolean;
  animateOnDrag?: boolean;
  autoplayOnLoad?: boolean;
  syncMaxDuration?: boolean;
  /** Advance clip time every frame even without input (webgi, default true) (v32). */
  autoIncrementTime?: boolean;
  /** Which pointer-drag axis scrubs the clip (webgi, default "y") (v32). */
  dragAxis?: "x" | "y";
  /** Inertial damping of drag scrubbing (webgi, default 0.3) (v32). */
  dragAnimationDamping?: number;
  /** Inertial damping of scroll scrubbing (webgi, default 0.1) (v32). */
  scrollAnimationDamping?: number;
}

// ─── Camera views & turntable (v15) ─────────────────────────────────────────

export interface CameraView {
  id: string;
  name: string;
  pose: CameraPose;
}

export interface TurntableOptions {
  revolutions?: number; // default 1
  durationMs?: number;  // default 6000
  clockwise?: boolean;  // default true
}

/** Camera-views tour config (v19, iJewel CameraViews panel). */
export interface CameraViewsConfig {
  viewLooping: boolean;
  viewPauseTime: number; // ms pause at each view
  animDuration: number;  // ms transition between views
  rotationOffset: number;
  seekOnScroll: boolean;
}

// ─── Split viewport (v60) ───────────────────────────────────────────────────
// Four panes in ONE canvas, drawn with renderer scissor/viewport — not four
// iframes. A second iframe would mean a second WebGL context, a second model
// load, and a second `window.viewerClient`; the host keys its RPC client on the
// iframe src and captures the element once, so there can only ever be one.

export type ViewportLayout = "single" | "quad";

/** Fixed pane slots, clockwise from the top-left of the canvas. */
export type ViewportPaneId = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

/**
 * What a pane shows.
 *
 * `x` / `y` / `z` are AXIS-LOCKED orthographic views: the camera orbits the
 * model about that one world axis and nothing else. Dragging sweeps it around a
 * single ring of orbit; elevation never changes. `perspective` is the free view.
 *
 * ⚠️ v61 replaced `top` / `front` / `right` — fixed views that could pan and
 * zoom but never rotate — with this. The panes are still orthographic and still
 * carry their own `OrthographicCamera`, because `CameraController` clamps polar
 * angle away from the poles (OrbitControls is singular there) and cannot serve
 * them.
 */
export type ViewportPaneMode = "x" | "y" | "z" | "perspective";

export interface ViewportPaneState {
  id: ViewportPaneId;
  mode: ViewportPaneMode;
  /** `false` once the pane has been switched to `perspective`. Derived from
   *  `mode`, carried explicitly so a host toggle does not have to re-derive it. */
  locked: boolean;
}

export interface ViewportLayoutState {
  layout: ViewportLayout;
  /** The pane that owns picking, the transform gizmo and the ground mirror.
   *  Follows the pointer: clicking in a pane makes it active. */
  active: ViewportPaneId;
  panes: ViewportPaneState[];
}

// ─── Interaction prompt (v15) ───────────────────────────────────────────────
// A viewer-side "drag to rotate" overlay. Lives in the iframe because pointer
// events land there; hidden on first pointer interaction.

export interface InteractionPromptSettings {
  enabled: boolean;
  text: string;
  showOnLoad: boolean;  // appear shortly after a model loads
  idleDelay: number;    // seconds of inactivity before re-showing (0 = never)
  /** Full iJewel param set (v19) — wired where feasible, else stored. */
  animationDuration: number;       // ms
  animationDistance: number;       // px
  animationPauseDuration: number;  // ms between loops
  rotationDistance: number;        // demo-rotate amount
  yOffset: number;
  autoStart: boolean;
  autoStartDelay: number;          // ms idle before showing
  autoStop: boolean;
  autoStartOnObjectLoad: boolean;
  autoStartOnObjectLoadDelay: number;
}

export interface AnimationRecordOptions {
  width?: number;
  height?: number;
  fps?: number;
  bitrate?: number;
  mimeType?: string;
  transparent?: boolean;
}

export interface LoaderConfig {
  logoUrl?: string;
  text?: string;
  blur?: number;
  bgColor?: string;
  spinnerColor?: string;
  // Full iJewel LoadingScreen fields (v24) — wired where the overlay supports
  // them (text/colors/blur/opacity/logo), else stored & round-tripped so the UI
  // is byte-faithful to iJewel.
  enabled?: boolean;
  loadingTextHeader?: string;
  errorTextHeader?: string;
  showFileNames?: boolean;
  showProcessStates?: boolean;
  showProgress?: boolean;
  hideOnOnlyErrors?: boolean;
  hideOnFilesLoad?: boolean;
  hideOnSceneObjectLoad?: boolean;
  minimizeOnSceneObjectLoad?: boolean;
  showOnFilesLoading?: boolean;
  showOnSceneEmpty?: boolean;
  hideDelay?: number;
  backgroundOpacity?: number;
  backgroundBlur?: number;
  background?: string;       // overlay background color (iJewel key)
  textColor?: string;
  logoImage?: string;        // data-URI / URL (iJewel key, == logoUrl)
}

/** Cross-fade-on-change toggle (iJewel FrameFade, v24). */
export interface FrameFadeConfig { enabled: boolean; }

/** S3 upload connection (iJewel AWSClientPlugin, v24). Backend-bound — fields
 * store locally and the action is best-effort/no-op without a server. */
export interface S3Config {
  accessKeyId: string;
  accessKeySecret: string;
  endpointURL: string;
  pathPrefix: string;
  remember: boolean;
  connected: boolean;
}

export interface ModelInfo {
  id: string;
  name: string;
  /** v65 — present when the model's file carried a scene (`OF_scene`, a
   *  "Ring + scene" GLB): whether that scene is still to be applied, was
   *  applied, was skipped by the load's `embeddedScene` option, or failed.
   *  Absent for a file with no scene in it. */
  embeddedScene?: EmbeddedSceneStatus;
}

/** Post-load normalization for non-glTF / CAD imports (v15). Applied before the
 * model enters the scene; glTF jewelry assets generally need none of these. */
export interface LoadOptions {
  /** Recenter the model's bounding box at the world origin. */
  autoCenter?: boolean;
  /** Uniformly scale so the bounding-sphere radius ≈ autoScaleRadius. Useful for
   * CAD exports (OBJ/STL/FBX) that arrive in mm or arbitrary units. */
  autoScale?: boolean;
  autoScaleRadius?: number; // default 2
  /** v65 — what to do with a scene the file carries (a "Ring + scene" GLB).
   *  Default "apply" for `addModelUrl`/`addModelFile`/`addModelFiles`, which
   *  then resolve once it is applied — as a replace import (fields the file
   *  does not name return to the boot baseline), its materials matched to the
   *  model's own parts by group id, then label, never by kind, so a model
   *  already loaded beside it keeps its materials. It is applied ONCE: removing
   *  or adding other models later never re-applies it. A failed apply does not
   *  fail the add — `ModelInfo.embeddedScene` says "failed". A host that
   *  restores its own saved scene right after the load passes "ignore". (Boot
   *  `?model=` applies it too, unless `?vjson` or `?config` is given.) */
  embeddedScene?: EmbeddedSceneMode;
}

/** Tiled high-resolution still export (v15). Renders the frame in tiles via
 * camera view-offset so output can exceed the GPU max-renderbuffer size. */
export interface TiledSnapshotOptions {
  width: number;
  height: number;
  tilesX?: number;       // default ceil(width / 2048)
  tilesY?: number;       // default ceil(height / 2048)
  framesPerTile?: number; // accumulation frames per tile, default 24
  format?: "png" | "jpeg" | "webp";
  quality?: number;       // 0..1 for jpeg/webp
  transparent?: boolean;
  crop?: { x: number; y: number; width: number; height: number };
}

/** Turntable / animation video export (v15). mp4 via WebCodecs + mp4-muxer when
 * available, else a WebM (MediaRecorder) fallback. */
export interface VideoExportOptions {
  width?: number;
  height?: number;
  fps?: number;          // default 30
  durationMs?: number;   // default 6000
  revolutions?: number;  // turntable spins, default 1
  format?: "mp4" | "webm";
  bitrate?: number;
  // Full iJewel CanvasRecorder fields (v24) — FFmpeg-path options stored/round-trip.
  convergeMode?: boolean;     // wait for progressive convergence per frame
  imageType?: string;         // intermediate frame format ("jpeg")
  crf?: number;               // constant rate factor
  pixFmt?: string;            // "yuv420p"
  preset?: string;            // ffmpeg preset
  colorTrc?: string;          // "bt709"
  colorSpace?: string;        // "bt709"
  turntableAngle?: number;    // sweep angle (deg)
  turntableCCW?: boolean;     // counter-clockwise
  endPaddingMs?: number;      // padding after recording
}

/** GLB export options (v15 extends v11 `compress`). When compressing, the
 * quantization bits trade size for precision (Draco). */
export interface GlbExportOptions {
  compress?: boolean;
  quantizePosition?: number; // default 14
  quantizeNormal?: number;   // default 10
  quantizeTexcoord?: number; // default 12
  // Full iJewel AssetExporter fields (v24).
  name?: string;             // output filename
  encodeSpeed?: number;      // Draco encode speed 1..10
  quantizeColor?: number;    // COLOR bits
  quantizeGeneric?: number;  // GENERIC bits
  viewerConfig?: boolean;    // embed full viewer config (default true)
  embedImagePreviews?: boolean;
  onlyVisible?: boolean;
  encrypt?: boolean;
  encryptKey?: string;
  encodeUint16Rgbe?: boolean;
  convertMeshToIndexed?: boolean;
  ignoreInvalidMorphTargetTracks?: boolean;
  ignoreInvalidTextures?: boolean;
  // v26: which plugin configs to embed in the export (iJewel's per-plugin
  // include checkboxes). Empty/undefined = all. Stored — informational on our side.
  pluginIncludeList?: string[];
  /** v65 — write the whole scene into the GLB as an of-scene/2 "ring-scene"
   *  block (see `exportSceneFile`), so opening this one file brings back the ring
   *  AND its look. Implies `onlyVisible: false` (hidden parts are flagged
   *  instead — glTF has no visibility); incompatible with `encrypt` (rejects).
   *  Replaces the older `viewerConfig` payload (not written beside it). The
   *  ring's pose rides in the scene rather than in the file's nodes; on a Ring
   *  Builder route the scene leaves out materials and stage (the builder owns
   *  them). Each material carries `extras.jewelry.{groupId,label}` and a metal is
   *  named after its part, as hints for other software. */
  embedScene?: boolean;
  /** v65 — the host's own settings to carry in the embedded scene (see `SceneHostBlock`). */
  sceneHost?: SceneHostBlock | null;
}

// ─── Scene file (v65) ───────────────────────────────────────────────────────

/** The schema marker of the v65 whole-scene document. */
export const OF_SCENE_SCHEMA = "of-scene/2";
/** The root glTF extension the document rides in (`extensions.OF_scene`). */
export const OF_SCENE_EXTENSION = "OF_scene";
/** `scene` = a `.jscene` (settings only, an empty glTF scene); `ring-scene` =
 *  a GLB whose meshes ARE the ring the settings were saved with. */
export type SceneFileKind = "scene" | "ring-scene";
export type SceneSectionId = "render" | "environment" | "background" | "ground" | "lighting" | "effects" | "materials" | "configurator" | "stage" | "animation" | "presentation" | "plugins" | "camera";
export type EmbeddedSceneMode = "apply" | "ignore";
export type EmbeddedSceneStatus = "pending" | "applied" | "ignored" | "failed";
/** Opaque to the viewer: the host's own settings (configurator, watermark, …) travelling in the file. */
export interface SceneHostBlock { app?: string; version?: number; [k: string]: unknown }
export interface SceneExportOptions {
  /** embed = every asset's bytes inside (default for exportSceneFile); reference = re-fetchable http(s) URLs, bytes inlined only under `inlineBudgetBytes`; session = handles into this viewer session's asset store (Undo snapshots — not portable). */
  assets?: "embed" | "reference" | "session";
  inlineBudgetBytes?: number;
  host?: SceneHostBlock | null;
  /** Write only these sections. Absent = all. */
  sections?: SceneSectionId[];
  pretty?: boolean;
}
export interface SceneImportOptions {
  /** replace (default for of-scene/2) = absent fields reset to the boot baseline so the result matches the file exactly; merge (default for of-vjson/1 and iJewel .vjson) = only what the file names. */
  mode?: "replace" | "merge";
  /** Only these sections are touched; every other section is SKIPPED (left exactly as it is), whatever the mode. Absent = all. */
  sections?: SceneSectionId[];
  /** keep (default) = this device keeps its own quality tier / pixel ratio / gem bounce cap. */
  deviceSettings?: "keep" | "apply";
  /** How far material matching may fall back when group ids differ: exact → byName (label / mesh names) → byKind. Default "byKind". */
  materialMatch?: "exact" | "byName" | "byKind";
}
export interface SceneImportResult {
  schema: string;            // the schema the source was written in (of-scene/2, of-vjson/1, webgi vjson)
  kind: SceneFileKind;
  host: SceneHostBlock | null;
  /** saved group id → live group id(s) it was applied to */
  groupMap: Record<string, string[]>;
  matched: { exact: number; byName: number; byKind: number; total: number };
  unmatchedGroups: string[];
  warnings: string[];
}
export interface SceneFileInfo { kind: SceneFileKind; schema: string; contractVersion?: number; hasHost: boolean; hasModel: boolean; assetCount: number; byteLength: number }
export interface SceneAppliedEventPayload { origin: "file" | "model"; modelId: string | null; result: SceneImportResult }

// ─── Scene extras (v16) ─────────────────────────────────────────────────────

export type GeometryKind = "plane" | "box" | "sphere" | "circle" | "torus" | "cylinder";

export interface GenerateGeometryOptions {
  kind: GeometryKind;
  size?: number;       // overall scale, default 1
  segments?: number;   // tessellation, per-kind default
  radius?: number;     // sphere/circle/torus/cylinder
  tube?: number;       // torus tube radius
  height?: number;     // cylinder height
  /** Classify the generated mesh as a gem (ray-traced) instead of metal. */
  asGem?: boolean;
}

export interface PoseOptions {
  /** Re-orient so the largest principal axis is horizontal (PCA). Default true. */
  orient?: boolean;
  /** Drop the model so its bounding-box floor sits on y=0. Default true. */
  dropToGround?: boolean;
}

export interface HdriGroundOptions {
  /** GroundedSkybox projection radius (world units). */
  radius?: number;
  /** GroundedSkybox dome height. */
  height?: number;
  /** Virtual tripod height matching the HDRI shooting height (v18). */
  tripodHeight?: number;
  /** Projection origin offset (v18). */
  originPosition?: Vec3;
}

export interface TextDecalOptions {
  color?: string;       // text colour, default #1a1a1a
  font?: string;        // CSS font family, default sans-serif
  /** Which material channel(s) to write the text into. */
  channel?: "map" | "bump" | "alpha";
  repeat?: number;      // texture tiling, default 1
  rotationDeg?: number; // text rotation
}

// ─── Ground / shadow lights / gradient / HDRi-ground (v18, iJewel Scene tab) ──

export interface Vec2 { x: number; y: number; }

/** The Ground's "Randomized Light" — the jittered area light that casts the
 * baked contact shadow. Maps onto GroundShadowBaker where fields align. */
export interface GroundLightParams {
  color: string;
  intensity: number;
  enabled: boolean;
  focus: number;
  spread: number;
  distanceScale: number;
  direction: Vec3;
  radius: number;
  bias: number;
}

/** The optional extra crisp directional key light with progressively-refined
 * soft shadows (iJewel "Progressive Shadow (Directional Light)"). */
export interface ProgressiveShadowLightParams {
  enabled: boolean;
  visible: boolean;
  intensity: number;
  color: string;
  shadowEnabled: boolean;
  focus: number;
  spread: number;
  distanceScale: number;
  direction: Vec3;
  normalDirection: Vec3;
  radius: number;
  frustumSize: number;
  bias: number;
  normalBias: number;
}

/** Full Ground panel config (v18). Plane props + the randomized shadow light +
 * the progressive directional shadow light. Fields our engine implements wire
 * through; the rest are stored and round-trip. */
export interface GroundConfig {
  visible: boolean;
  size: number;
  renderToDepth: boolean;
  limitCameraAboveGround: boolean;
  tonemapGround: boolean;
  yOffset: number;
  autoAdjustTransform: boolean;
  bakedShadows: boolean;
  maxFrameNumber: number;
  alphaVignette: boolean;
  groundReflection: boolean;
  autoFrustumSize: boolean;
  physicalReflections: boolean;
  smoothShadow: boolean;
  light: GroundLightParams;
  progressiveShadow: ProgressiveShadowLightParams;
  /** The ground plane's own surface material (v31, webgi Ground `material`
   * ref → resources.materials). Tints/roughens the ground mirror; color
   * defaults to white (identity), so an unset material leaves the ground look
   * unchanged. */
  material?: GroundMaterialParams;
}
/** Ground-plane surface material (v31). */
export interface GroundMaterialParams { color?: string; roughness?: number; metalness?: number; }
export type GroundConfigPatch = Partial<Omit<GroundConfig, "light" | "progressiveShadow" | "material">> & {
  light?: Partial<GroundLightParams>;
  progressiveShadow?: Partial<ProgressiveShadowLightParams>;
  material?: GroundMaterialParams;
};

/** HDRi grounded-dome projection (v18, full). */
export interface HdriGroundConfig {
  enabled: boolean;
  worldRadius: number;
  tripodHeight: number;
  originPosition: Vec3;
}

/** Live gradient-SVG background generator (v18, iJewel GradientSvgPlugin). */
export interface GradientConfig {
  enabled: boolean;
  type: "linear" | "radial";
  angle: number;       // linear, degrees
  radius: number;      // radial, 0..1
  position1: Vec2;
  position2: Vec2;
  center: Vec2;        // radial
  focus: Vec2;         // radial
  color1: string;
  color2: string;
  applyToBackground: boolean;
}


export interface OptionItem { id: string; label: string; }
export interface MetalPresetItem { id: string; label: string; color: number; }
export interface ProfileItem { id: string; label: string; iconPath: string; }
export interface RingSizeItem { size: number; eu: number; diameterMM: number; }
export interface DiamondStyleItem { id: DiamondStyle; label: string; defaultCount: number; defaultSpreadDeg: number; }
/**
 * A split-pattern tile.
 *
 * `ratios` (v42) is the pattern's actual balance. It is served so a host can
 * DRAW the tile instead of parsing digits out of `label` — which "Center Inlay"
 * (5:1:5) and "Twin Inlay" (4:1:1:4) have none of, and which no localised label
 * would survive. Optional only because a pre-v42 viewer omits it; hosts fall
 * back to the label parse for those.
 */
export interface SplitPatternItem { id: string; label: string; mode: SplitMode; ratios?: number[]; }
/** v39: a diamond-colour tile. `swatch` is a CSS hex for the picker chip — the
 * real shading comes from the preset + tint the viewer applies. */
export interface DiamondColorItem { id: DiamondColor; label: string; swatch: string; }

/** Static option catalogs the host UI needs to build dropdowns/swatches.
 * Served by the viewer so the data modules are not duplicated into the host. */
/**
 * v51 — milgrain, made adjustable (item 4).
 *
 * WHAT WAS FIXED, because "Milgrain is not working" needs unpacking: a bead-size
 * slider DID exist, but it was the shared groove WIDTH, and the bead law
 * (`min(0.6, max(0.25, width * 0.5))`) clamps at 0.60 mm — so the top 60% of a
 * 0.25…3.00 mm track was dead, and the same number also sized every carved
 * groove on the ring. Row count came from the joint-type enum (`milgrain2` ⇒ 2)
 * and could not reach 3; spacing, sink depth and tessellation had no path at all.
 *
 * Every field is optional and absent means exactly the shipped constant, so a
 * design that never touches this is bit-identical.
 */
export interface MilgrainConfig {
  /** Bead diameter, mm. Absent ⇒ derived from the groove width as before. */
  beadSizeMM?: number;
  /** Rows of beads per rail, 1–3. Absent ⇒ the joint type decides (2 for the
   *  "×2" variants, else 1) — which is why "Double Milgrain" was an enum value
   *  rather than a number. */
  rows?: number;
  /** Separation between rows, as a fraction of the bead diameter. Absent ⇒ 0.55. */
  spacing?: number;
  /** How far a bead is seated INTO the metal, as a fraction of its radius —
   *  the "depth" control. 0 floats it on the surface, 1 buries it to the
   *  equator. Absent ⇒ 0.45. */
  sink?: number;
  /** Sphere tessellation. "fine" doubles it, for a close-up render; the count
   *  multiplies by several hundred beads, hence the choice. Absent ⇒ "standard". */
  quality?: "standard" | "fine";
}

export interface Catalogs {
  metalPresets: MetalPresetItem[];
  metalFinishes: OptionItem[];
  gemPresets: OptionItem[];
  envPresets: OptionItem[];
  profiles: ProfileItem[];
  ringSizes: RingSizeItem[];
  splitPatterns: SplitPatternItem[];
  diamondStyles: DiamondStyleItem[];
  edgeStyles: OptionItem[];
  groovePatterns: OptionItem[];
  stoneShapes: OptionItem[];
  metalKarats: OptionItem[];
  bandLayouts: OptionItem[];
  lightPresets: LightPresetItem[];
  sceneLightTypes: OptionItem[];
  // v37 Ring Builder dropdowns. OPTIONAL because catalogs are fetched once at
  // host mount and a v36 viewer simply omits them — read them as `?? []`.
  ringShapes?: OptionItem[];
  divisionStyles?: OptionItem[];
  jointTypes?: OptionItem[];
  // v39 diamond dropdowns, same rule — absent on an older viewer, read as `?? []`.
  // Placement / Sides / Side Setting / Type are 2–4 fixed options the host
  // hard-codes (as it already does for `SideEdgeStyle`); only these three carry
  // data the host would otherwise have to duplicate.
  diamondColors?: DiamondColorItem[];
  stoneSpacings?: OptionItem[];
  diamondSettings?: OptionItem[];
  // v40: the free editor's pattern stamps (v43: ten). `id` is a
  // `FreePatternId`; the arrangement itself is generated engine-side by
  // `applyFreePattern`.
  freePatterns?: OptionItem[];
  // v43: the diamond SPECIFICATION lists (Origin / Colour Quality / Clarity),
  // served from `DIAMOND_ORIGINS` / `GEM_COLOR_GRADES` / `GEM_CLARITIES` so the
  // long clarity labels are not retyped in the host. Same `?? []` rule.
  diamondOrigins?: OptionItem[];
  gemColorGrades?: OptionItem[];
  gemClarities?: OptionItem[];
  // v50: the stone catalogue republished as the TWO AXES it always was.
  //
  // `stoneShapes` above is the cartesian product of these two flattened into
  // one list — "Round Claw", "Round Leaf", "Oval Claw", 48 entries — because
  // the 43 diamond-with-prong composites were bolted onto the same `stoneShape`
  // field the five bare cuts already used. A host reading only that list has to
  // render 48 tiles on one axis.
  //
  // These change no state: `stoneShape` keeps the identical ids, and the host
  // composes one from (cut, variant) and decomposes it back for display. Same
  // `?? []` rule as every catalog above — a v49 viewer omits them and the host
  // falls back to the flat list.
  stoneCuts?: StoneCutItem[];
  prongVariants?: OptionItem[];
}

/**
 * One cut on the stone-shape axis (v50).
 *
 * `shapes` maps a `prongVariants` id to the `stoneShape` id for that cell, so
 * the host never does string surgery on an id — a missing key means the cell was
 * never modelled (seven of the fifty were not) and the chip must be DISABLED
 * rather than substituted: the engine answers an unknown stone id with the first
 * bare cut, a Round, silently, with no error and no log. It also keeps the one
 * place that knows the composite folder spells cushion "coushion" engine-side.
 *
 * `bare` is the prong-less id for this cut, used by the settings that hold no
 * metal over the girdle (Flush, Only). `null` where we do not ship one yet.
 */
export interface StoneCutItem {
  id: string;
  label: string;
  shapes: Record<string, string>;
  bare: string | null;
}

export interface GroupInfo {
  /**
   * Opaque group key. Derived from model content, not from a three.js uuid, so
   * it is **stable across loads** of the same model (v47) and is safe to persist
   * — that is what lets a saved scene reattach its per-group state. It is only
   * meaningful for the model it came from: against a different model it simply
   * resolves to nothing. Never parse it; the display name is `label`.
   */
  id: string;
  label: string;
  meshCount: number;
  kind: MaterialKind;
  materialLabel: string;
}

/** A single mesh within a group (e.g. one diamond), individually addressable
 * by animation tracks. `id` is "${groupId}#${index}" — split on the LAST "#",
 * since group ids never contain one — and inherits `GroupInfo.id`'s stability:
 * fixed for the life of the loaded model, and the same again after a reload of
 * that model (v47). */
export interface ElementInfo {
  id: string;
  groupId: string;
  index: number;
  label: string;
  kind: MaterialKind;
}

// ─── Post-processing DTOs ───────────────────────────────────────────────────

// "NONE" (v31, webgi tonemap constant 0): NO tone-mapping operator AND exposure
// is ignored (the HDR beauty is clamped straight to display). Distinct from
// "LINEAR", which applies no operator but STILL multiplies by exposure.
export type TonemapMode = "NONE" | "LINEAR" | "REINHARD" | "CINEON" | "ACES_FILMIC" | "UNCHARTED2";

/** Progressive refinement: after interaction stops, the pipeline keeps
 * rendering sub-pixel-jittered frames and averages them (supersampled AA +
 * AO/SSR noise convergence), then halts. Render-on-demand is preserved —
 * once `maxFrames` is reached the loop stops completely. */
export interface ProgressiveSettings {
  enabled: boolean;
  /** Frames accumulated before the loop stops (quality vs settle-time). */
  maxFrames: number;
  /** Sub-pixel jitter each frame (drives temporal AA convergence). iJewel
   * default true; always on in our pipeline but exposed for parity. */
  jitter: boolean;
}

export interface SSRSettings {
  enabled: boolean;
  /** Blend weight of traced reflections over the env-map fallback (0..1). */
  intensity: number;
  /** Linear march samples per ray (quality/perf lever). */
  steps: number;
  /** Binary-search refinement steps after a hit. */
  refineSteps: number;
  /** Max ray travel as a multiple of the model bounding radius. */
  maxDistanceScale: number;
  /** Ray-hit thickness tolerance as a multiple of the model bounding radius. */
  thicknessScale: number;
  /** Stochastic roughness-cone scale (0..1); glossy reflections converge
   * over progressive frames instead of relying on screen-space blur alone. */
  roughnessJitter: number;
  /** Pixels rougher than this skip tracing (their env reflection is already
   * a good answer and rays would be too diffuse to converge). */
  maxRoughness: number;
  /** Confidence exponent (webgi `power`, default 1.1) — >1 sharpens the
   * falloff of low-confidence hits. */
  power: number;
  /** Camera-facing ray mask softness (webgi semantics, default -0.2):
   * weight = clamp(-4·dot(viewDir, N) + (4 + factor), 0, 1). */
  maskFrontFactor: number;
  /** Fresnel coefficient on traced reflections (three.js SSRPass style):
   * reflections fade head-on and strengthen toward grazing. Default true. */
  fresnel: boolean;
  /** First N progressive frames trace at half step count (perf, default 0). */
  lowQualityFrames: number;
  /** Per-channel (R,G,B) multiplier on the reflected colour — tint/boost
   * reflections by channel (iJewel `boost`, default 1,1,1). */
  boost: Vec3;
  /** Depth-comparison tolerance when a ray tests a surface hit (iJewel, 0.5). */
  tolerance: number;
  /** World-space scale reference for the march (iJewel, 1). Ignored when
   * autoRadius is on. */
  objectRadius: number;
  /** Auto-derive objectRadius from scene bounds (iJewel default true). */
  autoRadius: boolean;
  /** Discard reflection rays pointing back at the camera (iJewel, true). Gates
   * `maskFrontFactor`. */
  maskFrontRays: boolean;
  /** Trace at half resolution with a depth-aware upsample (perf). */
  halfRes: boolean;
  // ── Quality rewrite (v27) ──
  /** Edge-aware confidence-weighted bilateral denoise of the traced buffer —
   * smooths the dotted/jagged single-frame trace immediately (vs. relying on
   * the 32-frame accumulation). Default true. */
  denoise: boolean;
  /** Bilateral denoise half-kernel radius in taps (roughness widens it). */
  blurKernel: number;
  /** Denoise edge-stopping sharpness (higher preserves edges harder). */
  blurSharpness: number;
  /** Separable à-trous denoise iterations (1 = sharp only, 2–3 widen). */
  denoiseIterations: number;
  /** Temporal reprojection of the SSR buffer via the velocity buffer so
   * reflections stay smooth DURING camera motion (not only when idle). */
  temporal: boolean;
  /** History blend weight for temporal reprojection (0..1). */
  temporalBlend: number;
  /** Fill off-screen / missed rays with the dynamic reflection probe (smooth
   * cubemap) instead of leaving a dotted gap. Needs `reflectionProbe.enabled`. */
  probeFallback: boolean;
  /** Probe-fallback contribution (0..1). */
  probeIntensity: number;
  // ── Per-source reflection weights (v30) ──
  // How strongly a traced metal reflects each *kind* of surface it hits,
  // classified from the hit pixel's G-buffer material code. All default 1.0 ⇒
  // the reflection is byte-identical to today. Lower a weight to fade that
  // source out of metal reflections; raise it to over-emphasise. Gems are
  // weighted as a *source* only (still never an SSR destination — the
  // bit-identical invariant holds).
  /** Weight of the background / env (off-screen + probe-fallback) in metal
   * reflections. Default 1. */
  wBackground: number;
  /** Weight of other metal surfaces in metal reflections. Default 1. */
  wMetal: number;
  /** Weight of diamonds / gems in metal reflections (diamond→metal). Default 1. */
  wGem: number;
  /** Weight of the ground plane in metal reflections. Default 1. */
  wGround: number;
}

export interface SsaoSettings {
  enabled: boolean;
  /** Occlusion strength. */
  intensity: number;
  /** Derive the world-space radius from the model size (radius = model
   * bounding radius × radiusScale). Disable to use the absolute `radius`. */
  autoRadius: boolean;
  radiusScale: number;
  /** Absolute world-unit radius, used only when autoRadius is off. */
  radius: number;
  samples: number;
  /** Cosine-space bias suppressing self-occlusion on flat faces. */
  bias: number;
  /** Falloff exponent shaping the occlusion curve. */
  falloff: number;
  /** Bilateral blur edge preservation (webgi edgeSharpness, default 0.1). */
  edgeSharpness: number;
  /** AO weight on ground pixels (0 = the floor is never darkened). */
  groundDarkening: number;
  /** Bilateral smoothing pass on the AO buffer (iJewel `smoothEnabled`, true). */
  smoothEnabled: boolean;
  /** Compute AO at half resolution with a depth-aware upsample (perf). */
  halfRes: boolean;
}

export interface BloomSettings {
  enabled: boolean;
  intensity: number;
  /** Luminance threshold in linear HDR, pre-exposure (a white background
   * sits at ~1.0; env-driven metal glints reach far above it). */
  threshold: number;
  /** Soft-knee width as a fraction of the threshold (0 = hard cut). */
  smoothing: number;
  /** Upsample spread of the mip chain (0..1). */
  radius: number;
  /** Mip levels in the blur pyramid (more levels = wider halos). */
  levels: number;
  /** Per-level weight distribution exponent (webgi `power`, default 1). */
  power: number;
  /** Show only the bloom buffer (iJewel `bloomDebug`). Routes to debug "bloom". */
  debug: boolean;
}

/** Display grading applied after tone mapping (1/1 = neutral). */
export interface ColorGradeSettings { contrast: number; saturation: number; }

export interface VignetteSettings {
  enabled: boolean;
  darkness: number;
  offset: number;
  /** Falloff curve / corner-darkening strength (iJewel `power`, default 0.5). */
  power: number;
  /** Tint the darkened edges toward this colour (default black). */
  color: string;
}

/** Bokeh depth of field (v13). CoC from |viewDist − focusDist| / (depthRange ×
 * sceneRadius); golden-spiral gather scaled by near/far blur. */
export interface DofSettings {
  enabled: boolean;
  /**
   * Armed click-to-focus (v59). While true, a click on the canvas sets
   * `focalPoint` instead of selecting. The viewer's pointer handler already
   * distinguishes a click from an orbit drag by a 3px threshold, so this can
   * stay armed without taking orbit away.
   */
  enableEdit: boolean;
  /**
   * World point to hold in focus (v59). When set, the focal DISTANCE is
   * recomputed from the camera every frame, so orbiting keeps the clicked
   * feature sharp. `null` falls back to `focusDistance`.
   *
   * This is the difference that mattered: storing a distance pins the focal
   * PLANE in front of the camera, so the moment you orbit, the thing you
   * focused on drifts out of focus. The reference stores a point for exactly
   * this reason.
   */
  focalPoint: Vec3 | null;
  /** Focal distance in world units; < 0 = auto (orbit-target distance). Set by
   * click-to-focus (`setDofFocus`) when the ray MISSES. Ignored while
   * `focalPoint` is set. */
  focusDistance: number;
  /** In-focus depth band, a multiple of the model bounding radius. */
  depthRange: number;
  /** Near/far blur strength (0..1). */
  nearBlurScale: number;
  farBlurScale: number;
  /** Max blur radius in pixels at full strength. */
  maxBlur: number;
}

/** Screen-space contact shadows (v13) — a short march toward the key light;
 * darkens crevices/contacts the baked ground shadow & AO can't reach. */
export interface SsContactShadowSettings {
  enabled: boolean;
  /** Ray length as a multiple of the model bounding radius. */
  radius: number;
  intensity: number;
  /** Hit thickness tolerance (× the per-step depth span). */
  tolerance: number;
  stepCount: number;
  /** Show only the contact-shadow buffer (iJewel `onlySSCSDebug`). */
  onlySSCSDebug: boolean;
  halfRes: boolean;
  // ── Quality rewrite (v27) ──
  /** Soft-penumbra falloff: occlusion fades with hit distance along the ray
   * (0 = hard binary contact, 1 = soft). Smooth contact gradient. */
  softness: number;
  /** Edge-aware bilateral denoise of the shadow buffer (smooth in one frame). */
  denoise: boolean;
  /** Denoise half-kernel radius in taps. */
  blurKernel: number;
  /** Denoise edge-stopping sharpness. */
  blurSharpness: number;
  /** Separable à-trous denoise iterations. */
  blurIterations: number;
  /** Temporal reprojection via the velocity buffer (smooth during motion). */
  temporal: boolean;
}

/** Dynamic cubemap reflection probe (v27, mrdoob webgl_materials_cubemap_dynamic
 * technique). A CubeCamera baked at the model centre — re-baked only on
 * model/env change (camera-independent, cached), so render-on-demand is intact.
 * Primary use is the SSR miss-fallback (smooth off-screen reflections); can
 * optionally be assigned as a secondary metal env. Never touches gems by
 * default — `asGemEnv` is the explicit opt-in for genuine inter-object gem
 * reflection (diamond→diamond / diamond→metal). */
export interface ReflectionProbeSettings {
  enabled: boolean;
  /** Cube face resolution (128 / 256 / 512). */
  resolution: number;
  /** Feed the SSR miss-fallback (smooth gaps). */
  asSsrFallback: boolean;
  /** Assign as a secondary reflection env on metal materials only (off by
   * default — a single-point probe has no parallax on flat surfaces). */
  asMetalEnv: boolean;
  /** Sample the probe (which bakes ALL geometry — stones + metal) as a
   * secondary reflection inside the diamond shader, so a stone reflects its
   * neighbouring stones and the ring around it. The ONLY mechanism for real
   * diamond→diamond / diamond→metal reflection. Opt-in: enabling recompiles
   * the gem materials (the default gem shader stays bit-identical). Default
   * false. (v30) */
  asGemEnv: boolean;
  /** Strength of the probe reflection inside gems when `asGemEnv` is on
   * (0..1, default 0.5). No effect while `asGemEnv` is off. (v30) */
  gemProbeIntensity: number;
}

/** A reflection group — a metal cluster (a whole ring, or one metal part of a
 * ring) that the per-object reflection matrix can address. `id` indexes the
 * matrix rows/columns; `groupId` is the host-facing scene-group id. (v30) */
export interface ReflectionGroup {
  /** Matrix index 0..N-1 (stable within a model load). */
  id: number;
  /** The underlying scene-group id (as returned by `listGroups`). */
  groupId: string;
  label: string;
}

/** Per-object reflection matrix snapshot (v30). `weights[dest][src]` scales how
 * strongly the surface in group `dest` reflects the surface in group `src`
 * (1 = unchanged). Square, sized to `groups.length`. Disabled ⇒ inert and the
 * group-id prepass does not run (no perf cost, render byte-identical). */
export interface ReflectionMatrix {
  enabled: boolean;
  groups: ReflectionGroup[];
  weights: number[][];
}

/** Screen-space global illumination (v13, dev-tier) — one-bounce diffuse
 * gathered from the lit beauty. Never applied to gems or the background. */
export interface SsgiSettings {
  enabled: boolean;
  intensity: number;
  power: number;
  autoRadius: boolean;
  /** Ray reach as a multiple of the model bounding radius. */
  objectRadius: number;
  rayCount: number;
  stepCount: number;
  falloff: number;
  /** Depth-test tolerance for GI ray hits (iJewel, 1). */
  tolerance: number;
  /** Depth bias to avoid self-illumination artifacts (iJewel, 0.15). */
  bias: number;
  /** Bilateral smoothing of the GI buffer (iJewel `smoothEnabled`, true). */
  smoothEnabled: boolean;
  /** Recompute GI as the camera moves (iJewel `renderWithCamera`, true). */
  renderWithCamera: boolean;
  /** Sub-toggle for the GI term within the pass (iJewel `ssgiEnabled`, true). */
  ssgiEnabled: boolean;
  halfRes: boolean;
}

/** Screen-space bevel (v13) — rounds the G-buffer normals at edges so AO/SSR/
 * SSGI/SSCS see softened creases (beauty shading untouched). */
export interface SsBevelSettings {
  enabled: boolean;
  /** Edge-rounding reach in pixels. */
  radius: number;
  /** Apply the bevel scene-wide rather than per selected material (iJewel
   * `sceneBevel`, default false). */
  sceneBevel: boolean;
}

/** Small-scale cavity AO (v13) — webgi useSmallScaleAO: screen-space CURVATURE
 * cavity (concave pixels occlude), max-combined with the sampled AO.
 * `brightness` = webgi intensitySmallAO (flat lift after the smoothstep);
 * `intensity` scales the cavity term (1 = webgi-exact). */
export interface SsaoCavitySettings {
  enabled: boolean;
  intensity: number;
  brightness: number;
}

/** Selection/hover outline overlay (v14) — runs outside the pipeline, in both
 * render paths; replaces the old bbox-wireframe highlight. */
export interface OutlineSettings {
  enabled: boolean;
  /** Alpha scale 0..2. */
  intensity: number;
  /** Outline opacity 0..1. */
  transparency: number;
  color: string;
  /** Also outline the hovered group (at half strength). */
  hover: boolean;
  /** Outlining one mesh outlines its whole material group (always true here). */
  highlightSameMaterial: boolean;
  /** Filled highlight (tint) of selected meshes in addition to the edge
   * (iJewel `enableHighlight`, default false). */
  enableHighlight: boolean;
  /** Update the outline live as selection/hover changes (iJewel, true). */
  enableDynamicSelection: boolean;
  /** Debug visualization of the outline buffer (iJewel `debugOutline`). */
  debugOutline: boolean;
  /** Highlight every mesh that uses the selected material (iJewel, false). */
  highlightSelectedMaterials: boolean;
}

/** Picking behaviour (v14). */
export interface PickerSettings {
  enabled: boolean;
  /** Re-center the orbit target on the picked group. */
  autoFocus: boolean;
  /** Shift/ctrl-click adds to the selection. */
  multiSelect: boolean;
  /** Hover highlight + hoverChanged events. */
  hover: boolean;
}

/** A node in the scene hierarchy (v14). `id` is stable for the model's life. */
export interface SceneNodeInfo {
  id: string;
  name: string;
  kind: "group" | "mesh" | "light" | "camera";
  visible: boolean;
  meshCount: number;
  /** Material group id (meshes only) — links the tree to selection. */
  groupId?: string;
  children: SceneNodeInfo[];
}

/**
 * A scene node's inspectable state (v58).
 *
 * Every field here has a live getter AND a setter, which is the rule that
 * decided the shape: `SceneNodeInfo` carries what the TREE needs to draw
 * itself, this carries what an INSPECTOR needs to edit. Draw calls are
 * deliberately absent — `renderer.info` is per-frame and scene-wide, so there
 * is no honest per-node number to report.
 */
export interface NodeDetails {
  id: string;
  name: string;
  visible: boolean;
  position: Vec3;
  /** Local rotation in DEGREES, the same unit `setObjectRotation` takes. */
  rotationDeg: Vec3;
  scale: Vec3;
  castShadow: boolean;
  receiveShadow: boolean;
  frustumCulled: boolean;
  renderOrder: number;
}

/** Subtree geometry totals for one node (v58). */
export interface NodeGeometryInfo {
  vertices: number;
  triangles: number;
  /** Meshes contributing to the totals above. */
  meshes: number;
}

/** Fields `setNodeFlags` accepts — the render flags, without the transform. */
export type NodeFlagField = "castShadow" | "receiveShadow" | "frustumCulled" | "renderOrder";

export type TransformMode = "translate" | "rotate" | "scale";

/** Transform gizmo settings (v14, three TransformControls). */
export interface TransformControlsSettings {
  enabled: boolean;
  mode: TransformMode;
  size: number;
  space: "world" | "local";
  /** Snapping (null = off). */
  translationSnap: number | null;
  rotationSnapDeg: number | null;
  scaleSnap: number | null;
  /** Per-axis handle visibility (v19, iJewel showX/Y/Z). */
  showX: boolean;
  showY: boolean;
  showZ: boolean;
}

/** Pivot-controls gizmo settings (v19, iJewel PivotControls). Stored + round-
 * trip; `enabled` reuses the transform gizmo path. */
export interface PivotControlsSettings {
  enabled: boolean;
  gizmoScale: number;
  fixed: boolean;
  depthTest: boolean;
  annotations: boolean;
  translationSnap: number;
  rotationSnap: number; // degrees
  scaleSnap: number;
  uniformScaleEnabled: boolean;
  disableAxes: boolean;
  disableSliders: boolean;
  disableRotations: boolean;
  disableScaling: boolean;
}

/** Pivot-edit settings (v19, iJewel PivotEdit). */
export interface PivotEditSettings {
  enabled: boolean;
  editPivot: boolean;
  showPivotMarker: boolean;
  markerScale: number;
  markerColor: string;
}

/** Diamond manager settings (v19, iJewel Diamonds panel; v26 env slots). */
export interface DiamondManagerSettings {
  forceSceneEnvironment: boolean;
  envMapRotation: number;
  // v26: oriented-env flag + two extra dedicated gem-env slots.
  diamondOrientedEnvMap?: boolean;
  // v31: env2Url/env3Url are now loaded as RESIDENT gem-env textures (kept
  // alongside the primary, NOT swapped into it). A gem group then selects which
  // resident slot it samples via setGroupGemEnvSlot (0 = primary, 1 = env2,
  // 2 = env3). Empty = the slot stays unloaded. Setting these here triggers the
  // resident load; they do not change the primary env.
  env2Url?: string;
  env3Url?: string;
}

/** Advanced background/environment panel (v26, iJewel SimpleBackgroundEnvUiPlugin1).
 * The shared fields reuse the existing scene env/background RPCs; the genuinely-
 * new bits are a secondary env slot (Environment1) + fixed-direction + cache. */
export interface EnvAdvancedConfig {
  fixedDirection: boolean;   // lock env to world (== envFollowCamera 0)
  cache: boolean;            // stored hint (no-op)
  envmapAsBackground: boolean;
  env1Url: string;           // secondary env slot (stored; "Apply" loads it)
  env1Rotation: number;      // radians
}

/** Helper-widget visibility (v26, iJewel WidgetsPlugin). */
export interface WidgetsConfig { visible: boolean; }

/** Orbit-controls + camera parameters (v14, expanded v17 to the full iJewel
 * Camera panel). OrbitControls-native fields wire directly; the webgi-specific
 * ones (dollyZoom/autoPushTarget/autoPullTarget/clamp/autoNearFar/dollyFov/
 * minNear/maxFar) are stored and round-trip. */
export interface ControlsParams {
  enabled: boolean;
  enableDamping: boolean;
  dampingFactor: number;
  rotateSpeed: number;
  zoomSpeed: number;
  maxZoomSpeed: number;
  panSpeed: number;
  zoomToCursor: boolean;
  enableZoom: boolean;
  enablePan: boolean;
  autoRotate: boolean;
  autoRotateSpeed: number;
  dollyZoom: boolean;
  minDistance: number;
  maxDistance: number;
  minZoom: number;
  maxZoom: number;
  minPolarAngle: number;
  maxPolarAngle: number;
  minAzimuthAngle: number;
  maxAzimuthAngle: number;
  autoPushTarget: boolean;
  autoPullTarget: boolean;
  clampMin: Vec3;
  clampMax: Vec3;
  /** Camera-level (Camera section): auto near/far, dolly-fov, near/far clamps. */
  autoNearFar: boolean;
  dollyFov: boolean;
  minNear: number;
  maxFar: number;
}

/** Temporal anti-aliasing (v15). Active only during motion (restart frames);
 * idle frames use progressive accumulation and bypass TAA. The velocity buffer
 * renders automatically while enabled. */
export interface TaaSettings {
  enabled: boolean;
  /** History blend weight 0..1 (higher = smoother, more ghost-prone). Kept for
   * back-compat; the engine blends between feedbackMin/Max by motion. */
  feedback: number;
  /** History blend feedback at high motion (iJewel feedBack[0], default 0.88). */
  feedbackMin: number;
  /** History blend feedback at rest (iJewel feedBack[1], default 0.97). */
  feedbackMax: number;
  /** Seed jitter/noise by total frame count so TAA integrates decorrelated noise. */
  stableNoise: boolean;
}

/** Lens chromatic aberration (v13) — radial RGB split pre-tonemap. */
export interface ChromaticAberrationSettings { enabled: boolean; intensity: number; }

/** Film grain (v13) — post-tonemap IGN noise, seeded per restart so idle
 * refinement doesn't average it away. `multiply` modulates instead of adds. */
export interface FilmGrainSettings { enabled: boolean; intensity: number; multiply: boolean; }

/** Colour LUT (v13) — up to 3 sequential display-space 3D LUTs. The textures
 * are viewer-side state loaded via loadLut*; this is the global blend. */
export interface LutSettings {
  enabled: boolean;
  intensity: number;
  /** Also apply the LUT to the background (iJewel `lutBackground`, default true). */
  lutBackground: boolean;
  /** v68: also apply the LUT to the ground plane (its shadow / reflection
   *  pixels). Absent ⇒ true, today's behaviour. */
  lutGround?: boolean;
}

/** Colored-gem refraction shading toggle (iJewel GemRefractionPlugin). Gates the
 * IOR-bend + body-color absorption path for non-diamond stones. Never alters
 * diamond bit-output. */
export interface GemRefractionSettings { enabled: boolean; }

/** Explicit velocity (motion-vector) buffer toggle (iJewel VelocityBuffer).
 * Renders the buffer independently of TAA (e.g. for the velocity debug view). */
export interface VelocityBufferSettings { enabled: boolean; }

/** One loaded LUT slot (0..2). `size` is the cube edge (e.g. 32). */
export interface LutSlotInfo { slot: number; name: string; size: number; }

export type PipelineDebugView =
  | "off" | "ao" | "ssr" | "normals" | "depth" | "bloom" | "accum"
  | "coc" | "velocity" | "taa" | "sscs" | "ssgi" | "bevel"
  | "ssrDenoise" | "reflectionProbe"
  // v30: false-colour per-object group ids (tuning the reflection matrix).
  | "groupId";

/** Renderer output controls (v13): explicit width/height override (0 = follow
 * the container), a render-scale multiplier over pixelRatio, and the render
 * mode (full vs interactive preview). */
export interface RendererParams {
  width: number;
  height: number;
  renderScale: number;
  /** "full" = canvas follows the container (webgi default); "custom" = the
   * explicit width/height override drives the render size and survives host
   * resize() calls (webgi's "custom" size mode). The deprecated "preview" is
   * accepted as an input alias for "custom" at the RPC boundary, but the stored
   * & emitted value is always "custom". */
  mode: "full" | "custom";
}
/** Input alias set for RendererParams.mode — 'preview' is the pre-v31 spelling. */
export type RendererModeInput = "full" | "custom" | "preview";

/** Progressive baked ground shadow (webgi technique): N jittered-light depth
 * tests accumulate into a mean-occlusion map — physically soft, crisp at
 * contact, camera-independent (orbiting never re-bakes). */
export interface GroundShadowBakeSettings {
  enabled: boolean;
  /** 0..1 → light cone half-angle = softness × 90° (0 = hard sun, 1 = sky). */
  softness: number;
  /** Total samples for a converged bake (quality). */
  sampleCount: number;
  /** Bake budget per rendered frame. */
  samplesPerFrame: number;
  /** Shadow opacity multiplier. */
  brightness: number;
  /** Power curve on the occlusion (>1 = denser core, lighter tail). */
  falloff: number;
  /** Bilateral blur radius in texels (0 disables). */
  blurRadius: number;
  /** Bake buffer resolution (256 / 512 / 1024). */
  resolution: number;
  /** Shadow plane half-extent = model max dimension × planeScale. */
  planeScale: number;
  /** Depth-compare bias multiplier (raise on acne, lower on peter-panning). */
  biasMultiplier: number;
  /** Re-bake continuously (EMA tracking) while animations play. */
  rebakeOnAnimation: boolean;
}
export type GroundShadowBakePatch = Partial<GroundShadowBakeSettings>;

/** Diagnostic buffer views. `view` picks a buffer; `split` is the screen-x
 * fraction where the debug half starts (0 = full-screen debug view). */
export interface PipelineDebugSettings { view: PipelineDebugView; split: number; }

/** Adaptive dynamic-resolution during sustained camera motion (v32). The engine
 * drops the internal render scale one notch at a time while orbiting a heavy
 * scene, then restores full resolution on idle. `minScale` is the FLOOR the notch
 * ladder may reach (0.3..1); `enabled:false` pins full resolution (feature off,
 * ≡ minScale 1). Default is a near-invisible 0.85 floor. */
export interface MotionSettings {
  enabled: boolean;
  minScale: number;
  /** Per-frame SUPERSAMPLING factor during motion (>1), used when the notch is
   * OFF (`enabled:false`, quality-first). Renders the internal targets at
   * drawingBuffer×ssaa while orbiting so a single moving frame is spatially
   * supersampled — high-frequency diamond sparkle stays sharp/brilliant during
   * motion (matching webgi's per-frame-clean look) instead of aliasing into
   * noise or dulling under temporal blend. 1 = off (default). Clamped [1,2].
   * Costs ssaa² pixels per motion frame — a deliberate quality-over-fps knob. */
  ssaa?: number;
}

export interface PostFXSettings {
  enabled: boolean;
  progressive: ProgressiveSettings;
  ssr: SSRSettings;
  ssao: SsaoSettings;
  bloom: BloomSettings;
  tonemap: TonemapMode;
  /** Include the background in tone mapping. iJewel's captured scenes (e.g.
   * 2rings.glb.vjson) run TRUE — a white background ACES-rolls to ~0.80 gray
   * (the studio backdrop). Set FALSE for a pure-white embed (#ffffff stays 255)
   * or when the canvas is transparent. Ring/band default: TRUE. */
  tonemapBackground: boolean;
  /** Include ground-plane pixels (reflection/shadow) in tone mapping. iJewel
   * runs TRUE (slaved to the background). Ring/band default: TRUE. */
  tonemapGround: boolean;
  grade: ColorGradeSettings;
  vignette: VignetteSettings;
  /** Bokeh depth of field (v13). */
  dof: DofSettings;
  /** Lens chromatic aberration (v13). */
  chromaticAberration: ChromaticAberrationSettings;
  /** Film grain (v13). */
  grain: FilmGrainSettings;
  /** Colour LUT blend (v13). */
  lut: LutSettings;
  /** Hard-clip the background to [0,1] before display (v13, Tonemap panel). */
  clipBackground: boolean;
  /** Screen-space contact shadows (v13). */
  sscs: SsContactShadowSettings;
  /** Screen-space global illumination (v13, dev). */
  ssgi: SsgiSettings;
  /** Screen-space bevel — G-buffer normal rounding (v13). */
  ssbevel: SsBevelSettings;
  /** Small-scale cavity AO (v13). */
  ssaoCavity: SsaoCavitySettings;
  /** Temporal anti-aliasing (v15). */
  taa: TaaSettings;
  /** Colored-gem refraction toggle (v17, iJewel GemRefraction). */
  gemRefraction: GemRefractionSettings;
  /** Explicit velocity-buffer toggle (v17, iJewel VelocityBuffer). */
  velocityBuffer: VelocityBufferSettings;
  /** Output the depth buffer instead of beauty (v17, iJewel Tonemap renderDepth).
   * Routes to the depth debug view while true. */
  renderDepth: boolean;
  /** Dynamic cubemap reflection probe (v27) — SSR miss-fallback + optional
   * secondary metal env. */
  reflectionProbe: ReflectionProbeSettings;
  /** Adaptive dynamic-resolution notch floor during sustained motion (v32). */
  motion: MotionSettings;
  debug: PipelineDebugSettings;
}

/** Nested-partial patch for setPostFX (band-style partial setter). Unknown
 * fields — e.g. from a v9 settings export — are ignored, never an error. */
export interface PostFXSettingsPatch {
  enabled?: boolean;
  progressive?: Partial<ProgressiveSettings>;
  ssr?: Partial<SSRSettings>;
  ssao?: Partial<SsaoSettings>;
  bloom?: Partial<BloomSettings>;
  tonemap?: TonemapMode;
  tonemapBackground?: boolean;
  tonemapGround?: boolean;
  grade?: Partial<ColorGradeSettings>;
  vignette?: Partial<VignetteSettings>;
  dof?: Partial<DofSettings>;
  chromaticAberration?: Partial<ChromaticAberrationSettings>;
  grain?: Partial<FilmGrainSettings>;
  lut?: Partial<LutSettings>;
  clipBackground?: boolean;
  sscs?: Partial<SsContactShadowSettings>;
  ssgi?: Partial<SsgiSettings>;
  ssbevel?: Partial<SsBevelSettings>;
  ssaoCavity?: Partial<SsaoCavitySettings>;
  taa?: Partial<TaaSettings>;
  gemRefraction?: Partial<GemRefractionSettings>;
  velocityBuffer?: Partial<VelocityBufferSettings>;
  renderDepth?: boolean;
  reflectionProbe?: Partial<ReflectionProbeSettings>;
  motion?: Partial<MotionSettings>;
  debug?: Partial<PipelineDebugSettings>;
}

// ─── Scene lights (dynamic multi-light system) ──────────────────────────────

export type SceneLightType = "directional" | "point" | "spot" | "area";

export interface SceneLightShadowParams {
  /** Shadow map resolution (square texels). */
  mapSize: number;
  /** VSM blur radius (directional/spot; PCF radius for point lights). */
  radius: number;
  /** VSM blur pass sample count (directional/spot only). */
  blurSamples: number;
  bias: number;
  normalBias: number;
  /** Per-light shadow opacity 0..1 — independent "ranges of shadows". */
  opacity: number;
}

export interface SceneLightState {
  /** Viewer-assigned, stable for the light's lifetime ("light_1", ...). */
  id: string;
  /** User-facing label, e.g. "Spot 1". */
  name: string;
  type: SceneLightType;
  color: string;
  /** Base intensity, composed with setLightMultiplier. Point/spot intensity
   * is distance-compensated internally so presets stay portable across
   * differently sized models. */
  intensity: number;
  // Placement is polar around the model so it survives model swaps/refits.
  azimuthDeg: number;
  elevationDeg: number;
  /** Distance from the model center as a multiple of its bounding radius. */
  distanceFactor: number;
  /** Spot cone half-angle in degrees. */
  angleDeg?: number;
  /** Spot edge softness 0..1. */
  penumbra?: number;
  /** Point/spot falloff exponent (2 = physically correct). */
  decay?: number;
  /** Area-light size as multiples of the model radius. */
  widthFactor?: number;
  heightFactor?: number;
  /** Rejected for "area" lights (three.js limitation); shadow casters are
   * capped per scene — see addSceneLight. */
  castShadow: boolean;
  shadow?: SceneLightShadowParams;
  helperVisible: boolean;
}

export type SceneLightInit = Partial<Omit<SceneLightState, "id">> & { type: SceneLightType };
export type SceneLightPatch = Partial<Omit<SceneLightState, "id" | "type">>;

export interface LightPresetItem { id: string; label: string; description: string; }

/** Full snapshot of all readable scene/camera/light state. Lets a host panel
 * build its controls from a single round-trip instead of dozens of getters. */
/** Randomized self-shadow (v32) — jitters the engine's key/self-shadow directional
 * light across progressive-refine frames so cast model shadows converge to a soft
 * penumbra (the model-shadow analog of the baked ground shadow). Default OFF.
 * Structurally mirrors SceneLights.RandomizedSelfShadowParams. */
export interface RandomizedSelfShadowParams {
  enabled: boolean;
  /** Jitter cone width [0,1] (webgi spread). */
  spread: number;
  /** Fraction of refine frames that jitter [0,1] (webgi focus). */
  focus: number;
  /** Light-distance nudge; >=0.01 (round-trip parity). */
  distanceScale: number;
  /** Base direction (FROM target TO light). {0,0,0} ⇒ follow the key-light dir. */
  direction: Vec3;
}

/**
 * Everything the viewer's configurator surface holds, in one restorable blob
 * (v48). See `SceneState.configurator`.
 */
export interface ConfiguratorSnapshot {
  /** Per-group material variations. `params` is the same `GroupMaterialParams`
   *  shape `of-vjson` already stores for the live material, so a variation is
   *  restorable without the model it came from being byte-identical. */
  materialVariations: Array<{
    groupId: string;
    activeIndex: number;
    variations: Array<{ id: string; name: string; params: GroupMaterialParams }>;
  }>;
  /** Switch nodes, including which option is showing — node VISIBILITY is not
   *  captured anywhere else, so this one is re-applied on restore. */
  switchNodes: SwitchNodeState[];
  /** glTF KHR_materials_variants selection, by name. Null when the model has
   *  no variants or none was chosen. */
  activeMaterialVariant?: string | null;
}

export interface SceneState {
  background: string | null;
  backgroundIntensity: number;
  /**
   * Which environment the intensity/rotation below were authored against (v44).
   * `envPresetId` is an ENV_PRESETS key, or "custom" when an HDR was loaded by
   * URL — in which case `envUrl` carries it. Without these a preset saved under
   * one HDRI and applied while another was loaded landed its intensity and
   * rotation on the wrong environment, producing lighting that matched neither.
   * Both optional: a state captured before v44 simply leaves the env alone.
   */
  envPresetId?: string;
  envUrl?: string | null;
  envIntensity: number;
  envFollowCamera: number;
  /**
   * The Metal HDR override the two numbers below were authored against (v45),
   * or `null` when no override was loaded (metals on the scene environment, or
   * on the entry project's boot HDR — neither is a choice worth replaying, and
   * restoring one as an override would make it permanent). Emitted only
   * for a URL that can be re-fetched — a `blob:` from `loadMetalEnvFromFile`
   * dies with the page, so a file-loaded HDR round-trips nothing. Absent in a
   * pre-v45 snapshot, which leaves the current metal env alone.
   */
  metalEnvUrl?: string | null;
  /**
   * Whether a metal HDR is loaded AT ALL right now (v48). Read-only, and the
   * thing a host must gate "Metal Rotation" and "Metal HDR Brightness Cap" on:
   * both setters are no-ops without a live HDR, and until this existed the two
   * rows stayed enabled and kept reporting values while doing nothing. The
   * first Environment pick disposes the entry project's boot HDR
   * (`_dropDefaultMetalEnv`), so this flips to false without the user touching
   * anything in this section.
   */
  metalEnvActive?: boolean;
  /**
   * WHICH metal env is loaded (v48). `metalEnvUrl: null` could not tell
   * "explicitly cleared, metals belong on the scene env" apart from "never
   * overridden, still on the boot HDR", so a scene saved in the first state
   * reloaded into the second. This is the same ambiguity `backgroundSource`
   * was introduced to kill for the background. Absent in a pre-v48 snapshot,
   * which keeps the old behaviour.
   */
  metalEnvSource?: "none" | "default" | "override";
  /**
   * The viewer's OWN configurator state (v48) — material variations, switch
   * nodes and the glTF KHR_materials_variants selection.
   *
   * None of it was persisted anywhere: `of-vjson/1` had no field for it and
   * neither did SceneState, so every configurator a user built inside the
   * viewer was gone on reload. (The Vault's own configurator DEFINITION, saved
   * through `saveModelConfig`, round-trips fine and is a different thing.)
   * Absent in a pre-v48 snapshot, which restores exactly as before.
   */
  configurator?: ConfiguratorSnapshot;
  metalEnvRotation: Vec3;
  /** May be Infinity (structured-clone preserves it). JSON writes Infinity as
   *  `null`, and a `null` restores as "no cap" (v65; it used to be skipped). */
  metalEnvMaxBrightness: number;
  envRotation: Vec3;
  showEnvAsBackground: boolean;
  backgroundBlur: number;
  /**
   * Which writer owns the background right now (v46). Read-only — a consequence
   * of the last background call, not a setting: `applySceneState` never applies
   * it, it only READS it to tell the two meanings of `background: null` apart
   * ("the scene was transparent" vs "an image or the env owned the background",
   * which used to be replayed as transparency). A host needs it to know whether
   * Background Blur applies: an env background blurs through three's
   * `backgroundBlurriness` and an image / gradient through the viewer's own
   * pyramid blur, while a flat colour and transparency have nothing to blur.
   * Absent from a pre-v46 viewer.
   */
  backgroundSource?: "color" | "image" | "env" | "transparent";
  /**
   * The image/gradient backdrop (v45), when an image is the ACTIVE background
   * source — a colour, an env background or transparency wins instead and the
   * field is absent. An http(s) URL round-trips as itself; an image chosen with
   * `setBackgroundImageFile` has no address of its own, so the viewer INLINES it
   * as a `data:` URL (v46) and it survives a reload like any other — the
   * original bytes when they fit the viewer's ~4 MB cap, a bounded downscaled
   * re-encode when they do not. `null` alongside `backgroundSource: "image"`
   * therefore means "the backdrop on screen is local only and was NOT saved":
   * warn about it instead of showing an empty URL box. Restoring such a snapshot
   * keeps whatever background is live — the viewer never blanks a scene to
   * transparent over a backdrop it cannot re-load.
   */
  backgroundImageUrl?: string | null;
  /**
   * How that backdrop is framed (v49). Absent ⇒ the cover fit with no zoom,
   * pan or rotation, i.e. exactly how every pre-v49 scene already looked.
   * Only meaningful while `backgroundSource` is "image".
   */
  backgroundImageFraming?: BackgroundImageFraming;
  /**
   * Per-group gem inclusion materials (v45), one entry per group that has any
   * inclusion state. Restoring is idempotent and only touches the groups named
   * here; absent in a pre-v45 snapshot, which leaves every gem alone.
   */
  gemInclusions?: GroupGemInclusionState[];

  groundShadow: boolean;
  groundReflection: boolean;
  groundReflectionOpacity: number;
  /** 0..1 frosted-blur amount of the ground reflection. v68: up to 4 — above
   *  1 the reflection is frosted further (more blur passes); 0..1 unchanged. */
  groundReflectionBlur: number;

  /** Baked ground shadow enabled (the old "depth shadow" toggle). */
  depthShadow: boolean;
  /** Mirror of groundShadowBake.brightness. */
  depthShadowOpacity: number;
  /** Full baked-shadow configuration (replaces the v10 flat shadow fields). */
  groundShadowBake: GroundShadowBakeSettings;
  lightHelperVisible: boolean;

  toneMappingExposure: number;
  lightMultiplier: number;

  keyLightIntensity: number;
  keyLightColor: string;
  keyLightDirection: Vec3;
  fillLightIntensity: number;
  fillLightColor: string;
  fillLightDirection: Vec3;
  hemiLightIntensity: number;
  hemiSkyColor: string;
  hemiGroundColor: string;

  /** Dynamic scene lights (beyond the built-in key/fill/hemi rig). */
  sceneLights: SceneLightState[];
  /** Meshes receive shadows from casting lights (default off). */
  selfShadowing: boolean;
  /** Randomized (progressively-softened) self-shadow config (v32). */
  randomizedSelfShadow: RandomizedSelfShadowParams;

  postFX: PostFXSettings;

  fov: number;
  polarLimits: [number, number];
  quality: QualityMode;
  pixelRatio: number;
  cameraPosition: Vec3;
  cameraTarget: Vec3;
}

export interface MetalMaterialParams {
  kind: "metal";
  color: string;
  roughness: number;
  metalness: number;
  envMapIntensity: number;
  ior: number;
  clearcoat: number;
  clearcoatRoughness: number;
  specularIntensity: number;
  sheen: number;
  anisotropy: number;
}

export interface GemMaterialParams {
  kind: "gem";
  meshCount: number;
  bounces: number;
  ior: number;
  dispersion: number;
  reflectivity: number;
  envMapIntensity: number;
  sparkleBoost: number;
  absorptionFactor: number;
  gammaFactor: number;
  squashFactor: number;
  geometryFactor: number;
  envRotation: number;
  /** Overall diamond reflectivity dials (v30; default 1 = unchanged). */
  surfaceReflBoost: number;
  internalEnvBoost: number;
  boostFactors: [number, number, number];
  color: [number, number, number];
  opacity: number;
}

export type GroupMaterialParams = MetalMaterialParams | GemMaterialParams;

// ─── Modifiers (v23, iJewel Modifiers tab) ──────────────────────────────────

/** Radial duplication (iJewel Object Rotations) — N rotated copies around a pivot. */
export interface ObjectRotationOptions {
  rotations: number;          // copy count
  rotateAroundParent: boolean;
  offset: Vec3;               // per-copy positional offset
  skips: string;              // comma indices to skip
}

/** Extrude a 2D shape along a circular tube path (iJewel Extrude Tube Shapes). */
export interface ExtrudeTubeOptions {
  shapeSegments: number;      // cross-section resolution
  tubularSegments: number;    // path resolution
  shapeScale: Vec2;           // cross-section scale
  materialSplits: string;     // normalized split positions, e.g. "0.3, 0.6"
  horizontalSplits: boolean;
  /** Master toggle (webgi). false = the modifier is off (no geometry built,
   * extrudeTube returns ""). Default true. (v31) */
  enabled?: boolean;
  /** Close the extrusion into a circular tube (webgi `extrudeCirceTube`, the
   * default). false = a straight open extruded strip. (v31) */
  extrudeCirceTube?: boolean;
}

/** Full text-decal config (iJewel Simple Text). Bakes an SVG-style text texture
 * to the chosen material channels. */
export interface SimpleTextConfig {
  text: string;
  fontSize: number;
  width: number;
  height: number;
  xOffset: number;
  yOffset: number;
  /**
   * ⚠️ STORED, NEVER READ. webgi uses these as the SVG viewBox; our
   * `makeSimpleTextTexture` sizes its canvas from `width`/`height` alone and
   * never looks at them, so setting them changes nothing. Kept rather than
   * removed, per the rule the inert-control triage set down: removing a
   * contract field is a breaking change and an unread one costs nothing. If you
   * are here because a value did not take effect — this is why.
   */
  boxWidth: number;
  boxHeight: number;
  fontFamily: string;
  fontPath: string;
  maskText: boolean;
  innerShadow: boolean;
  textColor: string;
  bgFillColor: string;
  svgBackground: string;
  transparentBg: boolean;
  applyToMap: boolean;
  applyToBumpMap: boolean;
  applyToAlphaMap: boolean;
  inverseAlphaMap: boolean;
}

/** Mesh-simplify config (iJewel meshopt simplify; we use three SimplifyModifier). */
export interface SimplifyConfig { simplifyFactor: number; errorThreshold: number; lockBorder: boolean; }

/** Relief/parallax-occlusion mapping (stored; onBeforeCompile POM is future). */
export interface ParallaxMappingConfig { enabled: boolean; debugNormals: boolean; debugHitHeight: boolean; stepCount: number; binaryStepCount: number; }

/**
 * Pointer/device-tilt parallax camera (stored).
 *
 * ⚠️ `enableEdit` and `cameraView` are STORED, NEVER READ —
 * `ParallaxCameraController` consumes only `enabled`, `invert`, `sensitivity`
 * and `damping`. `cameraView` would name the base view the parallax offsets
 * from, which needs the CameraViews registry wiring; `enableEdit` is an
 * editor-only affordance. Kept rather than removed, same rule as
 * `SimpleTextConfig.boxWidth`.
 */
export interface ParallaxCameraConfig { enabled: boolean; enableEdit: boolean; invert: boolean; sensitivity: number; damping: number; cameraView: string; }

/** Rhino .3dm import options (stored; read by the loader on next .3dm load). */
export interface RhinoImportOptions {
  importMaterials: boolean;
  forceLayerMaterials: boolean;
  replaceWithInstancedMesh: boolean;
  hideLineMesh: boolean;
  hidePointMesh: boolean;
  loadUserDataStrings: boolean;
}

// ─── Decorative / niche plugins (v25, iJewel utility_plugins parity) ─────────
// All OFF by default; animated ones drive a per-frame loop only while active.

/** cannon-es rigid-body physics (drop/collide). */
export interface PhysicsConfig { enabled: boolean; stepCount: number; delta: number; }

/** Decorative falling-particle effect. */
export interface SnowConfig {
  enabled: boolean;
  maxRange: number;       // particle-field size
  particleSize: number;
  particleSpeed: number;
  minRangeY: number;      // floor where particles reset
  paused: boolean;
}

/** Animated rippling ground surface. */
export interface WaveGroundConfig { enabled: boolean; noiseScale: number; amplitude: number; paused: boolean; }

/** Animate watch-hand meshes to a time; matches hand meshes by name/regex. */
export interface WatchHandsConfig {
  enabled: boolean;
  invertAxis: boolean;
  analog: boolean;        // continuous vs stepped
  hourOffset: number;     // 0..12
  minuteOffset: number;   // 0..60
  secondOffset: number;   // 0..60
  hour: string;           // mesh-name matcher
  minute: string;
  second: string;
  regex: boolean;         // treat matchers as regex
}

/** CSS3D overlay layer (HTML/iframe positioned in 3D). */
export interface Css3dConfig { enabled: boolean; overCanvas: boolean; }

/** Scroll-driven camera (dev preview / landing-page embeds). */
export interface ScrollCameraConfig {
  enabled: boolean;
  enableDamping: boolean;
  damping: number;
  initAnimationTime: number;
  canvasUp: boolean;
}

/** Auto-tint a set of stones across a rainbow (gem body color only). */
export interface RainbowDiamondConfig { prefix: string; count: number; loops: number; firstColor: string; }

// ─── Configurators (v22, iJewel product customization) ──────────────────────

/** A saved material appearance the buyer can switch to. */
export interface MaterialVariation { id: string; name: string; }

/** A group's underlying material identity (v31). */
export interface GroupMaterialInfo { uuid: string; name: string; type: string; }

/** Per-group material configurator — switchable material variations. */
export interface MaterialConfiguratorState {
  groupId: string;
  groupLabel: string;
  activeIndex: number; // -1 = none active
  variations: MaterialVariation[];
}

/** One geometry option in a switch node (a scene-tree node shown/hidden). */
export interface SwitchNodeOption { id: string; name: string; nodeId: string; }

/** Switchable geometry group — exactly one option visible at a time. */
export interface SwitchNodeState {
  id: string;
  name: string;
  enabled: boolean;
  activeIndex: number;
  options: SwitchNodeOption[];
  /** Move the camera to this distance from the orbit target when a variant is
   * applied (webgi SwitchNodePlugin `camDistance`; 0 = don't move). (v31) */
  camDistance?: number;
  /** Whether applying a variant triggers a scene update / render (webgi
   * `updateSceneOnSelectionChange`; default true). (v31) */
  updateSceneOnSelectionChange?: boolean;
}

/**
 * Full MeshPhysicalMaterial editor surface (v14.3, iJewel Part-4). Superset of
 * MetalMaterialParams covering sheen/clearcoat/iridescence/transmission/
 * specular/anisotropy/emission. Gem groups have no physical material, so
 * getGroupPhysicalParams returns null for them (they stay on the gem-uniform
 * surface). Every field optional in the patch.
 */
export interface PhysicalMaterialParams {
  color: string;
  roughness: number;
  metalness: number;
  ior: number;
  reflectivity: number;
  envMapIntensity: number;
  /** When true (webgi `separateEnvMapIntensity`), `envMapIntensity` is ABSOLUTE
   * — it is NOT multiplied by the global scene environment intensity at stamp
   * time. Default false. (v31) */
  overrideEnvironment?: boolean;
  opacity: number;
  transparent: boolean;
  side: "front" | "back" | "double";
  flatShading: boolean;
  specularIntensity: number;
  specularColor: string;
  clearcoat: number;
  clearcoatRoughness: number;
  sheen: number;
  sheenColor: string;
  sheenRoughness: number;
  iridescence: number;
  iridescenceIOR: number;
  iridescenceThicknessMin: number;
  iridescenceThicknessMax: number;
  transmission: number;
  thickness: number;
  attenuationColor: string;
  attenuationDistance: number;
  anisotropy: number;
  anisotropyRotation: number;
  emissive: string;
  emissiveIntensity: number;
  // Texture-map scalar companions + render flags (v20).
  bumpScale: number;
  legacyBumpScale: boolean;
  normalScaleX: number;
  normalScaleY: number;
  displacementScale: number;
  aoMapIntensity: number;
  lightMapIntensity: number;
  alphaTest: number;
  depthTest: boolean;
  depthWrite: boolean;
  colorWrite: boolean;
  polygonOffset: boolean;
  polygonOffsetFactor: number;
  polygonOffsetUnits: number;
}

// ─── Material texture maps (v20) ────────────────────────────────────────────

export type MaterialMapSlot =
  | "map" | "roughnessMap" | "metalnessMap" | "normalMap" | "bumpMap"
  | "displacementMap" | "aoMap" | "lightMap" | "emissiveMap" | "alphaMap"
  | "clearcoatMap" | "clearcoatRoughnessMap" | "clearcoatNormalMap"
  | "sheenColorMap" | "sheenRoughnessMap" | "iridescenceMap"
  | "iridescenceThicknessMap" | "transmissionMap" | "thicknessMap"
  // v31: secondary bump channel consumed by the CustomBumpMap material
  // extension (webgi CustomBumpMapPlugin) — layered over the primary bumpMap.
  | "customBumpMap";

/** A texture's tiling sub-controls (iJewel texture_transform). */
export interface TextureTransform {
  repeat: Vec2;
  offset: Vec2;
  center: Vec2;
  rotation: number; // radians
  flipY: boolean;
  anisotropy: number; // 1..16
}

export interface MaterialMapInfo {
  slot: MaterialMapSlot;
  present: boolean;
  transform: TextureTransform;
}

// ─── Material extensions (v21, iJewel Shared-PBR EXTENSION rows) ─────────────
// Per-(non-gem)-material shading add-ons. Native three.js features wire through
// (anisotropy/iridescence/flat); FragmentClipping uses real clipping planes;
// the webgi-specific injections (sparkle/triplanar/custom-bump/clearcoat-tint/
// SSBevel) are stored and round-trip (applied where the engine supports them).
export interface MaterialExtensionConfig {
  // Anisotropy (brushed-metal streaks) — native.
  anisotropyEnabled: boolean;
  anisotropyFactor: number;   // -2..2
  anisotropyNoise: number;    // 0..2
  // Triplanar (UV-less projection).
  triplanarEnabled: boolean;
  triplanarScale: number;     // 0.1..10
  triplanarBlend: number;     // 1..10
  // ThinFilmLayer (iridescent thin film) — native iridescence.
  thinFilmEnabled: boolean;
  thinFilmIntensity: number;  // 0..1
  // FragmentClipping (cutaway) — real clipping plane.
  clippingEnabled: boolean;
  clipPosition: Vec3;
  clipInvert: boolean;
  // Flat Material (render as flat / 2D).
  flat2D: boolean;
  // SparkleBump / NoiseBump (procedural flakes).
  sparkleEnabled: boolean;
  sparkleBumpScale: number;   // 0..0.001
  sparkleFlakeScale: number;  // 100..10000
  sparkleFlakeRadius: number; // 0.01..1
  sparkleColored: boolean;
  // CustomBumpMap (extra bicubic bump channel).
  customBumpEnabled: boolean;
  customBumpScale: number;    // -1..1
  // ClearcoatTint (tinted clearcoat) — native clearcoat + tint.
  clearcoatTintEnabled: boolean;
  clearcoatTintColor: string;
  clearcoatTintThickness: number;
  clearcoatTintIOR: number;   // 0.8..2.5
  // Per-material SSBevel.
  ssBevelEnabled: boolean;
  ssBevelRadius: number;      // 0..16
  // LayeredMaterial (webgi cavity layer, gltf ext WEBGI_materials_layered). A
  // screen-space curvature "worn crevice / patina" darkening — NOT a second
  // albedo layer. Metal-only; default OFF ⇒ material stays pristine (v32).
  layeredEnabled: boolean;        // default false
  layeredCutoff: number;          // curvature threshold; default -0.65; range -2..2
  layeredCavityColor: string;     // hex; default "#585757"
  layeredCavityRoughness: number; // default 0.5; range 0..1
  layeredCavityMetalness: number; // default 0.5; range 0..1
  layeredCavityScale: number;     // default 1; range 0..4
  layeredStepSize: number;        // SS curvature sample step; default 5; range 0..16
}

/** A library material the user can apply to a group (v14.3). */
export interface MaterialPresetItem {
  id: string;
  label: string;
  category: "metal" | "gem" | "ceramic" | "pearl" | "other";
  /** Swatch hint (hex) for the grid. */
  color?: number;
}

/**
 * Auto-assign info on load (iJewel "N materials with supported layer names").
 * ⚠️ No name is matched anywhere: `count` is the number of material groups, and
 * `applyDefaultMaterials` repaints every group yellow gold (polished) except
 * the groups classified as gems, which take the first diamond preset. A host
 * that wants name matching does it itself (the playground reads what the file
 * names a part, and calls neither method).
 */
export interface AutoAssignInfo {
  /**
   * The number of material groups in the scene — meshes grouped by their
   * source material, `listGroups().length` — i.e. every part, not a match
   * count.
   */
  count: number;
  /**
   * `count > 0` until `applyDefaultMaterials` runs. There is no dismiss call
   * in the viewer (a host's own "no thanks" stays host-side), and every
   * regrouping — a model added, swapped or removed, an object array or a
   * variation rebuild — opens the offer again.
   */
  available: boolean;
}

export type MetalParamName =
  | "roughness" | "metalness" | "envMapIntensity" | "ior"
  | "clearcoat" | "clearcoatRoughness" | "specularIntensity" | "sheen" | "anisotropy";

export type GemUniformName =
  | "bounces" | "ior" | "dispersion" | "reflectivity" | "envMapIntensity"
  | "sparkleBoost" | "absorptionFactor" | "gammaFactor" | "squashFactor"
  | "geometryFactor" | "envRotation"
  // v30: overall diamond-reflectivity intensities. Both default 1.0 (identity).
  // `surfaceReflBoost` scales the first-surface env reflection; `internalEnvBoost`
  // scales the env light exiting after internal bounces. ×1.0 ⇒ bit-identical.
  | "surfaceReflBoost" | "internalEnvBoost";

// ─── Band DTOs (duplicated, serializable mirror of band/BandConfig.ts) ──────

/**
 * How many metal partitions the band is divided into — a LABEL for the count,
 * not the count itself. The geometry is driven by `partitionRatios.length`
 * (see `resolvePartitionRatios`); this only names the reference's five tiles
 * Single · Duo · Tri · Quad · Multi.
 *
 * v42 added "quad" and "multi"; the three original members are unchanged, so a
 * saved design loads and renders exactly as before. "multi" is FIVE OR MORE —
 * `splitModeForCount` collapses everything past four onto it, which is why the
 * count must never be re-derived from the mode.
 */
export type SplitMode = "none" | "two-tone" | "tricolor" | "quad" | "multi";

/** Per-partition metal overrides (custom color + advanced PBR) layered over
 * preset+finish. Mirrors the editable fields of MetalMaterialParams. */
export interface MetalParamOverride {
  color?: string;
  roughness?: number;
  metalness?: number;
  envMapIntensity?: number;
  ior?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  specularIntensity?: number;
  sheen?: number;
  anisotropy?: number;
  reflectivity?: number;
  specularColor?: string;
}

export interface PartitionMaterial {
  presetId: string;
  finishId: string;
  override?: MetalParamOverride;
  /** v41: display name for this partition ("Metal 1", "Metal 2", …). The engine
   * fills the positional default on read, so a host can print `label` blindly;
   * `setPartitionLabel` overrides it and `null` restores the default. */
  label?: string;
}

export type DiamondStyle =
  | "none" | "rubbed-in" | "side-entry" | "waste" | "side-shift" | "channel"
  | "canal-across" | "side-channel" | "tension-1" | "tension-2" | "free-stones"
  | "prong-4" | "prong-6" | "memoir"
  // v37 (Ring Builder): flush "eye" setting (stone sunk into a dimple), the
  // three cross settings (stones carried across the seam of a division), and
  // "free-scatter" — a deterministic golden-angle scatter. NOTE it is a NEW id,
  // not a redefinition of "free-stones": that one stays the even fan it has
  // always been so saved designs keep rendering identically.
  | "eye" | "cross-bezel" | "cross-channel" | "cross-around" | "free-scatter"
  // v39: the reference's remaining top "Setting Type" tiles. "vertical" is the
  // plain circumferential row; "clamp" seats it between two metal rails (the
  // channel cut), "clamp-angled" runs that cut on the diagonal.
  | "vertical" | "clamp" | "clamp-angled"
  // v40: their "Free" tile — EXPLICIT per-stone positions from
  // `BandState.freeStones`, dragged onto the unrolled band. A third id again,
  // not a redefinition: "free-stones" stays the even fan and "free-scatter" the
  // golden-angle scatter.
  | "free-place"
  // v67: a parametric stone PATTERN from `BandState.stonePattern` (1–4
  // layers expanded by `buildStonePattern`). Its own id: not side by side with
  // the run, which keeps the run-only paths (warnings, CNC rows) untouched.
  | "pattern";

/** v39: which surface a stone run is set into. Omit ⇒ "top" (today: the outer
 * surface). "side" is geometrically distinct — the seat is carved into the flat
 * side wall and the stone's table faces along the finger axis. */
export type DiamondPlacement = "top" | "side";

/** v39: which side wall a `placement: "side"` run occupies. "left" is the −Y
 * half of the cross-section, matching `EdgeSides`. Omit ⇒ "both". */
export type DiamondSides = "left" | "right" | "both";

/** v39: the metal holding a SIDE-set stone. "channel" is one milled groove the
 * whole run drops into (the reference photo); "bezel" is a collar per stone;
 * "prong" is a pair of bead prongs. Omit ⇒ "bezel". */
export type SideSetting = "none" | "bezel" | "channel" | "prong";

/** v39: the reference's top-surface "Diamond Setting". "only" means stones with
 * no metal hardware at all. Omit ⇒ whatever the `DiamondStyle` implies today. */
export type DiamondSetting = "channel" | "prongs" | "flush" | "only"
  // v67: pavé. "pave" holds each stone by small beads raised at every junction
  // between neighbours (both sides of the row, end beads at a row's ends);
  // "shared-prong" by one claw shared by the two stones it sits between. Both
  // are metal (counted in the weight, never in the stone or bead counts).
  | "pave" | "shared-prong";

/** v39: stone-to-stone pitch. The first four are multiples of the stone
 * diameter; the last three distribute the run evenly over half / a third / all
 * of the ring. Omit ⇒ today's 1.05× diameter. */
export type StoneSpacing =
  | "touching" | "half-stone" | "full-stone" | "double"
  | "half-ring" | "third-ring" | "full-ring"
  // v67: "close" — the tightest pitch the builder lays out: 1.03 d, never
  // less than the builder's minimum web between girdles (0.06 mm). For pavé
  // and shared claws, whose metal sits beside the row, not between stones.
  | "close";

/** v39: the five colour tiles. Each maps onto an existing gem preset plus a
 * `gemParams` tint — there is no second gem material path. */
export type DiamondColor = "white" | "black" | "yellow" | "pink" | "ruby";

// v43 DIAMOND SPECIFICATION (the reference's Custom Settings: Diamond Origin ·
// Color Quality (GIA) · Diamond Clarity). These three are SPECIFICATION AND
// PRICE metadata — they say what stone is being quoted, not how it is drawn.
// Nothing here ever reaches the gem shader: colour/clarity do not tint, dim or
// re-bake anything, and a design that only changes them renders bit-identically.
// (`DiamondColor` above is the separate LOOK control and does drive the shader.)

/** v43: GIA colour grade, D (colourless) … K (faint tint). */
export type GiaColorGrade = "D" | "E" | "F" | "G" | "H" | "I" | "J" | "K";

/** v43: clarity grade, FL (flawless) … I (included). The reference's long
 * labels are in `GEM_CLARITIES`. */
export type DiamondClarity = "FL" | "IF" | "VVS" | "VS" | "SI" | "I";

/** Labels verbatim from the reference. `DiamondOrigin`'s third member "skip"
 * is the engagement centre stone's "no stone" case and is deliberately not
 * offered for a band run. */
export const DIAMOND_ORIGINS: { id: DiamondOrigin; label: string }[] = [
  { id: "natural", label: "Natural Diamonds" },
  { id: "lab", label: "Lab Grown Diamonds" },
];
export const GEM_COLOR_GRADES: { id: GiaColorGrade; label: string }[] =
  (["D", "E", "F", "G", "H", "I", "J", "K"] as const).map((id) => ({ id, label: id }));
export const GEM_CLARITIES: { id: DiamondClarity; label: string }[] = [
  { id: "FL", label: "FL (Flawless)" },
  { id: "IF", label: "IF (Internally Flawless)" },
  { id: "VVS", label: "VVS (Very Very Slightly Included)" },
  { id: "VS", label: "VS (Very Slightly Included)" },
  { id: "SI", label: "SI (Slightly Included)" },
  { id: "I", label: "I (Included)" },
];

/** v39: a run carries gems or metal beads. A gold bead is METAL: it is named so
 * the gem classifier can never match it, takes the band metal, and is excluded
 * from carat totals (but not from metal volume/weight). Omit ⇒ "diamond". */
export type StoneType = "diamond" | "goldBead";

/** v39: one stone set into the INSIDE of the band (their Engraving tab's
 * None · Diamond). Absent ⇒ no inner stone, i.e. today. */
export interface InnerDiamondConfig {
  enabled: boolean;
  /** Stone diameter, mm. Omit ⇒ derived from the band width. */
  sizeMM?: number;
  /** Circumferential angle, degrees. Omit ⇒ 0 (opposite the default engraving). */
  angleDeg?: number;
}

/**
 * v40: ONE explicitly placed stone (`DiamondStyle` "free-place"). The host's
 * unrolled-band editor is a direct view of this array — the strip's length is
 * `thetaDeg`, its width is `u`.
 *
 * Every optional field falls back to the run-level `DiamondConfig` value, so an
 * arrangement of plain stones is `{ id, thetaDeg, u }` and nothing more.
 */
export interface FreeStone {
  /** Stable within the arrangement; the host addresses stones by it. */
  id: string;
  /** Angle around the ring, degrees, 0–360 (the strip's length). */
  thetaDeg: number;
  /** Position across the band width, −1 … +1 — the same normalised convention
   * as `DiamondConfig.rowPosition`. ±1 is as far as the stone can go WITHOUT
   * overhanging, so a bigger stone reaches a smaller absolute offset; the
   * engine clamps, and the host cannot express an off-band stone. */
  u: number;
  /** Carats, ABSOLUTE. Omit ⇒ the run's `stoneSizeMM`.
   *  Prefer `sizeScale` for a stamp: an absolute carat pins the stone, so the
   *  run's Stone Size slider stops moving it. */
  sizeCt?: number;
  /** v52 (item 23.3) — size as a MULTIPLE of the run's stone size, so a stamped
   *  arrangement keeps its graduation while the Stone Size slider still drives
   *  the whole set. Wins over `sizeCt` when both are present. */
  sizeScale?: number;
  /** Gem or gold bead. Omit ⇒ the run's `type`. */
  type?: StoneType;
  /** Colour tile. Omit ⇒ the run's `color`. */
  color?: DiamondColor;
  /** Hardware override. Omit ⇒ the run's `setting`. */
  setting?: DiamondSetting;
  // v43 per-stone specification overrides. Each omitted ⇒ the run's value.
  // Metadata only — see the DIAMOND SPECIFICATION note above.
  origin?: DiamondOrigin;
  colorGrade?: GiaColorGrade;
  clarity?: DiamondClarity;
}

/** v40: the pattern stamps. The engine generates the arrangement from the live
 * band geometry (`applyFreePattern`), so it scales with circumference and width;
 * the host only sends the id. Deterministic — no RNG anywhere.
 *
 * v43: the union of the reference's six tiles (listed first, stamped from its
 * MEASURED artwork) and the four generated curves of ours it has no counterpart
 * for. Every pre-v43 id stays valid — `scatter` and `cluster` are in both sets
 * and now stamp the measured artwork instead of the R₂ scatter. */
export type FreePatternId =
  | "scatter" | "wave" | "cascade" | "dipper" | "cluster" | "halo"
  | "line" | "s-curve" | "arc" | "rosette";

/** The pattern tiles, the reference's six in its own order first. `count` is the
 * stamp's natural stone count, which `applyFreePattern` uses unless the caller
 * overrides it (measured stamps ignore an override — see `buildFreePattern`). */
export const FREE_PATTERNS: {
  id: FreePatternId;
  label: string;
  count: number;
  /**
   * v52 (item 22) — can this stamp be re-sampled to a different stone count?
   *
   * "path": the arrangement is a CURVE, so N stones can be spaced along it and
   * it is still recognisably the same pattern. "fixed": the arrangement IS the
   * pattern (a scatter, a cluster, a halo) and re-sampling it would just be a
   * different scatter, so `count` is ignored and the tile keeps its own.
   *
   * Absent ⇒ "path" for the generated curves, which have always honoured
   * `count`, and "fixed" for a measured stamp, which is what shipped.
   */
  resample?: "path" | "fixed";
}[] = [
  { id: "scatter", label: "Scatter",  count: 13, resample: "fixed" },
  { id: "wave",    label: "Wave",     count: 15, resample: "path" },
  { id: "cascade", label: "Cascade",  count: 12, resample: "path" },
  { id: "dipper",  label: "Dipper",   count: 7,  resample: "path" },
  { id: "cluster", label: "Cluster",  count: 9,  resample: "fixed" },
  { id: "halo",    label: "Halo",     count: 11, resample: "fixed" },
  { id: "line",    label: "Line",     count: 5,  resample: "path" },
  { id: "s-curve", label: "S-Curve",  count: 9,  resample: "path" },
  { id: "arc",     label: "Arc",      count: 7,  resample: "path" },
  { id: "rosette", label: "Rosette",  count: 7,  resample: "fixed" },
];

/**
 * Normalise an arrangement so no host can store an invalid one: angles wrap into
 * 0–360, `u` is clamped to the normalised ±1 (which is itself the "no overhang"
 * range — `gemPlacement` scales it by `halfWidth − stoneR`, per stone, so a
 * bigger stone reaches a smaller absolute offset), and every stone ends up with
 * an id. Ids are positional, never random: the harness needs reproducibility.
 */
export function clampFreeStones(stones: readonly Partial<FreeStone>[]): FreeStone[] {
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  return stones.map((s, i) => {
    const out: FreeStone = {
      id: s.id || `fs${i + 1}`,
      thetaDeg: ((((s.thetaDeg ?? 0) % 360) + 360) % 360),
      u: clamp(s.u ?? 0, -1, 1),
    };
    if (s.sizeCt != null) out.sizeCt = Math.max(0.001, s.sizeCt);
    if (s.type) out.type = s.type;
    if (s.color) out.color = s.color;
    if (s.setting) out.setting = s.setting;
    // v43 per-stone specification. These were DECLARED on FreeStone in v43 but
    // never copied here, and this function is on the write path of BOTH
    // `setFreeStones` and `updateFreeStone` — so every save, every drag and
    // every pattern stamp erased whatever origin / colour grade / clarity the
    // host had set on an individual stone. Metadata only: it feeds the spec
    // sheet and the price, and never reaches the gem shader.
    if (s.sizeScale != null) out.sizeScale = Math.max(0.05, Math.min(4, s.sizeScale));
    if (s.origin) out.origin = s.origin;
    if (s.colorGrade) out.colorGrade = s.colorGrade;
    if (s.clarity) out.clarity = s.clarity;
    return out;
  });
}

/**
 * v53: the reference's six tiles, RE-MEASURED off its live ring.
 *
 * v43 read these off the tile ARTWORK (viewBox 40x96 — cx across the width, cy
 * around the ring, r the size). The shapes it recovered were right; the two
 * SCALES it converted them with were not, and the error is why the stamps read
 * as sparse and small:
 *
 *  · ANGLE. The artwork's 96-unit height is 96 DEGREES, 1:1 — measured, their
 *    Wave spans 95.95 deg. v43 stretched each tile to fill the ring instead
 *    (Wave 316.5 deg, 3.3x too wide), so a motif meant to sit in a 96 deg arc
 *    of touching stones was smeared around the whole band with three times the
 *    metal between stones. Halo was worst: 57.8 deg against a true 17.5.
 *  · WIDTH. Their `u` is a fraction of the HALF-WIDTH; v43's was 0.800x that,
 *    uniformly across five of the six tiles, so every stamp also swung too
 *    little across the band.
 *  · SIZE. The tiles carry ABSOLUTE stone diameters, quantised to 0.1 mm
 *    (0.5 - 1.8 mm) and identical on two rings of different widths. v43
 *    normalised them to a peak of 1.0, which made every stamped stone at best
 *    the run's nominal size and usually smaller. They are stored here as a
 *    scale against the reference's own 0.92 mm nominal, so the default band
 *    reproduces its millimetres while the run's Stone Size slider still drives
 *    the whole arrangement (v52, item 23.3).
 *
 * Method and raw numbers: `docs/plans/ijewel-free-pattern-measurements.md`.
 * Measured against the BAND's bounding box, never a circle fitted to the
 * stones — Halo spans 17.5 deg and a fit on that arc is degenerate (it
 * reported 5.1 deg).
 *
 * `u` here is our own convention: a fraction of `rowLimitMM(halfWidth, dia)`,
 * the seat-bounded legal range, which `freeRun` resolves PER STONE. That is a
 * different curve from the reference's fraction-of-half-width, so these values
 * are exact on the 3.50 mm default band and drift up to ~15% wider at 5.00 mm.
 * The seat bound is a real guarantee (it is what stops a pocket being cut off
 * the rim), so it wins over matching the reference exactly on wide bands.
 * Halo's three outermost stones want u 1.01 and are stored clamped to 1.0 —
 * 0.014 mm shy of the reference.
 *
 * Their tile is the unrolled band, so an entry's angle is already absolute
 * around the ring and `u` is already band-relative — which is why the pitch
 * scaling the four generated curves need does not apply to these. The artwork's
 * centre line is 180 deg, so a stamp is re-centred on `centerDeg`.
 */
const MEASURED_CENTER_DEG = 180;
const MEASURED_FREE_PATTERNS: Partial<Record<FreePatternId, readonly { u: number; thetaDeg: number; size: number }[]>> = {
  scatter: [
    { u: -0.1524, thetaDeg: 138.03, size: 0.6522 },
    { u: 0.3908, thetaDeg: 146.03, size: 0.8696 },
    { u: -0.4956, thetaDeg: 153.02, size: 1.087 },
    { u: 0.127, thetaDeg: 160.02, size: 0.6522 },
    { u: -0.3479, thetaDeg: 167.01, size: 1.3043 },
    { u: 0.4187, thetaDeg: 173.01, size: 0.8696 },
    { u: 0, thetaDeg: 180, size: 1.7391 },
    { u: -0.5421, thetaDeg: 187, size: 1.087 },
    { u: 0.254, thetaDeg: 192.99, size: 0.6522 },
    { u: -0.1984, thetaDeg: 199.99, size: 1.5217 },
    { u: 0.4884, thetaDeg: 206.98, size: 0.8696 },
    { u: -0.4646, thetaDeg: 213.98, size: 1.087 },
    { u: 0.1905, thetaDeg: 221.97, size: 0.6522 },
  ],
  wave: [
    { u: 0, thetaDeg: 132.025, size: 0.6522 },
    { u: -0.3188, thetaDeg: 138.875, size: 0.7717 },
    { u: -0.6091, thetaDeg: 145.735, size: 0.9022 },
    { u: -0.804, thetaDeg: 152.585, size: 1.0217 },
    { u: -0.8587, thetaDeg: 159.445, size: 1.1522 },
    { u: -0.7345, thetaDeg: 166.295, size: 1.2717 },
    { u: -0.4395, thetaDeg: 173.145, size: 1.4022 },
    { u: 0, thetaDeg: 180.005, size: 1.5217 },
    { u: 0.4395, thetaDeg: 186.855, size: 1.4022 },
    { u: 0.7345, thetaDeg: 193.705, size: 1.2717 },
    { u: 0.8587, thetaDeg: 200.565, size: 1.1522 },
    { u: 0.804, thetaDeg: 207.415, size: 1.0217 },
    { u: 0.6091, thetaDeg: 214.265, size: 0.9022 },
    { u: 0.3188, thetaDeg: 221.125, size: 0.7717 },
    { u: 0, thetaDeg: 227.975, size: 0.6522 },
  ],
  cascade: [
    { u: -1, thetaDeg: 140, size: 1.087 },
    { u: -0.5715, thetaDeg: 147.27, size: 0.6522 },
    { u: -0.4001, thetaDeg: 154.55, size: 1.3043 },
    { u: -0.5164, thetaDeg: 161.82, size: 0.8696 },
    { u: -0.2976, thetaDeg: 169.09, size: 1.5217 },
    { u: 0.1084, thetaDeg: 176.36, size: 1.087 },
    { u: -0.0889, thetaDeg: 183.64, size: 0.6522 },
    { u: 0.2609, thetaDeg: 190.91, size: 1.3043 },
    { u: 0.5164, thetaDeg: 198.18, size: 0.8696 },
    { u: 0.4564, thetaDeg: 205.45, size: 1.5217 },
    { u: 0.6969, thetaDeg: 212.73, size: 1.087 },
    { u: 0.8509, thetaDeg: 220, size: 0.6522 },
  ],
  dipper: [
    { u: 0.2088, thetaDeg: 154, size: 1.3043 },
    { u: -0.5219, thetaDeg: 156, size: 1.3043 },
    { u: 0.2783, thetaDeg: 169, size: 1.3043 },
    { u: -0.4523, thetaDeg: 170, size: 1.3043 },
    { u: -0.4646, thetaDeg: 182, size: 1.087 },
    { u: -0.5885, thetaDeg: 194, size: 1.087 },
    { u: -0.4646, thetaDeg: 206, size: 1.087 },
  ],
  cluster: [
    { u: 0.2674, thetaDeg: 156.02, size: 0.5435 },
    { u: -0.0762, thetaDeg: 162.01, size: 0.6522 },
    { u: 0.1954, thetaDeg: 168.01, size: 0.8696 },
    { u: -0.3097, thetaDeg: 174, size: 1.087 },
    { u: 0, thetaDeg: 180, size: 1.9565 },
    { u: 0.3407, thetaDeg: 185.99, size: 1.087 },
    { u: -0.1675, thetaDeg: 191.99, size: 0.8696 },
    { u: 0.0635, thetaDeg: 197.98, size: 0.6522 },
    { u: -0.2674, thetaDeg: 203.98, size: 0.5435 },
  ],
  halo: [
    { u: -0.3124, thetaDeg: 171.23, size: 0.7609 },
    { u: 0.3124, thetaDeg: 171.23, size: 0.7609 },
    { u: -0.8177, thetaDeg: 174.58, size: 0.7609 },
    { u: 0.8177, thetaDeg: 174.58, size: 0.7609 },
    { u: 0, thetaDeg: 180, size: 1.7391 },
    { u: -1, thetaDeg: 180, size: 0.7609 },
    { u: 1, thetaDeg: 180, size: 0.7609 },
    { u: 0.8177, thetaDeg: 185.42, size: 0.7609 },
    { u: -0.8177, thetaDeg: 185.42, size: 0.7609 },
    { u: 0.3124, thetaDeg: 188.77, size: 0.7609 },
    { u: -0.3124, thetaDeg: 188.77, size: 0.7609 },
  ],
};

/**
 * Generate one pattern stamp.
 *
 * Two families. The reference's six are stamped verbatim from the measured
 * artwork above, whose axes are already band-relative. Our four generated
 * curves are expressed in PITCH UNITS — one unit is one stone-to-stone step
 * — and the caller supplies how big a pitch is in each axis (`stepDeg` around
 * the ring, `stepU` across the width). That is what makes a stamp scale with the
 * band instead of being a fixed set of coordinates, and it keeps the round
 * patterns actually round: the same pitch in both axes is the same distance in
 * mm on the real band.
 *
 * It lives in the contract, next to `FreePatternId`, for the same reason
 * `UK_RING_SIZES` does: the engine stamps with it and the host's pattern
 * thumbnails are drawn from it, and an icon that disagreed with what it stamps
 * would be a lie. One implementation, both repos.
 */
/**
 * Space `n` stones evenly along a measured stamp's own polyline (v52, item 22).
 *
 * The points are treated as an ordered path in (thetaDeg, u) and re-sampled by
 * ARC LENGTH, so the stones stay evenly spread rather than bunching wherever the
 * original happened to be dense. `size` is interpolated too, which is what keeps
 * a graduated stamp graduated at a different count.
 *
 * Only ever called for a `resample: "path"` tile — see `FREE_PATTERNS`.
 */
function resampleAlong(
  pts: readonly { u: number; thetaDeg: number; size: number }[],
  n: number,
): { u: number; thetaDeg: number; size: number }[] {
  if (pts.length < 2 || n < 1) return [...pts];
  if (n === 1) return [pts[Math.floor(pts.length / 2)]];
  // Cumulative arc length. `thetaDeg` and `u` are different units, so `u` is
  // weighted to a comparable scale — the stamps span ~±0.5 in u against tens of
  // degrees, and without this the path length would be pure angle.
  const U_TO_DEG = 40;
  const acc = [0];
  for (let i = 1; i < pts.length; i++) {
    const dT = pts[i].thetaDeg - pts[i - 1].thetaDeg;
    const dU = (pts[i].u - pts[i - 1].u) * U_TO_DEG;
    acc.push(acc[i - 1] + Math.hypot(dT, dU));
  }
  const total = acc[acc.length - 1] || 1;
  const out: { u: number; thetaDeg: number; size: number }[] = [];
  for (let k = 0; k < n; k++) {
    const want = (k / (n - 1)) * total;
    let i = 1;
    while (i < acc.length - 1 && acc[i] < want) i++;
    const span = acc[i] - acc[i - 1] || 1;
    const t = Math.min(1, Math.max(0, (want - acc[i - 1]) / span));
    const a = pts[i - 1], b = pts[i];
    out.push({
      thetaDeg: a.thetaDeg + (b.thetaDeg - a.thetaDeg) * t,
      u: a.u + (b.u - a.u) * t,
      size: a.size + (b.size - a.size) * t,
    });
  }
  return out;
}

export function buildFreePattern(
  id: FreePatternId,
  opts: {
    count?: number;
    sizeCt?: number;
    stepDeg?: number;
    stepU?: number;
    centerDeg?: number;
    /** v52 (item 22) — compress or stretch the stamp's ANGULAR extent, degrees.
     *  Absent ⇒ the stamp's own span, so nothing moves unasked. */
    spanDeg?: number;
  } = {},
): FreeStone[] {
  const preset = FREE_PATTERNS.find((p) => p.id === id) ?? FREE_PATTERNS[0];
  const n = Math.max(1, Math.round(opts.count ?? preset.count));
  const stepDeg = opts.stepDeg ?? 6;
  const stepU = opts.stepU ?? 0.5;
  const centerDeg = opts.centerDeg ?? 0;

  const measured = MEASURED_FREE_PATTERNS[preset.id];
  if (measured) {
    // A "fixed" stamp IS its arrangement — a scatter re-sampled is just a
    // different scatter — so `count` is ignored for those, exactly as before.
    // A "path" stamp is a curve, and item 22 ("I want to increase Number of
    // diamonds in Patterns") is asking to space more stones along it.
    const path = preset.resample === "path";
    const src = path && opts.count != null ? resampleAlong(measured, n) : measured;

    // Item 22's other half: "in wave it goes extremely left to right". The
    // stamp's own angular extent is preserved unless a span is asked for, so no
    // saved design and no untouched stamp moves.
    const spread = src.map((p) => p.thetaDeg - MEASURED_CENTER_DEG);
    const natural = Math.max(...spread) - Math.min(...spread);
    const k = opts.spanDeg != null && natural > 1e-6 ? opts.spanDeg / natural : 1;

    return clampFreeStones(src.map((p, i) => ({
      id: `${preset.id}-${i + 1}`,
      thetaDeg: centerDeg + (p.thetaDeg - MEASURED_CENTER_DEG) * k,
      u: p.u,
      // `size` is a DIAMETER fraction; `sizeCt` is carats, and diameter goes as
      // the cube root of carats (`caratToStoneMM`), hence the cube.
      //
      // v52: emitted as a SCALE, so the run's Stone Size slider still drives the
      // whole arrangement while the stamp keeps its graduation (item 23.3). An
      // explicit `sizeCt` still wins, for a caller that wants absolute carats.
      ...(opts.sizeCt != null ? { sizeCt: opts.sizeCt * p.size ** 3 } : { sizeScale: p.size }),
    })));
  }

  // (along the ring, across the width), both in pitch units.
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1) - 0.5; // −0.5 … +0.5 along the stamp
    switch (id) {
      case "line":    pts.push([t * (n - 1), 0]); break;
      case "s-curve": pts.push([t * (n - 1), 1.1 * Math.sin(2 * Math.PI * t)]); break;
      case "arc":     pts.push([t * (n - 1), 1.1 * Math.cos(Math.PI * t)  - 0.55]); break;
      case "rosette": {
        if (i === 0) { pts.push([0, 0]); break; }
        const k = (i - 1) / Math.max(1, n - 1);
        const ang = k * Math.PI * 2;
        pts.push([1.15 * Math.cos(ang), 1.15 * Math.sin(ang)]);
        break;
      }
    }
  }

  // A narrow band cannot hold the stamp's full across-extent. SCALE it to fit
  // rather than let the clamp flatten every outlying stone onto the edge — a
  // squashed rosette is still a rosette, a clamped one is a straight line.
  const reach = Math.max(...pts.map(([, across]) => Math.abs(across))) * stepU;
  const fit = reach > 1 ? 1 / reach : 1;

  return clampFreeStones(pts.map(([along, across], i) => ({
    id: `${id}-${i + 1}`,
    thetaDeg: centerDeg + along * stepDeg,
    u: across * stepU * fit,
    ...(opts.sizeCt != null ? { sizeCt: opts.sizeCt } : {}),
  })));
}

/**
 * A stamp's own angular extent, in degrees — what `applyFreePattern` will really
 * lay down when no `spanDeg` is asked for.
 *
 * Lives here, next to the table it reads, for the same reason `buildFreePattern`
 * does: the host's Spread control has to START at the stamp's own value or the
 * first touch of any slider silently re-stamps the tile at someone else's width.
 * That cannot be a constant — since the v53 re-measure the six reference tiles
 * run from Halo's 17.5 deg to Wave's 96 — and a host deriving it separately
 * would be a second implementation free to drift.
 *
 * Stamps are centred on 0 and `clampFreeStones` wraps into 0-360, so the extent
 * is the complement of the largest gap, not `max - min`.
 */
export function freePatternSpanDeg(id: FreePatternId): number {
  const t = buildFreePattern(id, {})
    .map((s) => s.thetaDeg)
    .sort((a, b) => a - b);
  if (t.length < 2) return 0;
  let gap = t[0] + 360 - t[t.length - 1];
  for (let i = 1; i < t.length; i++) gap = Math.max(gap, t[i] - t[i - 1]);
  return 360 - gap;
}

// ─── Stone patterns (v67, band designs round W2 — `DiamondStyle` "pattern") ──
//
// A pattern is 1–4 LAYERS, each a small parametric description (a path, a
// stone count and size, a sizing law, spacing, groups, a setting, an optional
// pocket and rails, repeat / mirror) that `buildStonePattern` expands into
// stone POSITIONS. Everything stays live: the layers are the truth, a library
// entry is only where they came from (`StonePattern.libraryId`, bookkeeping).
//
// THE PLANE is the path-groove plane (`DesignGroovePath`): `arcMM` is arc
// length round the ring at the outer crest radius from 12 o'clock (+ toward
// 3 o'clock), `yMM` is mm from the band's centre line (+ toward the +Y edge).
// Layers are in mm, never in FreeStone's per-stone-scaled `u`; the engine
// converts to its stone frame only at expansion.
//
// THE GENERATOR decides positions only — sinks, settings, girdle clamps and
// seats stay in the engine. It is deterministic by construction: no
// Math.random, no Math.sin/cos/pow/hypot/atan (they are not required to be
// correctly rounded, so two engines could disagree in the last bit and flip a
// keep/reject decision). Every number comes from + − × ÷ and √, which IEEE-754
// rounds exactly, plus an integer RNG (`Math.imul`). The trigonometry it needs
// is evaluated by its own polynomial (`patternSin`). verify:patterns greps this
// block for the forbidden calls.
//
// SIZE-INDEPENDENT: a layer whose span is in mm (or has none) expands to the
// SAME stones on every ring size, so a CNC batch across sizes keeps the
// pattern; only `spanTurns`, `repeat` and the wrap at 6 o'clock read the
// circumference.

/** The layer's path. "row" runs round the ring; "diagonal" is a row tilted by
 * `angleDeg` (default 25); "sweep" crosses the band in a half sine;
 * "chevron" a V; "zigzag" a triangle wave and "wave" a sine (`waves`
 * periods); "arc" a parabola (a smile, a frown with a negative amplitude);
 * "s-curve" one full sine; "multi-row" 1–3 parallel rows (`rows`, staggered
 * by default); "cluster" a packed group; "rosette" a centre stone in a ring
 * of petals; "scatter" a seeded random spread (with `groups`: a
 * constellation of small clusters); "custom" the polyline in `points`. */
export type StonePatternPath =
  | "row" | "diagonal" | "sweep" | "chevron" | "zigzag" | "arc" | "wave" | "s-curve"
  | "multi-row" | "cluster" | "rosette" | "scatter" | "custom";

export type StonePatternSizingMode = "uniform" | "graduated" | "alternating" | "random";

/** How stone size varies along the layer, as a SCALE of the base size. */
export interface StonePatternSizing {
  /** Omit ⇒ "uniform" ("graduated" for a rosette: the centre is the big stone). */
  mode?: StonePatternSizingMode;
  /** graduated: the scale at the far end, 0.2 … 3 (clamped). Omit ⇒ 0.6. */
  endScale?: number;
  /** graduated: the exponent of the fall-off, 0.25 … 4. Omit ⇒ 1 (linear). */
  curve?: number;
  /** graduated: "centred" = biggest in the middle (or the centre of a cluster /
   *  rosette), "one-sided" = biggest at the first stone (a comet). Omit ⇒ "centred". */
  anchor?: "centred" | "one-sided";
  /** alternating: the scale of every other run, 0.2 … 3. Omit ⇒ 0.7. */
  altScale?: number;
  /** alternating: stones per run, 1 … 4. Omit ⇒ 1. */
  period?: number;
  /** random: the seeded range, 0.2 … 3 each. Omit ⇒ 0.6 … 1.2. */
  min?: number;
  max?: number;
}

/** The setting a layer's stones get, mapped onto the existing vocabulary by
 * `STONE_PATTERN_SETTINGS` ("bezel" = flush seat + a collar per stone). */
export type StonePatternSetting = "flush" | "bezel" | "pave" | "shared-prong" | "prongs" | "only";

/** "capsule": the convex hull of the seats (a round-ended trough, tapered when
 * the stones are graduated); "channel": a constant-width trough following
 * the stones; "navette": a pointed eye with filleted tips; "plate": a
 * rounded rectangle. The last two are convex. */
export type StonePatternPocketShape = "none" | "capsule" | "channel" | "navette" | "plate";

export interface StonePatternPocket {
  /** Omit ⇒ "none". */
  shape?: StonePatternPocketShape;
  /** Floor depth below the uncut surface, 0.05 … 1 mm. Omit ⇒ 0.3. */
  depthMM?: number;
  /** Metal between each seat footprint (0.53 d) and the pocket wall,
   *  ≥ 0.05 mm (`STONE_PATTERN_MIN_CLEARANCE_MM`) … 1. Omit ⇒ 0.08. */
  clearanceMM?: number;
  /** navette tip / plate corner fillet, 0.05 … 3 mm; never more than the
   *  smallest seat radius. Omit ⇒ 0.25. */
  cornerRadiusMM?: number;
  /** One pocket per GROUP or one per LAYER copy. Omit ⇒ "layer" for a channel,
   *  "group" for every other shape. */
  per?: "group" | "layer";
  /** Groups with fewer stones get no pocket (ref: three in a trough, one alone,
   *  three in a trough). 1 … 50. Omit ⇒ 1. */
  minGroup?: number;
}

/** Thin grooves beside the stones. */
export interface StonePatternRails {
  /** "left" is the −Y side, as `EdgeSides`. Omit ⇒ "both". */
  sides?: "left" | "right" | "both";
  /** Girdle-to-rail-edge gap, 0 … 3 mm. Omit ⇒ 0.2. */
  gapMM?: number;
  /** 0.15 … 1.5 mm (clamped; `DESIGN_GROOVE_MIN_WIDTH_MM` is the floor). Omit ⇒ 0.3. */
  widthMM?: number;
  /** 0.1 … 0.6 mm. Omit ⇒ 0.2. */
  depthMM?: number;
  /** "stones": only beside the stones (a path groove that follows them);
   *  "all-round": a horizontal groove round the whole ring. Omit ⇒ "stones". */
  extent?: "stones" | "all-round";
}

/** "across" mirrors about the band's centre line (y → −y); "along" about
 * 12 o'clock (arc → −arc); "point" both at once; "both" = all four copies. */
export type StonePatternMirror = "none" | "across" | "along" | "point" | "both";

/**
 * v67: one layer of a stone pattern. EVERY field is optional; the defaults
 * are `resolveStonePatternLayer`'s and are listed per field.
 */
export interface StonePatternLayer {
  /** Omit ⇒ "row". */
  path?: StonePatternPath;
  /** The layer's centre, arc mm from 12 o'clock. Omit ⇒ 0. */
  centerArcMM?: number;
  /** The layer's centre, mm from the centre line (−10 … 10, then held on the
   *  band). Omit ⇒ 0. */
  centerYMM?: number;
  /** The along-path extent, mm (0 … 1000). Omit ⇒ the stones' own length. */
  spanMM?: number;
  /** The extent as a fraction of the circumference, 0.01 … 1 — 0.5 is a half
   *  eternity, 1 a full one. Wins over `spanMM`. */
  spanTurns?: number;
  /** Slides the stones along the path, mm, the path itself staying put. Omit ⇒ 0. */
  offsetMM?: number;
  /** Tilt of the whole path in the plane, −90 … 90 deg (+ turns toward +Y).
   *  Omit ⇒ 25 for "diagonal", else 0. */
  angleDeg?: number;
  /** The cross-band excursion of the curved paths, −12 … 12 mm. A curve that
   *  would leave the band is squashed onto it, so a large value means "as
   *  wide as the band allows". Omit ⇒ sweep 12 (auto), chevron 0.8, zigzag
   *  0.6, arc 0.7, wave 0.6, s-curve 0.8. */
  amplitudeMM?: number;
  /** Periods over the span for "wave" / "zigzag", 0.5 … 12. Omit ⇒ 2 / 3. */
  waves?: number;
  /** "custom": the polyline relative to the centre, before `angleDeg`;
   *  2 … 64 finite points. */
  points?: { arcMM: number; yMM: number }[];
  /** "multi-row": 1 … 3. Omit ⇒ 2. */
  rows?: number;
  /** "multi-row": girdle gap between rows, 0.06 … 3 mm. Omit ⇒ `gapMM`. */
  rowGapMM?: number;
  /** "multi-row": brick-stagger alternate rows by half a pitch. Omit ⇒ true. */
  stagger?: boolean;
  /** Stones in one copy, 0 … 1000; 0 / absent with a span ⇒ as many as fit,
   *  absent without one ⇒ 7 (scatter 13). `groups` wins when given. */
  count?: number;
  /** Base stone diameter, 0.5 … 6 mm. Omit ⇒ the run's `stoneSizeMM`. */
  sizeMM?: number;
  sizing?: StonePatternSizing;
  /** "gap": stones `gapMM` apart, girdle to girdle; "fit": spread evenly over
   *  the span. Omit ⇒ "fit" when a span is given, else "gap". */
  spacing?: "gap" | "fit";
  /** Girdle-to-girdle gap, ≥ 0.06 mm (`STONE_PATTERN_MIN_GAP_MM`) … 5. In
   *  "fit" spacing it is the SMALLEST gap allowed when counting. Omit ⇒ 0.2. */
  gapMM?: number;
  /** Stones per group in order, e.g. [3, 1, 3]; 1 … 50 each, ≤ 24 groups. */
  groups?: number[];
  /** Extra gap between groups, 0 … 20 mm. Omit ⇒ 0.6. */
  groupGapMM?: number;
  /** Omit ⇒ "flush". */
  setting?: StonePatternSetting;
  /** Omit ⇒ no pocket. */
  pocket?: StonePatternPocket;
  /** Omit ⇒ no rails. */
  rails?: StonePatternRails;
  /** Copies evenly round the ring, 1 … 60. Omit ⇒ 1. */
  repeat?: number;
  /** Omit ⇒ "none". */
  mirror?: StonePatternMirror;
  /** "scatter" and the "random" sizing, 0 … 2^31−1. Omit ⇒ 1. */
  seed?: number;
}

/** v67: `BandState.stonePattern`, read only by `DiamondStyle` "pattern". */
export interface StonePattern {
  /** 1 … 4 layers (`STONE_PATTERN_MAX_LAYERS`). */
  layers: StonePatternLayer[];
  /** The library entry the layers were started from. Bookkeeping only. */
  libraryId?: string;
  /**
   * v68: metal kept on the OUTER FACE beside each seat, mm, −0.6 … 0.25. Omit ⇒
   * 0.25 (`MIN_SEAT_RIM_MM`, the rim that takes a polish instead of tearing) —
   * today's placement exactly. Lower lets an edge row hug the band edge the way
   * catalogue pavé does, and a rim under 0.25 raises a spec warning that says so.
   * 0 … 0.25: the seat itself never leaves the outer face (the placer keeps its
   * seat allowance whatever the rim).
   * NEGATIVE (`STONE_PATTERN_SEAT_RIM_MIN_MM`): the row may run OVER the band's
   * edge — a stone's seat may reach that far past where the outer face ends,
   * so its girdle can stand over the rounded edge and the side wall, the way
   * an edge-set catalogue row runs to the rim. Each seat is then cut only on
   * the outer face (clipped where the face ends), the stone rests on what is
   * cut there, and a spec warning says the row overhangs.
   */
  seatRimMM?: number;
}

/** What the generator needs to know about the band. */
export interface StonePatternBand {
  widthMM: number;
  /** 2π · the outer crest radius (the path-groove plane's period). Omit ⇒ 65. */
  circumferenceMM?: number;
  /** The run's stone size, used when a layer has no `sizeMM`. Omit ⇒ 1.5. */
  stoneSizeMM?: number;
  /** Metal kept between a girdle and the band edge, mm. Omit ⇒ 0.15.
   *  v68: may be negative (down to `STONE_PATTERN_SEAT_RIM_MIN_MM`): a girdle
   *  may then stand that far past the edge (an overhanging edge row). */
  edgeMarginMM?: number;
}

/** One generated stone. `index` is its position in the result; `group` is
 * unique per (copy, group) within its layer; `chain` is the copy
 * (repeat × 4 + mirror: 0 original, 1 across, 2 along, 3 point). */
export interface PatternStone {
  arcMM: number;
  yMM: number;
  /** Girdle diameter, mm. */
  d: number;
  group: number;
  layer: number;
  index: number;
  chain: number;
}

export interface PatternPocket {
  layer: number;
  chain: number;
  /** The `PatternStone.group` it holds, −1 for a per-layer pocket. */
  group: number;
  shape: Exclude<StonePatternPocketShape, "none">;
  depthMM: number;
  clearanceMM: number;
  cornerRadiusMM: number;
  /** `PatternStone.index` of every stone it holds, in path order. */
  stones: number[];
  /** Closed outline, counter-clockwise, arcs unwrapped from the first stone
   *  (may run past ±C/2; the plane wraps). Empty for `ring`. */
  outline: { arcMM: number; yMM: number }[];
  /** capsule / channel: the same pocket as a constant-width path source
   *  (`pocketPath`), which contains every seat too. */
  path?: { points: { arcMM: number; yMM: number }[]; widthMM: number };
  /** A channel that runs all the way round: a horizontal band. */
  ring?: { yMinMM: number; yMaxMM: number };
}

export interface PatternRail {
  layer: number;
  /** −1 for an all-round rail. */
  chain: number;
  side: "left" | "right";
  widthMM: number;
  depthMM: number;
  /** "path": `points` is the centreline (≤ 64 points, a `DesignGroovePath`);
   *  "horizontal": a horizontal design groove at `yMM`. */
  kind: "path" | "horizontal";
  points?: { arcMM: number; yMM: number }[];
  yMM?: number;
}

export interface StonePatternResult {
  stones: PatternStone[];
  pockets: PatternPocket[];
  rails: PatternRail[];
  /** Stones asked for before copies (Σ over layers). */
  requested: number;
  /** Coincident stones removed after repeat / mirror. */
  removed: number;
  /** True when `STONE_PATTERN_MAX_STONES` cut the expansion short. */
  clipped: boolean;
}

export interface StonePatternIssue {
  code:
    | "empty" | "stone.off-band" | "stone.overlap" | "stone.size" | "cap"
    | "pocket.footprint" | "pocket.off-band" | "rail.overlap" | "rail.off-band";
  layer: number;
  message: string;
}

/** Most layers in a pattern. */
export const STONE_PATTERN_MAX_LAYERS = 4;
/** Most stones a whole pattern expands to (layers × repeat × mirror). */
export const STONE_PATTERN_MAX_STONES = 1000;
/** The seat footprint's radius as a share of the girdle diameter. */
export const STONE_PATTERN_SEAT_FACTOR = 0.53;
/** Least metal between a seat footprint and its pocket wall, mm. */
export const STONE_PATTERN_MIN_CLEARANCE_MM = 0.05;
/** Least girdle-to-girdle gap a layer may ask for, mm (the builder's own
 *  `MIN_STONE_WEB_MM`). */
export const STONE_PATTERN_MIN_GAP_MM = 0.06;

/** A pattern setting on the existing vocabulary: the run's `DiamondSetting`
 * plus whether each stone gets a bezel collar. */
export const STONE_PATTERN_SETTINGS: Record<StonePatternSetting, { setting: DiamondSetting; bezel: boolean; label: string }> = {
  flush: { setting: "flush", bezel: false, label: "Flush" },
  bezel: { setting: "flush", bezel: true, label: "Bezel" },
  pave: { setting: "pave", bezel: false, label: "Pavé" },
  "shared-prong": { setting: "shared-prong", bezel: false, label: "Shared claws" },
  prongs: { setting: "prongs", bezel: false, label: "Claws" },
  only: { setting: "only", bezel: false, label: "Stones only" },
};

// ── deterministic arithmetic (no transcendental Math calls in this block) ──

const PAT_PI = 3.141592653589793;
const PAT_TWO_PI = 6.283185307179586;
const PAT_SQRT3_2 = 0.8660254037844386;

/** sin(x) from + − × ÷ and floor only: range-reduced Taylor to x^15, error
 *  < 1e-11 on [−π/2, π/2]. */
export function patternSin(x: number): number {
  const k = Math.floor(x / PAT_TWO_PI + 0.5);
  let r = x - k * PAT_TWO_PI;
  if (r > PAT_PI / 2) r = PAT_PI - r;
  else if (r < -PAT_PI / 2) r = -PAT_PI - r;
  const r2 = r * r;
  return r * (1 - (r2 / 6) * (1 - (r2 / 20) * (1 - (r2 / 42) * (1 - (r2 / 72) * (1 - (r2 / 110) * (1 - (r2 / 156) * (1 - r2 / 210)))))));
}
function patternCos(x: number): number { return patternSin(x + PAT_PI / 2); }

/** t^p for t ∈ [0, 1], p ∈ [0.25, 4]: whole powers by multiplication, the
 *  fraction by 12 binary digits of repeated √. */
function patternPow(t: number, p: number): number {
  if (t <= 0) return 0;
  let r = 1;
  const ip = Math.floor(p);
  let f = p - ip;
  for (let i = 0; i < ip; i++) r *= t;
  let root = t;
  for (let b = 0; b < 12 && f > 0; b++) {
    root = Math.sqrt(root);
    f *= 2;
    if (f >= 1) { r *= root; f -= 1; }
  }
  return r;
}

/** mulberry32: an integer RNG (Math.imul), uniform in [0, 1). */
export function patternRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Twelve unit directions at 30° steps, as literals (no trig at run time). */
const PAT_DIRS: readonly (readonly [number, number])[] = [
  [1, 0], [PAT_SQRT3_2, 0.5], [0.5, PAT_SQRT3_2], [0, 1], [-0.5, PAT_SQRT3_2], [-PAT_SQRT3_2, 0.5],
  [-1, 0], [-PAT_SQRT3_2, -0.5], [-0.5, -PAT_SQRT3_2], [0, -1], [0.5, -PAT_SQRT3_2], [PAT_SQRT3_2, -0.5],
];

const patClamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const patFinite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

// ── normalisation ──

const PATTERN_PATHS: readonly StonePatternPath[] = [
  "row", "diagonal", "sweep", "chevron", "zigzag", "arc", "wave", "s-curve",
  "multi-row", "cluster", "rosette", "scatter", "custom",
];
const PATTERN_SETTINGS: readonly StonePatternSetting[] = ["flush", "bezel", "pave", "shared-prong", "prongs", "only"];
const PATTERN_POCKETS: readonly StonePatternPocketShape[] = ["none", "capsule", "channel", "navette", "plate"];
const PATTERN_MIRRORS: readonly StonePatternMirror[] = ["none", "across", "along", "point", "both"];
const PATTERN_SIZINGS: readonly StonePatternSizingMode[] = ["uniform", "graduated", "alternating", "random"];

/**
 * Normalise one layer so no host can store an invalid one. Numbers are
 * clamped to the documented ranges, non-finite numbers and unknown enum values
 * are DROPPED (so the default applies), and nothing absent is filled in — an
 * untouched field stays absent, exactly as `clampDesignGrooves` keeps a
 * design lean. Idempotent.
 */
export function clampStonePatternLayer(src: Partial<StonePatternLayer> | null | undefined): StonePatternLayer {
  const s = (src ?? {}) as Partial<StonePatternLayer>;
  const out: StonePatternLayer = {};
  const num = (k: keyof StonePatternLayer, lo: number, hi: number, int = false) => {
    const v = s[k];
    if (patFinite(v)) (out as Record<string, unknown>)[k] = patClamp(int ? Math.round(v) : v, lo, hi);
  };
  if (s.path && PATTERN_PATHS.includes(s.path)) out.path = s.path;
  num("centerArcMM", -1000, 1000);
  num("centerYMM", -10, 10);
  num("spanMM", 0, 1000);
  num("spanTurns", 0.01, 1);
  num("offsetMM", -1000, 1000);
  num("angleDeg", -90, 90);
  num("amplitudeMM", -12, 12);
  num("waves", 0.5, 12);
  if (Array.isArray(s.points) && s.points.length >= 2 && s.points.length <= DESIGN_GROOVE_PATH_MAX_POINTS
      && s.points.every((p) => patFinite(p?.arcMM) && patFinite(p?.yMM))) {
    out.points = s.points.map((p) => ({ arcMM: patClamp(p.arcMM, -1000, 1000), yMM: patClamp(p.yMM, -10, 10) }));
  }
  num("rows", 1, 3, true);
  num("rowGapMM", STONE_PATTERN_MIN_GAP_MM, 3);
  if (typeof s.stagger === "boolean") out.stagger = s.stagger;
  num("count", 0, STONE_PATTERN_MAX_STONES, true);
  num("sizeMM", 0.5, 6);
  if (s.sizing && typeof s.sizing === "object") {
    const z = s.sizing;
    const sz: StonePatternSizing = {};
    if (z.mode && PATTERN_SIZINGS.includes(z.mode)) sz.mode = z.mode;
    if (patFinite(z.endScale)) sz.endScale = patClamp(z.endScale, 0.2, 3);
    if (patFinite(z.curve)) sz.curve = patClamp(z.curve, 0.25, 4);
    if (z.anchor === "centred" || z.anchor === "one-sided") sz.anchor = z.anchor;
    if (patFinite(z.altScale)) sz.altScale = patClamp(z.altScale, 0.2, 3);
    if (patFinite(z.period)) sz.period = patClamp(Math.round(z.period), 1, 4);
    if (patFinite(z.min)) sz.min = patClamp(z.min, 0.2, 3);
    if (patFinite(z.max)) sz.max = patClamp(z.max, 0.2, 3);
    if (sz.min != null && sz.max != null && sz.max < sz.min) sz.max = sz.min;
    out.sizing = sz;
  }
  if (s.spacing === "gap" || s.spacing === "fit") out.spacing = s.spacing;
  num("gapMM", STONE_PATTERN_MIN_GAP_MM, 5);
  if (Array.isArray(s.groups)) {
    const g = s.groups.filter(patFinite).map((v) => patClamp(Math.round(v), 1, 50)).slice(0, 24);
    let total = 0;
    const kept: number[] = [];
    for (const v of g) { if (total + v > STONE_PATTERN_MAX_STONES) break; kept.push(v); total += v; }
    if (kept.length) out.groups = kept;
  }
  num("groupGapMM", 0, 20);
  if (s.setting && PATTERN_SETTINGS.includes(s.setting)) out.setting = s.setting;
  if (s.pocket && typeof s.pocket === "object") {
    const p = s.pocket;
    const pk: StonePatternPocket = {};
    if (p.shape && PATTERN_POCKETS.includes(p.shape)) pk.shape = p.shape;
    if (patFinite(p.depthMM)) pk.depthMM = patClamp(p.depthMM, 0.05, 1);
    if (patFinite(p.clearanceMM)) pk.clearanceMM = patClamp(p.clearanceMM, STONE_PATTERN_MIN_CLEARANCE_MM, 1);
    if (patFinite(p.cornerRadiusMM)) pk.cornerRadiusMM = patClamp(p.cornerRadiusMM, 0.05, 3);
    if (p.per === "group" || p.per === "layer") pk.per = p.per;
    if (patFinite(p.minGroup)) pk.minGroup = patClamp(Math.round(p.minGroup), 1, 50);
    out.pocket = pk;
  }
  if (s.rails && typeof s.rails === "object") {
    const r = s.rails;
    const rl: StonePatternRails = {};
    if (r.sides === "left" || r.sides === "right" || r.sides === "both") rl.sides = r.sides;
    if (patFinite(r.gapMM)) rl.gapMM = patClamp(r.gapMM, 0, 3);
    if (patFinite(r.widthMM)) rl.widthMM = patClamp(r.widthMM, DESIGN_GROOVE_MIN_WIDTH_MM, 1.5);
    if (patFinite(r.depthMM)) rl.depthMM = patClamp(r.depthMM, 0.1, 0.6);
    if (r.extent === "stones" || r.extent === "all-round") rl.extent = r.extent;
    out.rails = rl;
  }
  num("repeat", 1, 60, true);
  if (s.mirror && PATTERN_MIRRORS.includes(s.mirror)) out.mirror = s.mirror;
  num("seed", 0, 2147483647, true);
  return out;
}

/** v68: `StonePattern.seatRimMM`'s ceiling and default — the engine's
 *  `MIN_SEAT_RIM_MM`. */
export const STONE_PATTERN_SEAT_RIM_MAX_MM = 0.25;
/** v68: `StonePattern.seatRimMM`'s floor — the furthest a seat may reach past
 *  the outer face's edge (an overhanging edge row; see `seatRimMM`). */
export const STONE_PATTERN_SEAT_RIM_MIN_MM = -0.6;

/** Normalise a whole pattern: 1 … 4 layers (extra layers dropped), each
 *  through `clampStonePatternLayer`; `seatRimMM` clamped to −0.6 … 0.25 (absent
 *  stays absent). No layers ⇒ undefined (no pattern). */
export function clampStonePattern(p: Partial<StonePattern> | null | undefined): StonePattern | undefined {
  if (!p || !Array.isArray(p.layers) || p.layers.length === 0) return undefined;
  const out: StonePattern = { layers: p.layers.slice(0, STONE_PATTERN_MAX_LAYERS).map(clampStonePatternLayer) };
  if (typeof p.libraryId === "string" && p.libraryId.length > 0 && p.libraryId.length <= 64) out.libraryId = p.libraryId;
  if (typeof p.seatRimMM === "number" && Number.isFinite(p.seatRimMM)) out.seatRimMM = Math.min(STONE_PATTERN_SEAT_RIM_MAX_MM, Math.max(STONE_PATTERN_SEAT_RIM_MIN_MM, p.seatRimMM));
  return out;
}

/** Every field of a layer with its default filled in — what the generator
 *  reads, and what a host shows as a slider's starting value. */
export interface ResolvedStonePatternLayer {
  path: StonePatternPath;
  centerArcMM: number;
  centerYMM: number;
  spanMM: number | null;
  spanTurns: number | null;
  offsetMM: number;
  angleDeg: number;
  amplitudeMM: number;
  waves: number;
  points: { arcMM: number; yMM: number }[] | null;
  rows: number;
  rowGapMM: number;
  stagger: boolean;
  /** 0 = as many as fit the span. */
  count: number;
  sizeMM: number | null;
  sizing: Required<StonePatternSizing>;
  spacing: "gap" | "fit";
  gapMM: number;
  groups: number[] | null;
  groupGapMM: number;
  setting: StonePatternSetting;
  pocket: Required<StonePatternPocket>;
  rails: Required<StonePatternRails> | null;
  repeat: number;
  mirror: StonePatternMirror;
  seed: number;
}

const DEFAULT_AMPLITUDE: Partial<Record<StonePatternPath, number>> = {
  sweep: 12, chevron: 0.8, zigzag: 0.6, arc: 0.7, wave: 0.6, "s-curve": 0.8,
};

export function resolveStonePatternLayer(layer: StonePatternLayer | null | undefined): ResolvedStonePatternLayer {
  const l = clampStonePatternLayer(layer);
  const path = l.path ?? "row";
  const hasSpan = l.spanTurns != null || l.spanMM != null;
  const groups = l.groups && l.groups.length ? l.groups : null;
  const z = l.sizing ?? {};
  const pk = l.pocket ?? {};
  const shape = pk.shape ?? "none";
  const gapMM = l.gapMM ?? 0.2;
  return {
    path,
    centerArcMM: l.centerArcMM ?? 0,
    centerYMM: l.centerYMM ?? 0,
    spanMM: l.spanMM ?? null,
    spanTurns: l.spanTurns ?? null,
    offsetMM: l.offsetMM ?? 0,
    angleDeg: l.angleDeg ?? (path === "diagonal" ? 25 : 0),
    amplitudeMM: l.amplitudeMM ?? DEFAULT_AMPLITUDE[path] ?? 0,
    waves: l.waves ?? (path === "zigzag" ? 3 : 2),
    points: path === "custom" && l.points ? l.points : null,
    rows: path === "multi-row" ? (l.rows ?? 2) : 1,
    rowGapMM: l.rowGapMM ?? gapMM,
    stagger: l.stagger ?? true,
    count: groups ? groups.reduce((a, b) => a + b, 0) : l.count ?? (hasSpan ? 0 : path === "scatter" ? 13 : 7),
    sizeMM: l.sizeMM ?? null,
    sizing: {
      mode: z.mode ?? (path === "rosette" ? "graduated" : "uniform"),
      endScale: z.endScale ?? 0.6,
      curve: z.curve ?? 1,
      anchor: z.anchor ?? "centred",
      altScale: z.altScale ?? 0.7,
      period: z.period ?? 1,
      min: z.min ?? 0.6,
      max: z.max ?? Math.max(z.min ?? 0.6, 1.2),
    },
    spacing: l.spacing ?? (hasSpan ? "fit" : "gap"),
    gapMM,
    groups,
    groupGapMM: l.groupGapMM ?? 0.6,
    setting: l.setting ?? "flush",
    pocket: {
      shape,
      depthMM: pk.depthMM ?? 0.3,
      clearanceMM: pk.clearanceMM ?? 0.08,
      cornerRadiusMM: pk.cornerRadiusMM ?? 0.25,
      per: pk.per ?? (shape === "channel" ? "layer" : "group"),
      minGroup: pk.minGroup ?? 1,
    },
    rails: l.rails ? {
      sides: l.rails.sides ?? "both",
      gapMM: l.rails.gapMM ?? 0.2,
      widthMM: l.rails.widthMM ?? 0.3,
      depthMM: l.rails.depthMM ?? 0.2,
      extent: l.rails.extent ?? "stones",
    } : null,
    repeat: l.repeat ?? 1,
    mirror: l.mirror ?? "none",
    seed: l.seed ?? 1,
  };
}

// ── the generator ──

interface PatMotifStone { a: number; y: number; d: number; g: number }
interface PatCtx {
  L: ResolvedStonePatternLayer;
  C: number;
  halfW: number;
  margin: number;
  base: number;
  rng: () => number;
}

/** Scale of stone i of n by the sizing law; `t` is its distance from the
 *  anchor, 0 (the big end / centre) … 1 (the far end). */
function patScale(L: ResolvedStonePatternLayer, t: number, i: number, rng: () => number): number {
  const z = L.sizing;
  switch (z.mode) {
    case "graduated": return 1 + (z.endScale - 1) * patternPow(patClamp(t, 0, 1), z.curve);
    case "alternating": return Math.floor(i / z.period) % 2 === 1 ? z.altScale : 1;
    case "random": return z.min + (z.max - z.min) * rng();
    default: return 1;
  }
}

/** Diameters of a linear run of n stones. */
function patRunSizes(ctx: PatCtx, n: number): number[] {
  const { L, base, rng } = ctx;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = n <= 1 ? 0 : L.sizing.anchor === "one-sided" ? i / (n - 1) : Math.abs((2 * i) / (n - 1) - 1);
    out.push(patClamp(base * patScale(L, t, i, rng), 0.3, 8));
  }
  return out;
}

/** The group of each stone of a run (and whether a group boundary precedes it). */
function patGroupsOf(L: ResolvedStonePatternLayer, n: number): number[] {
  const g: number[] = [];
  if (!L.groups) { for (let i = 0; i < n; i++) g.push(0); return g; }
  L.groups.forEach((k, gi) => { for (let j = 0; j < k; j++) g.push(gi); });
  return g.slice(0, n);
}

/** Σ of what a run of n stones needs along its path, open or closed. */
function patRunLength(d: number[], g: number[], gap: number, groupGap: number, closed: boolean): number {
  let s = 0;
  for (let i = 0; i < d.length; i++) {
    s += d[i];
    if (i > 0) s += gap + (g[i] !== g[i - 1] ? groupGap : 0);
  }
  if (closed && d.length > 0) s += gap + (g[0] !== g[g.length - 1] ? groupGap : 0);
  return s;
}

/** Largest stone count whose run fits `length` at the smallest gap. */
function patAutoCount(ctx: PatCtx, length: number, closed: boolean): number {
  const { L } = ctx;
  if (L.groups) return L.count;
  const estimate = Math.floor((length + (closed ? 0 : L.gapMM)) / (ctx.base * 0.3 + L.gapMM));
  for (let n = Math.min(STONE_PATTERN_MAX_STONES, Math.max(1, estimate)); n >= 1; n--) {
    const d = patRunSizes(ctx, n);
    if (patRunLength(d, patGroupsOf(L, n), L.gapMM, L.groupGapMM, closed) <= length + 1e-9) return n;
  }
  return 1;
}

/** Local (u along, v across) curve of the function paths. */
function patCurveV(L: ResolvedStonePatternLayer, u: number, S: number): number {
  const A = L.amplitudeMM;
  const x = S > 1e-9 ? u / S : 0; // −0.5 … 0.5 over the window
  switch (L.path) {
    case "sweep": return A * patternSin(PAT_PI * patClamp(x, -0.5, 0.5));
    case "chevron": return A * (1 - 4 * Math.abs(x));
    case "zigzag": {
      const w = x * L.waves;
      return A * (1 - 4 * Math.abs(w - Math.floor(w + 0.5)));
    }
    case "arc": return A * (8 * x * x - 1);
    case "wave": return A * patternSin(PAT_TWO_PI * L.waves * x);
    case "s-curve": return A * patternSin(PAT_TWO_PI * patClamp(x, -0.5, 0.5));
    default: return 0;
  }
}

/** A dense polyline in band coordinates (arc relative to the centre, y
 *  absolute) with arc lengths, the window's arc-length bounds, and each
 *  sample's local u. Squashed across the band so the window fits. */
interface PatPolyline { a: number[]; y: number[]; u: number[]; cum: number[]; w0: number; w1: number }

function patPolyline(ctx: PatCtx, S: number, maxR: number, rAt?: (u: number) => number): PatPolyline {
  const { L } = ctx;
  const th = (L.angleDeg * PAT_PI) / 180;
  const c = patternCos(th), s = patternSin(th);
  const lim = Math.max(0, ctx.halfW - ctx.margin - maxR);
  const cY = patClamp(L.centerYMM, -lim, lim);
  // Local samples (u, v), with the window [i0, i1].
  const us: number[] = [], vs: number[] = [];
  let i0 = 0, i1 = 0;
  if (L.points) {
    const P = L.points;
    const ext = 5 + S;
    const d0x = P[1].arcMM - P[0].arcMM, d0y = P[1].yMM - P[0].yMM;
    const n0 = Math.sqrt(d0x * d0x + d0y * d0y) || 1;
    const k = P.length - 1;
    const d1x = P[k].arcMM - P[k - 1].arcMM, d1y = P[k].yMM - P[k - 1].yMM;
    const n1 = Math.sqrt(d1x * d1x + d1y * d1y) || 1;
    const pts: [number, number][] = [[P[0].arcMM - (d0x / n0) * ext, P[0].yMM - (d0y / n0) * ext], ...P.map((p) => [p.arcMM, p.yMM] as [number, number]), [P[k].arcMM + (d1x / n1) * ext, P[k].yMM + (d1y / n1) * ext]];
    for (let j = 1; j < pts.length; j++) {
      const [ax, ay] = pts[j - 1], [bx, by] = pts[j];
      const len = Math.sqrt((bx - ax) * (bx - ax) + (by - ay) * (by - ay));
      const steps = Math.max(1, Math.ceil(len / 0.05));
      if (j === 1) { us.push(ax); vs.push(ay); }
      for (let q = 1; q <= steps; q++) { us.push(ax + ((bx - ax) * q) / steps); vs.push(ay + ((by - ay) * q) / steps); }
      if (j === 1) i0 = us.length - 1;
      if (j === pts.length - 2) i1 = us.length - 1;
    }
  } else {
    const half = S / 2;
    const ext = S + 5;
    const total = S + 2 * ext;
    const n = patClamp(Math.ceil(total / 0.05), 200, 8000);
    for (let q = 0; q <= n; q++) {
      const u = -half - ext + (total * q) / n;
      us.push(u);
      vs.push(patCurveV(L, u, S));
    }
    const step = total / n;
    i0 = Math.round(ext / step);
    i1 = Math.round((ext + S) / step);
  }
  // Rotate; squash across the band about the (held) centre line.
  const da: number[] = [], dy: number[] = [];
  let f = 1;
  for (let q = 0; q < us.length; q++) {
    const a = us[q] * c - vs[q] * s;
    const y = us[q] * s + vs[q] * c;
    da.push(a); dy.push(y);
    if (q >= i0 && q <= i1) {
      // The band holds each STONE by its own radius (`rAt`, once the stones
      // are known), not every point of the line by the largest one's: a
      // graduated diagonal's small end stones reach nearer the edges.
      const lq = rAt ? Math.max(0, ctx.halfW - ctx.margin - rAt(us[q])) : lim;
      if (y > 1e-12) f = Math.min(f, (lq - cY) / y);
      else if (y < -1e-12) f = Math.min(f, (lq + cY) / -y);
    }
  }
  f = Math.max(0, f);
  const ys = dy.map((y) => cY + f * y);
  const cum = [0];
  for (let q = 1; q < da.length; q++) {
    const ea = da[q] - da[q - 1], ey = ys[q] - ys[q - 1];
    cum.push(cum[q - 1] + Math.sqrt(ea * ea + ey * ey));
  }
  return { a: da, y: ys, u: us, cum, w0: cum[i0], w1: cum[i1] };
}

/** Point on the polyline at arc length s (linear beyond either end). */
function patAt(P: PatPolyline, s: number): { a: number; y: number; u: number } {
  const n = P.cum.length;
  let lo = 0, hi = n - 1;
  if (s <= P.cum[0]) { hi = 1; }
  else if (s >= P.cum[n - 1]) { lo = n - 2; hi = n - 1; }
  else {
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (P.cum[mid] <= s) lo = mid; else hi = mid; }
  }
  const span = P.cum[hi] - P.cum[lo] || 1;
  const t = (s - P.cum[lo]) / span;
  return {
    a: P.a[lo] + (P.a[hi] - P.a[lo]) * t,
    y: P.y[lo] + (P.y[hi] - P.y[lo]) * t,
    u: P.u[lo] + (P.u[hi] - P.u[lo]) * t,
  };
}

/** Walk the polyline from arc length s0, each next stone at the first point
 *  whose CHORD to the previous stone is its pitch — so gaps are exact even
 *  where the path bends. */
function patChordWalk(P: PatPolyline, s0: number, pitches: number[]): number[] {
  const out = [s0];
  let s = s0;
  const step = 0.05;
  for (const p of pitches) {
    const q = patAt(P, s);
    let lo = s, hi = s + step;
    let guard = 0;
    for (;;) {
      const r = patAt(P, hi);
      const da = r.a - q.a, dy = r.y - q.y;
      if (da * da + dy * dy >= p * p || guard++ > 200000) break;
      lo = hi; hi += step;
    }
    for (let k = 0; k < 40; k++) {
      const mid = (lo + hi) / 2;
      const r = patAt(P, mid);
      const da = r.a - q.a, dy = r.y - q.y;
      if (da * da + dy * dy >= p * p) hi = mid; else lo = mid;
    }
    s = hi;
    out.push(s);
  }
  return out;
}

function patPitches(d: number[], g: number[], gap: number, groupGap: number): number[] {
  const out: number[] = [];
  for (let i = 1; i < d.length; i++) out.push(d[i - 1] / 2 + d[i] / 2 + gap + (g[i] !== g[i - 1] ? groupGap : 0));
  return out;
}

/** row / diagonal / sweep / chevron / zigzag / arc / wave / s-curve / custom. */
function patCurveMotif(ctx: PatCtx): { stones: PatMotifStone[]; closed: boolean } {
  const { L, C } = ctx;
  const spanAbs = L.spanTurns != null ? L.spanTurns * C : L.spanMM;
  const straight = L.path === "row" || L.path === "diagonal";
  const closed = L.spanTurns != null && L.spanTurns >= 0.999 && straight && Math.abs(L.angleDeg) < 1e-9;

  let n = L.count;
  if (n <= 0) {
    // As many as fit the span (a span exists: `resolve` gave 0 only then).
    n = patAutoCount(ctx, closed ? C : Math.max(spanAbs ?? 0, ctx.base), closed);
  }
  n = Math.min(n, STONE_PATTERN_MAX_STONES);
  const d = patRunSizes(ctx, n);
  const g = patGroupsOf(L, n);
  const maxR = Math.max(...d) / 2;

  if (closed) {
    // A loop round the whole ring: no ends, no polyline.
    const need = patRunLength(d, g, 0, L.groupGapMM, true) - 0; // stones + group gaps
    const gap = L.spacing === "fit" ? Math.max(0, (C - need) / n) : L.gapMM;
    const lim = Math.max(0, ctx.halfW - ctx.margin - maxR);
    const y = patClamp(L.centerYMM, -lim, lim);
    const pos: number[] = [0];
    for (let i = 1; i < n; i++) pos.push(pos[i - 1] + d[i - 1] / 2 + d[i] / 2 + gap + (g[i] !== g[i - 1] ? L.groupGapMM : 0));
    const shift = -(pos[0] + pos[n - 1]) / 2 + L.offsetMM;
    return { stones: pos.map((a, i) => ({ a: a + shift, y, d: d[i], g: g[i] })), closed: true };
  }

  const pitchesAtGap = patPitches(d, g, L.gapMM, L.groupGapMM);
  const runLen = pitchesAtGap.reduce((a, b) => a + b, 0) + (d[0] + d[n - 1]) / 2;
  const fit = L.spacing === "fit" && spanAbs != null && n > 1;

  let S = spanAbs ?? runLen;
  if (L.points) S = 0;
  let P = patPolyline(ctx, S, maxR);
  let svals: number[] = [];
  const place = () => {
    if (fit) {
      const lw = P.w1 - P.w0;
      const gap = (lw - patRunLength(d, g, 0, L.groupGapMM, false)) / (n - 1);
      const pitches = patPitches(d, g, gap, L.groupGapMM);
      svals = [P.w0 + d[0] / 2 + L.offsetMM];
      for (const p of pitches) svals.push(svals[svals.length - 1] + p);
      return;
    }
    const mid = (P.w0 + P.w1) / 2;
    const trial = patChordWalk(P, mid, pitchesAtGap);
    const used = trial[trial.length - 1] - trial[0];
    svals = patChordWalk(P, mid - used / 2 + L.offsetMM, pitchesAtGap);
  };
  place();
  // Without a span, the window is the stones' own extent: iterate the span
  // until the curve's u-extent matches what the stones occupy (fixed point).
  if (spanAbs == null && !L.points && n > 1) {
    for (let it = 0; it < 6; it++) {
      const u0 = patAt(P, svals[0]).u, u1 = patAt(P, svals[n - 1]).u;
      const next = Math.max(0.5, Math.abs(u1 - u0) + (d[0] + d[n - 1]) / 2);
      if (Math.abs(next - S) < 1e-6) break;
      S = next;
      P = patPolyline(ctx, S, maxR);
      place();
    }
  }
  // Stones of different sizes: squash the line by each stone's OWN radius (the
  // stone at local u holds the line there), re-placing the stones on the
  // re-squashed line. With one size this is the squash above, exactly, and is
  // skipped. Before, a graduated diagonal / sweep was held by its LARGEST
  // stone everywhere, so its small end stones never reached the edges and
  // every angle past the first few gave the same line (review gap 1).
  if (!L.points && n > 1 && Math.min(...d) < Math.max(...d) - 1e-9) {
    for (let it = 0; it < 3; it++) {
      const at = svals.map((sv, i) => ({ u: patAt(P, sv).u, r: d[i] / 2 })).sort((x, y) => x.u - y.u);
      const rAt = (u: number): number => {
        if (u <= at[0].u) return at[0].r;
        if (u >= at[at.length - 1].u) return at[at.length - 1].r;
        let k = 1;
        while (k < at.length - 1 && at[k].u < u) k++;
        const a = at[k - 1], b = at[k];
        const t = b.u > a.u ? (u - a.u) / (b.u - a.u) : 0;
        // The larger of the two near a stone, so the line never runs a big
        // stone off the band on the way to a small one.
        return Math.max(a.r + (b.r - a.r) * t, t < 0.5 ? a.r : b.r);
      };
      P = patPolyline(ctx, S, maxR, rAt);
      place();
    }
  }
  return {
    stones: svals.map((sv, i) => { const p = patAt(P, sv); return { a: p.a, y: p.y, d: d[i], g: g[i] }; }),
    closed: false,
  };
}

/** multi-row: 1 … 3 parallel (optionally staggered) rows. */
function patMultiRowMotif(ctx: PatCtx): { stones: PatMotifStone[]; closed: boolean } {
  const { L, C } = ctx;
  const R = L.rows;
  const spanAbs = L.spanTurns != null ? L.spanTurns * C : L.spanMM;
  const closed = L.spanTurns != null && L.spanTurns >= 0.999 && Math.abs(L.angleDeg) < 1e-9;
  const dmax = ctx.base * Math.max(1, L.sizing.mode === "graduated" ? L.sizing.endScale : L.sizing.mode === "alternating" ? L.sizing.altScale : L.sizing.mode === "random" ? L.sizing.max : 1);
  let perRow: number[];
  if (L.count > 0) {
    perRow = [];
    for (let k = 0; k < R; k++) perRow.push(Math.floor(L.count / R) + (k < L.count % R ? 1 : 0));
  } else {
    const len = closed ? C : Math.max(spanAbs ?? dmax, dmax);
    const nk = closed ? Math.max(1, Math.floor(C / (dmax + L.gapMM))) : Math.max(1, Math.floor((len - dmax) / (dmax + L.gapMM)) + 1);
    perRow = new Array(R).fill(nk);
  }
  const rowPitch = L.stagger ? (dmax + L.rowGapMM) * PAT_SQRT3_2 : dmax + L.rowGapMM;
  const th = (L.angleDeg * PAT_PI) / 180;
  const c = patternCos(th), s = patternSin(th);
  const lim = Math.max(0, ctx.halfW - ctx.margin - dmax / 2);
  const cY = patClamp(L.centerYMM, -lim, lim);
  const out: PatMotifStone[] = [];
  perRow.forEach((nk, k) => {
    if (nk <= 0) return;
    const d = patRunSizes(ctx, nk);
    let pitch = dmax + L.gapMM;
    if (closed) pitch = C / nk;
    else if (L.spacing === "fit" && spanAbs != null && nk > 1) pitch = (spanAbs - dmax) / (nk - 1);
    const shift = L.stagger && k % 2 === 1 ? pitch / 2 : 0;
    const v = (k - (R - 1) / 2) * rowPitch;
    for (let i = 0; i < nk; i++) {
      const u = (i - (nk - 1) / 2) * pitch + shift + L.offsetMM;
      out.push({ a: u * c - v * s, y: cY + u * s + v * c, d: d[i], g: 0 });
    }
  });
  return { stones: out, closed };
}

/** cluster: the n lattice sites nearest the centre (an elongated hexagonal
 *  packing that stays on the band). */
function patClusterMotif(ctx: PatCtx): { stones: PatMotifStone[]; closed: boolean } {
  const { L } = ctx;
  const n = Math.max(1, L.count || 7);
  const big = ctx.base * (L.sizing.mode === "graduated" ? Math.max(1, L.sizing.endScale) : L.sizing.mode === "random" ? L.sizing.max : L.sizing.mode === "alternating" ? Math.max(1, L.sizing.altScale) : 1);
  const p = big + L.gapMM;
  const lim = Math.max(0, ctx.halfW - ctx.margin - big / 2);
  const cY = patClamp(L.centerYMM, -lim, lim);
  const K = Math.ceil(Math.sqrt(n)) + 3;
  const sites: { x: number; y: number; key: number }[] = [];
  for (let j = -K; j <= K; j++) {
    for (let i = -2 * K; i <= 2 * K; i++) {
      const x = (i + j / 2) * p;
      const y = j * p * PAT_SQRT3_2;
      if (Math.abs(cY + y) > lim + 1e-9) continue;
      sites.push({ x, y, key: x * x + 2.6 * y * y });
    }
  }
  sites.sort((a, b) => a.key - b.key || a.y - b.y || a.x - b.x);
  const take = sites.slice(0, n);
  const th = (L.angleDeg * PAT_PI) / 180;
  const c = patternCos(th), s = patternSin(th);
  const one = L.sizing.anchor === "one-sided";
  const xs = take.map((t) => t.x);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  return {
    stones: take.map((t, r) => {
      const tt = take.length <= 1 ? 0 : one ? (xmax > xmin ? (t.x - xmin) / (xmax - xmin) : 0) : r / (take.length - 1);
      const d = patClamp(ctx.base * patScale(L, tt, r, ctx.rng), 0.3, 8);
      return { a: t.x * c - t.y * s + L.offsetMM, y: cY + t.x * s + t.y * c, d, g: 0 };
    }),
    closed: false,
  };
}

/** rosette: a centre stone in a ring of n − 1 petals. */
function patRosetteMotif(ctx: PatCtx): { stones: PatMotifStone[]; closed: boolean } {
  const { L } = ctx;
  const n = Math.max(1, L.count || 7);
  const dc = patClamp(ctx.base * patScale(L, 0, 0, ctx.rng), 0.3, 8);
  const dp = patClamp(ctx.base * patScale(L, 1, 1, ctx.rng), 0.3, 8);
  const k = n - 1;
  const lim = Math.max(0, ctx.halfW - ctx.margin - dc / 2);
  const cY = patClamp(L.centerYMM, -lim, lim);
  const out: PatMotifStone[] = [{ a: L.offsetMM, y: cY, d: dc, g: 0 }];
  if (k >= 1) {
    let R = dc / 2 + dp / 2 + L.gapMM;
    if (k >= 2) R = Math.max(R, (dp / 2 + L.gapMM / 2) / patternSin(PAT_PI / k));
    const th0 = (L.angleDeg * PAT_PI) / 180;
    for (let j = 0; j < k; j++) {
      const ang = th0 + (PAT_TWO_PI * j) / k;
      out.push({ a: L.offsetMM + R * patternCos(ang), y: cY + R * patternSin(ang), d: dp, g: 0 });
    }
  }
  return { stones: out, closed: false };
}

/** scatter (and, with `groups`, a constellation): seeded dart throwing,
 *  every keep/reject decision on + − × only. */
function patScatterMotif(ctx: PatCtx): { stones: PatMotifStone[]; closed: boolean } {
  const { L, C, rng } = ctx;
  const closed = L.spanTurns != null && L.spanTurns >= 0.999;
  const groups = L.groups ?? null;
  const n = groups ? L.count : Math.max(1, L.count || 13);
  const sizes: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = n <= 1 ? 0 : L.sizing.anchor === "one-sided" ? i / (n - 1) : Math.abs((2 * i) / (n - 1) - 1);
    sizes.push(patClamp(ctx.base * patScale(L, t, i, rng), 0.3, 8));
  }
  const bandAllow = Math.max(0, ctx.halfW - ctx.margin);
  const meanD = sizes.reduce((a, b) => a + b, 0) / Math.max(1, n);
  const S = closed ? C : L.spanTurns != null ? L.spanTurns * C : L.spanMM ?? Math.max(4 * meanD, (n * (meanD + L.gapMM) * (meanD + L.gapMM) * 1.8) / Math.max(0.2, 2 * bandAllow));
  const placed: PatMotifStone[] = [];
  const da = (a: number, b: number) => {
    let x = a - b;
    if (closed) x -= C * Math.floor(x / C + 0.5);
    return x;
  };
  const free = (a: number, y: number, r: number, g: number, sameGap: number, otherGap: number) => {
    for (const q of placed) {
      const ea = da(a, q.a), ey = y - q.y;
      const need = r + q.d / 2 + (q.g === g ? sameGap : otherGap);
      if (ea * ea + ey * ey < need * need) return false;
    }
    return true;
  };
  const yLim = (r: number) => Math.max(0, bandAllow - r);
  if (!groups) {
    for (let i = 0; i < n; i++) {
      const r = sizes[i] / 2;
      for (let attempt = 0; attempt < 120; attempt++) {
        const a = (rng() - 0.5) * S;
        const y = (2 * rng() - 1) * yLim(r);
        if (free(a, y, r, 0, L.gapMM, L.gapMM)) { placed.push({ a, y, d: sizes[i], g: 0 }); break; }
      }
    }
    placed.sort((p, q) => p.a - q.a || p.y - q.y);
  } else {
    let i = 0;
    groups.forEach((gs, gi) => {
      let prev: PatMotifStone | null = null;
      for (let j = 0; j < gs; j++, i++) {
        const r = sizes[i] / 2;
        if (!prev) {
          for (let attempt = 0; attempt < 160; attempt++) {
            const a = (rng() - 0.5) * S;
            const y = (2 * rng() - 1) * yLim(r);
            if (free(a, y, r, gi, L.gapMM, L.groupGapMM)) { prev = { a, y, d: sizes[i], g: gi }; placed.push(prev); break; }
          }
          continue;
        }
        const from: PatMotifStone = prev;
        const start = Math.floor(rng() * 12);
        let got: PatMotifStone | null = null;
        for (let t = 0; t < 12 && !got; t++) {
          const [ux, uy] = PAT_DIRS[(start + t * 5) % 12];
          const dist: number = from.d / 2 + r + L.gapMM + 1e-6;
          const a: number = from.a + ux * dist, y: number = from.y + uy * dist;
          if (Math.abs(y) <= yLim(r) && free(a, y, r, gi, L.gapMM, L.groupGapMM)) got = { a, y, d: sizes[i], g: gi };
        }
        if (got) { placed.push(got); prev = got; }
      }
    });
  }
  return { stones: placed.map((p) => ({ ...p, a: p.a + L.offsetMM })), closed };
}

const PAT_MIRROR_SETS: Record<StonePatternMirror, number[]> = {
  none: [0], across: [0, 1], along: [0, 2], point: [0, 3], both: [0, 1, 2, 3],
};

/**
 * v67: expand ONE layer into stone positions, pockets and rails — pure,
 * deterministic, beside `buildFreePattern`. Positions only: the engine sinks,
 * sets and girdle-clamps the stones, then rebuilds the pockets from their
 * FINAL positions with `pocketOutline` / `pocketPath`.
 *
 * Order: the motif (sizes, path, spacing, groups, the squash onto the band),
 * then `repeat` copies, then `mirror`, wrap into ±C/2, each stone held on the
 * band, coincident stones removed (centres closer than a quarter of the
 * smaller girdle, wrap-aware), and at most `cap` stones kept.
 */
export function buildStonePattern(
  layer: StonePatternLayer,
  band: StonePatternBand,
  layerIndex = 0,
  cap: number = STONE_PATTERN_MAX_STONES,
): StonePatternResult {
  const L = resolveStonePatternLayer(layer);
  const C = patFinite(band.circumferenceMM) ? Math.max(10, band.circumferenceMM) : 65;
  const halfW = Math.max(0.1, (patFinite(band.widthMM) ? band.widthMM : 4) / 2);
  const margin = patFinite(band.edgeMarginMM) ? Math.max(STONE_PATTERN_SEAT_RIM_MIN_MM, band.edgeMarginMM) : 0.15;
  const base = L.sizeMM ?? (patFinite(band.stoneSizeMM) ? patClamp(band.stoneSizeMM, 0.5, 6) : 1.5);
  const ctx: PatCtx = { L, C, halfW, margin, base, rng: patternRng(Math.imul(L.seed + 1, 0x9e3779b1) ^ 0x5bd1e995) };

  const motif =
    L.path === "multi-row" ? patMultiRowMotif(ctx)
    : L.path === "cluster" ? patClusterMotif(ctx)
    : L.path === "rosette" ? patRosetteMotif(ctx)
    : L.path === "scatter" ? patScatterMotif(ctx)
    : patCurveMotif(ctx);
  const G = Math.max(1, ...motif.stones.map((s) => s.g + 1));

  const wrap = (a: number) => a - C * Math.floor(a / C + 0.5);
  const kept: PatternStone[] = [];
  let removed = 0, clipped = false;
  const mirrors = PAT_MIRROR_SETS[L.mirror];
  outer: for (let k = 0; k < L.repeat; k++) {
    const shift = (k * C) / L.repeat;
    for (const m of mirrors) {
      for (const st of motif.stones) {
        let a = L.centerArcMM + st.a, y = st.y;
        if (m === 1 || m === 3) y = -y;
        if (m === 2 || m === 3) a = -a;
        a = wrap(a + shift);
        const lim = Math.max(0, halfW - margin - st.d / 2);
        y = patClamp(y, -lim, lim);
        let dup = false;
        for (const q of kept) {
          const ea = wrap(a - q.arcMM), ey = y - q.yMM;
          const tol = 0.25 * Math.min(st.d, q.d);
          if (ea * ea + ey * ey < tol * tol) { dup = true; break; }
        }
        if (dup) { removed++; continue; }
        if (kept.length >= cap) { clipped = true; break outer; }
        kept.push({ arcMM: a, yMM: y, d: st.d, group: (k * 4 + m) * G + st.g, layer: layerIndex, index: kept.length, chain: k * 4 + m });
      }
    }
  }

  return {
    stones: kept,
    pockets: patPockets(L, kept, layerIndex, motif.closed, C),
    rails: patRails(L, kept, layerIndex, motif.closed, C, halfW),
    requested: motif.stones.length,
    removed,
    clipped,
  };
}

/** Arcs of a set unwrapped onto one sheet, relative to its first stone. */
function patUnwrap(stones: PatternStone[], C: number): { arcMM: number; yMM: number; d: number }[] {
  if (!stones.length) return [];
  const a0 = stones[0].arcMM;
  return stones.map((s) => {
    let x = s.arcMM - a0;
    x -= C * Math.floor(x / C + 0.5);
    return { arcMM: a0 + x, yMM: s.yMM, d: s.d };
  });
}

function patPockets(L: ResolvedStonePatternLayer, stones: PatternStone[], layer: number, closed: boolean, C: number): PatternPocket[] {
  const pk = L.pocket;
  if (pk.shape === "none" || !stones.length) return [];
  const shape = pk.shape;
  const sets = new Map<string, PatternStone[]>();
  for (const s of stones) {
    const key = pk.per === "layer" ? `c${s.chain}` : `g${s.group}`;
    const list = sets.get(key);
    if (list) list.push(s); else sets.set(key, [s]);
  }
  const out: PatternPocket[] = [];
  for (const list of sets.values()) {
    if (list.length < pk.minGroup) continue;
    const opts = { clearanceMM: pk.clearanceMM, cornerRadiusMM: pk.cornerRadiusMM };
    const pocket: PatternPocket = {
      layer, chain: list[0].chain, group: pk.per === "layer" ? -1 : list[0].group, shape,
      depthMM: pk.depthMM, clearanceMM: pk.clearanceMM, cornerRadiusMM: pk.cornerRadiusMM,
      stones: list.map((s) => s.index), outline: [],
    };
    if (shape === "channel" && closed && pk.per === "layer") {
      const rho = (s: PatternStone) => STONE_PATTERN_SEAT_FACTOR * s.d + pk.clearanceMM;
      pocket.ring = { yMinMM: Math.min(...list.map((s) => s.yMM - rho(s))), yMaxMM: Math.max(...list.map((s) => s.yMM + rho(s))) };
    } else {
      const flat = patUnwrap(list, C);
      pocket.outline = pocketOutline(shape, flat, opts);
      if (shape === "capsule" || shape === "channel") pocket.path = pocketPath(shape, flat, opts);
    }
    out.push(pocket);
  }
  return out;
}

/** Offset a polyline sideways by `dist` (+ = left of travel), mitred. */
function patOffsetPolyline(pts: { arcMM: number; yMM: number }[], dist: number): { arcMM: number; yMM: number }[] {
  const n = pts.length;
  const out: { arcMM: number; yMM: number }[] = [];
  const dir = (i: number) => {
    const ea = pts[i + 1].arcMM - pts[i].arcMM, ey = pts[i + 1].yMM - pts[i].yMM;
    const len = Math.sqrt(ea * ea + ey * ey) || 1;
    return [ea / len, ey / len] as const;
  };
  for (let i = 0; i < n; i++) {
    const d0 = dir(Math.max(0, i - 1)), d1 = dir(Math.min(n - 2, i));
    const n0x = -d0[1], n0y = d0[0], n1x = -d1[1], n1y = d1[0];
    let mx = n0x + n1x, my = n0y + n1y;
    const ml = Math.sqrt(mx * mx + my * my);
    if (ml < 1e-9) { mx = n1x; my = n1y; } else { mx /= ml; my /= ml; }
    const cosHalf = Math.max(0.25, mx * n1x + my * n1y);
    out.push({ arcMM: pts[i].arcMM + (mx * dist) / cosHalf, yMM: pts[i].yMM + (my * dist) / cosHalf });
  }
  return out;
}

function patDownsample<T>(pts: T[], max: number): T[] {
  if (pts.length <= max) return pts;
  const out: T[] = [];
  for (let k = 0; k < max; k++) out.push(pts[Math.round((k * (pts.length - 1)) / (max - 1))]);
  return out;
}

function patRails(L: ResolvedStonePatternLayer, stones: PatternStone[], layer: number, closed: boolean, C: number, halfW: number): PatternRail[] {
  const R = L.rails;
  if (!R || !stones.length) return [];
  const sides: ("left" | "right")[] = R.sides === "both" ? ["left", "right"] : [R.sides];
  const off = R.gapMM + R.widthMM / 2;
  const out: PatternRail[] = [];
  const lineLike = !["cluster", "rosette", "scatter", "multi-row"].includes(L.path);
  const horizontal = (side: "left" | "right", list: PatternStone[], chain: number) => {
    const yMM = side === "right"
      ? Math.max(...list.map((s) => s.yMM + s.d / 2)) + off
      : Math.min(...list.map((s) => s.yMM - s.d / 2)) - off;
    out.push({ layer, chain, side, widthMM: R.widthMM, depthMM: R.depthMM, kind: "horizontal", yMM: patClamp(yMM, -halfW, halfW) });
  };
  if (R.extent === "all-round" || closed) {
    for (const side of sides) horizontal(side, stones, -1);
    return out;
  }
  const chains = new Map<number, PatternStone[]>();
  for (const s of stones) { const l = chains.get(s.chain); if (l) l.push(s); else chains.set(s.chain, [s]); }
  for (const [chain, list] of chains) {
    let flat = patUnwrap(list, C);
    if (!lineLike || flat.length < 2) {
      for (const side of sides) {
        const r = Math.max(...flat.map((s) => s.d / 2));
        const a0 = Math.min(...flat.map((s) => s.arcMM)) - r - R.gapMM;
        const a1 = Math.max(...flat.map((s) => s.arcMM)) + r + R.gapMM;
        const y = side === "right" ? Math.max(...flat.map((s) => s.yMM + s.d / 2)) + off : Math.min(...flat.map((s) => s.yMM - s.d / 2)) - off;
        out.push({ layer, chain, side, widthMM: R.widthMM, depthMM: R.depthMM, kind: "path", points: [{ arcMM: a0, yMM: y }, { arcMM: a1, yMM: y }] });
      }
      continue;
    }
    if (flat[flat.length - 1].arcMM < flat[0].arcMM) flat = [...flat].reverse();
    const maxR = Math.max(...flat.map((s) => s.d / 2));
    // Extend both ends along the end segments by the end stone's radius + gap.
    const ext = (p: { arcMM: number; yMM: number }, q: { arcMM: number; yMM: number }, by: number) => {
      const ea = p.arcMM - q.arcMM, ey = p.yMM - q.yMM;
      const len = Math.sqrt(ea * ea + ey * ey) || 1;
      return { arcMM: p.arcMM + (ea / len) * by, yMM: p.yMM + (ey / len) * by };
    };
    const centre = [ext(flat[0], flat[1], flat[0].d / 2 + R.gapMM), ...flat.map((s) => ({ arcMM: s.arcMM, yMM: s.yMM })), ext(flat[flat.length - 1], flat[flat.length - 2], flat[flat.length - 1].d / 2 + R.gapMM)];
    for (const side of sides) {
      const pts = patOffsetPolyline(centre, side === "right" ? maxR + off : -(maxR + off));
      out.push({ layer, chain, side, widthMM: R.widthMM, depthMM: R.depthMM, kind: "path", points: patDownsample(pts, DESIGN_GROOVE_PATH_MAX_POINTS) });
    }
  }
  return out;
}

/**
 * v67: expand a whole pattern (every layer, `STONE_PATTERN_MAX_STONES` in all,
 * earlier layers first). Stone `index` is global; pockets and rails refer to it.
 */
export function expandStonePattern(pattern: StonePattern | null | undefined, band: StonePatternBand): StonePatternResult {
  const p = clampStonePattern(pattern);
  const res: StonePatternResult = { stones: [], pockets: [], rails: [], requested: 0, removed: 0, clipped: false };
  if (!p) return res;
  p.layers.forEach((layer, li) => {
    const left = STONE_PATTERN_MAX_STONES - res.stones.length;
    if (left <= 0) { res.clipped = true; return; }
    const r = buildStonePattern(layer, band, li, left);
    const offset = res.stones.length;
    res.stones.push(...r.stones.map((s) => ({ ...s, index: s.index + offset })));
    res.pockets.push(...r.pockets.map((q) => ({ ...q, stones: q.stones.map((i) => i + offset) })));
    res.rails.push(...r.rails);
    res.requested += r.requested;
    res.removed += r.removed;
    res.clipped = res.clipped || r.clipped;
  });
  return res;
}

// ── pockets ──

type PatPt = { arcMM: number; yMM: number };
type PatSeat = { arcMM: number; yMM: number; d: number; r?: number };

/** A polygon that CONTAINS the circle (circumscribed, N sides). */
function patCircle(cx: number, cy: number, r: number, N = 32): PatPt[] {
  const R = r / patternCos(PAT_PI / N);
  const out: PatPt[] = [];
  for (let k = 0; k < N; k++) {
    const t = (PAT_TWO_PI * (k + 0.5)) / N;
    out.push({ arcMM: cx + R * patternCos(t), yMM: cy + R * patternSin(t) });
  }
  return out;
}

/** Convex hull (Andrew's monotone chain), counter-clockwise. */
function patHull(pts: PatPt[]): PatPt[] {
  const p = [...pts].sort((a, b) => a.arcMM - b.arcMM || a.yMM - b.yMM);
  if (p.length < 3) return p;
  const cross = (o: PatPt, a: PatPt, b: PatPt) => (a.arcMM - o.arcMM) * (b.yMM - o.yMM) - (a.yMM - o.yMM) * (b.arcMM - o.arcMM);
  const lower: PatPt[] = [];
  for (const q of p) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], q) <= 0) lower.pop(); lower.push(q); }
  const upper: PatPt[] = [];
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], q) <= 0) upper.pop(); upper.push(q); }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

/** Seat-footprint radius + clearance: what every pocket must hold. */
function patRho(s: PatSeat, clearance: number): number {
  return (s.r ?? STONE_PATTERN_SEAT_FACTOR * s.d) + Math.max(STONE_PATTERN_MIN_CLEARANCE_MM, clearance);
}

/** The group's axis: first → last centre (x) and its normal. */
function patAxis(seats: PatSeat[]): { ox: number; oy: number; ux: number; uy: number } {
  const f = seats[0], l = seats[seats.length - 1];
  const ea = l.arcMM - f.arcMM, ey = l.yMM - f.yMM;
  const len = Math.sqrt(ea * ea + ey * ey);
  const ox = seats.reduce((a, s) => a + s.arcMM, 0) / seats.length;
  const oy = seats.reduce((a, s) => a + s.yMM, 0) / seats.length;
  return len > 1e-9 ? { ox, oy, ux: ea / len, uy: ey / len } : { ox, oy, ux: 1, uy: 0 };
}

/**
 * v67: the closed outline (mm, counter-clockwise, path-groove plane) of a
 * pocket holding `seats`. Every seat footprint — `r` if given (the engine
 * passes a shaped cut's own outline radius), else 0.53 d — plus the
 * clearance (never under 0.05 mm) lies inside it:
 *  · "capsule": the convex hull of the footprint circles (tapers with
 *    graduated stones);
 *  · "channel": a constant-width trough along the stones in order, round ends
 *    (not convex in general);
 *  · "navette": the smallest pointed lens (two circular arcs) over the group's
 *    axis that holds them, opened by `cornerRadiusMM` so the tips are
 *    filleted — convex;
 *  · "plate": the rounded rectangle on the group's axis — convex.
 * Fillets are never larger than the smallest footprint, which is what keeps
 * every footprint inside (an opening by r keeps every disc of radius ≥ r).
 * Circles are drawn CIRCUMSCRIBED, so the polygon never cuts a footprint.
 */
export function pocketOutline(
  shape: Exclude<StonePatternPocketShape, "none">,
  seats: PatSeat[],
  opts: { clearanceMM?: number; cornerRadiusMM?: number } = {},
): PatPt[] {
  if (!seats.length) return [];
  const clear = opts.clearanceMM ?? 0.08;
  const rhos = seats.map((s) => patRho(s, clear));
  const minRho = Math.min(...rhos);
  if (shape === "capsule" || (seats.length === 1 && shape === "channel")) {
    return patHull(seats.flatMap((s, i) => patCircle(s.arcMM, s.yMM, rhos[i])));
  }
  if (shape === "channel") {
    const w = Math.max(...rhos);
    const pts = seats.map((s) => ({ arcMM: s.arcMM, yMM: s.yMM }));
    const left = patOffsetPolyline(pts, w);
    const right = patOffsetPolyline(pts, -w);
    const cap = (c: PatPt, from: PatPt) => {
      // Half turn counter-clockwise from `from` round the far side of c, circumscribed.
      const ax = from.arcMM - c.arcMM, ay = from.yMM - c.yMM;
      const N = 16;
      const R = w / patternCos(PAT_PI / (2 * N));
      const out: PatPt[] = [];
      for (let k = 0; k < N; k++) {
        const t = (PAT_PI * (k + 0.5)) / N;
        const cs = patternCos(t), sn = patternSin(t);
        const rx = (ax * cs - ay * sn) / w, ry = (ax * sn + ay * cs) / w;
        out.push({ arcMM: c.arcMM + rx * R, yMM: c.yMM + ry * R });
      }
      return out;
    };
    const n = pts.length;
    // Travel along `left` (on the +normal side), round the end, back along `right`.
    return [
      ...right,
      ...cap(pts[n - 1], right[n - 1]),
      ...[...left].reverse(),
      ...cap(pts[0], left[0]),
    ];
  }
  const ax = patAxis(seats);
  const loc = seats.map((s, i) => {
    const ea = s.arcMM - ax.ox, ey = s.yMM - ax.oy;
    return { x: ea * ax.ux + ey * ax.uy, y: -ea * ax.uy + ey * ax.ux, r: rhos[i] };
  });
  const toBand = (x: number, y: number): PatPt => ({ arcMM: ax.ox + x * ax.ux - y * ax.uy, yMM: ax.oy + x * ax.uy + y * ax.ux });
  if (shape === "plate") {
    const x0 = Math.min(...loc.map((p) => p.x - p.r)), x1 = Math.max(...loc.map((p) => p.x + p.r));
    const y0 = Math.min(...loc.map((p) => p.y - p.r)), y1 = Math.max(...loc.map((p) => p.y + p.r));
    const rc = Math.max(0.01, Math.min(opts.cornerRadiusMM ?? 0.25, minRho, (x1 - x0) / 2, (y1 - y0) / 2));
    const corners = [[x0 + rc, y0 + rc], [x1 - rc, y0 + rc], [x1 - rc, y1 - rc], [x0 + rc, y1 - rc]];
    return patHull(corners.flatMap(([x, y]) => patCircle(x, y, rc, 32)).map((p) => toBand(p.arcMM, p.yMM)));
  }
  // navette: lens of tip half-length T, half-width B about the local x axis.
  const xc = (Math.min(...loc.map((p) => p.x - p.r)) + Math.max(...loc.map((p) => p.x + p.r))) / 2;
  const yc = loc.reduce((a, p) => a + p.y, 0) / loc.length;
  const lp = loc.map((p) => ({ x: p.x - xc, y: p.y - yc, r: p.r }));
  const maxRho = Math.max(...rhos);
  let T = Math.max(...lp.map((p) => Math.abs(p.x) + p.r)) + 0.6 * maxRho;
  const fits = (Tv: number, B: number) => {
    const R = (Tv * Tv + B * B) / (2 * B);
    const k = R - B;
    return lp.every((p) => {
      const d1 = Math.sqrt(p.x * p.x + (p.y + k) * (p.y + k));
      const d2 = Math.sqrt(p.x * p.x + (p.y - k) * (p.y - k));
      return d1 + p.r <= R + 1e-12 && d2 + p.r <= R + 1e-12;
    });
  };
  let guard = 0;
  while (!fits(T, T) && guard++ < 60) T *= 1.15;
  let lo = 1e-6, hi = T;
  for (let it = 0; it < 60; it++) { const mid = (lo + hi) / 2; if (fits(T, mid)) hi = mid; else lo = mid; }
  const B = hi;
  const R = (T * T + B * B) / (2 * B);
  const k = R - B;
  const rf = Math.max(0.01, Math.min(opts.cornerRadiusMM ?? 0.25, minRho, B * 0.95));
  const Tp = Math.sqrt(Math.max(0, (R - rf) * (R - rf) - k * k)); // eroded tip
  const pts: PatPt[] = [];
  // Big arcs between their tangency with the tip circles, circumscribed.
  const xt = (R * Tp) / (R - rf);
  const N = 64;
  const xs: number[] = [], hs: number[] = [];
  let chord = 0;
  for (let q = 0; q <= N; q++) {
    const x = -xt + (2 * xt * q) / N;
    xs.push(x);
    hs.push(Math.sqrt(Math.max(0, R * R - x * x)));
    if (q > 0) {
      const ex = x - xs[q - 1], eh = hs[q] - hs[q - 1];
      chord = Math.max(chord, Math.sqrt(ex * ex + eh * eh));
    }
  }
  // Push every sample out radially so each chord clears the true arc.
  const push = 1 / Math.sqrt(Math.max(1e-12, 1 - (chord * chord) / (4 * R * R)));
  for (let q = 0; q <= N; q++) {
    pts.push({ arcMM: xs[q] * push, yMM: -k + hs[q] * push }); // upper arc, centre (0, −k)
    pts.push({ arcMM: xs[q] * push, yMM: k - hs[q] * push });  // lower arc, centre (0, +k)
  }
  pts.push(...patCircle(Tp, 0, rf, 32), ...patCircle(-Tp, 0, rf, 32));
  return patHull(pts).map((p) => toBand(p.arcMM + xc, p.yMM + yc));
}

/**
 * v67: a capsule / channel pocket as ONE constant-width path source (what the
 * kernel carves as a path outline: a flat floor, round ends). The width
 * covers every footprint + clearance, so it contains `pocketOutline`'s seats
 * too (a tapered capsule becomes its widest stadium).
 */
export function pocketPath(
  shape: "capsule" | "channel",
  seats: PatSeat[],
  opts: { clearanceMM?: number } = {},
): { points: PatPt[]; widthMM: number } {
  if (!seats.length) return { points: [], widthMM: 0 };
  const clear = opts.clearanceMM ?? 0.08;
  if (shape === "channel" || seats.length < 2) {
    const pts = seats.map((s) => ({ arcMM: s.arcMM, yMM: s.yMM }));
    const one = pts.length === 1 ? [pts[0], { arcMM: pts[0].arcMM + 1e-3, yMM: pts[0].yMM }] : pts;
    return { points: patDownsample(one, DESIGN_GROOVE_PATH_MAX_POINTS), widthMM: 2 * Math.max(...seats.map((s) => patRho(s, clear))) };
  }
  const a = seats[0], b = seats[seats.length - 1];
  const ea = b.arcMM - a.arcMM, ey = b.yMM - a.yMM;
  const l2 = ea * ea + ey * ey || 1e-12;
  let half = 0;
  for (const s of seats) {
    const t = patClamp(((s.arcMM - a.arcMM) * ea + (s.yMM - a.yMM) * ey) / l2, 0, 1);
    const px = a.arcMM + ea * t - s.arcMM, py = a.yMM + ey * t - s.yMM;
    half = Math.max(half, Math.sqrt(px * px + py * py) + patRho(s, clear));
  }
  return { points: [{ arcMM: a.arcMM, yMM: a.yMM }, { arcMM: b.arcMM, yMM: b.yMM }], widthMM: 2 * half };
}

// ── checks ──

/** Even–odd point in polygon. */
export function pointInPatternOutline(p: PatPt, poly: PatPt[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.yMM > p.yMM) !== (b.yMM > p.yMM)) {
      const x = a.arcMM + ((p.yMM - a.yMM) * (b.arcMM - a.arcMM)) / (b.yMM - a.yMM);
      if (p.arcMM < x) inside = !inside;
    }
  }
  return inside;
}

/** The two stones closest girdle to girdle (wrap-aware), and that gap. */
export function stonePatternTightestPair(stones: readonly PatternStone[], circumferenceMM = 65): { a: number; b: number; gapMM: number } | null {
  const C = circumferenceMM;
  let best: { a: number; b: number; gapMM: number } | null = null;
  for (let i = 0; i < stones.length; i++) {
    const p = stones[i];
    for (let j = i + 1; j < stones.length; j++) {
      const q = stones[j];
      let ea = q.arcMM - p.arcMM;
      ea -= C * Math.floor(ea / C + 0.5);
      const ey = q.yMM - p.yMM;
      const reach = (p.d + q.d) / 2 + (best ? best.gapMM : Infinity);
      if (ea > reach || -ea > reach || ey > reach || -ey > reach) continue;
      const gap = Math.sqrt(ea * ea + ey * ey) - (p.d + q.d) / 2;
      if (!best || gap < best.gapMM) best = { a: p.index, b: q.index, gapMM: gap };
    }
  }
  return best;
}

/**
 * v67: the layer checks a pattern must pass — every stone on the band and of
 * a legal size, no two girdles closer than `STONE_PATTERN_MIN_GAP_MM`
 * (wrap-aware, across layers too), at most `STONE_PATTERN_MAX_STONES`, every
 * pocket holding every seat footprint (0.53 d + 0.05 mm) and on the band,
 * every rail clear of the girdles and on the band. Empty ⇒ it passes. The
 * engine reads the overlap entry for its "lower this layer to N stones"
 * warning; Surprise only returns patterns with none.
 */
export function stonePatternIssues(res: StonePatternResult, band: StonePatternBand): StonePatternIssue[] {
  const out: StonePatternIssue[] = [];
  const C = patFinite(band.circumferenceMM) ? Math.max(10, band.circumferenceMM) : 65;
  const halfW = Math.max(0.1, band.widthMM / 2);
  const margin = patFinite(band.edgeMarginMM) ? Math.max(STONE_PATTERN_SEAT_RIM_MIN_MM, band.edgeMarginMM) : 0.15;
  const eps = 1e-6;
  if (!res.stones.length) out.push({ code: "empty", layer: 0, message: "the pattern places no stones" });
  if (res.stones.length > STONE_PATTERN_MAX_STONES || res.clipped) out.push({ code: "cap", layer: 0, message: `more than ${STONE_PATTERN_MAX_STONES} stones` });
  for (const s of res.stones) {
    if (!(patFinite(s.arcMM) && patFinite(s.yMM) && patFinite(s.d)) || s.d < 0.3 || s.d > 8) {
      out.push({ code: "stone.size", layer: s.layer, message: `stone ${s.index} has an illegal size or position` });
    } else if (Math.abs(s.yMM) + s.d / 2 > halfW - margin + eps) {
      out.push({ code: "stone.off-band", layer: s.layer, message: `stone ${s.index} overhangs the band edge` });
    }
  }
  const tight = stonePatternTightestPair(res.stones, C);
  if (tight && tight.gapMM < STONE_PATTERN_MIN_GAP_MM - eps) {
    const L = res.stones[tight.a]?.layer ?? 0;
    out.push({ code: "stone.overlap", layer: L, message: `stones ${tight.a} and ${tight.b} are ${tight.gapMM.toFixed(3)} mm apart (least ${STONE_PATTERN_MIN_GAP_MM})` });
  }
  for (const p of res.pockets) {
    const seats = p.stones.map((i) => res.stones[i]);
    if (p.ring) {
      if (seats.some((s) => s.yMM - STONE_PATTERN_SEAT_FACTOR * s.d - STONE_PATTERN_MIN_CLEARANCE_MM < p.ring!.yMinMM - eps
        || s.yMM + STONE_PATTERN_SEAT_FACTOR * s.d + STONE_PATTERN_MIN_CLEARANCE_MM > p.ring!.yMaxMM + eps)) {
        out.push({ code: "pocket.footprint", layer: p.layer, message: "a seat is outside its channel" });
      }
      if (Math.max(-p.ring.yMinMM, p.ring.yMaxMM) > halfW + eps) out.push({ code: "pocket.off-band", layer: p.layer, message: "a channel runs off the band" });
      continue;
    }
    const flat = patUnwrap(seats, C);
    if (!patternOutlineHolds(p.outline, flat)) out.push({ code: "pocket.footprint", layer: p.layer, message: "a seat footprint is outside its pocket" });
    if (p.outline.some((q) => Math.abs(q.yMM) > halfW + eps)) out.push({ code: "pocket.off-band", layer: p.layer, message: "a pocket runs off the band" });
  }
  for (const r of res.rails) {
    const layerStones = res.stones.filter((s) => s.layer === r.layer && (r.chain < 0 || s.chain === r.chain));
    if (r.kind === "horizontal") {
      const y = r.yMM ?? 0;
      if (Math.abs(y) + r.widthMM / 2 > halfW + eps) out.push({ code: "rail.off-band", layer: r.layer, message: "a rail runs off the band" });
      if (layerStones.some((s) => Math.abs(s.yMM - y) < s.d / 2 + r.widthMM / 2 - eps)) out.push({ code: "rail.overlap", layer: r.layer, message: "a rail crosses a stone" });
    } else {
      const pts = r.points ?? [];
      if (pts.some((q) => Math.abs(q.yMM) + r.widthMM / 2 > halfW + eps)) out.push({ code: "rail.off-band", layer: r.layer, message: "a rail runs off the band" });
      const flat = patUnwrap(layerStones, C);
      if (flat.some((s) => patDistToPolyline(s, pts) < s.d / 2 + r.widthMM / 2 - 1e-4)) out.push({ code: "rail.overlap", layer: r.layer, message: "a rail crosses a stone" });
    }
  }
  return out;
}

/** True when every footprint (0.53 d, or `r`, + 0.05 mm) is inside `outline`. */
export function patternOutlineHolds(outline: PatPt[], seats: PatSeat[]): boolean {
  if (outline.length < 3) return false;
  for (const s of seats) {
    const rad = (s.r ?? STONE_PATTERN_SEAT_FACTOR * s.d) + STONE_PATTERN_MIN_CLEARANCE_MM - 1e-6;
    for (let k = 0; k < 24; k++) {
      const [ux, uy] = PAT_DIRS[k % 12];
      const half = k >= 12;
      // 24 directions: the 12 table directions and their bisectors.
      const bx = half ? (ux + PAT_DIRS[(k + 1) % 12][0]) : ux;
      const by = half ? (uy + PAT_DIRS[(k + 1) % 12][1]) : uy;
      const bl = Math.sqrt(bx * bx + by * by);
      if (!pointInPatternOutline({ arcMM: s.arcMM + (bx / bl) * rad, yMM: s.yMM + (by / bl) * rad }, outline)) return false;
    }
    if (!pointInPatternOutline({ arcMM: s.arcMM, yMM: s.yMM }, outline)) return false;
  }
  return true;
}

function patDistToPolyline(p: PatPt, pts: PatPt[]): number {
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const ea = b.arcMM - a.arcMM, ey = b.yMM - a.yMM;
    const l2 = ea * ea + ey * ey || 1e-12;
    const t = patClamp(((p.arcMM - a.arcMM) * ea + (p.yMM - a.yMM) * ey) / l2, 0, 1);
    const dx = a.arcMM + ea * t - p.arcMM, dy = a.yMM + ey * t - p.yMM;
    best = Math.min(best, Math.sqrt(dx * dx + dy * dy));
  }
  return best;
}

// ── the library ──

/** The gallery's category chips, in display order. */
export const STONE_PATTERN_CATEGORIES = [
  "Line", "Offset line", "Double row", "Triple row", "Diagonal", "Sweep", "Chevron", "Zigzag",
  "Wave", "S-curve", "Smile", "Comet", "Trio", "Eye trio", "Capsule groups", "Channel",
  "Cluster", "Rosette", "Starry scatter", "Constellation", "Cross row", "Stations",
  "Alternating", "Graduated", "Half eternity", "Three-quarter eternity", "Full eternity",
] as const;
export type StonePatternCategory = (typeof STONE_PATTERN_CATEGORIES)[number];

export interface StonePatternLibraryEntry {
  /** Stable FOREVER — saved designs and recipes name it. Never renumber. */
  id: string;
  /** Plain words; no brand, no mark. */
  name: string;
  category: StonePatternCategory;
  layers: StonePatternLayer[];
  /** The band width the entry is drawn for (its thumbnail, its checks). */
  widthMM: number;
}

/** The band every library entry is checked and pinned on, but for its width. */
export const STONE_PATTERN_LIBRARY_BAND: Readonly<Required<StonePatternBand>> = {
  widthMM: 4, circumferenceMM: 62, stoneSizeMM: 1.5, edgeMarginMM: 0.15,
};

/**
 * Family rules × variant tables, expanded here so the contract stays lean.
 * ⚠️ IDS ARE STABLE FOREVER: a family's id scheme and its variant tables may
 * only ever GROW (append a value, add a family). Removing a value or changing
 * an id breaks every saved design and recipe that names it — verify:patterns
 * pins each entry's stones against `docs/verify/fixtures/stone-pattern-golden.json`.
 */
function buildStonePatternLibrary(): StonePatternLibraryEntry[] {
  const out: StonePatternLibraryEntry[] = [];
  const add = (id: string, name: string, category: StonePatternCategory, layers: StonePatternLayer[], widthMM = 4) =>
    out.push({ id, name, category, layers, widthMM });
  const t = (v: number) => String(Math.round(v * 10)); // 1.4 → "14"
  const mm = (v: number) => `${Math.round(v * 10) / 10} mm`;
  const lab = (s: StonePatternSetting) => STONE_PATTERN_SETTINGS[s].label;
  const gapOf = (s: StonePatternSetting) => (s === "pave" ? 0.08 : s === "shared-prong" ? 0.08 : 0.25);

  // Line — a centred row.
  for (const n of [3, 5, 7, 9, 13]) for (const d of [1.0, 1.4, 1.8]) for (const s of ["flush", "pave"] as const) {
    add(`line-${n}-${t(d)}-${s}`, `Line · ${n} × ${mm(d)} · ${lab(s)}`, "Line",
      [{ path: "row", count: n, sizeMM: d, gapMM: gapOf(s), setting: s }]);
  }
  // Offset line — a row toward one edge, with or without a rail on the other side.
  for (const n of [5, 9]) for (const d of [1.0, 1.4]) for (const side of [-1, 1]) for (const rail of [false, true]) {
    const sideName = side < 0 ? "left" : "right";
    add(`offset-${n}-${t(d)}-${sideName}${rail ? "-rail" : ""}`,
      `Offset line · ${n} × ${mm(d)} · ${sideName}${rail ? " · with rail" : ""}`, "Offset line",
      [{ path: "row", count: n, sizeMM: d, gapMM: 0.25, centerYMM: side * 0.6, setting: "flush",
        ...(rail ? { rails: { sides: side < 0 ? "right" : "left", extent: "all-round", gapMM: 0.35, widthMM: 0.25, depthMM: 0.15 } as StonePatternRails } : {}) }]);
  }
  // Double / triple rows.
  for (const n of [10, 16, 24]) for (const d of [0.9, 1.2]) for (const st of [true, false]) {
    add(`double-${n}-${t(d)}-${st ? "stagger" : "square"}`, `Double row · ${n} × ${mm(d)} · ${st ? "staggered" : "side by side"}`, "Double row",
      [{ path: "multi-row", rows: 2, count: n, sizeMM: d, gapMM: 0.1, stagger: st, setting: "pave" }]);
  }
  for (const n of [15, 24]) for (const d of [0.8, 1.0]) for (const st of [true, false]) {
    add(`triple-${n}-${t(d)}-${st ? "stagger" : "square"}`, `Triple row · ${n} × ${mm(d)} · ${st ? "staggered" : "side by side"}`, "Triple row",
      [{ path: "multi-row", rows: 3, count: n, sizeMM: d, gapMM: 0.1, stagger: st, setting: "pave" }], d > 0.9 ? 4.5 : 4);
  }
  // Diagonal — a tilted row; the steeper the tilt, the fewer stones fit across.
  for (const [a, n, d] of [[10, 9, 1.0], [10, 7, 1.4], [15, 7, 1.0], [15, 5, 1.4], [20, 6, 1.0], [20, 5, 1.2],
    [25, 5, 1.0], [25, 4, 1.3], [30, 4, 1.0], [35, 4, 0.9], [40, 3, 1.1], [45, 3, 1.0]] as const) {
    add(`diagonal-${a}-${n}-${t(d)}`, `Diagonal · ${a}° · ${n} × ${mm(d)}`, "Diagonal",
      [{ path: "diagonal", angleDeg: a, count: n, sizeMM: d, gapMM: 0.2, setting: "flush" }]);
  }
  // Sweep — across the band edge to edge.
  for (const n of [7, 9, 11, 15]) for (const d of [1.0, 1.4]) for (const g of [false, true]) {
    add(`sweep-${n}-${t(d)}${g ? "-graduated" : ""}`, `Sweep · ${n} × ${mm(d)}${g ? " · graduated" : ""}`, "Sweep",
      [{ path: "sweep", count: n, sizeMM: d, gapMM: 0.2, setting: "flush", ...(g ? { sizing: { mode: "graduated", endScale: 0.55 } } : {}) }]);
  }
  // Chevron.
  for (const n of [5, 7, 9]) for (const amp of [0.5, 0.9]) for (const d of [1.0, 1.3]) {
    add(`chevron-${n}-${t(amp)}-${t(d)}`, `Chevron · ${n} × ${mm(d)} · ${amp < 0.7 ? "shallow" : "deep"}`, "Chevron",
      [{ path: "chevron", count: n, amplitudeMM: amp, sizeMM: d, gapMM: 0.2, setting: "flush" }]);
  }
  // Zigzag.
  for (const w of [2, 3, 4]) for (const n of [9, 13, 17]) for (const d of [0.9, 1.2]) {
    add(`zigzag-${w}-${n}-${t(d)}`, `Zigzag · ${w} peaks · ${n} × ${mm(d)}`, "Zigzag",
      [{ path: "zigzag", waves: w, count: n, amplitudeMM: 0.55, sizeMM: d, gapMM: 0.15, setting: "flush" }]);
  }
  // Wave.
  for (const w of [2, 3]) for (const n of [11, 15, 21]) for (const amp of [0.5, 0.9]) {
    add(`wave-${w}-${n}-${t(amp)}`, `Wave · ${w} waves · ${n} stones · ${amp < 0.7 ? "gentle" : "deep"}`, "Wave",
      [{ path: "wave", waves: w, count: n, amplitudeMM: amp, sizeMM: 1.0, gapMM: 0.15, setting: "flush" }]);
  }
  // S-curve.
  for (const n of [7, 9, 13]) for (const d of [1.0, 1.4]) for (const g of [false, true]) {
    add(`s-curve-${n}-${t(d)}${g ? "-graduated" : ""}`, `S-curve · ${n} × ${mm(d)}${g ? " · graduated" : ""}`, "S-curve",
      [{ path: "s-curve", count: n, amplitudeMM: 0.8, sizeMM: d, gapMM: 0.2, setting: "flush", ...(g ? { sizing: { mode: "graduated", endScale: 0.6 } } : {}) }]);
  }
  // Smile / frown.
  for (const n of [5, 7, 9]) for (const amp of [0.5, 0.9]) for (const dir of [1, -1]) {
    add(`${dir > 0 ? "smile" : "frown"}-${n}-${t(amp)}`, `${dir > 0 ? "Smile" : "Frown"} · ${n} stones · ${amp < 0.7 ? "gentle" : "deep"}`, "Smile",
      [{ path: "arc", count: n, amplitudeMM: dir * amp, sizeMM: 1.2, gapMM: 0.2, setting: "flush" }]);
  }
  // Comet — big stone first, tailing away.
  for (const n of [5, 7, 9, 12]) for (const e of [0.4, 0.6]) for (const p of ["row", "arc"] as const) {
    add(`comet-${p}-${n}-${t(e)}`, `Comet · ${n} stones · ${p === "arc" ? "curved" : "straight"} · ${e < 0.5 ? "long tail" : "short tail"}`, "Comet",
      [{ path: p, count: n, sizeMM: 1.6, gapMM: 0.2, setting: "flush", ...(p === "arc" ? { amplitudeMM: 0.5 } : {}),
        sizing: { mode: "graduated", endScale: e, anchor: "one-sided" } }]);
  }
  // Trio — three graduated stones.
  for (const d of [1.5, 2.0, 2.5]) for (const e of [0.6, 0.8]) for (const s of ["flush", "prongs", "bezel"] as const) {
    add(`trio-${t(d)}-${t(e)}-${s}`, `Trio · ${mm(d)} centre · ${lab(s)}${e < 0.7 ? " · small sides" : ""}`, "Trio",
      [{ path: "row", count: 3, sizeMM: d, gapMM: s === "prongs" ? 0.35 : 0.25, setting: s, sizing: { mode: "graduated", endScale: e } }], d > 2.2 ? 4.5 : 4);
  }
  // Eye trio — three graduated stones sunk in a navette or a plate.
  for (const d of [1.5, 2.0, 2.5]) for (const e of [0.55, 0.75]) for (const sh of ["navette", "plate"] as const) {
    add(`eye-trio-${sh}-${t(d)}-${t(e)}`, `Eye trio · ${sh === "navette" ? "navette" : "plate"} · ${mm(d)} centre${e < 0.7 ? " · small sides" : ""}`, "Eye trio",
      [{ path: "row", count: 3, sizeMM: d, gapMM: 0.15, setting: "flush", sizing: { mode: "graduated", endScale: e },
        pocket: { shape: sh, depthMM: 0.3, clearanceMM: 0.1, cornerRadiusMM: sh === "navette" ? 0.2 : 0.35 } }], d > 2.2 ? 4.5 : 4);
  }
  // Capsule groups — stones in round-ended troughs; a lone stone stays bare.
  const GROUPS: [string, number[]][] = [["3-1-3", [3, 1, 3]], ["2-2-2", [2, 2, 2]], ["3-3", [3, 3]], ["2-1-2", [2, 1, 2]], ["4-4", [4, 4]], ["1-3-1", [1, 3, 1]]];
  for (const [gid, groups] of GROUPS) for (const g of [false, true]) {
    add(`capsule-${gid}${g ? "-graduated" : ""}`, `Capsule groups · ${groups.join(" + ")}${g ? " · graduated" : ""}`, "Capsule groups",
      [{ path: "row", groups, groupGapMM: 0.7, sizeMM: 1.2, gapMM: 0.12, setting: "flush", ...(g ? { sizing: { mode: "graduated", endScale: 0.6 } } : {}),
        pocket: { shape: "capsule", depthMM: 0.3, clearanceMM: 0.08, minGroup: 2 } }]);
  }
  // Channel — a row sunk in one trough.
  for (const n of [5, 9, 15]) for (const d of [1.2, 1.6]) {
    add(`channel-${n}-${t(d)}`, `Channel · ${n} × ${mm(d)}`, "Channel",
      [{ path: "row", count: n, sizeMM: d, gapMM: 0.1, setting: "flush", pocket: { shape: "channel", depthMM: 0.3, clearanceMM: 0.06 } }]);
  }
  // Cluster.
  for (const n of [5, 7, 9, 12, 19]) for (const d of [0.8, 1.1]) {
    add(`cluster-${n}-${t(d)}`, `Cluster · ${n} × ${mm(d)}`, "Cluster",
      [{ path: "cluster", count: n, sizeMM: d, gapMM: 0.12, setting: "pave", sizing: { mode: "graduated", endScale: 0.7 } }]);
  }
  // Rosette.
  for (const p of [5, 6, 7, 8]) for (const d of [1.6, 2.2]) {
    add(`rosette-${p}-${t(d)}`, `Rosette · ${p} petals · ${mm(d)} centre`, "Rosette",
      [{ path: "rosette", count: p + 1, sizeMM: d, gapMM: 0.12, setting: "flush", sizing: { mode: "graduated", endScale: 0.55 } }], d > 2 ? 6 : 4.5);
  }
  // Starry scatter.
  for (const [n, span] of [[9, 18], [13, 24], [19, 32], [25, 0]] as const) for (const seed of [1, 2, 3]) {
    add(`starry-${n}-s${seed}`, `Starry scatter · ${n} stones · ${span ? `${span} mm` : "all round"} · no. ${seed}`, "Starry scatter",
      [{ path: "scatter", count: n, sizeMM: 1.1, gapMM: 0.3, setting: "flush", seed, sizing: { mode: "random", min: 0.6, max: 1.2 },
        ...(span ? { spanMM: span } : { spanTurns: 1 }) }]);
  }
  // Constellation — small clusters scattered.
  const CONST: [string, number[]][] = [["a", [1, 2, 3, 2]], ["b", [2, 3, 2, 3]], ["c", [3, 1, 2, 2, 1]]];
  for (const [cid, groups] of CONST) for (const seed of [1, 2]) for (const turns of [0.5, 1]) {
    add(`constellation-${cid}-s${seed}-${turns === 1 ? "full" : "half"}`,
      `Constellation · ${groups.length} clusters · ${turns === 1 ? "all round" : "half round"} · no. ${seed}`, "Constellation",
      [{ path: "scatter", groups, groupGapMM: 1.2, gapMM: 0.12, sizeMM: 1.1, setting: "flush", seed, spanTurns: turns, sizing: { mode: "random", min: 0.6, max: 1.1 } }]);
  }
  // Cross row — stones across the band, repeated round the ring.
  for (const [n, d] of [[2, 1.0], [2, 1.4], [3, 0.8], [3, 1.0]] as const) for (const r of [1, 3, 5, 8]) {
    add(`cross-${n}-${t(d)}-x${r}`, `Cross row · ${n} × ${mm(d)}${r > 1 ? ` · ${r} times round` : ""}`, "Cross row",
      [{ path: "row", angleDeg: 90, count: n, sizeMM: d, gapMM: 0.15, setting: "flush", repeat: r }]);
  }
  // Stations — one stone or a trio, repeated.
  for (const r of [3, 4, 6, 8]) for (const trio of [false, true]) {
    add(`stations-${trio ? "trio" : "solo"}-x${r}`, `Stations · ${r} × ${trio ? "trio" : "single stone"}`, "Stations",
      [{ path: "row", count: trio ? 3 : 1, sizeMM: trio ? 1.4 : 1.6, gapMM: 0.2, setting: trio ? "pave" : "bezel", repeat: r,
        ...(trio ? { sizing: { mode: "graduated", endScale: 0.7 } } : {}) }]);
  }
  // Alternating.
  for (const n of [7, 11, 15]) for (const a of [0.6, 0.75]) {
    add(`alternating-${n}-${t(a)}`, `Alternating · ${n} stones · ${a < 0.7 ? "big and small" : "subtle"}`, "Alternating",
      [{ path: "row", count: n, sizeMM: 1.4, gapMM: 0.15, setting: "flush", sizing: { mode: "alternating", altScale: a } }]);
  }
  // Graduated row.
  for (const n of [5, 7, 9, 11, 15]) for (const e of [0.5, 0.7]) {
    add(`graduated-${n}-${t(e)}`, `Graduated row · ${n} stones · ${e < 0.6 ? "steep" : "soft"}`, "Graduated",
      [{ path: "row", count: n, sizeMM: 1.6, gapMM: 0.15, setting: "flush", sizing: { mode: "graduated", endScale: e } }]);
  }
  // Eternities: as many stones as fit. A full eternity is spread evenly so the
  // loop closes without a seam; a half / three-quarter one keeps its stones
  // at the set gap (nearly touching) and centres them on its share of the ring.
  const ETERNITY: [StonePatternCategory, string, number][] = [["Half eternity", "half", 0.5], ["Three-quarter eternity", "three-quarter", 0.75], ["Full eternity", "full", 1]];
  for (const [cat, slug, turns] of ETERNITY) {
    const sizes = turns === 0.75 ? [1.0, 1.5, 2.0] : [1.0, 1.3, 1.6, 2.0, 2.5];
    const sets: StonePatternSetting[] = turns === 0.75 ? ["pave", "shared-prong"] : ["pave", "shared-prong", "flush"];
    for (const d of sizes) for (const s of sets) {
      add(`${slug}-eternity-${s}-${t(d)}`, `${cat} · ${mm(d)} · ${lab(s)}`, cat,
        [{ path: "row", spanTurns: turns, sizeMM: d, gapMM: s === "flush" ? 0.3 : 0.08, setting: s, ...(turns < 1 ? { spacing: "gap" as const } : {}) }], d > 2.2 ? 4.5 : 4);
    }
  }

  // The reference looks (plain names; their ids are what the recipes use).
  add("offset-pave-rail-9", "Offset pavé line beside a rail · 9 stones", "Offset line",
    [{ path: "row", count: 9, sizeMM: 1.3, gapMM: 0.08, centerYMM: 0.45, setting: "pave",
      rails: { sides: "left", extent: "all-round", gapMM: 0.3, widthMM: 0.25, depthMM: 0.15 } }], 3.2);
  add("eye-navette-rail-3", "Eye trio in a navette beside a rail", "Eye trio",
    [{ path: "row", count: 3, sizeMM: 1.7, gapMM: 0.15, setting: "flush", sizing: { mode: "graduated", endScale: 0.6 },
      pocket: { shape: "navette", depthMM: 0.3, clearanceMM: 0.1, cornerRadiusMM: 0.2 },
      rails: { sides: "right", extent: "all-round", gapMM: 0.3, widthMM: 0.25, depthMM: 0.15 } }], 3.2);
  add("capsule-3-1-3-diagonal", "Two capsules and a lone stone · graduated, slightly tilted", "Capsule groups",
    [{ path: "diagonal", angleDeg: 8, groups: [3, 1, 3], groupGapMM: 0.5, sizeMM: 1.8, gapMM: 0.12, setting: "flush",
      sizing: { mode: "graduated", endScale: 0.5 }, pocket: { shape: "capsule", depthMM: 0.3, clearanceMM: 0.08, minGroup: 2 } }], 3.2);
  add("half-row-claws-step-10", "Ten claw-set stones in one half, beside a line", "Offset line",
    [{ path: "row", count: 10, sizeMM: 1.6, gapMM: 0.1, centerYMM: -0.7, setting: "shared-prong",
      rails: { sides: "right", extent: "all-round", gapMM: 0.25, widthMM: 0.25, depthMM: 0.25 } }], 5);
  add("half-eternity-tight-pave-11", "Half eternity · tight pavé · 1.1 mm", "Half eternity",
    [{ path: "row", spanTurns: 0.5, sizeMM: 1.1, gapMM: 0.06, setting: "pave", spacing: "gap" }]);
  add("constellation-star-25", "Star-set constellation · 25 stones all round", "Constellation",
    [{ path: "scatter", groups: [2, 3, 1, 2, 3, 2, 1, 3, 2, 3, 1, 2], groupGapMM: 1.6, gapMM: 0.12, sizeMM: 1.2, setting: "flush",
      seed: 8, spanTurns: 1, sizing: { mode: "random", min: 0.55, max: 1.0 } }]);
  add("sweep-graduated-long-11", "Long graduated sweep · 11 stones", "Sweep",
    [{ path: "sweep", count: 11, sizeMM: 1.8, gapMM: 0.12, setting: "flush", sizing: { mode: "graduated", endScale: 0.5 } }], 4.5);
  return out;
}

/** v67: 300+ named starting points for a pattern. Ids are stable forever. */
export const STONE_PATTERN_LIBRARY: readonly StonePatternLibraryEntry[] = buildStonePatternLibrary();

/** A library entry as a `BandState.stonePattern` (a fresh copy, `libraryId` set). */
export function stonePatternFromLibrary(id: string): StonePattern | null {
  const e = STONE_PATTERN_LIBRARY.find((x) => x.id === id);
  if (!e) return null;
  return { layers: e.layers.map((l) => clampStonePatternLayer(JSON.parse(JSON.stringify(l)) as StonePatternLayer)), libraryId: e.id };
}

// ── Surprise me ──

/**
 * v67: a seeded random pattern for `band` that passes `stonePatternIssues`.
 * Same seed, same band ⇒ same pattern. Tries up to 24 candidates drawn from
 * ranges that usually pass, keeps the first clean one, and falls back to a
 * plain centred line sized to the band (which always passes).
 */
export function surpriseStonePattern(seed: number, band: StonePatternBand): StonePattern {
  const rng = patternRng(Math.imul((seed >>> 0) ^ 0x632be5ab, 0x85ebca6b) ^ 0x27d4eb2f);
  const W = patFinite(band.widthMM) ? band.widthMM : 4;
  const margin = patFinite(band.edgeMarginMM) ? Math.max(0, band.edgeMarginMM) : 0.15;
  const C = patFinite(band.circumferenceMM) ? Math.max(10, band.circumferenceMM) : 65;
  const usable = Math.max(0.6, W - 2 * margin);
  const r2 = (v: number) => Math.round(v * 100) / 100;
  const range = (lo: number, hi: number) => r2(lo + (hi - lo) * rng());
  const int = (lo: number, hi: number) => lo + Math.floor(rng() * (hi - lo + 1));
  const pick = <T,>(arr: readonly T[]): T => arr[Math.floor(rng() * arr.length)];
  const sizes = [0.8, 0.9, 1.0, 1.1, 1.2, 1.3, 1.5, 1.7, 2.0].filter((d) => d <= usable * 0.42);
  const sizeList = sizes.length ? sizes : [0.5];
  const PATHS: StonePatternPath[] = ["row", "row", "diagonal", "sweep", "sweep", "chevron", "zigzag", "arc", "wave", "wave",
    "s-curve", "multi-row", "cluster", "scatter", "scatter", ...(usable >= 4 ? ["rosette" as const] : [])];
  const SETTINGS: StonePatternSetting[] = ["flush", "flush", "flush", "pave", "shared-prong", "prongs", "bezel", "only"];

  const mainLayer = (): StonePatternLayer => {
    const path = pick(PATHS);
    const d = pick(sizeList);
    const setting = pick(SETTINGS);
    const gapMM = setting === "pave" || setting === "shared-prong" ? range(0.07, 0.14) : setting === "prongs" ? range(0.25, 0.45) : range(0.15, 0.4);
    const l: StonePatternLayer = { path, sizeMM: d, gapMM, setting };
    const room = Math.max(0, usable / 2 - d / 2 - 0.05);
    const lineLike = path === "row" || path === "diagonal";
    switch (path) {
      case "row":
        if (rng() < 0.15) l.spanTurns = pick([0.33, 0.5, 0.75, 1]);
        else l.count = int(3, 15);
        if (rng() < 0.3 && room > 0.25) l.centerYMM = r2((rng() < 0.5 ? -1 : 1) * range(0.2, room));
        break;
      case "diagonal": l.angleDeg = range(8, 35); l.count = int(3, 7); break;
      case "multi-row": {
        l.rows = usable >= 3 * (d + gapMM) + 0.2 && rng() < 0.4 ? 3 : 2;
        l.count = l.rows * int(4, 10);
        l.stagger = rng() < 0.6;
        break;
      }
      case "cluster": l.count = int(4, 12); break;
      case "rosette": l.count = int(6, 8); break;
      case "scatter":
        l.count = int(7, 25);
        l.seed = int(1, 1000000);
        if (rng() < 0.25) l.spanTurns = 1; else l.spanMM = range(14, 40);
        if (rng() < 0.4) { l.count = undefined; l.groups = Array.from({ length: int(3, 8) }, () => int(1, 3)); l.groupGapMM = range(0.8, 2); }
        break;
      default:
        l.count = int(5, 15);
        l.amplitudeMM = r2((path === "arc" && rng() < 0.5 ? -1 : 1) * range(0.3, Math.max(0.35, usable / 2)));
        if (path === "wave" || path === "zigzag") l.waves = int(1, 4);
    }
    const sz = rng();
    if (path !== "rosette" && path !== "scatter") {
      if (sz < 0.5) { /* uniform */ }
      else if (sz < 0.8) l.sizing = { mode: "graduated", endScale: range(0.5, 0.85), anchor: rng() < 0.75 ? "centred" : "one-sided" };
      else if (sz < 0.9) l.sizing = { mode: "alternating", altScale: range(0.6, 0.8) };
      else l.sizing = { mode: "random", min: 0.6, max: range(0.9, 1.2) };
    } else if (path === "scatter") {
      l.sizing = { mode: "random", min: 0.6, max: range(0.9, 1.2) };
    }
    if (lineLike && l.count != null && l.count >= 5 && rng() < 0.2) {
      l.groups = pick([[3, 1, 3], [2, 2, 2], [2, 1, 2], [3, 3]]);
      l.count = undefined;
      l.groupGapMM = range(0.4, 1.2);
    }
    const n = l.groups ? l.groups.reduce((a, b) => a + b, 0) : l.count ?? 0;
    if (lineLike && n > 0 && n <= 7 && rng() < 0.3) {
      const shape = pick(["capsule", "plate", "navette"] as const);
      l.pocket = { shape, depthMM: range(0.2, 0.4), clearanceMM: range(0.06, 0.12), ...(l.groups ? { minGroup: 2 } : {}) };
      if (shape !== "capsule") l.setting = "flush";
    } else if (path === "row" && rng() < 0.1) {
      l.pocket = { shape: "channel", depthMM: range(0.2, 0.4), clearanceMM: 0.06 };
    }
    if ((path === "row" || path === "sweep" || path === "arc") && rng() < 0.2) {
      l.rails = { sides: pick(["left", "right", "both"] as const), extent: pick(["stones", "all-round"] as const), gapMM: range(0.15, 0.4), widthMM: 0.25, depthMM: 0.15 };
    }
    if (n > 0 && n <= 5 && rng() < 0.25) l.repeat = int(2, 6);
    if (l.centerYMM && rng() < 0.3) l.mirror = "across";
    return l;
  };
  const accent = (): StonePatternLayer => ({
    path: pick(["cluster", "row", "rosette"] as const),
    count: int(3, 5),
    sizeMM: pick(sizeList.slice(0, Math.max(1, Math.ceil(sizeList.length / 2)))),
    gapMM: range(0.1, 0.25),
    setting: pick(["flush", "pave"] as const),
    centerArcMM: r2((rng() < 0.5 ? -1 : 1) * range(C * 0.15, C * 0.35)),
  });

  for (let attempt = 0; attempt < 24; attempt++) {
    const layers = [mainLayer()];
    if (rng() < 0.2) layers.push(accent());
    const pattern = clampStonePattern({ layers })!;
    const res = expandStonePattern(pattern, band);
    if (stonePatternIssues(res, band).length === 0) return pattern;
  }
  const d = r2(patClamp(usable * 0.55, 0.5, 1.5));
  return clampStonePattern({ layers: [{ path: "row", count: 5, sizeMM: d, gapMM: 0.25, setting: "flush" }] })!;
}

// Open string id (v28): the band stone-shape catalog grew beyond the original
// five bare cuts to include the full diamond-with-prong settings library, and
// is delivered as catalog data. The well-known bare cuts remain valid ids.
export type StoneShape = string;

/** Advanced gem uniform overrides layered over the gem preset
 * (diamond-material.json). Mirrors the editable fields of GemMaterialParams. */
export interface GemParamOverride {
  bounces?: number;
  ior?: number;
  dispersion?: number;
  reflectivity?: number;
  envMapIntensity?: number;
  sparkleBoost?: number;
  absorptionFactor?: number;
  gammaFactor?: number;
  squashFactor?: number;
  geometryFactor?: number;
  opacity?: number;
  boostFactors?: [number, number, number];
  color?: [number, number, number];
}

export interface DiamondConfig {
  style: DiamondStyle;
  stoneSizeMM: number;
  /**
   * Stones in the run; 0 or below ⇒ the style's auto count. Clamped by the
   * viewer, lossy like the stone size: at most 1000 for a run round the ring
   * and 60 for a cross row (cross-bezel / cross-channel / cross-around) — an
   * unbounded count ran the seat carve out of heap.
   */
  count: number;
  placementDeg: number;
  spreadDeg: number;
  rowPosition: number;
  /**
   * The stone's tilt and spin in its seat (−90 / 0 / 0 sets the table square
   * to the surface). The CNC export (v66) reads where the builder really put
   * each stone: a table tilted off the radial line (Rotate X other than −90,
   * or Rotate Y other than 0) has no seat a radial tool can cut and is listed
   * as not machined.
   */
  rotateXDeg: number;
  rotateYDeg: number;
  rotateZDeg: number;
  /** Raises (+) or sinks (−) every stone along its seat's normal, mm. The CNC export plans each seat at the girdle where the stone then sits, and says by how much it differs from the style's sink. */
  depthOffsetMM: number;
  stoneShape: StoneShape;
  gemPresetId: string;
  gemParams?: GemParamOverride;
  // v39 (plan §2.4). Every field optional ⇒ absent reproduces today's run.
  /** Outer surface (default) vs the band's side wall. */
  placement?: DiamondPlacement;
  /** Which side wall(s) a side run occupies. Read only when placement="side". */
  sides?: DiamondSides;
  /** Side-wall hardware. Read only when placement="side". Omit ⇒ "bezel". */
  sideSetting?: SideSetting;
  /** Radial height of a side run across the wall: −1 inner … 0 mid … +1 outer. */
  sideRowPosition?: number;
  /** Top-surface hardware, overriding what the style implies. */
  setting?: DiamondSetting;
  /** Stone-to-stone pitch. Omit ⇒ today's 1.05× diameter. */
  spacing?: StoneSpacing;
  /** Colour tile; drives `gemPresetId` + a `gemParams` tint. */
  color?: DiamondColor;
  /** Gems (default) or gold beads. */
  type?: StoneType;
  // v43 run-level specification. Metadata only — see the DIAMOND SPECIFICATION
  // note above `GiaColorGrade`; a `FreeStone` may override any of the three.
  /** Natural vs lab grown. Omit ⇒ "natural". */
  origin?: DiamondOrigin;
  /** GIA colour grade (`gemColorGrades`). Omit ⇒ unspecified. */
  colorGrade?: GiaColorGrade;
  /** Clarity grade (`gemClarities`). Omit ⇒ unspecified. */
  clarity?: DiamondClarity;
  /**
   * v67: the junction hardware's size (setting "pave" beads / "shared-prong"
   * claws) as a multiple of the law's, 0.5 … 1.6 (clamped). Omit ⇒ 1. Read
   * only by those two settings.
   */
  junctionScale?: number;
  /**
   * v68: which junction pieces a "pave" / "shared-prong" run builds. Absent ⇒
   * today's hardware (both sides, end pieces, beads grown to bridge a gap).
   */
  junctionOptions?: JunctionOptions;
}

/** v68: `DiamondConfig.junctionOptions`. Every field optional; absent ⇒ today. */
export interface JunctionOptions {
  /** Which edge of the row gets junction pieces, in the row's own frame (+y =
   * toward the band's +Y edge). Absent ⇒ "both". */
  sides?: "both" | "+y" | "-y";
  /** End pieces at the two ends of an open row (and round a stone too far from
   * its neighbour to share). Absent ⇒ true. */
  ends?: boolean;
  /** Pavé only: a bead may grow (≤ 0.35 d) to bridge a wide gap. Absent ⇒ true.
   * false ⇒ the base size always; a bead that then cannot reach both girdles
   * sits at the notch midway between them on the row's edge, resting on the
   * floor (decorative, not counted as holding the stones). A claw never grows. */
  grow?: boolean;
}

/** v67: `DiamondConfig.junctionScale` range. */
export const JUNCTION_SCALE_MIN = 0.5;
export const JUNCTION_SCALE_MAX = 1.6;

export type ChannelCutShape = "round" | "square";

/**
 * v54 — the channel fields a user is allowed to take ownership of.
 *
 * `setDiamond` re-derives six channel fields from the style on EVERY call, and
 * the Gems tab makes 27 of those calls, so a plain slider over any of them was
 * reset by the next click on anything else. Listing a field here makes
 * `setDiamond` skip it.
 *
 * `position`, `enabled` and `fullThrough` are deliberately absent and must stay
 * absent. The first two were always excluded; `fullThrough` was NOT, and that
 * was a mistake shipped in v54 — it is a STRUCTURAL MODE SELECTOR, not a
 * dimension. `tension-1` / `tension-2` are defined by `fullThrough: true`: it is
 * what splits the ring so the stone can float in a real gap. Letting a user own
 * it meant `setChannelManual({ fullThrough: false })` — one click of the Cut
 * Through toggle — permanently converted every tension setting into a SOLID
 * ring with a bridge floating over unbroken metal and the stone pushed 0.2 mm
 * into it, with no way back, because `manual` survives every later `setDiamond`.
 * `position` is not an independent value — it is a shadow of `diamond.rowPosition`,
 * resolved through the STONES' own placement law (`channelCenterU`), and
 * `groove-coupling-check` pins that moving the row afterwards takes the trench
 * with it. `enabled` is the style's decision (`info.needsGroove`). Locking
 * either would let the trench drift off the run it exists to hold.
 */
export type ChannelManualField =
  | "widthMM" | "heightMM" | "depthMM" | "diagonalDeg" | "centerAngleDeg";

/** v54 — `null` hands a field back to the style; a number/boolean takes it over. */
export type ChannelManualPatch = { [K in ChannelManualField]?: ChannelParams[K] | null };

export interface ChannelParams {
  enabled: boolean;
  depthMM: number;
  widthMM: number;
  /**
   * Circumferential extent of the cut, in mm of ARC — how far round the ring it
   * runs, not how far across the band. `setDiamond` writes **100** for every
   * non-tension style, which is a sentinel meaning "no arc limit" (it is far
   * past the ~65.7 mm circumference of a size-7 band), so the field only starts
   * to mean anything once a user takes it over.
   */
  heightMM: number;
  position: number;
  diagonalDeg: number;
  /**
   * Where round the ring the cut is centred, degrees, in the STONE frame
   * (`atan2(z, x)` on the un-rotated lathe): 0 is 3 o'clock (world +X), −90
   * is 12 o'clock (the default `placementDeg`), increasing toward 6 o'clock.
   */
  centerAngleDeg: number;
  fullThrough: boolean;
  cutShape: ChannelCutShape;
  /** v54 — fields the user owns; the style stops re-deriving them. Absent or
   *  empty ⇒ everything derived, exactly as before v54. */
  manual?: ChannelManualField[];
}

/**
 * v55 — the fields of a CROSS channel a user can take over, same law as
 * `ChannelManualField`: `setDiamond` re-derives every one of them from the run
 * on every call, and listing a field here makes it skip exactly that one.
 *
 * `cutShape` is absent on purpose and must stay absent — it is not derived from
 * anything, so there is nothing for a lock to protect it from. It is a plain
 * sticky mode with a default, like `ChannelParams.cutShape`.
 */
export type CrossChannelManualField =
  | "widthMM" | "depthMM" | "centerAngleDeg" | "spanMM" | "positionMM" | "diagonalDeg";

/** v55 — `null` hands a field back to the run; a number takes it over. */
export type CrossChannelManualPatch = {
  [K in CrossChannelManualField]?: CrossChannelParams[K] | null;
};

/**
 * v55 — the trench a `cross-channel` run is set into.
 *
 * ⚠️ THIS IS NOT `ChannelParams` AND MUST NOT BORROW ITS ROWS. A channel groove
 * runs AROUND the ring: its `widthMM` is measured across the band and its
 * `heightMM` is an arc length. A cross channel runs ACROSS the band, so the two
 * axes are swapped — `widthMM` here is the arc the trench occupies (how wide it
 * looks from the side of the finger) and `spanMM` is how far across the band it
 * reaches. Wiring one control to the other would rotate every edit 90 degrees.
 *
 * Absent from `BandState` ⇒ every field derived from the run, which is what the
 * three hard-coded expressions in `crossChannelCarve` did before this wave.
 */
export interface CrossChannelParams {
  /** Arc the trench occupies, mm, measured at the band's outer radius.
   *  Derived: `stoneSizeMM + 0.4`. */
  widthMM: number;
  /** Depth of the cut, mm. Derived: `min(thicknessMM x 0.45, stoneSizeMM x 0.62)`. */
  depthMM: number;
  /** Where round the ring the trench is centred, degrees. Derived: `placementDeg`. */
  centerAngleDeg: number;
  /** How far across the band the trench reaches, mm. Derived: the band's own
   *  width, i.e. edge to edge — which is what a cross channel is. A smaller
   *  value leaves a rail of metal at each edge. */
  spanMM: number;
  /** Where across the band the trench is centred, mm from the band's middle.
   *  Only visible once `spanMM` is short enough to leave a rail. Derived: 0. */
  positionMM: number;
  /** Tilt of the trench across the band, degrees; 0 runs straight across the
   *  finger. Derived: 0. */
  diagonalDeg: number;
  /** Floor shape. "square" is the flat-bottomed, vertical-walled cut that
   *  shipped before v55 and stays the default; "round" scoops it. */
  cutShape: ChannelCutShape;
  /** Fields the user owns; the run stops re-deriving them. Absent or empty ⇒
   *  everything derived. */
  manual?: CrossChannelManualField[];
}

export type EngravingSide = "left" | "center" | "right";

export interface EngravingConfig {
  fontFamily: string;
  fontSizePx: number;
  fontWeight: "normal" | "bold";
  /** The mark's ink colour (the reference's "Mark Color"). */
  color: string;
  side: EngravingSide;
  angleDeg: number;
  rotationDeg: number;
  widthScale: number;
  arcScale: number;
  // v43 RELIEF (the reference's engraving Custom Settings). An inscription there
  // is a physically carved mark that catches light, not a flat printed decal.
  /** Drive a bump map from the glyph mask so the mark reads as cut metal.
   * ⚠️ Absent ⇒ TRUE — the reference ships this on, and matching it is the point
   * of the field. This is the one v43 default that is not today's behaviour: an
   * existing design's inscription gains relief once the engine honours it. */
  bumpMap?: boolean;
  /** Relief with no ink: the mark takes the band's own metal and reads only by
   * its carved shading. Omit ⇒ false (coloured mark). */
  bumpOnly?: boolean;
  /** Relief depth, three.js `bumpScale` units. Measured range 0 … 0.01, the
   * reference's live value 0.0020. Omit ⇒ 0.002. */
  bumpScale?: number;
  /** View-dependent depth illusion on the mark (parallax offset of the glyph
   * lookup). Measured default OFF. Omit ⇒ false. */
  parallax?: boolean;
  /** The mark's own finish, 0 = MATT … 1 = GLOSS, independent of the band
   * surface. Measured range 0 … 1, the reference's live value 0.50. */
  markRoughness?: number;
  /** v67: a small mark (a maker's logo) inside the band, beside the
   * inscription or instead of it, drawn into the same mirrored decal and
   * carved by the same relief. Absent or null ⇒ no logo: the decal is the one
   * every earlier design drew, byte for byte. Personal, like the text — the
   * live link never mirrors it (`ringLink`). Sanitised by the engine: a logo it
   * cannot accept is dropped, never drawn. */
  logo?: EngravingLogo | null;
}

/** v67: where the logo goes. "with-text" sits it before the inscription (in
 * reading order); "only" draws the logo alone even when there is text. */
export type EngravingLogoMode = "with-text" | "only";

/**
 * v67: the inside-the-band logo. Give `svg` OR `url`; `svg` wins when both are.
 *
 * `svg` is NORMALISED PATH DATA — the `d` attribute alone, never SVG markup:
 * only the commands M L H V C S Q T A Z (either case), numbers, spaces and
 * commas, starting with a moveto, at most `ENGRAVING_LOGO_SVG_MAX_CHARS`. The
 * engine parses it with a whitelist and draws it as a canvas path; anything
 * else (a tag, an href, a style, a stray letter) rejects the whole logo. A host
 * that takes an uploaded .svg flattens it to this form first.
 *
 * `url` is a RASTER (PNG / JPEG / WebP / GIF, sniffed from its bytes — never
 * an SVG) over http(s) with CORS, or a `data:image/…;base64,` URL. It loads
 * asynchronously; the decal is redrawn once when it arrives. Dark ink on a
 * light ground, light ink on a dark ground and ink on transparency all read as
 * the mark.
 */
export interface EngravingLogo {
  svg?: string;
  url?: string;
  /** Height of the mark's ink, mm (the path's bounding box / the raster's
   * inked rows). Clamped 0.3 … 6, and to what the band's width can hold. */
  heightMM: number;
  mode: EngravingLogoMode;
  /** Fill rule for `svg` (logos exported with holes often need "evenodd").
   * Omit ⇒ "nonzero", the SVG default. */
  fillRule?: "nonzero" | "evenodd";
}

export type EdgeStyle = "sharp" | "rounded" | "bevel" | "milgrain";

/** v38: per-side edge treatment. "edge" is the chamfer; the two milgrain values
 * sweep real metal beads along that edge (the global `EdgeStyle.milgrain` keeps
 * its legacy profile rail so existing designs are untouched). */
export type SideEdgeStyle = "none" | "edge" | "milgrain" | "milgrain2";

/** v38: which band edge. `left` is the −Y half of the cross-section. */
export interface EdgeSides {
  left: SideEdgeStyle;
  right: SideEdgeStyle;
}

export type GroovePattern = "none" | "single" | "double" | "triple" | "offset";

/** v37: cross-section of a carved groove ("joint"). Omit ⇒ today's cut.
 * v38 adds the two bead ("milgrain") joints, which add metal instead of
 * removing it — every groove family carves nothing when one is selected. */
export type JointType =
  | "v60" | "v120" | "uround" | "convexu" | "square"
  | "milgrain" | "milgrain2";

export interface GrooveConfig {
  pattern: GroovePattern;
  depthMM: number;
  widthMM: number;
  // v37 joint grooves. All optional ⇒ existing designs are unaffected.
  jointType?: JointType;
  /** Carve the groove along the metal-partition seam(s) as well. */
  seam?: boolean;
  /** Carve a matching groove along both band edges. */
  edges?: boolean;
  /** v38: per-boundary enables for the seam grooves, index-aligned with the
   * split pattern's boundaries (a 3-partition ring has 2). Absent — or shorter
   * than the boundary list — ⇒ that boundary is ON, i.e. today's behaviour. */
  seamBoundaries?: boolean[];
  /** v43: finish on THIS groove's own cut floor — the reference's Separation
   * Surface row (Polished · Matt · Sand), which is a different control from the
   * partition's 7-option metal Surface higher up the same panel. Omit ⇒
   * "polished", i.e. today, where the floor takes the partition's finish. */
  surface?: GrooveSurface;
}

/** v41: the finish on a groove's own cut surface (v43: both groove families —
 * the global/seam/edge `GrooveConfig` and one `DesignGroove` list entry). */
/** v68 adds "shadow": a POLISHED cut drawn with the darkness a narrow polished
 * V shows in a studio photograph — its two walls return the camera's own side
 * of the room after a second bounce, which a single-bounce renderer does not
 * trace, so its walls keep a fraction of their reflection. Machined, finished
 * and billed exactly as "polished"; only the picture differs. */
export type GrooveSurface = "polished" | "matt" | "sand" | "shadow";

/** v41: which way a decorative groove runs on the unrolled band — "horizontal"
 * around the circumference, "vertical" across the band width at one angle.
 * v66: "path" follows a polyline drawn on the unrolled band (`DesignGroove.path`). */
export type GrooveDirection = "horizontal" | "vertical" | "path";

/**
 * v66: the centreline of a "path" groove, drawn on the UNROLLED outer face.
 *
 * THE PLANE. `arcMM` is arc length round the ring at the outer crest radius
 * (`maxR`, the radius the CNC unrolls at), measured from 12 O'CLOCK — the top
 * of the ring as the builder shows it (world +Y), where the default stone row
 * sits (`placementDeg` −90) and which the CNC puts under C0. +arcMM runs from
 * 12 toward 3 o'clock (world +X, the right as the default camera sees the
 * ring from its +Y edge), i.e. clockwise seen from the +Y edge, and maps to
 * +C at `kinematics.signC` +1: C = signC · arcMM / R · 180/π. It wraps; a
 * negative arcMM runs toward 9 o'clock. `yMM` is mm from the band's centre
 * line, + toward the +Y edge, exactly as a horizontal groove's `positionMM`
 * (CNC Y = signY · yMM).
 *
 * ⚠️ NOT the zero of the stone-frame angles. `ChannelParams.centerAngleDeg`
 * and a VERTICAL groove's `positionMM` count from 3 o'clock (stone-frame
 * θ = 0); in their terms arcMM = maxR · wrap(θ + 90°), so a channel at
 * `centerAngleDeg` −90 sits at arcMM 0 and a vertical groove at `positionMM`
 * p sits at arcMM p + maxR · π/2. The path plane is anchored on the CNC's C0
 * so a preset (and its `mirror`) is centred on the design and does not move
 * with the ring size.
 *
 * Segments are straight in this plane — a helix on the band, which the machine
 * cuts as one G1 each. Neither coordinate is clamped: a point off the band
 * simply carves nothing there.
 */
export interface DesignGroovePath {
  /** 2 … 64 finite points, in order. `radiusMM` is THIS corner's fillet,
   *  0 … 5 mm (clamped; 0 = sharp), overriding `cornerRadiusMM` — a path can
   *  round one corner and keep the next one square. Ignored on an open path's
   *  two ends, which are never filleted. */
  points: { arcMM: number; yMM: number; radiusMM?: number }[];
  /** Fillet on the centreline at every corner without its own `radiusMM`,
   *  0 … 5 mm (clamped). A fillet is reduced where a segment is too short to
   *  hold it and the one at its other end — the two share the segment in
   *  proportion to what they ask for. Omit ⇒ 0, sharp. */
  cornerRadiusMM?: number;
  /** Join the last point back to the first (3 points or more). Omit ⇒ false. */
  closed?: boolean;
  /** "point" also cuts a copy rotated 180° about (0, 0) of the plane —
   *  (arcMM, yMM) → (−arcMM, −yMM) — with the same section. Omit ⇒ "none". */
  mirror?: "none" | "point";
}

/**
 * v66: the bypass stepped-shoulder preset — the shop's sample program's
 * 1.3 mm end-mill motif, as Design grooves. The defaults
 * (`BYPASS_SHOULDER_DEFAULTS`) reproduce that program's end-mill work on its
 * own blank (8.95 mm wide): every groove is a 1.3 mm tool-centre line, so
 * grooves that abut clear a wider step exactly as the sample's passes do. The
 * points the preset generates are each groove's truth; these parameters only
 * let a host show the preset's sliders again and regenerate it.
 *
 * One SHOULDER is a diagonal STEP across the band, a TRACK on either side of
 * it running round the ring to where it breaks out through the band edge, and
 * three small cuts that blunt the sharp corners the step leaves (see
 * `bypassShoulderGrooves`). The preset point-mirrors every groove, so the two
 * shoulders stand either side of the design centre (a stone row through
 * arcMM 0) the way a bypass ring's two ends do.
 */
export interface BypassShoulderParams {
  /** The step's angle from the round-the-ring direction, degrees; 90 crosses
   *  the band square. 10 … 90. */
  angleDeg: number;
  /** Arc distance from the design centre to where the step's centreline
   *  crosses the band's centre line — how far the shoulder stands off the
   *  stone row. 0 … 20 mm. */
  offsetMM: number;
  /** How far each track's centreline sits in from the motif's edge
   *  (`spanMM / 2`); half the width of it is the metal rim the track leaves at
   *  the edge. 0 … spanMM / 2. */
  insetMM: number;
  /** The motif's extent across the band, edge to edge — normally the band
   *  width. 1 … 12 mm. */
  spanMM: number;
  /** Groove width, 0.25 … 3 mm (the preset keeps its own floor; a plain
   *  `DesignGroove` may go to 0.15 since v67) — one pass of the
   *  end mill that cuts it. */
  widthMM: number;
  /** Groove depth, 0.10 … 1 mm, as `DesignGroove.depthMM`. */
  depthMM: number;
  /** Fillet on the centreline where the far track turns into the step, 0 … 5 mm. */
  cornerRadiusMM: number;
}

/**
 * v67: a step in the outer face that STOPS part way round — the lowered
 * stretch a row of stones sits on beside a step line, ending where the line
 * turns and runs out through the band's edge (`partialStepGrooves`). A step
 * the whole way round is the parametric profile's (`ParamProfile.step`).
 *
 * In the path-groove plane (`DesignGroovePath`): arc mm from 12 o'clock, y mm
 * from the band's centre line.
 */
export interface PartialStepParams {
  /** The LOWERED side, as `ParamProfileStep.side`. */
  side: "+y" | "-y";
  /** Where the step line runs, mm from the band's centre line (+ toward +Y). */
  positionMM: number;
  /** How far the lowered stretch is cut down, mm, 0.10 … 1 (a groove's depth). */
  depthMM: number;
  /** Arc mm from 12 o'clock to the middle of the lowered stretch. −60 … 60. */
  centerArcMM: number;
  /** Length of the lowered stretch along the ring, mm, between where the step
   *  line starts to turn out at each end. 1 … 60. */
  lengthMM: number;
  /** The angle the step line leaves through the edge at each end, degrees
   *  from the round-the-ring direction: 90 = straight out, smaller = a long
   *  slanting run-out. 20 … 90. */
  exitDeg: number;
  /** Fillet where the step line turns to run out, mm, 0 … 5. */
  cornerRadiusMM: number;
  /** The band's width (the motif's extent across it), mm, 1 … 12 — the
   *  lowered stretch is cut from the step line out past that edge. */
  spanMM: number;
}

/** v67: `PartialStepParams` defaults — half of a 3 mm band, lowered 0.25 mm
 *  over 16 mm of arc centred at 12 o'clock, turning out at 45°. */
export const PARTIAL_STEP_DEFAULTS: Readonly<PartialStepParams> = {
  side: "+y",
  positionMM: 0,
  depthMM: 0.25,
  centerArcMM: 0,
  lengthMM: 16,
  exitDeg: 45,
  cornerRadiusMM: 1.5,
  spanMM: 3,
};

/** v66: which preset generated a Design groove, so a host can show its sliders.
 *  v67 adds "partialStep" (`partialStepGrooves`). */
export type DesignGroovePreset =
  | {
      kind: "bypassShoulder";
      /** Ties together every groove one application of the preset produced. */
      group: string;
      params: BypassShoulderParams;
    }
  | { kind: "partialStep"; group: string; params: PartialStepParams };

/**
 * v41: ONE entry in the reference's "Design" groove list.
 *
 * `GrooveConfig` is the single global groove (its `pattern` places 1–3 of them
 * at fixed fractions of the width); this is the arbitrary-length list beside it,
 * every entry independently placed and shaped. Both feed the SAME max-depth
 * outer carve, so overlapping grooves cut to the deeper of the two rather than
 * to the sum, and every carved groove removes metal from `spec.metalVolumeMM3`
 * — i.e. adding one lowers the price, as it does in the reference.
 */
export interface DesignGroove {
  /** Stable within the list; the host addresses grooves by it. */
  id: string;
  /** Omit ⇒ "horizontal". */
  direction?: GrooveDirection;
  /** Horizontal: mm from the band's centre line, + toward the +Y edge.
   * Vertical: arc-length mm round the ring at the outer crest radius from
   * stone-frame θ = 0 — 3 O'CLOCK (world +X), increasing toward 6 o'clock,
   * wrapping. That is what the engine carves (`applyOuterCarve`: centre angle
   * = positionMM / maxR, against θ = atan2(z, x)) and the frame
   * `ChannelParams.centerAngleDeg` uses, whose 0 is also 3 o'clock. ⚠️ The
   * v41 text called this the 12-o'clock reference; it never was, and the
   * engine was left alone so saved designs keep their grooves where they are.
   * 12 o'clock is positionMM = −maxR · π/2.
   * Path: always 0 — the path's own points place it.
   * NOT clamped: a position off the band simply carves nothing, which keeps the
   * stored value stable while the host drags the width slider under it. */
  positionMM: number;
  /** Cross-section. Omit ⇒ the legacy parabola. The two bead joints sweep
   * metal beads instead of carving (horizontal grooves only — see below). */
  jointType?: JointType;
  /** 0.15 … 3.00 mm (clamped; the floor was 0.25 until v67). */
  widthMM: number;
  /** 0.10 … 1.00 mm (clamped). */
  depthMM: number;
  /** Finish on the groove's cut surface. Omit ⇒ "polished".
   * ⚠️ Carried in state and round-tripped, but the groove floor currently
   * renders with the partition's own finish: a per-groove finish needs its own
   * material group, which the outer carve does not yet emit. */
  surface?: GrooveSurface;
  /** v66, "path" grooves only: the centreline. A "path" groove without one
   *  carves nothing. */
  path?: DesignGroovePath;
  /** v66: the preset that generated this groove, if any. Bookkeeping only —
   *  the carve and the CNC read `path`, never this. (v67: except that a
   *  "partialStep" groove is carved as a POCKET — a floor stones are set
   *  into, whose depth counts toward the stones' 0.6-of-wall seat cap.) */
  preset?: DesignGroovePreset;
  /** v67: a TRUE V of this included angle, degrees, 30 … 150. Set, the groove
   *  is cut as a straight-sided V ("v60" section, which `jointType` becomes)
   *  whose depth is DERIVED from its width — depth = (width / 2) / tan(angle /
   *  2) — clamped to the depth range, the width then re-derived from that
   *  depth so the angle stays exact wherever both ranges allow. Absent ⇒ the
   *  joint's own section, as before ("v60"/"v120" are names, not angles: their
   *  walls follow whatever width and depth they are given). */
  vAngleDeg?: number;
}

/** v67: the narrowest groove the builder carves, mm (was 0.25 until v66). */
export const DESIGN_GROOVE_MIN_WIDTH_MM = 0.15;

/** v66: the most points a `DesignGroovePath` may hold. */
export const DESIGN_GROOVE_PATH_MAX_POINTS = 64;

/**
 * Normalise a Design list so no host can store an invalid groove: ids are filled
 * positionally (never randomised — the harness needs reproducibility), width and
 * depth are clamped to the reference's ranges, and the defaults ("horizontal",
 * "polished") are dropped rather than written, so an untouched groove serialises
 * to the three fields that matter.
 *
 * v66: a "path" groove keeps its `path` (2 … 64 finite points; any other path
 * is dropped whole, never trimmed, so the groove carves nothing rather than a
 * different shape), its `positionMM` is 0, and its defaults (`mirror` "none",
 * `closed` false, `cornerRadiusMM` 0) are dropped too. `path` on any other
 * direction is dropped. `preset` is kept on any groove when well formed. A
 * list with neither field normalises exactly as it did before v66.
 */
export function clampDesignGrooves(list: readonly Partial<DesignGroove>[]): DesignGroove[] {
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  return list.map((g, i) => {
    const isPath = g.direction === "path";
    const out: DesignGroove = {
      id: g.id || `dg${i + 1}`,
      positionMM: isPath ? 0 : g.positionMM ?? 0,
      widthMM: clamp(g.widthMM ?? 0.6, DESIGN_GROOVE_MIN_WIDTH_MM, 3),
      depthMM: clamp(g.depthMM ?? 0.25, 0.1, 1),
    };
    if (g.direction === "vertical") out.direction = "vertical";
    if (isPath) out.direction = "path";
    if (g.jointType) out.jointType = g.jointType;
    const va = g.vAngleDeg;
    if (typeof va === "number" && Number.isFinite(va) && !(g.jointType === "milgrain" || g.jointType === "milgrain2")) {
      // v67 true V: the section is a straight V of this angle, depth from width.
      const angle = clamp(va, 30, 150);
      const tan = Math.tan((angle * Math.PI) / 360);
      out.vAngleDeg = angle;
      out.jointType = "v60";
      out.depthMM = clamp(out.widthMM / 2 / tan, 0.1, 1);
      out.widthMM = clamp(2 * out.depthMM * tan, DESIGN_GROOVE_MIN_WIDTH_MM, 3);
      out.depthMM = clamp(out.widthMM / 2 / tan, 0.1, 1);
    }
    if (g.surface && g.surface !== "polished") out.surface = g.surface;
    const path = isPath ? clampGroovePath(g.path) : undefined;
    if (path) out.path = path;
    const preset = clampGroovePreset(g.preset);
    if (preset) out.preset = preset;
    return out;
  });
}

function clampGroovePath(path: DesignGroovePath | undefined): DesignGroovePath | undefined {
  const src = path?.points;
  if (!path || !Array.isArray(src) || src.length < 2 || src.length > DESIGN_GROOVE_PATH_MAX_POINTS) return undefined;
  if (!src.every((p) => Number.isFinite(p?.arcMM) && Number.isFinite(p?.yMM))) return undefined;
  const points = src.map((p) => {
    const point: DesignGroovePath["points"][number] = { arcMM: p.arcMM, yMM: p.yMM };
    // A corner's own fillet: kept when it is a length (0 = sharp is a real
    // answer — it overrides the path's radius), clamped like it; else dropped.
    if (typeof p.radiusMM === "number" && p.radiusMM >= 0) point.radiusMM = Math.min(p.radiusMM, 5);
    return point;
  });
  const out: DesignGroovePath = { points };
  const r = path.cornerRadiusMM;
  if (typeof r === "number" && r > 0) out.cornerRadiusMM = Math.min(r, 5); // NaN fails `> 0`
  if (path.closed === true && points.length >= 3) out.closed = true;
  if (path.mirror === "point") out.mirror = "point";
  return out;
}

const BYPASS_SHOULDER_KEYS: readonly (keyof BypassShoulderParams)[] = [
  "angleDeg", "offsetMM", "insetMM", "spanMM", "widthMM", "depthMM", "cornerRadiusMM",
];

const PARTIAL_STEP_KEYS: readonly Exclude<keyof PartialStepParams, "side">[] = [
  "positionMM", "depthMM", "centerArcMM", "lengthMM", "exitDeg", "cornerRadiusMM", "spanMM",
];

function clampGroovePreset(preset: DesignGroovePreset | undefined): DesignGroovePreset | undefined {
  if (typeof preset?.group !== "string" || !preset.group) return undefined;
  if (preset.kind === "partialStep") {
    const src = preset.params as Partial<PartialStepParams> | undefined;
    if (!src || (src.side !== "+y" && src.side !== "-y") || !PARTIAL_STEP_KEYS.every((k) => Number.isFinite(src[k]))) return undefined;
    const params = { side: src.side } as PartialStepParams;
    for (const k of PARTIAL_STEP_KEYS) params[k] = src[k] as number;
    return { kind: "partialStep", group: preset.group, params };
  }
  if (preset.kind !== "bypassShoulder") return undefined;
  const src = preset.params as Partial<BypassShoulderParams> | undefined;
  if (!src || !BYPASS_SHOULDER_KEYS.every((k) => Number.isFinite(src[k]))) return undefined;
  const params = {} as BypassShoulderParams;
  for (const k of BYPASS_SHOULDER_KEYS) params[k] = src[k] as number;
  return { kind: "bypassShoulder", group: preset.group, params };
}

/**
 * v66: `BypassShoulderParams` defaults — the shop's sample motif on its own
 * 8.95 mm blank: a 30° step crossing the centre line 6.834 mm off the design
 * centre, tracks 1.15 mm in from the edges (a 0.5 mm rim outside a 1.3 mm
 * groove), all cut 0.9 mm deep by one pass of the 1.3 mm end mill
 * (`jointType` "square" is the floor it leaves), the far track turning into
 * the step on a 1.5 mm fillet. Measured, not assumed: generated through the
 * export, the preset's end-mill passes sweep the sample's to an IoU of 0.997
 * and a symmetric Hausdorff distance of 0.045 mm (`verify:cnc`, p6).
 */
export const BYPASS_SHOULDER_DEFAULTS: Readonly<BypassShoulderParams> = {
  angleDeg: 30,
  offsetMM: 6.834,
  insetMM: 1.15,
  spanMM: 8.95,
  widthMM: 1.3,
  depthMM: 0.9,
  cornerRadiusMM: 1.5,
};

/**
 * The bypass shoulder's fixed geometry, mm — fitted to the sample program's
 * end-mill passes and not exposed as sliders. A NOSE is the radius of metal a
 * blunting cut leaves on the corner it rounds; the cut's own centreline fillet
 * is that plus half the groove width, so a narrower cutter rounds the same
 * corner to the same nose.
 */
const BYPASS = {
  /** Arc from the design centre to where each track breaks out through the band edge. */
  reachMM: 19.5,
  /** The side step's hairpin, from the step into the near track. */
  sideStepNoseMM: 0.4,
  /** The hook past the far track: how far beyond the motif's edge its back
   *  leg runs, the nose it rounds, and how far the leg runs on past its fillet. */
  hookRiseMM: 0.385,
  hookNoseMM: 0.57,
  hookTailMM: 0.2,
  /** The edge notch below the step's foot: its length back from the widened
   *  step, the nose of the hairpin at its outer end, where that hairpin's cut
   *  comes in from (relative to its corner), the blend up into the widened
   *  step, and how far up the step the widening runs. */
  notchLengthMM: 7.24,
  notchNoseMM: 0.2,
  notchLead: { arcMM: 3.564, yMM: 2.008 },
  notchBlendMM: 3.77,
  widenMM: 1.93,
} as const;

/**
 * v66: the Design grooves of a bypass stepped shoulder (`BypassShoulderParams`).
 * Pure; missing or non-finite params take `BYPASS_SHOULDER_DEFAULTS`, the rest
 * are clamped to the documented ranges. Ids are `<group>-1` … `<group>-5`; an
 * empty `group` falls back to "bypass". Every groove is a "path" groove of
 * the given width and depth with a "square" floor, mirrored "point", and
 * carries the resolved params in `preset`, so a host can find the set by
 * `group` and regenerate it.
 *
 * With h = spanMM / 2, the tracks at y = ±t (t = h − insetMM), the notch line
 * at y = −(t + widthMM) (abutting the near track, so together they clear the
 * band from t − widthMM/2 out to the edge) and the STEP the line through
 * (offsetMM, 0) rising at angleDeg, the un-mirrored shoulder is (on the +arc
 * side; its mirror is the −arc one):
 *
 *   1  the far track: in through the +y edge at `BYPASS.reachMM`, round the
 *      ring along +t, turning on `cornerRadiusMM` into the step, and down the
 *      whole step to the notch line;
 *   2  the near track: from the step's foot on −t out to the reach, and out
 *      through the −y edge (sharp corners — the sample's own);
 *   3  the side step: a hairpin from the step into the near track, blunting
 *      the wedge of metal between them;
 *   4  the hook: up the step past the far track and back over the +y edge,
 *      blunting the sliver the step leaves against that edge;
 *   5  the edge notch: along the notch line, hooked at its outer end and
 *      blended up into the step widened by half a groove.
 *
 * The straight runs of 3 and 4 re-cut what 1 and 2 already cleared, on
 * purpose and as the sample does: they start inside the step 1 has cut, so
 * the end mill never plunges into metal there. 5 starts where the sample's
 * stone-row cut had already cleared; on a band without that row its first
 * passes plunge into metal, 0.1 mm at a time like every other contour.
 */
export function bypassShoulderGrooves(params: Partial<BypassShoulderParams>, group: string): DesignGroove[] {
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  const pick = (k: keyof BypassShoulderParams) => {
    const v = params[k];
    return typeof v === "number" && Number.isFinite(v) ? v : BYPASS_SHOULDER_DEFAULTS[k];
  };
  const spanMM = clamp(pick("spanMM"), 1, 12);
  const p: BypassShoulderParams = {
    angleDeg: clamp(pick("angleDeg"), 10, 90),
    offsetMM: clamp(pick("offsetMM"), 0, 20),
    insetMM: clamp(pick("insetMM"), 0, spanMM / 2),
    spanMM,
    widthMM: clamp(pick("widthMM"), 0.25, 3),
    depthMM: clamp(pick("depthMM"), 0.1, 1),
    cornerRadiusMM: clamp(pick("cornerRadiusMM"), 0, 5),
  };
  const name = group || "bypass";

  type Point = DesignGroovePath["points"][number];
  const w = p.widthMM;
  const h = p.spanMM / 2;
  const t = h - p.insetMM;
  const notchY = -(t + w);
  const a = (p.angleDeg * Math.PI) / 180;
  const k = 1 / Math.tan(a);
  const up = { arcMM: Math.cos(a), yMM: Math.sin(a) };
  /** The step's centreline at height y, shifted `across` mm round the ring. */
  const step = (y: number, across = 0): Point => ({ arcMM: p.offsetMM + across + y * k, yMM: y });
  const along = (q: Point, d: number): Point => ({ arcMM: q.arcMM + up.arcMM * d, yMM: q.yMM + up.yMM * d });
  /** Tangent length of a fillet of radius r on a hairpin between the step and a run round the ring. */
  const hairpin = (r: number) => r * Math.tan((Math.PI - a) / 2);

  const far = step(t);
  const near = step(-t);
  const reach = Math.max(BYPASS.reachMM, far.arcMM + w);
  const out = h + w; // past the band edge: the cutter has left the metal there
  const sideR = w / 2 + BYPASS.sideStepNoseMM;
  const sideT = hairpin(sideR);
  const hookY = h + BYPASS.hookRiseMM;
  const hook = step(hookY);
  const hookR = w / 2 + BYPASS.hookNoseMM;
  const hookT = hairpin(hookR);
  // Half a groove further round: the step's widened edge, where it meets the notch line.
  const widened = step(notchY, w / 2 / Math.sin(a));
  const notch: Point = { arcMM: widened.arcMM - BYPASS.notchLengthMM, yMM: notchY };

  const paths: Point[][] = [
    [{ arcMM: reach, yMM: out }, { arcMM: reach, yMM: t }, { ...far, radiusMM: p.cornerRadiusMM }, step(notchY)],
    [near, { arcMM: reach, yMM: -t }, { arcMM: reach, yMM: -out }],
    [along(near, sideT + w), { ...near, radiusMM: sideR }, { arcMM: near.arcMM + sideT + w, yMM: -t }],
    [along(hook, -(hookT + w)), { ...hook, radiusMM: hookR },
      { arcMM: hook.arcMM - hookT - BYPASS.hookTailMM, yMM: hookY }],
    [{ arcMM: notch.arcMM + BYPASS.notchLead.arcMM, yMM: notch.yMM + BYPASS.notchLead.yMM },
      { ...notch, radiusMM: w / 2 + BYPASS.notchNoseMM }, { ...widened, radiusMM: BYPASS.notchBlendMM },
      along(widened, BYPASS.widenMM)],
  ];

  // 0.1 µm: keeps the stored points readable (90° leaves k at 6e-17, not 0)
  // without moving anything a cutter could see.
  const round = (v: number) => Math.round(v * 1e4) / 1e4;
  return clampDesignGrooves(paths.map((points, i) => ({
    id: `${name}-${i + 1}`,
    direction: "path",
    positionMM: 0,
    jointType: "square",
    widthMM: p.widthMM,
    depthMM: p.depthMM,
    path: {
      points: points.map((q) => ({
        arcMM: round(q.arcMM), yMM: round(q.yMM), ...(q.radiusMM !== undefined ? { radiusMM: round(q.radiusMM) } : {}),
      })),
      mirror: "point",
    },
    preset: { kind: "bypassShoulder", group: name, params: p },
  })));
}

/**
 * v67: the Design groove of a step that stops part way (`PartialStepParams`).
 * Pure; missing or non-finite params take `PARTIAL_STEP_DEFAULTS`, the rest
 * are clamped to the documented ranges. One "path" groove, id `<group>-1`
 * (an empty `group` falls back to "step"), with a "square" (flat) floor, that
 * carries the resolved params in `preset`.
 *
 * The groove's centreline runs round the ring at y = c, half a groove width
 * beyond the step line on the lowered side, and its width reaches from the
 * step line out past the band edge by 0.4 mm — so its inner wall IS the step
 * line and its floor is the lowered stretch. At each end the centreline turns
 * (filleted, `cornerRadiusMM`) and runs out through the edge at `exitDeg`, so
 * the step line turns and leaves the band rather than stopping in a wall.
 * The width is a groove's, ≤ 3 mm: a lowered side wider than 2.6 mm is cut
 * that far from the step line and no further.
 *
 * The engine carves it as a POCKET (a floor stones are set into, counted in
 * their seat cap) and seats any outer-face stone whose girdle lies wholly on
 * it on its floor (`gemPlacement.floorSources`).
 */
export function partialStepGrooves(params: Partial<PartialStepParams>, group: string): DesignGroove[] {
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  const pick = (k: Exclude<keyof PartialStepParams, "side">) => {
    const v = params[k];
    return typeof v === "number" && Number.isFinite(v) ? v : PARTIAL_STEP_DEFAULTS[k];
  };
  const spanMM = clamp(pick("spanMM"), 1, 12);
  const h = spanMM / 2;
  const p: PartialStepParams = {
    side: params.side === "-y" ? "-y" : params.side === "+y" ? "+y" : PARTIAL_STEP_DEFAULTS.side,
    positionMM: clamp(pick("positionMM"), -(h - 0.2), h - 0.2),
    depthMM: clamp(pick("depthMM"), 0.1, 1),
    centerArcMM: clamp(pick("centerArcMM"), -60, 60),
    lengthMM: clamp(pick("lengthMM"), 1, 60),
    exitDeg: clamp(pick("exitDeg"), 20, 90),
    cornerRadiusMM: clamp(pick("cornerRadiusMM"), 0, 5),
    spanMM,
  };
  const name = group || "step";
  const s = p.side === "+y" ? 1 : -1;
  const OVERHANG = 0.4;
  // Step line to the edge, plus the overhang: the groove's whole width.
  const widthMM = clamp(h - s * p.positionMM + OVERHANG, DESIGN_GROOVE_MIN_WIDTH_MM, 3);
  const c = p.positionMM + (s * widthMM) / 2;
  const a = (p.exitDeg * Math.PI) / 180;
  // The run-out leg: from the turn out until the WHOLE groove has left the band.
  const legY = h + widthMM - s * c;
  const leg = legY / Math.max(Math.sin(a), 1e-6);
  const a0 = p.centerArcMM - p.lengthMM / 2;
  const a1 = p.centerArcMM + p.lengthMM / 2;
  const round = (v: number) => Math.round(v * 1e4) / 1e4;
  const pt = (arcMM: number, yMM: number, radiusMM?: number) => ({
    arcMM: round(arcMM), yMM: round(yMM), ...(radiusMM !== undefined ? { radiusMM: round(radiusMM) } : {}),
  });
  const points = [
    pt(a0 - leg * Math.cos(a), c + s * leg * Math.sin(a)),
    pt(a0, c, p.cornerRadiusMM),
    pt(a1, c, p.cornerRadiusMM),
    pt(a1 + leg * Math.cos(a), c + s * leg * Math.sin(a)),
  ];
  return clampDesignGrooves([{
    id: `${name}-1`,
    direction: "path",
    positionMM: 0,
    jointType: "square",
    widthMM,
    depthMM: p.depthMM,
    path: { points },
    preset: { kind: "partialStep", group: name, params: p },
  }]);
}

/**
 * v41 Custom Balance: normalise free partition ratios to `count` entries summing
 * to 1. Short lists are padded with an equal share, long ones truncated, and
 * every entry floored at 1% so a partition can be made thin but never vanish
 * (a zero-width partition would leave a material with no geometry).
 *
 * Shared so the host's ratio sliders and the engine's seam placement agree —
 * the same reason `buildFreePattern` lives here.
 */
export function normalizeRatios(ratios: readonly number[], count: number): number[] {
  const n = Math.max(1, Math.round(count));
  // A MISSING or nonsense entry falls back to an equal share; an explicit 0 is
  // a legitimate "as thin as it goes" and rides the floor below instead.
  const src = Array.from({ length: n }, (_, i) => {
    const v = ratios[i];
    return Number.isFinite(v) && (v as number) >= 0 ? (v as number) : 1 / n;
  });
  const floor = 0.01;
  const total = src.reduce((a, b) => a + b, 0) || 1;
  const scaled = src.map((v) => Math.max(floor, v / total));
  const after = scaled.reduce((a, b) => a + b, 0);
  return scaled.map((v) => v / after);
}

/**
 * v42: the most metal partitions a band may be split into.
 *
 * The kernel is generic in the partition count (it walks `partitionRatios`), so
 * this is a manufacturing/UI limit, not a technical one: past eight the seams
 * are thinner than the saw. Shared so the host's count control and the engine's
 * clamp cannot drift.
 */
export const MAX_PARTITIONS = 8;

/** v42: the `SplitMode` label for a partition count. Five and up are all
 * "multi" — the mode is a label, the count is the truth. */
export function splitModeForCount(count: number): SplitMode {
  const n = Math.max(1, Math.min(MAX_PARTITIONS, Math.round(count)));
  return n <= 1 ? "none" : n === 2 ? "two-tone" : n === 3 ? "tricolor" : n === 4 ? "quad" : "multi";
}

/**
 * v42: id of the generated even N-way split pattern.
 *
 * The catalog carries hand-picked presets up to five partitions; beyond that
 * (and for any count whose preset list has no entry in the wanted direction)
 * the pattern is SYNTHESISED from its id rather than stored, so the catalog
 * does not have to enumerate every count. `findSplitPattern` parses these back.
 */
export function evenSplitPatternId(count: number, direction: "angular" | "horizontal" = "angular"): string {
  const n = Math.max(1, Math.min(MAX_PARTITIONS, Math.round(count)));
  return `n${n}${direction === "horizontal" ? "-h" : ""}-even`;
}

export type BandStyle = "standard" | "crossover";

export interface TwistConfig {
  strands: number;
  twists: number;
  strandRadiusMM: number;
  amplitudeMM: number;
}

/** v39: per-run stone tallies behind `BandSpec.stoneCount`, so the host can
 * report "62 stones on the left side". Gems only — beads are `beadCount`. */
export interface StoneCounts {
  /** Stones on the outer surface (the whole run when placement is "top"). */
  top: number;
  /** Stones in the −Y side wall. */
  left: number;
  /** Stones in the +Y side wall. */
  right: number;
  /** The inner-wall stone: 0 or 1. */
  inner: number;
  /** Stones carried by the engagement head, if one is attached. */
  head: number;
}

/** Computed read-only spec for cost/quote (weight, carats, dimensions). */
export interface BandSpec {
  metalVolumeMM3: number;
  weightGrams: number;
  surfaceAreaMM2: number;
  /** GEMS only. v39: tallied as stones are placed, so a run whose stones are
   * skipped can no longer over-report. */
  stoneCount: number;
  perStoneCarat: number;
  totalCarat: number;
  innerDiameterMM: number;
  circumferenceMM: number;
  // v39. Absent on a pre-v39 viewer — read them as `?? 0`.
  /** Per-run breakdown of `stoneCount`. */
  stoneCounts?: StoneCounts;
  /** Gold beads placed. Metal, so they are in the weight but never the carats. */
  beadCount?: number;
}

/** v37: ring outline seen down the finger axis. Omit/"classic" ⇒ today's circle.
 * v38 completes the reference's set with "octagon" (the polar octagon, softened
 * like the hexagon) and "wavy" (the axial undulation of "wave", but three times
 * as many and a little over half as deep). */
export type RingShape =
  | "classic" | "oval" | "cushion" | "hexagon" | "wave" | "octagon" | "wavy";

/**
 * The British (BS 6820) ring-size letter scale, A–Z with halves.
 *
 * `A` is a 37.8 mm inner circumference and every half-letter step adds 0.625 mm
 * (≈1.25 mm per full letter). Shared here for the same reason as
 * `BAND_WIDTH_MAX_MM`: the host's dropdown and the engine's `setRingSizeUK`
 * conversion must read the identical table or a picked letter would not be the
 * size that gets built.
 */
export const UK_RING_SIZES: { label: string; diameterMM: number }[] = Array.from(
  { length: 52 },
  (_, i) => ({
    label: String.fromCharCode(65 + (i >> 1)) + (i & 1 ? "½" : ""),
    diameterMM: Math.round(((37.8 + i * 0.625) / Math.PI) * 100) / 100,
  }),
);

/** v37: how a multi-metal split seam runs around the band. Omit ⇒ "vertical". */
export type DivisionStyle = "vertical" | "axial" | "diagonal" | "wavy";

/**
 * v67: the id of the PARAMETRIC cross-section. Every other `profileId` names a
 * fixed preset (or "custom", an imported .3dm curve); this one is built from
 * `BandState.paramProfile` — absent ⇒ `PARAM_PROFILE_DEFAULTS`.
 */
export const PARAM_PROFILE_ID = "param";

/**
 * v67: a ONE-SIDED step in the outer face (`ParamProfile.step`): everything on
 * one side of a line running round the ring is lowered by `heightMM`, the two
 * levels joined by a wall at `wallDeg`. A step that stops part way round is a
 * different thing — a Design groove pocket (`partialStepGrooves`).
 */
export interface ParamProfileStep {
  /** The LOWERED side: "+y" lowers the face from the line out to the band's +Y
   *  edge (`EdgeSides.right`), "-y" toward the −Y edge (`left`). */
  side: "+y" | "-y";
  /** Where the step line runs across the width: mm from the band's centre
   *  line, + toward the +Y edge (as a horizontal `DesignGroove.positionMM`).
   *  Clamped to leave at least 0.3 mm of face on each side. */
  positionMM: number;
  /** How far the lowered side sits below the raised one, mm. 0.05 … 0.6, and
   *  never more than leaves a quarter of the thickness standing at that edge. */
  heightMM: number;
  /** The step wall's angle from the face, degrees: 90 = a vertical wall,
   *  smaller = a sloping one. 20 … 90. Omit ⇒ 90. */
  wallDeg?: number;
}

/**
 * v67: the parametric cross-section (`profileId` "param"). Every length is in
 * mm on the FINISHED band — the section is built at the ring's own width and
 * thickness, so a 0.3 mm crown is 0.3 mm whatever the slider says, and
 * `thicknessMM` is the section's greatest radial extent (bore at mid-width to
 * the highest point of the face). Every field is optional: absent ⇒ its
 * `PARAM_PROFILE_DEFAULTS` value. Stored as given; `clampParamProfile` is the
 * range the engine builds from, applied at build time so the stored value
 * survives a width or thickness drag that would clamp it for a moment.
 *
 * Kept on the state while another profile is chosen (as `freeStones` survive a
 * parametric style), so switching back to "param" restores the sliders.
 */
export interface ParamProfile {
  /** How far the middle of the face rises above its edges, mm. 0 = flat top.
   *  0 … 0.6 × thickness. */
  crownMM?: number;
  /** Width of the flat on top of the crown, as a fraction of the band width,
   *  0 … 1. 0 = a dome all the way across; 1 = flat (the crown is ignored). */
  flatTop?: number;
  /** Radius of the four rim corners, mm. 0 = a truly crisp corner; large
   *  values are limited by the wall they round, so the largest is FULLY ROUND
   *  (a side that is one continuous arc). 0 … 3. */
  edgeRadiusMM?: number;
  /** A 45° chamfer on each outer edge, mm across the width. 0 = none. 0 … 0.4
   *  × width, and never deeper than the side wall. */
  bevelMM?: number;
  /** Comfort fit: how far the bore bulges toward the finger at mid-width,
   *  relative to its edges, mm. 0 = flat inside. 0 … 0.3 × thickness. */
  comfortMM?: number;
  /** A one-sided step. Absent ⇒ none. */
  step?: ParamProfileStep;
}

/** v67: what an absent `ParamProfile` field means — a slim comfort dome with
 *  softly rounded edges, the everyday wedding band. */
export const PARAM_PROFILE_DEFAULTS: Readonly<Required<Omit<ParamProfile, "step">>> = {
  crownMM: 0.35,
  flatTop: 0,
  edgeRadiusMM: 0.35,
  bevelMM: 0,
  comfortMM: 0.2,
};

/** v67: a `ParamProfile` resolved against its defaults and clamped to what the
 *  given band can hold — what the engine actually builds. Pure, so a host's
 *  sliders can show the same limits. */
export interface ResolvedParamProfile {
  crownMM: number;
  flatTop: number;
  edgeRadiusMM: number;
  bevelMM: number;
  comfortMM: number;
  step?: { side: 1 | -1; positionMM: number; heightMM: number; wallDeg: number };
}

/** v67: least bend radius of the parametric crown where it leaves the flat top, mm (`clampParamProfile`). */
export const PARAM_CROWN_MIN_RADIUS_MM = 0.3;

export function clampParamProfile(p: ParamProfile | undefined, widthMM: number, thicknessMM: number): ResolvedParamProfile {
  const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  const D = PARAM_PROFILE_DEFAULTS;
  const W = Math.max(0.5, num(widthMM, 3));
  const T = Math.max(0.3, num(thicknessMM, 1.6));
  const flatTop = clamp(num(p?.flatTop, D.flatTop), 0, 1);
  let crownMM = flatTop >= 1 ? 0 : clamp(num(p?.crownMM, D.crownMM), 0, 0.6 * T);
  // The crown falls over the run from the flat's edge to the band's edge as
  // 1 − cos(π/2·s), so it bends at the flat's edge with radius
  // run² / (crown · π²/4). Held to ≥ PARAM_CROWN_MIN_RADIUS_MM: a 0.9 flat top
  // on a narrow band left a 0.075 mm run under a 0.5 mm crown — a 0.005 mm
  // radius, i.e. an unrounded 78° kink (review finding).
  const run = (W / 2) * (1 - flatTop);
  crownMM = Math.min(crownMM, (4 * run * run) / (Math.PI * Math.PI * PARAM_CROWN_MIN_RADIUS_MM));
  let comfortMM = clamp(num(p?.comfortMM, D.comfortMM), 0, 0.3 * T);
  // The side wall is what is left at the edge: keep a fifth of the thickness.
  const wallRoom = 0.8 * T;
  if (crownMM + comfortMM > wallRoom) {
    const k = wallRoom / (crownMM + comfortMM);
    crownMM *= k;
    comfortMM *= k;
  }
  const wall = T - crownMM - comfortMM;
  const bevelMM = clamp(num(p?.bevelMM, D.bevelMM), 0, Math.min(0.4 * W, 0.8 * wall));
  // A rim corner may not take more than 45 % of the band's width: the two
  // bore corners met at mid-width and folded the bore back on itself at
  // 3 mm on a 6 mm band (review finding). Fully round is still reached.
  const edgeRadiusMM = clamp(num(p?.edgeRadiusMM, D.edgeRadiusMM), 0, Math.min(3, 0.45 * W));
  const out: ResolvedParamProfile = { crownMM, flatTop, edgeRadiusMM, bevelMM, comfortMM };
  const st = p?.step;
  if (st && (st.side === "+y" || st.side === "-y")) {
    const side = st.side === "+y" ? 1 : -1;
    const lim = Math.max(0, W / 2 - 0.3);
    // A quarter of the thickness stays standing at the lowered edge.
    const heightMM = clamp(num(st.heightMM, 0.2), 0.05, Math.max(0.05, Math.min(0.6, wall - bevelMM - 0.25 * T)));
    out.step = {
      side,
      positionMM: clamp(num(st.positionMM, 0), -lim, lim),
      heightMM,
      wallDeg: clamp(num(st.wallDeg, 90), 20, 90),
    };
  }
  return out;
}

export interface BandState {
  profileId: string;
  /** v67: the parametric section's sliders, read while `profileId` is
   *  `PARAM_PROFILE_ID`. Absent ⇒ `PARAM_PROFILE_DEFAULTS` there, and nothing
   *  anywhere else — every preset renders exactly as before. */
  paramProfile?: ParamProfile;
  ringSizeUS: number;
  ringSizeEU: number;
  widthMM: number;
  thicknessMM: number;
  splitMode: SplitMode;
  splitPatternId: string;
  materials: PartitionMaterial[];
  metalKarat: string;
  angularSegments: number;
  edgeStyle: EdgeStyle;
  grooves: GrooveConfig;
  bandStyle: BandStyle;
  twist: TwistConfig;
  diamond: DiamondConfig;
  channel: ChannelParams;
  /** v55 — the cross-channel trench. Absent ⇒ every field derived from the run,
   *  exactly as before v55. Only read while `diamond.style === "cross-channel"`. */
  crossChannel?: CrossChannelParams;
  engravingText: string;
  engraving: EngravingConfig;
  spec?: BandSpec;
  // v37 (Ring Builder). All optional ⇒ a v36 state object still typechecks and
  // an existing design renders identically.
  /** Non-round silhouette. Omit/"classic" ⇒ a round band. */
  shape?: RingShape;
  /** Inner-curve bulge into the finger hole, in mm. Omit/0 ⇒ flat inside. */
  comfortFitMM?: number;
  /** Seam path for the split-mode partitions. Omit ⇒ "vertical" (today's). */
  division?: DivisionStyle;
  // v38 geometry. All optional ⇒ a v37 state still typechecks and renders the
  // same pixels.
  /** Independent Left/Right edge treatment. Omit ⇒ `edgeStyle` drives both. */
  edgeSides?: EdgeSides;
  /** Force the inner wall to a polished finish whatever the outer surface is. */
  insidePolished?: boolean;
  /** Drive thickness from width (half of it, clamped to 1–3 mm). */
  autoThickness?: boolean;
  // v39. Absent ⇒ no inner stone, i.e. today.
  /** One stone set into the inside of the band, with its own seat carve. */
  innerDiamond?: InnerDiamondConfig;
  // v40 free placement. Read only by `DiamondStyle` "free-place", so the array
  // survives switching to a parametric style and back. Absent ⇒ today's run.
  /** The explicitly placed stones. */
  freeStones?: FreeStone[];
  /** N-fold repetition of the whole arrangement around the ring. Omit ⇒ 1. */
  freeRepeat?: number;
  /** Spins the whole arrangement around the band, −180 … +180. Omit ⇒ 0. */
  freeRotateDeg?: number;
  /** v67: the stone pattern (`DiamondStyle` "pattern"; read only by that
   *  style, so it survives switching away and back, as `freeStones` does).
   *  Normalised by `clampStonePattern`. Absent ⇒ today's run. */
  stonePattern?: StonePattern;
  // v41 long tail. Absent ⇒ today: one global groove, preset ratios.
  /** The "Design" list — N independently placed decorative grooves, carved by
   * the same max-depth pass as `grooves`. Absent/empty ⇒ nothing extra. */
  designGrooves?: DesignGroove[];
  /** v51 (item 4): bead size / rows / spacing / sink for every milgrain rail.
   *  Absent ⇒ the pre-v51 constants, so nothing moves without being asked. */
  milgrain?: MilgrainConfig;
  /** Custom Balance: free partition ratios replacing the split pattern's preset
   * ones. Stored normalised (`normalizeRatios`) and re-fitted whenever the
   * partition count changes. Absent ⇒ the pattern's own ratios. */
  customRatios?: number[];
  // v43 ring-set metadata. PURE METADATA: neither field has any geometric or
  // material meaning — the engine stores them, echoes them back and saves/loads
  // them so the ring-manager modal can label its rows after a reload, and that
  // is all. Absent ⇒ the host's positional default.
  /** What this ring IS in the manager's list ("Wedding ring" / "Memoire" /
   * "Engagement ring"). Independent of `engagement.enabled`, which is what
   * actually attaches a head. */
  ringType?: "wedding" | "memoire" | "engagement";
  /** The row's editable name (their `title=Rename` button). Absent ⇒ the host's
   * positional label ("Her Ring", "His Ring", "Ring 3" …). */
  ringName?: string;
}

export type BandLayoutPreset =
  | "side-by-side" | "his-flat-her-standing" | "leaning" | "interlocked" | "stacked"
  /** v67: the catalogue pose. Ring 0 stands in front, tilted toward a raised
   * three-quarter camera and slightly turned; ring 1 rests on the same floor
   * BEHIND it, leaning (turned about the VIEW axis, so the lean is visible on
   * a round band — "leaning" turns about the ring's own axis, which is not),
   * and overlapping it in the picture without touching it in 3-D (bounding
   * cylinders kept clear). Rings 2+ alternate sides further back. Selecting it
   * also frames the set from the matching catalogue camera. A lone ring is
   * never moved. An older viewer does not list it in `Catalogs.bandLayouts`. */
  | "catalogue"
  /** v67: the standing pair. Both rings stand on their edge on one floor,
   * axis level and turned well away from a camera just above the floor (tall
   * ellipses, refs 1/3/4/9); the stone ring in front and to the left with its
   * stones (local +Y) turned to the side facing the camera; the partner behind
   * and to the right, the two leaning toward each other, never touching
   * (bounding cylinders kept clear). Selecting it frames the set from that
   * camera. A lone ring is never moved. Listed only by a viewer that has it. */
  | "standing";

/**
 * v68: one ring's override of its layout PRESET's law (a photographed pose,
 * e.g. a "Start from a design" recipe matched to its catalogue photo). Every
 * field is optional; an absent field is the preset's own constant, so an empty
 * pose IS the preset, bit for bit. Ring i of `BandLayoutPose.rings` is the i-th
 * VISIBLE ring (hidden rings are skipped, as the layout skips them).
 */
export interface BandLayoutRingPose {
  // ── "standing" ──
  /** 0…90: the ring axis's angle from the view (90 = seen edge-on). Absent ⇒ 68 (ring 0) / 72 (partners). */
  turnDeg?: number;
  /** −45…45: about the view axis, + toward the partner's side. Absent ⇒ 9 (ring 0) / 5 (partners). */
  leanDeg?: number;
  /** −90…90: the design's 12 o'clock (local +Y, where the stones are) lifted this
   * far above the side that faces the camera. Absent ⇒ 18 on ring 0; a partner
   * is un-spun (local +Y up), as today. */
  spinDeg?: number;
  // ── "catalogue" ──
  /** −90…90: tips the ring's top toward the camera (about X). Absent ⇒ 22 / 30. */
  tiltDeg?: number;
  /** −90…90: turns it about the vertical. Absent ⇒ −10 / 8. */
  yawDeg?: number;
  /** −90…90: turns it about the view axis. Absent ⇒ 3 / 24. */
  rollDeg?: number;
  /** −1…1: partner centre above ring 0's in the picture, × ring 0's outer diameter. Absent ⇒ 0.3. */
  riseD0?: number;
  // ── both ──
  /** −2…2: partner centre to its side, × ring 0's outer diameter (before clearing).
   * Absent ⇒ 0.42 ("standing") / 0.1 ("catalogue"). Ignored on ring 0. */
  sideD0?: number;
  /** −1…2: the START depth behind ring 0, × ring 0's outer diameter (− = in front).
   * The bounding-cylinder clearance solver only moves the ring further along its
   * own sign from there. Absent ⇒ 0 (today). Ignored on ring 0. */
  depthD0?: number;
}

/** v68: the layout camera of a photographed pose. Every field optional. */
export interface BandLayoutCamera {
  /** −10…80, degrees above the floor. Absent ⇒ the preset's own (12 standing / 40 catalogue). */
  elevationDeg?: number;
  /** −180…180, degrees about +Y (0 = from +Z). Absent ⇒ 0. */
  azimuthDeg?: number;
  /** 2…60, vertical field of view. Absent ⇒ the camera's current fov (today). */
  fovDeg?: number;
  /** 0.3…0.95: the projected bounding box of the sampled metal fills this share
   * of the BINDING output axis (height or width, whichever is tighter). Absent ⇒
   * today's margin of 1.4. Held at every output aspect (live canvas, snapshot,
   * tiled export) until the user moves the camera. */
  fill?: number;
  /** 0.2…0.8 each: where that box's centre lands, [x right, y down] as shares
   * of the output frame. Absent ⇒ the box centre framed (today). */
  center?: [number, number];
}

/** v68: a per-design override of the "standing" / "catalogue" laws (`setBandLayout`'s optional second argument). */
export interface BandLayoutPose {
  /** By VISIBLE ring order. */
  rings?: BandLayoutRingPose[];
  camera?: BandLayoutCamera;
  /** 0…0.2: bounding-cylinder clearance between rings, × ring 0's outer diameter. Absent ⇒ 0.02 (today). */
  clearanceD0?: number;
  /** Swap the sides (the stone ring on the right). Absent ⇒ false. */
  mirror?: boolean;
}

/** v68: what `getBandLayout` reports. */
export interface BandLayoutInfo {
  preset: BandLayoutPreset;
  /** The stored (clamped) pose, or null when the preset's own law applies. */
  pose: BandLayoutPose | null;
  /** The layout camera in force (null for the presets that keep the free camera). */
  camera: { elevationDeg: number; azimuthDeg: number; fovDeg: number } | null;
}

const clampPoseNum = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;

/**
 * v68: a `BandLayoutPose` made safe, for `preset`. Pure: drops non-finite and
 * unknown fields, clamps every range, and returns null when nothing is left or
 * when the preset is neither "standing" nor "catalogue" (the only laws with
 * anything to override). Fields of the OTHER law are dropped (a "standing"
 * pose keeps no tilt), so a stored pose is exactly what applies.
 */
export function clampBandLayoutPose(p: unknown, preset: BandLayoutPreset): BandLayoutPose | null {
  if (preset !== "standing" && preset !== "catalogue") return null;
  if (!p || typeof p !== "object") return null;
  const src = p as Record<string, unknown>;
  const out: BandLayoutPose = {};
  if (Array.isArray(src.rings)) {
    const rings: BandLayoutRingPose[] = [];
    for (const r of src.rings.slice(0, 16)) {
      const q = (r && typeof r === "object" ? r : {}) as Record<string, unknown>;
      const ring: BandLayoutRingPose = {};
      const put = (k: keyof BandLayoutRingPose, lo: number, hi: number) => {
        const v = clampPoseNum(q[k], lo, hi);
        if (v !== undefined) ring[k] = v;
      };
      if (preset === "standing") { put("turnDeg", 0, 90); put("leanDeg", -45, 45); put("spinDeg", -90, 90); }
      else { put("tiltDeg", -90, 90); put("yawDeg", -90, 90); put("rollDeg", -90, 90); put("riseD0", -1, 1); }
      put("sideD0", -2, 2);
      put("depthD0", -1, 2);
      rings.push(ring);
    }
    while (rings.length && !Object.keys(rings[rings.length - 1]).length) rings.pop();
    if (rings.length) out.rings = rings;
  }
  if (src.camera && typeof src.camera === "object") {
    const c = src.camera as Record<string, unknown>;
    const cam: BandLayoutCamera = {};
    const e = clampPoseNum(c.elevationDeg, -10, 80); if (e !== undefined) cam.elevationDeg = e;
    const a = clampPoseNum(c.azimuthDeg, -180, 180); if (a !== undefined) cam.azimuthDeg = a;
    const f = clampPoseNum(c.fovDeg, 2, 60); if (f !== undefined) cam.fovDeg = f;
    const fi = clampPoseNum(c.fill, 0.3, 0.95); if (fi !== undefined) cam.fill = fi;
    if (Array.isArray(c.center) && c.center.length === 2) {
      const x = clampPoseNum(c.center[0], 0.2, 0.8), y = clampPoseNum(c.center[1], 0.2, 0.8);
      if (x !== undefined && y !== undefined) cam.center = [x, y];
    }
    if (Object.keys(cam).length) out.camera = cam;
  }
  const cl = clampPoseNum(src.clearanceD0, 0, 0.2); if (cl !== undefined) out.clearanceD0 = cl;
  if (src.mirror === true) out.mirror = true;
  return Object.keys(out).length ? out : null;
}

// ─── Ring Builder merged state (v37) ────────────────────────────────────────
// One builder: the wedding band is the base, and an engagement ring IS that band
// with an engagement block attached. Everything the band schema already owns —
// profileId, widthMM, ringSizeUS, edgeStyle, grooves, channel, splitMode,
// splitPatternId, materials, diamond — lives on the BandState half and is
// deliberately NOT duplicated on EngagementBlock (see the ShankConfig note).

/** The engagement-only half of a RingState. Composed from the existing
 * engagement DTOs; the band-grade shank settings are the BandState half. */
export interface EngagementBlock {
  /** Head assembly attached (true) vs a plain wedding band (false). */
  enabled: boolean;
  centerStone: CenterStoneConfig;
  sideStones: SideStonesConfig;
  head: HeadConfig;
  extras: EngagementExtras;
  /** Shank silhouette variant (was ShankConfig.style). */
  style?: ShankStyle;
  /** Shoulders rise to meet the head (was ShankConfig.cathedral). */
  cathedral?: boolean;
  /** Inner-profile fit (was ShankConfig.fit). */
  fit?: RingFit;
}

/** The merged Ring Builder state: a BandState plus an optional engagement block. */
export interface RingState extends BandState {
  engagement?: EngagementBlock;
  spec?: RingSpec;
}

/**
 * v67 — the RingState fields that describe the WEARER, not the design: what a
 * live link never mirrors and what `applyRingDesign` never applies. Ring size
 * (different fingers), the engraving TEXT (a personal inscription; the
 * engraving STYLE is design), the engagement head (it belongs to the engagement
 * ring, never to a band stacked beside it), the ring manager's labels, and the
 * spec (a read-out, never an input).
 */
export const RING_WEARER_FIELDS = [
  "ringSizeUS", "ringSizeEU", "engravingText", "engagement", "ringType", "ringName", "spec",
] as const;
export type RingWearerField = (typeof RING_WEARER_FIELDS)[number];

type RingDesignFields = Omit<RingState, RingWearerField>;

/**
 * v67 — a design, or any part of one, for `applyRingDesign`.
 *
 * Top-level fields are optional and only the ones present are applied. A
 * nested block (`diamond`, `grooves`, `engraving` …) is a PATCH merged by its
 * setter, as it always was; lists (`materials`, `freeStones`, `designGrooves`
 * …) are whole. `channel` and `crossChannel` are restored by OWNERSHIP, the
 * way `setChannelManual` / `setCrossChannelManual` store them: a field named
 * in the block's `manual` list is taken over at the value given (or the ring's
 * current one), every other ownable field is handed back to the style, and the
 * derived ones (`enabled`, `position`, `fullThrough`) are never read. A
 * "custom" `profileId` is skipped (its .3dm points never cross postMessage).
 */
export type RingDesignPatch = {
  [K in keyof RingDesignFields]?: NonNullable<RingDesignFields[K]> extends readonly unknown[]
    ? RingDesignFields[K]
    : NonNullable<RingDesignFields[K]> extends object
      ? Partial<NonNullable<RingDesignFields[K]>>
      : RingDesignFields[K];
};

/** BandSpec plus the engagement-only figures, populated when engagement is on. */
export interface RingSpec extends BandSpec {
  headVolumeMM3?: number;
  centerStoneMM?: { l: number; w: number; d: number };
  /** Manufacturability validator output (was EngagementSpec.warnings). */
  warnings?: string[];
}

// ─── Engagement-ring configurator (v33) ─────────────────────────────────────
// A third viewer mode ("engagement") that composes a full parametric engagement
// ring — center stone, head/setting, shank, pavé, extras — live in 3D. State is
// driven through EngagementApi (below) with coarse per-block partial-patch
// setters. The schema is frozen here; the assembly engine is built across later
// waves. Reuses the band metal (PartitionMaterial) and gem (GemParamOverride)
// param systems verbatim.

export type CenterStoneShape =
  | "round" | "oval" | "princess" | "emerald" | "cushion" | "cushion-elongated"
  | "radiant" | "radiant-elongated" | "pear" | "marquise" | "asscher" | "heart";
export type DiamondOrigin = "natural" | "lab" | "skip";
export type StoneLayout = "one" | "two" | "three";
export type HeadUnderstructure =
  | "none" | "basket" | "pave-basket" | "cathedral-basket"
  | "halo" | "hidden-halo" | "double-halo" | "bezel";
export type ProngCount = "4-classic" | "4-compass" | "6" | "8";
export type ProngTip =
  | "claw" | "petite-claw" | "rounded" | "tab" | "double-claw" | "double-petite-claw" | "v";
export type ShankStyle = "round" | "square" | "knife-edge" | "split" | "twist";
export type ShankPaveStyle = "none" | "french" | "channel" | "flush" | "triple-row";
export type RingFit = "comfort" | "standard";
export type EngravingFont = "block" | "cursive";

/** Which metal partition a metal edit targets ("Mixed" = head differs from shank). */
export type EngagementMetalPart = "head" | "shank";

export interface CenterStoneConfig {
  shape: CenterStoneShape;
  caratCt: number;                                      // 0.25–10
  origin: DiamondOrigin;
  gemPresetId: string;
  gemParams?: GemParamOverride;                         // reuse band gem param system
}
export interface SideStonesConfig {                     // layout !== "one"
  layout: StoneLayout;
  shape: CenterStoneShape;
  caratRatio: number;                                   // 0.3–0.5 of center
}
export interface HeadConfig {
  understructure: HeadUnderstructure;
  prongCount: ProngCount;
  prongTip: ProngTip;
  prongPave: boolean;
  haloStoneSizeMM: number;                              // 0.8–1.5, halo modes only
  bezelWallMM: number;                                  // 0.5–1.0, bezel mode only
  metal: PartitionMaterial;                             // reuse band metal type
}
/**
 * @deprecated v37 — folded into RingState. ShankConfig duplicated ten BandState
 * fields; the merged state owns each exactly once. Mapping:
 *   profileId, widthMM, ringSizeUS, edgeStyle, grooves, channel, splitMode,
 *   splitPatternId, materials, diamond  → the BandState half of RingState
 *   metal                               → RingState.materials[0]
 *   style, cathedral, fit               → RingState.engagement.{style,cathedral,fit}
 *   pave, paveCoverageDeg, paveStoneSizeMM, paveRows
 *                                       → RingState.diamond (the melee run)
 * Kept exported and intact while src/engagement/* and setShank still use it.
 */
export interface ShankConfig {
  style: ShankStyle;
  cathedral: boolean;
  widthMM: number;                                      // 1.5–4.0
  profileId: string;                                    // our 28 profiles ("A" default)
  pave: ShankPaveStyle;
  paveCoverageDeg: number;
  paveStoneSizeMM: number;
  paveRows: 1 | 2 | 3;
  ringSizeUS: number;                                   // 3–13.5 incl. half sizes
  fit: RingFit;
  metal: PartitionMaterial;
  // v34: band-grade shank surface (the band+head merge). Each reuses the band
  // DTO verbatim so the shank shares the RingGeometryBuilder edge/groove/channel/
  // split-partition/diamond-run pipeline.
  edgeStyle: EdgeStyle;
  grooves: GrooveConfig;
  channel: ChannelParams;
  splitMode: SplitMode;
  splitPatternId: string;
  materials: PartitionMaterial[];
  diamond: DiamondConfig;
}
/** Engagement-ONLY extras on an `EngagementBlock`. Engraving is deliberately
 * absent: `BandState.engravingText` + `BandState.engraving` are the single
 * source of truth in the merged builder (only `BandConfigurator` draws the
 * decal), and an EngagementBlock never re-declares a field BandState owns. */
export interface EngagementExtras {
  surpriseStones: { enabled: boolean; sizeMM: number; shape: "round" };
}
/** The pre-merge standalone engagement mode kept its own engraving here. Read
 * ONLY by the deprecated EngagementApi / EngagementState; a persisted design
 * that still carries these is migrated onto the band half on load
 * (`BandConfigurator.setEngagement`, `serializeRingState`). */
export interface LegacyEngagementExtras extends EngagementExtras {
  engravingText: string;                                // old UI capped at 14 chars; the band panel allows 40
  engravingFont: EngravingFont;
}
export interface EngagementSpec {                       // read-only, computed on rebuild
  headVolumeMM3: number;
  shankVolumeMM3: number;
  metalWeightGrams: Record<string, number>;             // per alloy
  stoneCount: number;
  totalPaveCaratCt: number;
  centerStoneMM: { l: number; w: number; d: number };
  warnings: string[];                                   // manufacturability validator output
}
export interface EngagementState {
  centerStone: CenterStoneConfig;
  sideStones: SideStonesConfig;
  head: HeadConfig;
  shank: ShankConfig;
  extras: LegacyEngagementExtras;
  spec?: EngagementSpec;
}

// ─── On Body (v63) ──────────────────────────────────────────────────────────
// Fit the loaded piece onto a body photograph that is already the 2D backdrop
// (`setBackgroundImageUrl` / `setBackgroundImageFile`). The viewer does not
// detect anything: the host hands over where the finger or neck is, in photo
// coordinates, and the viewer does the projection.

/**
 * The node id `getNodeDetails` / `setNodeTransform` accept for the model
 * CONTAINER — the parent of every loaded model. It is what `placeOnBody`
 * moves, so the host reads and nudges the placement through the v58 node
 * inspector instead of a second transform API. Never handed out by
 * `getSceneTree` (the tree lists the models under it).
 */
export const MODEL_ROOT_NODE_ID = "modelRoot";

/** What the piece is worn on. */
export type OnBodyKind = "finger" | "neck";

/**
 * Where the body part is in the photograph. Every point is NORMALISED to the
 * photo — `[x / photoWidth, y / photoHeight]`, y running DOWN — so it does not
 * depend on the resolution the photo is delivered at.
 *
 * Finger: `p0` is the ring finger's knuckle (MCP), `p1` its next joint (PIP),
 * `center` where the ring sits. Neck: `p0` is the base of the neck at the
 * front — where a chain's front sits — `p1` the chin, and `center` is normally
 * `p0`. In both, p0 → p1 is the body part's axis. A point may sit slightly
 * outside 0..1 (a chin cropped out of frame); it is used as given.
 */
export interface OnBodyAnchor {
  p0: [number, number];
  p1: [number, number];
  center: [number, number];
  /** The finger's (neck's) width as a fraction of the photo WIDTH. */
  width: number;
  /**
   * The axis direction in photo PIXELS with a relative depth (MediaPipe's):
   * `[dx, dy, dz]`, y down, `dz < 0` pointing toward the camera. Only the tilt
   * out of the picture plane is read from it — the in-plane direction always
   * comes from p0 → p1. Absent ⇒ the axis lies in the picture plane.
   * `axisWorld` wins when both are given.
   */
  axis3?: [number, number, number];
  /**
   * v64 — where the piece sits along p0 → p1, as a multiple of that segment:
   * the seat is `p0 + along · (p1 − p0)`, so 0 is the knuckle, 1 the middle
   * joint and 2 a whole bone past it (iJewel's "Along Finger", 0..2, default
   * 0.65 — MediaPipe's MCP landmark sits inside the hand, so 0.5 lands where
   * the finger merges into the hand). The sideways offset of `center` from the
   * p0 → p1 line is kept. Absent ⇒ `OnBodyParams.seat`, then `center` (v63).
   */
  along?: number;
  /**
   * v64 — the body part's 3-D direction (p0 → p1), from MediaPipe's WORLD
   * landmarks: `[x, y, z]` in their camera-aligned frame, x right, y DOWN, z
   * AWAY from the camera. Read for the pitch out of the picture only — the
   * in-plane direction still comes from p0 → p1 on the photo. With `axis3` too
   * the pitch is the median of the two cues and zero (MediaPipe's world and
   * image depths often disagree; a pitch both agree on is believed).
   */
  axisWorld?: [number, number, number];
  /**
   * v64 — the BACK-of-hand normal in the photo's frame (x right, y down, z
   * away from the camera) — the playground takes it from the image landmarks
   * of the wrist and the index / little knuckles. The head of the ring is
   * turned toward this normal, projected perpendicular to the finger — so on a
   * photo of a palm the head goes behind the finger, as on a real hand.
   * Absent (or `headToCamera`) ⇒ the head faces the camera, the v63 rule.
   */
  dorsal?: [number, number, number];
}

export interface OnBodyParams {
  kind: OnBodyKind;
  /** The photograph's pixel size. Only its aspect is used, and only when the
   *  backdrop has not decoded yet — the live texture's own size wins. */
  photo: { w: number; h: number };
  anchor: OnBodyAnchor;
  /**
   * Finger: the ring's INNER diameter as a multiple of the finger width
   * (default 1.04 — a ring sits a little proud of the skin). Neck: the loop's
   * inner width as a multiple of the neck width (default 1.15).
   */
  fit?: number;
  /**
   * 0..1 along p0 → p1 where the piece sits. Absent ⇒ the anchor's `center`.
   * The sideways offset of `center` from the p0 → p1 line is kept either way.
   * `anchor.along` (v64) wins when both are given.
   */
  seat?: number;
  /**
   * v64 — force the v63 orientation: the head faces the camera whatever the
   * hand does (iJewel's "the stone always shows" look). Default false: the
   * head follows `anchor.dorsal` when it is given.
   */
  headToCamera?: boolean;
}

/**
 * The depth-only stand-in for the body part, in WORLD units. A capsule: a
 * cylinder of `radius` whose total length (caps included) is `length`.
 */
export interface BodyOccluderConfig {
  center: Vec3;
  /** Unit vector along the body part. Normalised on the way in. */
  axis: Vec3;
  radius: number;
  length: number;
  /**
   * Draw the occluder as a translucent blue tube (iJewel's OnBody setup look)
   * so the user can see what the piece is fitted to. It still hides the far
   * side of the piece; only the tint is added. Default false.
   */
  showInSetup?: boolean;
  /**
   * v64 — contact shadow, 0..1 (default 0.5): how much the piece darkens the
   * skin on either side of where it sits on the finger. Analytic, on the photo
   * pixels the occluder covers only — the piece itself, the stones and every
   * pixel off the finger are untouched, and with no occluder nothing is drawn.
   * 0 turns it off. Finger only: a neck occluder ignores it.
   */
  contactShadow?: number;
}

/**
 * The geometry `placeOnBody` fits against, in WORLD units at the model's
 * current transform. Gems are ignored for every radius below — a stone does
 * not touch the finger.
 */
export interface ModelFit {
  boundsMin: Vec3;
  boundsMax: Vec3;
  center: Vec3;
  /**
   * Unit vector through the ring's hole (the finger axis). Found from the
   * geometry: of the three principal axes and the three model axes, the one
   * the metal leaves the largest empty circle around. Rings are authored with
   * the hole on +Z by convention, and that is the fallback.
   */
  holeAxis: Vec3;
  /** `"geometry"` when the hole test found a hole; `"convention"` when it did
   *  not and +Z (model space) was assumed. */
  axisSource: "geometry" | "convention";
  /** Centre of the hole, at the middle of the band along the axis. */
  holeCenter: Vec3;
  /** Smallest distance from the hole axis to any metal vertex. */
  innerRadius: number;
  /**
   * Unit vector from `holeCenter` toward the head (perpendicular to the axis):
   * toward the stones' centroid when the stones are off-centre, else toward the
   * metal's furthest reach, else the model's +Y. On a necklace this is the
   * front of the loop (the pendant side), which `placeOnBody` turns toward the
   * camera and seats on the base of the neck.
   */
  headDirection: Vec3;
  /**
   * Necklace heuristic: the loop's inner width, taken as the diameter of the
   * largest empty circle inside the loop (2 x `innerRadius`). Exact for a round
   * loop; for a U or V it is the width of the widest part of the opening.
   * ⚠️ A necklace is placed as a RIGID loop around the neck, tilted 20° so its
   * front drops and its back rides up behind the neck. A chain cannot drape:
   * a pendant authored hanging in the loop's own plane ends up pointing
   * forward-and-down, not lying flat on the chest.
   */
  loopWidth: number;
}

/** What `placeOnBody` did. */
export interface OnBodyPlacement {
  /** Always `MODEL_ROOT_NODE_ID` — the id to read and nudge the placement by. */
  nodeId: string;
  /** The model container's transform, exactly as `getNodeDetails` reports it. */
  position: Vec3;
  rotationDeg: Vec3;
  scale: Vec3;
  /** The finger / neck stand-in that was installed. */
  occluder: BodyOccluderConfig;
  /** The fit the placement was computed from (before the move). */
  fit: ModelFit;
  /** Distance from the camera to the anchor, world units. The camera is a
   *  fixed fov-25 front view, chosen so the piece keeps its true size. */
  cameraDistance: number;
}

// ─── Background image framing (v49) ─────────────────────────────────────────
/**
 * How a 2D backdrop is fitted into the viewport. Every field is optional and
 * every absent value reproduces the pre-v49 behaviour exactly — `fit: "cover"`,
 * no zoom, no pan, no rotation — so an older saved scene frames identically.
 *
 * Applied by `Engine._applyBackgroundImageFraming`, which is the SINGLE place
 * both the live canvas and `snapshotTiled` go through, so the preview and the
 * exported file cannot disagree.
 */
export interface BackgroundImageFraming {
  /**
   * `cover` (default) scales to fill and crops the overflow — what every other
   * "background image" control does. `contain` shows the whole picture; the
   * area beyond it samples the clamped edge pixels, so a `contain` backdrop
   * with a busy border smears rather than letterboxes. `stretch` is the
   * pre-v45 squash, kept because it is occasionally what a designer wants.
   */
  fit?: "cover" | "contain" | "stretch";
  /** Zoom about the framing centre. 1 = the fit above; >1 pushes in. */
  scale?: number;
  /**
   * Pan, as a fraction of the FITTED picture rather than of pixels, so a
   * framing survives a resize and a tiled export unchanged. +x moves the
   * picture right, +y moves it up.
   */
  offsetX?: number;
  offsetY?: number;
  /**
   * Rotation about the viewport centre, in radians. Aspect-corrected: the
   * rotation is applied in SCREEN space, not raw uv space, or a 45 degrees turn
   * on a 16:9 canvas would shear instead of rotate.
   */
  rotation?: number;
}

// ─── Gem inclusion material (v32, webgi parity v62) ─────────────────────────
// Two layers, both read inside the diamond ray-trace behind #ifdef
// GEM_INCLUSIONS — with inclusions off the gem shader is byte-identical.
//
//  · the SPECKLE layer (v32, ours): `mapUrl` is ADDED to the light at every
//    internal bounce, tinted by `color` and weighted by `bumpScale` — bright
//    flecks on black, the in-house /gem-inclusions/ maps.
//  · the WEBGI layer (v62): webgi 0.17's GemRefractionPlugin, the model the 46
//    inclusion presets in iJewel's library are authored against. Interior maps
//    are projected at each internal hit through a tangent basis of that facet;
//    the inclusion map MULTIPLIES the light, the inclusion normal map replaces
//    the facet normal the ray refracts through, and the inclusion roughness map
//    blurs the environment the ray leaves into. A surface layer bends and
//    roughens the crown reflection. Field names follow the iJewel panel rows.
//
// Every map field takes a URL and `""` clears it. Maps are sampled as sRGB, as
// webgi does for all of them (its glTF reader forces it and every library
// file declares it) — a tangent-space normal map therefore leans, exactly as
// it does in the reference. A URL on iJewel's own CDN is served from our mirror.
export interface GemInclusionParams {
  /** Compile the GEM_INCLUSIONS path on/off (iJewel "Enabled"). Omit ⇒ on as
   * soon as any map is bound. Enabling with nothing that would change the
   * stone — no map, no surface roughness, no variable blur — is refused
   * (rejected, and `enabled` rolled back). */
  enabled?: boolean;

  // ── Speckle layer (v32) ──
  /** Tint of the speckle map — hex string or packed sRGB int. Speckle only:
   * webgi has no inclusion tint; its maps carry their own colour. */
  color?: string | number;
  /** Alias of `inclusionScale`, kept for v32 hosts. When both are sent,
   * `inclusionScale` wins; the getter reports the one value under both names. */
  scale?: number;
  /** Per-bounce weight of the SPECKLE map (`mapUrl`). Never set by any caller ⇒
   * **1** (at webgi's 0.1 turning the speckle on was below the perceptual
   * floor). Not webgi's `bumpScale` — that is `surfaceBumpScale`. */
  bumpScale?: number;
  /** ADDITIVE speckle map URL (bright flecks on black). */
  mapUrl?: string;

  // ── webgi layer (v62) ── defaults are webgi's own (`GemRefractionPlugin`).
  /** Tiling of every interior map, and of the speckle map (iJewel
   * "InclusionScale", 0–10, default 1): 1 ⇒ one tile across the stone's
   * bounding sphere. */
  inclusionScale?: number;
  /** Interior colour map that MULTIPLIES the light leaving the stone at each
   * bounce — white is clear, dark is a flaw (iJewel "InclusionMap"). */
  inclusionMapUrl?: string;
  /** Interior tangent-space normal map; REPLACES the facet normal a ray
   * refracts and reflects through at each internal hit (iJewel
   * "InclusionNormalMap"). */
  inclusionNormalMapUrl?: string;
  /** XY strength of `inclusionNormalMapUrl` (iJewel "IncNormalScale", webgi
   * `normalScale`, 0–10, default 0 — at 0 a bound map leaves every facet
   * normal as it was). */
  incNormalScale?: number;
  /** Interior roughness map: red × 7 is the environment mip level of the light
   * a ray leaves into — the milky look (iJewel "InclusionRoughness"). */
  inclusionRoughnessMapUrl?: string;
  /** Tiling of the surface maps (iJewel "SurfaceScale", 0–10, default 1). */
  surfaceScale?: number;
  /** Surface tangent-space normal map; bends the crown reflection only
   * (iJewel "normalMap"). */
  normalMapUrl?: string;
  /** XY strength of `normalMapUrl` (iJewel "BumpScale", webgi `bumpScale`,
   * 0–10, default 0.1). */
  surfaceBumpScale?: number;
  /** Crown reflection roughness: × 7 is the environment mip level the
   * reflection is read at (iJewel "Surface Roughness", 0–1, default 0). It
   * also drives the interior blur, through `blurRadius`. */
  surfaceRoughness?: number;
  /** Multiplies `surfaceRoughness` (red channel; iJewel "RoughnessMap"). */
  roughnessMapUrl?: string;
  /** How far roughness blurs the stone's interior (iJewel "blurRadius", 0–1,
   * default 0.1). webgi blurs a screen-space copy of the interior to
   * `6 · min(1, roughness · blurRadius · distanceFalloff)` levels of a blur
   * pyramid. The viewer has no such buffer: it blurs what the interior is made
   * of — the environment each exiting ray reads, and the interior maps — by as
   * many screen pixels as that level spans. The same softening inside a facet,
   * without the blur across facet edges. */
  blurRadius?: number;
  /** Patchy, view-dependent blur (iJewel "VariableBlur", 0–1, default 0). A
   * SWITCH at 0.01, as in webgi — not a magnitude. */
  variableBlur?: number;
  /** Shapes the variable-blur noise (iJewel "VariableParam1", 0–10, default 1).
   * ⚠️ That row writes webgi's `variableParam2`; a `.dmat`'s
   * `variableParam2` lands here. */
  variableParam1?: number;
  /** Shapes the variable-blur noise (iJewel "VariableParam2", 0–10, default 1).
   * ⚠️ That row writes webgi's `variableParam3`; a `.dmat`'s
   * `variableParam3` lands here. webgi's `variableParam1` feeds a function its
   * shader never calls, so it is not represented. */
  variableParam2?: number;
  /** Clamp the blurred interior to 1.0 so blown highlights do not smear into
   * halos (iJewel "ClampHDR", webgi `clampFlag`, default false). */
  clampHDR?: boolean;
  /** Body colour map multiplied into the light that passed through the stone,
   * projected flat through the girdle plane (iJewel "Map", webgi 0.22 `map` —
   * the bicolour ametrine/tourmaline presets). webgi 0.17 reserves but does not
   * implement it, so this projection is ours. */
  colorMapUrl?: string;
  /** Scale of `colorMapUrl` about the stone's centre (iJewel "Color Map
   * Scale", webgi 0.22 `mapScale`, default 1 ⇒ the map spans the stone). */
  colorMapScale?: number;
}

/** A gem group's inclusion state tagged with the group it belongs to, so a
 * `SceneState` can carry what `setGroupGemInclusions` accumulated per group
 * (v45). Group ids survive a reload of the same model (v47) — before that they
 * were material uuids and this round-trip restored nothing. */
export interface GroupGemInclusionState extends GemInclusionParams {
  groupId: string;
}

// ─── Scene-orchestration plugins (v32) ──────────────────────────────────────
// VariationConfigurator (switchable material/object variations), VirtualCameras
// (named saved poses), ScenePresets (named of-vjson snapshots) and SceneLoop
// (kiosk playlist). The state shapes mirror the viewer-side plugin modules.

export type VariationType = "materials" | "objects";
export interface VariationCategoryDef {
  name: string;
  type: VariationType;
  prefix?: string;
  title?: string;
  icon?: string;
  items: string[];
  titles?: string[];
  icons?: string[];
  targetGroupId?: string;
  targetObjectName?: string;
  data?: Record<string, unknown>;
  selected?: number;
}
export interface VariationConfig {
  baseUrl?: string;
  materials?: VariationCategoryDef[];
  objects?: VariationCategoryDef[];
}
export interface VariationCategoryState {
  name: string;
  type: VariationType;
  title: string;
  items: string[];
  titles: string[];
  icons: string[];
  selected: number;
}
export interface VariationConfiguratorState {
  enabled: boolean;
  baseUrl: string;
  categories: VariationCategoryState[];
}
export type VariationPhase = "start" | "loaded" | "applied" | "error";
export interface VariationProgress {
  category: string;
  item: string;
  index: number;
  phase: VariationPhase;
  error?: string;
}

export interface VirtualCameraPose {
  position: [number, number, number];
  target: [number, number, number];
  fov: number;
}
export interface VirtualCameraState {
  name: string;
  enabled: boolean;
  pose: VirtualCameraPose;
}

export interface ScenePresetState {
  name: string;
  savedAt: number;
}

export type SceneLoopEntry = { kind: "model"; url: string } | { kind: "preset"; name: string };
export interface SceneLoopState {
  playing: boolean;
  intervalMs: number;
  index: number;
  count: number;
  entries: SceneLoopEntry[];
}

// ─── CNC program export (v66) ───────────────────────────────────────────────
// The wedding band as a 4-axis rotary .NC program in the shop's own madCAM-
// posted dialect, plus its air-cut twin and a setup sheet. Generated in the
// viewer (`src/band/cnc/`) from the ACTIVE ring's committed state; nothing here
// imports three.js.
//
// The MACHINE PROFILE is plain JSON the host may store and edit (a shop's
// dialect, tool table, seat standard and limits); `getCncDefaultProfile()`
// returns the one seeded verbatim from the shop's sample program, which is
// `calibrated: false` until a prove-out at the shop. Every type below the
// export options was moved here from `src/band/cnc/types.ts` unchanged, so the
// viewer and every host read ONE definition.
//
// ⚠️ MACHINE SAFETY: an export whose gate FAILS — the program's gate OR its
// air cut's — carries no machine text at all (`CncExportResult.nc` and
// `.airCut` both absent), so a host cannot offer a download of a program the
// gate refused. The `.cam.3dm` is the one exception, because it is not a
// program: it is still issued, with the verdict in its manifest and on
// screen, and — for a refused plan — without any tool-centre path. Every
// download is stamped UNCALIBRATED while `profile.calibrated` is false.

/**
 * A work-offset word. The sample assigns one per PHYSICAL cutter — the shop
 * holds each cutter's length in its own offset and runs every tool on H1 — so
 * a library larger than the six standard offsets needs Fanuc's EXTENDED
 * offsets, `G54.1P1` … `G54.1P48`, printed as one word pair with no space.
 * (Whether the shop's controller has the 48-set option is a shop question; a
 * program that uses one is an assumed-tool program and warns as such.)
 */
export type CncWcs = "G54" | "G55" | "G56" | "G57" | "G58" | "G59" | `G54.1P${number}`;

/**
 * Where a tool's numbers come from:
 *   shop     read off the shop's own program (T, offset, feeds, speeds) — the
 *            sample's four cutters;
 *   assumed  OURS: a cutter a design family needs that the shop has not yet
 *            confirmed it owns. Its T number, work offset and feeds are
 *            placeholders copied from the nearest shop tool. Any program that
 *            loads one carries a `tool.assumed` warn naming it.
 */
export type CncToolSource = "shop" | "assumed";

export type CncToolKind = "conical" | "flat" | "ball" | "vbit" | "drill";

/** A canned drill cycle: its feed and the words that select it. */
export interface CncDrillDef {
  /** Cycle feed, mm/min (`F500.` in the sample). */
  feed: number;
  /** The cycle's G words, printed before Z/R/F — `"G99G81"` (return to R, plain drill). */
  cycle: string;
}

/**
 * One physical cutter as the machine knows it.
 *
 * `id` is ours; the machine sees `t` (the T word), `h` (the length-offset H
 * word) and `wcs`. The sample loads TWO different cutters as T3 — a conical
 * seat tool on G54 and a 2.8 mm end mill on G55 — so a tool is identified by
 * its id, never by its T number.
 */
export interface CncToolDef {
  id: string;
  source: CncToolSource;
  t: number;
  h: number;
  wcs: CncWcs;
  kind: CncToolKind;
  diameterMM: number;
  /** Conical / V tools: full included angle at the tip. Unknown for the sample's "SERISE 136". */
  includedAngleDeg?: number;
  /** Conical / V tools: flat at the tip. */
  tipDiameterMM?: number;
  fluteLengthMM?: number;
  /** How the shop names the cutter in an operation comment, double spaces and all. */
  label: string;
  /** B-axis word on this tool's op-start line, degrees. */
  bDeg: number;
  spindleRpm: number;
  /** What follows S on the op-start line: `"M03M08"` (spindle + coolant) or the drill's bare `"M3"`. */
  spindleWords: string;
  /** G1 plunge feed, mm/min. */
  plungeFeed: number;
  /** G1 cutting feed (Y/C moves), raw G94 units — see `CncKinematics.feedMode`. */
  cutFeed: number;
  /**
   * G1 lift feed after a pass. ABSENT ⇒ the lift inherits the cut feed and
   * prints no F word, as T3 does in the sample; T5 lifts at an explicit F2000.
   */
  liftFeed?: number;
  /** Rapid clearance above the blank surface: 2 for T3/T4, 3 for T5. */
  clearanceAboveMM: number;
  /** Deepest single Z step per pass. Ignored by a straight G81 drill (one full-depth plunge). */
  maxStepDownMM: number;
  /** Present on a drill: the canned cycle it runs. */
  drill?: CncDrillDef;
}

/**
 * Line templates. Each `{KEY}` is filled with an already-formatted value; a
 * literal word (the `X.` / `X0.` of the op-start lines) is part of the
 * template on purpose — that is where the sample's quirks live.
 *
 * Keys: header {LABEL} {SIZE} {MODEL} · firstToolComment {DIA} · toolChange
 * {T} · op-start {WCS} {Y} {C} {B} {S} {SPINDLE} · lengthComp {H} {Z} ·
 * comment {TEXT} · drillCycle {CYCLE} {Z} {R} {F}.
 */
export interface CncFraming {
  /** Line terminator, written after EVERY line including the closing `%`. */
  eol: "\r\n" | "\n";
  /** Line 1. The sample's carries a trailing space: `"%{LABEL} SZ{SIZE} GSMOD{MODEL} "`. */
  header: string;
  /** Printed once, for the first tool only. `null` = no tool comment. */
  firstToolComment: string | null;
  safeStart: string[];
  /** Homing before the first tool change (`"G91 G28 Z0."`, spaced, in the sample). */
  initialHome: string[];
  toolChange: string;
  /** Lines before every tool change after the first: M5 · G91G28Z0. · G40 G49 · M0. */
  betweenTools: string[];
  /** The first operation's start line (the sample prints `X.` here). */
  firstOpStart: string;
  /** Every later operation's start line (`X0.`). */
  opStart: string;
  /** Tool-length compensation + the move to clearance, after the op-start line. */
  lengthComp: string;
  comment: string;
  drillCycle: string;
  drillCancel: string;
  footer: string[];
}

/** How numbers print. S/T/H/M words are always plain integers. */
export interface CncNumberRules {
  /** Most decimals printed; trailing zeros are stripped. */
  decimals: number;
  /** `0.257` (true) or `.257` (false). */
  leadingZero: boolean;
  /** Integers keep their point: `F30.`, `B-90.` (true) or `F30` (false). */
  trailingDot: boolean;
}

export interface CncKinematics {
  /** +1: a positive design angle is +C. Unconfirmed until the sign cut. */
  signC: 1 | -1;
  /** +1: a positive band-axis offset is +Y. Unconfirmed until the sign cut. */
  signY: 1 | -1;
  /**
   * Largest C change a generated CUTTING move may make; longer sweeps are
   * split so a controller that rolls C over cannot take the short way round.
   * (The sample's own clearance-height rapid at L1132 swings 175.41°.)
   */
  maxCPerBlockDeg: number;
  /** Whether Z may move in the same block as Y or C. Never in the sample. */
  allowSimultaneousZ: boolean;
  /**
   * `G94-raw`: keep the shop's F words as written under G94 mixed-axis feed
   * (a C-only move at F1200 is 1200 °/min, ~267 mm/min at R 12.75). The
   * shop tuned its feeds under that rule, so they are not "corrected".
   */
  feedMode: "G94-raw";
}

/** The motion law every milling pass follows. */
export interface CncMotionLaw {
  /** Rapid down to the previous floor + this, then G1 plunge (the blank surface counts as the first floor). */
  approachMM: number;
  /** G1 lift above the floor just cut, before the G0 to clearance. */
  liftMM: number;
}

export interface CncLimits {
  /** Least metal left between any cut floor and the bore. */
  minWallMM: number;
  /** Least metal left between neighbouring seats. */
  minWebMM: number;
}

/** Ring-size system the header's SZ field is read in. Quarter sizes ⇒ US. */
export type CncSizeSystem = "US" | "EU" | "UK" | "IN";

/**
 * One row of the shop's SEAT STANDARD: how a round stone of `stoneMM` is set —
 * which cutter plunges the seat and how deep, and which drill opens the light
 * hole under it and how deep (all depths below the blank surface R).
 *
 *   shop     a row the shop's own work states (the 2.8 mm row is the sample's
 *            stone row: conical 2.8 to 1.46, drill 1.3 to 1.76);
 *   derived  scaled from a shop row's proportions by `resolveSeat` (or stored
 *            that way) — a guess until the shop confirms it, so a
 *            `seat.derived` FAIL wherever a program uses it: that size is
 *            blocked until the shop enters it as its own row.
 *
 * SHAPED STONES (v66, additive): a row with a `cut` other than "round" seats
 * that cut — princess, oval, pear, cushion, marquise … as the band builder
 * names it. Its seat is a POCKET: the stone's girdle outline (the builder's
 * own outline table, at the stone's real spin) offset outward by
 * `clearanceMM` AT THE GIRDLE'S OWN DEPTH (the pocket is laid out at the
 * radius the girdle sits at, so its wall flares wider toward the surface),
 * cut to `seatDepthMM` with the flat end mill `seatToolId`, the light hole
 * drilled at the stone's centre. A round end mill cannot cut a sharp corner
 * square: every pocket corner is left rounded to at least the cutter's
 * radius, and a corner that leaves the stone standing in the metal fails the
 * gate's `seat.fit` (the setup sheet says by how much). A shaped row is
 * `shop` or `derived` exactly like a round one, and a derived one fails
 * `seat.derived` the same way.
 * `seat.fit` places every seated stone where the band builder put it (its
 * Depth Offset and rail seating included) and holds its pavilion and culet
 * against the cut as well as its girdle, round or shaped, a stone row's too:
 * a seat or light hole too shallow for the stone at the depth the design
 * draws it FAILS, saying how much higher the stone would rest.
 * A row without `cut` is a round stone, as every row before v66 is; a round
 * stone is never seated from a shaped row.
 */
export interface CncSeatStandard {
  /**
   * Girdle diameter of the round stone, mm. For a shaped row (`cut`), the
   * band builder's own size for the cut (`diamond.stoneSizeMM`): the
   * geometric mean √(L·W) of the girdle's length and width — a 6 × 4 mm oval
   * is 4.90. The setup sheet prints the L × W it stands for.
   */
  stoneMM: number;
  /**
   * The cut this row seats, as the band builder names it (`"princess"`,
   * `"oval"`, `"pear"`, `"cushion"`, `"marquise"` …). Absent or `"round"` ⇒ a
   * round stone.
   */
  cut?: string;
  /**
   * Tool id (in the profile) of the seat cutter. For a shaped row, the FLAT
   * end mill that pockets the outline.
   */
  seatToolId: string;
  /**
   * Shaped rows only (required there): how far the pocket wall stands outside
   * the stone's girdle, at the girdle's own depth, mm (0 … 1).
   */
  clearanceMM?: number;
  /** Seat depth below R, mm. */
  seatDepthMM: number;
  /**
   * Tool id of the light-hole drill — `null` when no drill in the profile is
   * small enough for this stone (`note` says so): the seat is cut without a
   * hole, never with an oversize one.
   */
  drillToolId: string | null;
  /** Light-hole depth below R, mm. 0 when `drillToolId` is null. */
  drillDepthMM: number;
  source: "shop" | "derived";
  /** How a derived row was reached (proportions, roundings, what is missing). */
  note?: string;
}

export interface CncMachineProfile {
  name: string;
  /** False until a prove-out at the shop (air cut, sign cut, brass first article). */
  calibrated: boolean;
  calibratedBy?: string;
  /** ISO date. */
  calibratedAt?: string;
  framing: CncFraming;
  numbers: CncNumberRules;
  kinematics: CncKinematics;
  motion: CncMotionLaw;
  tools: CncToolDef[];
  /**
   * Seat standard per round stone size and (v66) per shaped cut and size.
   * Sizes it does not hold are derived by `resolveSeat` — and a program using
   * a derived row fails `seat.derived`.
   */
  seatStandard: CncSeatStandard[];
  limits: CncLimits;
  sizeSystem: CncSizeSystem;
  /** The air-cut variant lifts every Z (and the G81 R and Z) by this much. */
  airCutOffsetMM: number;
}

export type CncCheckLevel = "fail" | "warn" | "pass";

/**
 * One gate check. A `fail` in either report (the program's or its air cut's)
 * withholds both `nc` and `airCut`; a `warn` travels with the program.
 */
export interface CncCheck {
  /** Stable id, e.g. `env.depth`, `motion.rapidContact`. */
  id: string;
  level: CncCheckLevel;
  message: string;
  /** 1-based .NC line of the first offence. */
  line?: number;
}

/**
 * Feed time in minutes under the two rules a controller may apply to a G1
 * that includes C (rapids are not counted — the machine's rapid rate is
 * unknown):
 *   combined    degrees count as mm in the feed vector (Fanuc-style);
 *   linearOnly  F applies to Y/Z only; a pure-C move runs F in °/min (LinuxCNC).
 * Both cover G1 moves only, as the dossier's 11.68 / 7.57 min for the sample
 * do; canned-cycle feeds (the same under either rule) are `cannedCycle`. The
 * shop's real cycle time says which rule its controller follows.
 */
export interface CncCycleTime {
  combined: number;
  linearOnly: number;
  /** G81 feed time (R plane → hole bottom per hole). Add it to either rule for a total. */
  cannedCycle: number;
}

/** What the stock simulation saw. */
export interface CncSimSummary {
  /** Rapids tested against the remaining stock. */
  rapidsTested: number;
  /**
   * Of those, pure-Z rapids passed without a geometric test because the same
   * tool had already been FED to that depth at that exact (C, Y) — its own
   * body cleared the space (see `verify/heightfield.ts`).
   */
  rapidsCredited: number;
  /** Rapids that touched it (each is also a `motion.rapidContact` fail). */
  contacts: number;
  /** Lowest simulated stock radius after the program, mm. */
  minRadius: number;
  /** How tools of unknown geometry were modelled. */
  notes: string[];
}

export interface CncReport {
  /** No check failed. */
  ok: boolean;
  checks: CncCheck[];
  /** Minutes. */
  cycleTimeMin: CncCycleTime;
  sim?: CncSimSummary;
}

/**
 * The feature families a band program is planned from. `include` in the
 * export options selects among them; one left out is listed as not machined.
 *
 *   stoneRow  the sample's motif: a straight row of stones (the cross channel,
 *             straight or diagonal) — conical seat channel + end-mill extend
 *             cut through the band edges + a drilled light hole per stone
 *   seats     single seats under stones on the outer face
 *   grooves   decorative / seam / edge / Design-list grooves
 *   slots     the channel groove and stopped cross-channel trenches
 */
export type CncFeatureFamily = "stoneRow" | "seats" | "grooves" | "slots";

/** What the host types for line 1 of the program (`%{label} SZ{size} GSMOD{model}`). */
export interface CncExportHeader {
  /**
   * `UGTH2310` in the sample. Letters, digits, `.`, `_` and `-` only, at most
   * 32 characters: it is written into line 1 AND the file name. Anything
   * else (a space, `#`, `/`, a parenthesis, `%`) rejects the export.
   */
  label: string;
  /** The model number, e.g. `"368222"`. Same character rule. */
  model: string;
  /**
   * The ring size as the shop writes it, e.g. `"10.75"`. Absent ⇒ the ring's
   * own size in `sizeSystem` (US and EU read off the ring; UK and Indian are
   * the size whose bore is nearest the ring's, noted as such).
   */
  size?: string;
}

export interface CncExportOptions {
  /** The machine profile. Absent ⇒ the default (`getCncDefaultProfile()`). */
  profile?: CncMachineProfile;
  header: CncExportHeader;
  /** How `header.size` is read. Absent ⇒ `profile.sizeSystem`. */
  sizeSystem?: CncSizeSystem;
  /** Families to plan. Absent ⇒ all of them. */
  include?: CncFeatureFamily[];
  /**
   * Also write the `.cam.3dm`, the Rhino reference geometry for programming
   * the part in madCAM (see `CncExportResult.cam3dm`). Omit ⇒ true.
   */
  cam3dm?: boolean;
}

/** A builder feature the program does not cut, and why. Never dropped silently. */
export interface CncNotMachined {
  /** Human name of the feature, e.g. "inside engraving". */
  feature: string;
  reason: string;
}

export interface CncExportResult {
  /** The program passed every gate check AND its air cut did too. */
  ok: boolean;
  /** `<MODEL>S<SIZE>.NC`, every `.` of the size written as `-` (`368222S10-75.NC`). */
  fileName: string;
  /** `<MODEL>S<SIZE>-AIRCUT.NC`. */
  airCutFileName: string;
  /** `<MODEL>S<SIZE>-SETUP.txt`. */
  setupSheetFileName: string;
  /**
   * The program text, exactly as it goes to the machine. ABSENT unless `ok`:
   * a fail in EITHER report — the program's or its air cut's — withholds it,
   * since a program whose air cut cannot be run cannot be proven.
   */
  nc?: string;
  /** The air-cut twin (every Z lifted clear of the part). ABSENT unless `ok`, exactly like `nc`. */
  airCut?: string;
  /** How far the air cut lifts every Z, mm. */
  airCutOffsetMM: number;
  /** Plain-text setup sheet — always present, so a refused program still explains itself. */
  setupSheet: string;
  /** The gate's verdict on the program TEXT (re-parsed, never the planner's own data). */
  report: CncReport;
  /** The gate's verdict on the air cut, in air mode. */
  airCutReport: CncReport;
  notMachined: CncNotMachined[];
  /** Planning notes (compromises, clamped values, what a family left out). */
  notes: string[];
  /** Everything a person must read before running it (UNCALIBRATED, assumed tools, …). */
  warnings: string[];
  /**
   * The seat-standard rows the program was planned from, one per distinct
   * row: the profile's own rows it used and every row it DERIVED (each derived
   * row is what a `seat.derived` fail names). A host offers a derived row to
   * the shop prefilled, to confirm or correct as its own. Absent ⇒ no stone
   * seat was planned.
   */
  seatStandards?: CncSeatStandard[];
  /** Copied from the profile: false ⇒ every download is stamped UNCALIBRATED. */
  calibrated: boolean;
  /** Stable hash of the design + profile + options the program was generated from. */
  designHash: string;
  /** The generator that wrote it, e.g. `of-cnc/1 (contract v66)`. */
  generatorVersion: string;
  /**
   * The `.cam.3dm`: REFERENCE GEOMETRY for madCAM / Rhino, never a machine
   * program. It holds the turned blank, every feature curve both wrapped onto
   * the blank and unrolled flat (arc s, axial y, at the blank radius), the
   * tool-centre paths of the planned program, drill points carrying their
   * depth and diameter as user strings, a `99_NOT_CNC` layer for every
   * feature the program does not cut, and an `of-cam/1` manifest.
   *
   * Issued EVEN WHEN THE GATE FAILS — the shop can still program the part by
   * hand from it — so its presence says nothing about `ok`. A REFUSED plan's
   * file, though, carries no tool-centre path and no unrolled operation: only
   * the blank, the refused operations drawn on the blank's surface under
   * `REFUSED_` layers (every object `gate: failed`), a visible `GATE FAILED:
   * NOT FOR CUTTING` marker with the fail ids, and the `99_NOT_CNC` list. It
   * is stamped UNCALIBRATED (while `calibrated` is false, like every other
   * output) and its manifest records the gate's verdict. Absent when `cam3dm:
   * false` was asked for, and when rhino3dm could not be loaded in time (a
   * `notes` entry says so; the program itself never waits on it for long).
   */
  cam3dm?: Uint8Array;
  /** `<MODEL>S<SIZE>.cam.3dm`. Present exactly when `cam3dm` is. */
  cam3dmFileName?: string;
}

/** `previewBandCnc` options: the export options, plus what to draw. */
export interface CncPreviewOptions extends CncExportOptions {
  /** Draw the rapids (dashed slate) as well as the cutting moves (cyan over a dark halo). Default true. */
  rapids?: boolean;
}

/**
 * `exportBandCncBatch` options: the export options, for several ring sizes.
 * `sizes` holds 1 … 40 sizes in the export's size system (`sizeSystem`, else
 * `profile.sizeSystem`) — as a number (`10.75`) or as the shop writes it
 * (`"K.5"`). Each size takes the place of `header.size`, which is ignored.
 */
export type CncBatchOptions = CncExportOptions & { sizes: readonly (number | string)[] };

/**
 * One size of a batch. The air cut and setup sheet are named as
 * `exportBandCnc` names them (`<MODEL>S<SIZE>-AIRCUT.NC`, `-SETUP.txt`).
 */
export interface CncBatchItem {
  /** The size as written into the header and the file name, e.g. `"10.75"`. */
  size: string;
  /** This size's program and its air cut passed every gate check. */
  ok: boolean;
  /** `<MODEL>S<SIZE>.NC`. */
  fileName: string;
  /** ABSENT unless `ok` — withheld exactly as `exportBandCnc` withholds it. */
  nc?: string;
  /** ABSENT unless `ok`, like `nc`. */
  airCut?: string;
  /** Always present, so a refused size still explains itself (for an `error` item, the error). */
  setupSheet: string;
  /**
   * As `CncExportResult.cam3dm`: issued even when this size's gate failed
   * (with the refusal marked, and no tool-centre paths). NOT issued for a
   * size outside the band builder's table — that size was planned on the
   * table's nearest ring, so the file would show the wrong blank under this
   * size's name (`notes` says so).
   */
  cam3dm?: Uint8Array;
  cam3dmFileName?: string;
  /** The `fail` checks of this size's program and air-cut reports. */
  fails: { id: string; message: string }[];
  /** The `warn` checks of this size's program and air-cut reports — gate checks only. */
  warns: { id: string; message: string }[];
  /**
   * What is not a gate check but must still be said about this size, each
   * `<id>: <message>`: `cam3dm.unavailable` (rhino3dm could not be loaded),
   * `cam3dm.failed` (the writer threw), `cam3dm.withheld` (a size outside the
   * builder's table). Absent when there is nothing to say.
   */
  notes?: string[];
  /**
   * The size could not be generated at all: an unreadable size, an exception
   * while planning it, or a design the batch could not rebuild faithfully at
   * the ring's own size (every item then carries that error). `ok` is false
   * and no program text is present; the other sizes are unaffected. A size
   * that IS readable but outside the band builder's table (US 3–13) is not an
   * error: it comes back withheld (`ok` false, a `blank.size` fail), planned
   * on the table's nearest ring so the setup sheet can explain the refusal.
   */
  error?: string;
}

export interface CncBatchResult {
  /** One item per entry of `sizes`, in the same order. */
  items: CncBatchItem[];
  /** Wall time of the whole batch, ms. */
  elapsedMs: number;
}

// ─── Ring viewer facade ─────────────────────────────────────────────────────
//
// Every method exposed over the bridge for the "/3dviewer" route. The host
// client presents a Promisified version of this interface (see Promisify).

export interface RingApi {
  // Lifecycle / paint
  requestRender(): void;
  resize(width: number, height: number): void;

  // Static option catalogs (metal/gem/env presets, profiles, ring sizes, ...)
  getCatalogs(): Catalogs;

  // Scene snapshot
  getSceneState(): SceneState;

  // Background / environment
  setBackground(color: string | null): void;
  getBackgroundColor(): string | null;
  setBackgroundIntensity(v: number): void;
  setEnvIntensity(v: number): void;
  setEnvFollowCamera(v: number): void;
  setEnvironment(id: string): Promise<void>;
  loadEnvFromUrl(url: string): Promise<void>;
  loadEnvFromFile(file: File): Promise<void>;
  loadMetalEnvFromUrl(url: string): Promise<void>;
  loadMetalEnvFromFile(file: File): Promise<void>;
  setMetalEnvRotation(x: number, y: number, z: number): void;
  getMetalEnvRotation(): Vec3;
  setMetalEnvMaxBrightness(v: number): void;
  getMetalEnvMaxBrightness(): number;
  /** Drop an explicitly loaded Metal HDR override (v45); metals fall back to
   * the scene environment, so the Environment picker reaches them again. A
   * no-op when no override is loaded. */
  clearMetalEnv(): void;
  /**
   * Reload the entry project's boot metal HDR (v48). Once `clearMetalEnv` or —
   * far more commonly — the first Environment pick has dropped it, there was NO
   * way back to it short of a page reload: the Metal HDR section offers a URL
   * box and a file picker and nothing else. Resolves to false when the entry
   * project configured no metal env of its own (band mode sets it null on
   * purpose), so a host can hide the affordance.
   */
  restoreDefaultMetalEnv(): Promise<boolean>;
  setEnvRotation(x: number, y: number, z: number): void;
  getEnvRotation(): Vec3;
  setShowEnvAsBackground(v: boolean): void;
  setBackgroundBlur(v: number): void;

  // Background image + diamond env + renderer params (v13).
  /** Use a 2D image as the background (equirect excluded; honors intensity). */
  setBackgroundImageUrl(url: string): Promise<void>;
  /** Same, from decoded bytes. The picture is inlined into
   *  `SceneState.backgroundImageUrl` as a `data:` URL so it survives a save +
   *  reload (v46) — see that field for the size cap and what a `null` there
   *  means. Resolves once the backdrop is on screen AND inlining has settled. */
  setBackgroundImageFile(file: File): Promise<void>;
  /** Clear the background to transparent (alpha 0). */
  setTransparentBackground(): void;
  /**
   * Re-frame the current 2D backdrop (v49) WITHOUT reloading it — these are
   * slider-driven, so they cannot ride on `setBackgroundImageUrl`. Patch
   * semantics: named fields change, omitted fields keep their current value.
   * Returns the settled framing so a host never has to mirror it locally.
   */
  setBackgroundImageFraming(patch: Partial<BackgroundImageFraming>): Promise<BackgroundImageFraming>;
  /** The framing in force, defaults included. */
  getBackgroundImageFraming(): Promise<BackgroundImageFraming>;
  /** Separate high-contrast gem environment used by the diamond ray-tracer. */
  loadDiamondEnvFromUrl(url: string): Promise<void>;
  loadDiamondEnvFromFile(file: File): Promise<void>;
  setDiamondEnvRotation(rad: number): void;
  /**
   * v68 (HDR editor): raycast the loaded model at a normalised canvas point
   * (nx, ny in 0..1, y DOWN — the same convention as `setDofFocus`). Null on a
   * miss, when no model is loaded, or when the On Body occluder is in front.
   * A pure query: nothing is selected, focused or re-rendered. NEW method name —
   * gate it (caps `hdrPickV68`); RPC has no timeout.
   */
  pickSurface(nx: number, ny: number): SurfaceHit | null;
  /**
   * v68 (HDR editor): install raw half-float pixels as the scene environment —
   * the texture `loadEnvFromFile` would make of the same values, PMREM'd and
   * applied identically (intensity kept, the boot metal HDR dropped as for any
   * picked env). Rejects a malformed `EnvPixels`. NEW method name — gate it
   * (caps `hdrEnvPixelsV68`); RPC has no timeout.
   */
  loadEnvFromPixels(px: EnvPixels, name?: string): Promise<void>;
  /** Renderer output: explicit size override + render-scale + mode. */
  getRendererParams(): RendererParams;
  setRendererParams(patch: Partial<Omit<RendererParams, "mode">> & { mode?: RendererModeInput }): RendererParams;

  // Ground / shadow. The ground shadow is progressively BAKED (v11) —
  // setGroundShadowBake is the canonical control surface; the old flat
  // setters remain callable for v10 hosts (remapped where sensible,
  // deprecated no-ops otherwise).
  setGroundShadow(v: boolean): void;
  setGroundReflection(v: boolean): void;
  setGroundReflectionOpacity(v: number): void;
  setGroundReflectionBlur(v: number): void;
  setDepthShadow(v: boolean): void;
  setDepthShadowOpacity(v: number): void;
  getGroundShadowBake(): GroundShadowBakeSettings;
  /** Nested-partial setter; returns the full effective settings. */
  setGroundShadowBake(patch: GroundShadowBakePatch): GroundShadowBakeSettings;
  /** Force a full re-bake (host-driven refresh). */
  rebakeGroundShadow(): void;
  /** @deprecated remapped → groundShadowBake.softness */
  setShadowRadius(v: number): void;
  /** @deprecated no-op (bake quality is sampleCount now) */
  setShadowBlurSamples(v: number): void;
  /** @deprecated remapped → groundShadowBake.resolution */
  setShadowMapSize(v: number): void;
  /** @deprecated remapped → groundShadowBake.planeScale */
  setShadowSpread(v: number): void;
  /** @deprecated no-op (baked contact darkening is physical) */
  setContactShadowRadius(v: number): void;
  /** @deprecated no-op */
  setContactShadowDarkness(v: number): void;
  /** @deprecated no-op (the artifact-prone realtime shader is gone) */
  setProgressiveSoftness(v: number): void;
  setLightHelperVisible(v: boolean): void;

  // Tone mapping / lights
  setToneMappingExposure(v: number): void;
  setToneMapping(type: number): void;
  setLightMultiplier(v: number): void;
  setKeyLightIntensity(v: number): void;
  setKeyLightColor(hex: string): void;
  setKeyLightDirection(x: number, y: number, z: number): void;
  setFillLightIntensity(v: number): void;
  setFillLightColor(hex: string): void;
  setFillLightDirection(x: number, y: number, z: number): void;
  setHemiLightIntensity(v: number): void;
  setHemiSkyColor(hex: string): void;
  setHemiGroundColor(hex: string): void;

  // Scene lights (dynamic multi-light system). Every mutator returns the
  // fresh full light list so the host can mirror state from a single
  // round-trip. Shadow casters are capped (4 desktop / 2 mobile, including
  // the built-in key light); exceeding the cap or requesting castShadow on
  // an "area" light rejects the call with a descriptive error.
  listSceneLights(): SceneLightState[];
  addSceneLight(init: SceneLightInit): SceneLightState[];
  updateSceneLight(id: string, patch: SceneLightPatch): SceneLightState[];
  removeSceneLight(id: string): SceneLightState[];
  clearSceneLights(): SceneLightState[];
  /** Replace the whole collection (settings import / programmatic setups). */
  setSceneLights(lights: SceneLightState[]): SceneLightState[];
  /** One-click studio setup: re-tunes the key/fill/hemi rig AND replaces the
   * scene-light collection. Returns the full new SceneState. */
  applyLightPreset(id: string): SceneState;
  /** Shadows fall on the jewelry itself, not just the ground (default off). */
  setSelfShadowing(v: boolean): void;
  getSelfShadowing(): boolean;

  // Post-processing (SSR / SSAO / bloom / vignette / tonemap / DOF / CA /
  // grain / LUT). setPostFX takes a nested partial patch and returns the full
  // effective settings.
  getPostFX(): PostFXSettings;
  setPostFX(patch: PostFXSettingsPatch): PostFXSettings;
  /**
   * Click-to-focus DOF: normalized canvas coords (0..1, origin top-left) →
   * raycast against the model.
   *
   * A hit sets `dof.focalPoint` to the world point, so the focus follows it as
   * the camera moves. A MISS clears `focalPoint` and falls back to the
   * orbit-target distance — i.e. clicking the background behaves like
   * "Focus Center" rather than doing nothing.
   *
   * (This docblock used to promise a crossfade. There is none in the pipeline
   * and there never was — the reference has a `crossFadeTime` and we have no
   * counterpart, so the focus snaps.)
   */
  setDofFocus(x: number, y: number): void;
  /** Load a colour LUT into slot 0..2 (.cube or N×N² strip .png). Returns the
   * full 3-slot array (null = empty slot). */
  loadLutUrl(slot: number, url: string): Promise<(LutSlotInfo | null)[]>;
  loadLutFile(slot: number, file: File): Promise<(LutSlotInfo | null)[]>;
  clearLut(slot: number): (LutSlotInfo | null)[];
  listLuts(): (LutSlotInfo | null)[];

  // Per-object reflection matrix (v30). Object *identity* (which metal cluster
  // reflects which), so it has its own methods rather than riding setPostFX.
  // `groups` is derived from the current scene grouping (one entry per metal
  // group, e.g. each ring / metal part); the matrix is re-sized to match on
  // model load / reclassify. All cells default 1.0 and the feature defaults
  // disabled ⇒ the group-id prepass never runs and the render is byte-identical.
  /** The addressable metal groups in matrix-index order. */
  getReflectionGroups(): ReflectionGroup[];
  /** Full matrix snapshot (enabled flag + groups + weights). */
  getReflectionMatrix(): ReflectionMatrix;
  /** Set one cell: how strongly group `dest` reflects group `src` (≥0). */
  setReflectionMatrixCell(dest: number, src: number, value: number): ReflectionMatrix;
  /** Turn the per-object matrix on/off (off ⇒ inert, no prepass). */
  setReflectionMatrixEnabled(enabled: boolean): ReflectionMatrix;
  /** Reset every cell to 1.0 (and leave enabled as-is). */
  resetReflectionMatrix(): ReflectionMatrix;

  // Loader
  setLoaderConfig(cfg: LoaderConfig): void;
  getLoaderConfig(): LoaderConfig;
  // Utility plugins (v24)
  getFrameFade(): FrameFadeConfig;
  setFrameFade(enabled: boolean): FrameFadeConfig;
  enterFullScreen(): void;
  exitFullScreen(): void;
  toggleFullScreen(): void;
  /** S3 upload (backend-bound; stores fields, action is best-effort/no-op). */
  getS3Config(): S3Config;
  setS3Config(patch: Partial<S3Config>): S3Config;
  s3Connect(): Promise<boolean>;
  /** Engine maintenance actions (iJewel Extras). */
  clearCaches(): void;
  autoCenterAllGeometries(): void;
  showLoader(): void;
  hideLoader(): void;

  // ─── Decorative / niche plugins (v25) ──────────────────────────────────────
  getPhysicsConfig(): PhysicsConfig;
  setPhysicsConfig(patch: Partial<PhysicsConfig>): PhysicsConfig;
  physicsStep(): void;
  /** Build rigid bodies for root objects + a ground plane; returns body count. */
  physicsMakeRootBodies(): Promise<number>;
  getSnowConfig(): SnowConfig;
  setSnowConfig(patch: Partial<SnowConfig>): SnowConfig;
  getWaveGroundConfig(): WaveGroundConfig;
  setWaveGroundConfig(patch: Partial<WaveGroundConfig>): WaveGroundConfig;
  getWatchHandsConfig(): WatchHandsConfig;
  setWatchHandsConfig(patch: Partial<WatchHandsConfig>): WatchHandsConfig;
  /** Re-apply the configured time to matched hand meshes; returns # matched. */
  refreshWatchHands(): number;
  getCss3dConfig(): Css3dConfig;
  setCss3dConfig(patch: Partial<Css3dConfig>): Css3dConfig;
  /** Attach an iframe/HTML card to the selected object in the CSS3D layer. */
  attachIframeToSelected(url?: string): boolean;
  getScrollCameraConfig(): ScrollCameraConfig;
  setScrollCameraConfig(patch: Partial<ScrollCameraConfig>): ScrollCameraConfig;
  generateScrollCode(): string;
  /** Generate UVs for the selected mesh (built-in box projection). */
  unwrapSelectedUVs(): boolean;
  getRainbowDiamondConfig(): RainbowDiamondConfig;
  setRainbowDiamondConfig(patch: Partial<RainbowDiamondConfig>): RainbowDiamondConfig;
  /** Tint matched gem stones across a rainbow; returns # colored. Gem-only. */
  applyRainbowDiamond(): number;

  // ─── Final parity (v26) ────────────────────────────────────────────────────
  getEnvAdvanced(): EnvAdvancedConfig;
  setEnvAdvanced(patch: Partial<EnvAdvancedConfig>): EnvAdvancedConfig;
  getWidgetsConfig(): WidgetsConfig;
  setWidgetsConfig(patch: Partial<WidgetsConfig>): WidgetsConfig;
  refreshWidgets(): void;
  /** Dolly the camera in/out by a factor (>1 = out, <1 = in). */
  zoomCamera(factor: number): void;
  /** v42: swing the camera half a turn about the target's vertical axis — the
   * reference's "flip" tool, which shows the other face of the ring set.
   * Camera-only: nothing in the scene moves, so no rebuild and no re-bake. */
  flipCamera(): void;

  // ─── Split viewport (v60) ─────────────────────────────────────────────────
  /** Switch between the single viewport and the four-pane CAD layout.
   *  `single` is the untouched original render path, bit-for-bit. */
  setViewportLayout(layout: ViewportLayout): ViewportLayoutState;
  getViewportLayout(): ViewportLayoutState;
  /** Lock a pane to orbit about one world axis, or unlock it into free 3-D. */
  setViewportPaneMode(id: ViewportPaneId, mode: ViewportPaneMode): ViewportLayoutState;
  /** Make a pane active (picking, gizmo, ground mirror). Also happens on click. */
  setActiveViewportPane(id: ViewportPaneId): ViewportLayoutState;

  // ─── On Body (v63) ────────────────────────────────────────────────────────
  // Gate all five on caps `onBodyV63`: brand-new method names, and RPC has no
  // timeout, so an older viewer would leave the promise hanging forever.
  /**
   * Fit the loaded piece onto the body photograph that is the current 2D
   * backdrop. Puts the camera on a fixed fov-25 front view (controls off),
   * maps the anchor through the live backdrop framing, lays the hole axis
   * along the body part, sizes and seats the piece, turns its head toward the
   * back of the hand (`anchor.dorsal`, v64; the camera without one, or with
   * `headToCamera`), installs the depth-only body occluder (with its contact
   * shadow) and switches the ground plane, reflection and baked shadow off. Calling it again re-fits from
   * scratch (that is "Auto Fit"); the state from before the FIRST call is what
   * `clearOnBody` restores. Re-applied by the viewer on canvas resize and on a
   * model swap. Resolves null when no model is loaded.
   */
  placeOnBody(params: OnBodyParams): OnBodyPlacement | null;
  /** Leave On Body: restore the camera (pose, fov, controls), the model
   *  container's transform, the ground state and the split layout, and remove
   *  the occluder. A no-op when On Body is not active. */
  clearOnBody(): void;
  /**
   * Adjust (patch) the body occluder directly — the host's Adjust mode. Omitted
   * fields keep their value; `null` removes the occluder (On Body itself stays
   * on). Before any `placeOnBody` this installs a free-standing occluder.
   * Returns the occluder now in force, or null.
   */
  setBodyOccluder(cfg: Partial<BodyOccluderConfig> | null): BodyOccluderConfig | null;
  getBodyOccluder(): BodyOccluderConfig | null;
  /**
   * The ring's hole axis / inner radius / head direction (or a necklace's loop
   * opening) as `placeOnBody` sees them, in world units at the current
   * transform. `kind` only changes which default the axis-sign and head rules
   * favour. Null when no model is loaded.
   */
  getModelFit(kind?: OnBodyKind): ModelFit | null;

  // Models
  /** Add a model; resolves with its id. v65: a file carrying a scene (a
   *  "Ring + scene" GLB) resolves once that scene is applied (see
   *  `LoadOptions.embeddedScene`); a GLB carrying a scene and NO model (a
   *  `.jscene`) mounts nothing — its scene is opened as `importScene` would
   *  (`sceneApplied` with origin "file") and the add resolves with "" (no id:
   *  nothing was added; with `embeddedScene: "ignore"` nothing happens at all). */
  addModelUrl(url: string, opts?: LoadOptions): Promise<string>;
  addModelFile(file: File, opts?: LoadOptions): Promise<string>;
  /** Multi-file import (v15) — e.g. OBJ + MTL + textures, or glTF + .bin +
   * images. The first recognised model file (.glb/.gltf/.obj/.fbx/.stl/.3dm) is
   * the entry; the rest resolve as companions by basename. */
  addModelFiles(files: File[], opts?: LoadOptions): Promise<string>;
  removeModel(id: string): void;
  listModels(): ModelInfo[];
  /** `embeddedScene` (v65): a scene the new file carries is IGNORED by default
   *  on a swap — the current look stays; pass "apply" to take the file's (the
   *  swap then resolves once it is applied, and the file's camera wins over the
   *  camera pose a swap otherwise keeps). */
  swapModel(urls: string | string[], opts?: { animate?: boolean; embeddedScene?: EmbeddedSceneMode }): Promise<void>;
  // Scene extras (v16)
  /** Add a procedural primitive (plane/box/sphere/…) as a model. */
  generateGeometry(opts: GenerateGeometryOptions): Promise<string>;
  /** PCA auto-orient + drop the loaded model to the ground plane. */
  computePose(opts?: PoseOptions): void;
  /** Project the scene environment as a grounded skybox dome (off = plain bg). */
  setHdriGround(enabled: boolean, opts?: HdriGroundOptions): void;
  /** Full Ground panel config (v18) — plane props + randomized + progressive
   * shadow lights. Returns the canonical config. */
  getGroundConfig(): GroundConfig;
  setGroundConfig(patch: GroundConfigPatch): GroundConfig;
  /** HDRi grounded-dome config (v18). */
  getHdriGroundConfig(): HdriGroundConfig;
  /** Live gradient-SVG background generator (v18). Builds the SVG and applies
   * it as the background when applyToBackground; returns the canonical config. */
  getGradientConfig(): GradientConfig;
  generateGradient(patch: Partial<GradientConfig>): GradientConfig;
  /** Add a non-jewelry stage prop (kept out of export + auto-fit). */
  addStageModel(url: string): Promise<string>;
  /** Add a stage prop from a local File (byte-transfer, mirrors addModelFile). (v31) */
  addStageModelFile(file: File): Promise<string>;
  /** Reposition the ground plane under the combined stage-prop + model bounds
   * so props rest on the ground (webgi Model Stage). false restores the
   * model-only ground placement. (v31) */
  setStageGroundBelow(enabled: boolean): void;
  /** Bake a text decal into a group's material channel (canvas texture). */
  applyTextToGroupMap(groupId: string, text: string, opts?: TextDecalOptions): void;
  /** Set a scene node's local rotation, in degrees. */
  setObjectRotation(nodeId: string, xDeg: number, yDeg: number, zDeg: number): void;
  // Modifiers (v23)
  /** Radially duplicate a node N times around a pivot. Returns new model ids. */
  applyObjectRotations(nodeId: string, opts: ObjectRotationOptions): Promise<string[]>;
  /** Extrude a 2D shape along a circular tube path → a new model. */
  extrudeTube(opts: ExtrudeTubeOptions): Promise<string>;
  /** Bake a full Simple-Text decal onto a group's material channel(s). */
  applyTextToGroup(groupId: string, config: Partial<SimpleTextConfig>): void;
  /** Decimate meshes. Uses meshoptimizer's MeshoptSimplifier when available
   * (honors errorThreshold + lockBorder), falling back to three's
   * SimplifyModifier ratio path otherwise. Gem meshes are skipped. (v31 adds
   * the opts arg.) */
  simplifyMeshes(ratio: number, selectedOnly: boolean, opts?: { errorThreshold?: number; lockBorder?: boolean }): Promise<{ before: number; after: number }>;
  getParallaxMapping(): ParallaxMappingConfig;
  setParallaxMapping(patch: Partial<ParallaxMappingConfig>): ParallaxMappingConfig;
  getParallaxCamera(): ParallaxCameraConfig;
  setParallaxCamera(patch: Partial<ParallaxCameraConfig>): ParallaxCameraConfig;
  getRhinoImportOptions(): RhinoImportOptions;
  setRhinoImportOptions(patch: Partial<RhinoImportOptions>): RhinoImportOptions;
  prefetchModel(urls: string | string[]): Promise<void>;
  /** The OLDER scene-settings formats: an iJewel/webgi `.vjson` or our own
   *  `of-vjson/1` (see `exportVjson`). Applies only the fields the file names
   *  (merge). For the v65 whole-scene format use `importScene`; v65 also routes
   *  of-scene/2 and GLB-container input given here to `importScene`. */
  loadVjsonUrl(url: string): Promise<void>;
  /** As `loadVjsonUrl`, from a local file. */
  loadVjsonFile(file: File): Promise<void>;
  /**
   * v65 — the whole scene as one `.jscene` file: a valid, empty glTF 2.0 GLB
   * whose root `extensions.OF_scene` holds the of-scene/2 document and whose
   * BIN chunk holds every asset's ORIGINAL bytes (environments, LUTs,
   * backdrop, textures, logo …), so it opens offline and in other apps.
   * Everything except the ring itself; `opts.assets` defaults to "embed".
   */
  exportSceneFile(opts?: SceneExportOptions): Promise<Blob>;
  /** v65 — the same document as JSON, assets as `dataUrl` / `url` / `session`
   *  handles per `opts.assets`. Default "embed": every asset inline as a
   *  `data:` URL, so the JSON stands alone. For Undo snapshots ("session") and
   *  the Vault's saved scene ("reference" + `inlineBudgetBytes`, default 4 MB). */
  exportSceneJSON(opts?: SceneExportOptions): Promise<string>;
  /**
   * v65 — open a scene: a `.jscene`, a "Ring + scene" GLB (its scene only — the
   * meshes are not loaded here), of-scene/2 JSON, or (upgraded on the way in)
   * of-vjson/1 and iJewel `.vjson`. A string is a URL or the JSON text itself.
   * of-scene/2 replaces by default (see `SceneImportOptions.mode`); the two
   * older formats merge, as `loadVjsonUrl` always has. Resolves once every load
   * it started has landed; also emits `sceneApplied`. A newer import started
   * meanwhile wins: this one then REJECTS (`name` "SceneImportSuperseded").
   * The ring's pose (root, container, node edits) applies only when every saved
   * material group matched by exact id — on another ring it is skipped with a
   * warning in the result; everything else (camera included) still applies.
   */
  importScene(source: File | Blob | string, opts?: SceneImportOptions): Promise<SceneImportResult>;
  /** v65 — read a file's scene header without applying anything (cheap: the
   *  GLB's JSON chunk only). Null when it carries no of-scene/2 document. */
  inspectSceneFile(source: File | Blob): Promise<SceneFileInfo | null>;

  // Camera
  resetView(): void;
  autoCenter(): void;
  autoFit(): void;
  setFov(v: number): void;
  setCameraPreset(preset: CameraPreset): void;
  setViewAngle(angle: ViewAngle, opts?: { animate?: boolean; duration?: number }): Promise<void>;
  getCameraPosition(): Vec3;
  getCameraTarget(): Vec3;
  setCameraPosition(x: number, y: number, z: number): void;
  setCameraTarget(x: number, y: number, z: number): void;
  getPolarLimits(): [number, number];
  setPolarLimits(min: number, max: number): void;
  setAutoRotate(enabled: boolean, speed?: number): void;
  saveCameraPose(): CameraPose;
  restoreCameraPose(pose: CameraPose): void;


  // Animation
  listAnimationPresets(): OptionItem[];
  applyAnimationPreset(id: AnimationPresetId): Promise<AnimationTimeline>;
  setAnimationTimeline(timeline: AnimationTimeline): AnimationPlaybackState;
  getAnimationTimeline(): AnimationTimeline | null;
  clearAnimationTimeline(): AnimationPlaybackState;
  getAnimationState(): AnimationPlaybackState;
  playAnimation(): AnimationPlaybackState;
  pauseAnimation(): AnimationPlaybackState;
  stopAnimation(): AnimationPlaybackState;
  seekAnimation(time: number): AnimationPlaybackState;
  setAnimationLoop(v: boolean): AnimationPlaybackState;
  setAnimationPlaybackRate(v: number): AnimationPlaybackState;

  // GLTF clip animations (v15) — clips baked into the loaded .glb/.gltf.
  // Mutually exclusive with the host-authored timeline above. Every discrete
  // transition fires the `animationStateChanged` event.
  listGltfAnimations(): GltfAnimationInfo[];
  getGltfPlaybackState(): GltfPlaybackState;
  playGltfAnimation(index?: number): GltfPlaybackState;
  pauseGltfAnimation(): GltfPlaybackState;
  stopGltfAnimation(): GltfPlaybackState;
  seekGltfAnimation(time: number): GltfPlaybackState;
  setGltfAnimationSpeed(speed: number): GltfPlaybackState;
  setGltfAnimationLoop(loop: boolean): GltfPlaybackState;
  /** Set the extended GLTF options (timeScale/scroll/drag/autoplay/sync) (v19). */
  setGltfAnimationOptions(opts: GltfAnimationOptions): GltfPlaybackState;

  // Camera views & turntable (v15)
  listCameraViews(): CameraView[];
  /** Capture the current camera pose as a new named view. */
  addCameraView(name?: string): CameraView;
  deleteCameraView(id: string): void;
  renameCameraView(id: string, name: string): void;
  flyToCameraView(id: string, durationMs?: number): Promise<void>;
  /** Tour every saved view in order (pausing briefly at each). */
  animateAllCameraViews(perViewMs?: number): Promise<void>;
  stopCameraTour(): void;
  playTurntable(opts?: TurntableOptions): Promise<void>;
  stopTurntable(): void;
  /** Camera-views tour config (v19). */
  getCameraViewsConfig(): CameraViewsConfig;
  setCameraViewsConfig(patch: Partial<CameraViewsConfig>): CameraViewsConfig;
  /** Jump to the next / previous saved view (v19). */
  focusNextCameraView(): Promise<void>;
  focusPreviousCameraView(): Promise<void>;
  /** Record a tour of all saved views to video (v19). */
  recordAllCameraViews(opts?: VideoExportOptions): Promise<Blob>;

  // Interaction prompt (v15)
  getInteractionPrompt(): InteractionPromptSettings;
  setInteractionPrompt(patch: Partial<InteractionPromptSettings>): InteractionPromptSettings;

  // Recording
  startRecording(opts?: AnimationRecordOptions): void;
  stopRecording(): Promise<Blob>;
  record360Video(opts?: AnimationRecordOptions & { duration?: number; revolutions?: number }): Promise<Blob>;
  exportAnimationVideo(timeline: AnimationTimeline, opts?: AnimationRecordOptions): Promise<Blob>;

  // Selection / groups
  listGroups(): GroupInfo[];
  /** Enumerate individual meshes (e.g. diamonds) for per-element animation.
   * Optionally filter to a single group. */
  listElements(groupId?: string): ElementInfo[];
  getSelectedGroupId(): string | null;
  selectGroup(id: string | null): void;
  clearSelection(): void;
  reclassifyGroup(id: string, kind: MaterialKind): void;
  // Multi-select + outline + picker behaviour (v14).
  setSelection(groupIds: string[]): void;
  getSelection(): string[];
  /** Drive the hover outline highlight programmatically (same path pointer
   * hover uses) so a host hierarchy tree can highlight on row hover. null
   * clears the hover. (v31) */
  setHoveredGroup(groupId: string | null): void;
  getOutline(): OutlineSettings;
  setOutline(patch: Partial<OutlineSettings>): OutlineSettings;
  getPickerSettings(): PickerSettings;
  setPickerSettings(patch: Partial<PickerSettings>): PickerSettings;

  // Scene hierarchy + transform gizmos + node ops (v14).
  getSceneTree(): SceneNodeInfo[];
  setNodeVisible(id: string, visible: boolean): void;
  getControlsParams(): ControlsParams;
  setControlsParams(patch: Partial<ControlsParams>): ControlsParams;
  getTransformControls(): TransformControlsSettings;
  setTransformControls(patch: Partial<TransformControlsSettings>): TransformControlsSettings;
  /** Attach the gizmo to a scene node (null detaches). */
  attachTransformControls(nodeId: string | null): void;
  /** Re-pivot a node to its bbox center / bottom / world origin. */
  setPivot(nodeId: string, mode: "center" | "bottom" | "origin"): void;

  // Node inspector (v58). Null for an id the scene tree cannot resolve — which
  // is normal, not exceptional: SceneTree.build() re-registers ids on every
  // getSceneTree(), so an id held across a model reload resolves to nothing.
  // v63: `MODEL_ROOT_NODE_ID` always resolves, to the model container.
  getNodeDetails(nodeId: string): Promise<NodeDetails | null>;
  /** Partial per axis: sending only `{ position: { y } }` moves nothing else. */
  setNodeTransform(
    nodeId: string,
    patch: { position?: Partial<Vec3>; rotationDeg?: Partial<Vec3>; scale?: Partial<Vec3> },
  ): void;
  setNodeFlags(nodeId: string, patch: Partial<Pick<NodeDetails, NodeFlagField>>): void;
  renameNode(nodeId: string, name: string): void;
  /**
   * Subtree vertex/triangle/mesh totals.
   *
   * Separate from `getNodeDetails` on purpose: this traverses, and the host's
   * Geometry section is collapsed by default — so the cost is paid when someone
   * opens it rather than on every selection change.
   */
  getNodeGeometryInfo(nodeId: string): Promise<NodeGeometryInfo | null>;
  /** Floating-toolbar object ops on the current selection / model. */
  focusSelected(): void;
  mirrorSelected(axis: "x" | "y" | "z"): void;
  resetSelectedRotation(): void;
  deleteSelected(): void;
  resetModelTransform(): void;
  /** Pivot-controls gizmo settings (v19, stored + round-trip). */
  getPivotControls(): PivotControlsSettings;
  setPivotControls(patch: Partial<PivotControlsSettings>): PivotControlsSettings;
  /** Pivot-edit settings (v19). */
  getPivotEdit(): PivotEditSettings;
  setPivotEdit(patch: Partial<PivotEditSettings>): PivotEditSettings;
  /** Pose auto-update-ground (v19, iJewel PosePlugin). */
  getPoseAutoUpdateGround(): boolean;
  setPoseAutoUpdateGround(v: boolean): void;
  /** Diamond-manager settings + actions (v19, iJewel Diamonds panel). */
  getDiamondSettings(): DiamondManagerSettings;
  setDiamondSettings(patch: Partial<DiamondManagerSettings>): DiamondManagerSettings;
  /** Select which resident gem-env slot a group's stones sample: 0 = primary
   * diamond env, 1 = env2, 2 = env3 (loaded via setDiamondSettings env2/3Url).
   * Only groups with a non-zero slot (or when env2/3 are resident) compile the
   * GEM_ENV_SLOTS shader path — the default gem shader stays byte-identical. (v31) */
  setGroupGemEnvSlot(groupId: string, slot: 0 | 1 | 2): void;
  /** Swap the PRIMARY diamond env to one of the stored slot URLs (0 = the
   * currently-loaded primary, 1 = env2Url, 2 = env3Url). Distinct from
   * setGroupGemEnvSlot, which keeps all slots resident and switches per group. (v31) */
  activateDiamondEnvSlot(slot: number): Promise<void>;
  /** Convert the current selection's material to/from the diamond shader. */
  makeDiamond(): void;
  makeStandard(): void;
  clearDiamondCache(): void;
  /** Material-library "Replace Material" mode (v19). */
  getMaterialLibraryReplace(): boolean;
  setMaterialLibraryReplace(v: boolean): void;

  // Material application
  applyMetalPresetToGroup(id: string, presetId: string, finishId: string): Promise<void>;
  applyGemPresetToGroup(id: string, presetId: string): Promise<void>;
  applyPmatUrlToGroup(id: string, url: string): Promise<void>;
  applyDmatUrlToGroup(id: string, url: string): Promise<void>;
  applyPmatFile(id: string, file: File): Promise<void>;
  applyDmatFile(id: string, file: File): Promise<void>;

  // Serializable material editing (replaces live-object mutation)
  getGroupMaterialParams(id: string): GroupMaterialParams | null;
  /** The identity of a group's underlying material — its three.js `uuid`,
   * `name` and `type` (e.g. "MeshPhysicalMaterial" / "ShaderMaterial"). Lets a
   * host hierarchy panel show which material a group resolves to. Null if the
   * group has no material. (v31) */
  getGroupMaterialInfo(id: string): GroupMaterialInfo | null;
  setGroupMetalParam(id: string, name: MetalParamName, value: number): void;
  setGroupMetalColor(id: string, hex: string): void;
  setGroupGemUniform(id: string, name: GemUniformName, value: number): void;
  setGroupGemVec3Component(id: string, uniform: "boostFactors" | "color", axis: "x" | "y" | "z", value: number): void;
  setGroupGemOpacity(id: string, value: number): void;

  // Full physical-material editor + preset library + auto-assign (v14.3).
  /** Null for gem groups (gems use the gem-uniform surface). */
  getGroupPhysicalParams(id: string): PhysicalMaterialParams | null;
  setGroupPhysicalParams(id: string, patch: Partial<PhysicalMaterialParams>): PhysicalMaterialParams | null;
  /** Texture-map slots on a (non-gem) group material (v20). Maps load by
   * URL/File with the right colorspace per slot; the sampler controls tiling. */
  setGroupMaterialMapUrl(id: string, slot: MaterialMapSlot, url: string): Promise<void>;
  setGroupMaterialMapFile(id: string, slot: MaterialMapSlot, file: File): Promise<void>;
  clearGroupMaterialMap(id: string, slot: MaterialMapSlot): void;
  setGroupMaterialSampler(id: string, slot: MaterialMapSlot, transform: Partial<TextureTransform>): void;
  listGroupMaterialMaps(id: string): MaterialMapInfo[];
  /** Per-material shading extensions (v21). Gem groups return null. */
  getGroupMaterialExtensions(id: string): MaterialExtensionConfig | null;
  setGroupMaterialExtensions(id: string, patch: Partial<MaterialExtensionConfig>): MaterialExtensionConfig | null;
  // Configurators (v22)
  /** Material configurator — switchable material variations per group. */
  listMaterialConfigurators(): MaterialConfiguratorState[];
  getMaterialConfigurator(groupId: string): MaterialConfiguratorState | null;
  addMaterialVariation(groupId: string, name?: string): MaterialConfiguratorState | null;
  applyMaterialVariation(groupId: string, variationId: string): Promise<MaterialConfiguratorState | null>;
  renameMaterialVariation(groupId: string, variationId: string, name: string): MaterialConfiguratorState | null;
  removeMaterialVariation(groupId: string, variationId: string): MaterialConfiguratorState | null;
  /** Empty a group's variations but KEEP its configurator entry (distinct from
   * clearMaterialConfigurator, which removes the entry entirely). (v31) */
  clearMaterialVariations(groupId: string): MaterialConfiguratorState | null;
  clearMaterialConfigurator(groupId: string): void;
  /** Switch nodes — switchable geometry (one option visible at a time). */
  listSwitchNodes(): SwitchNodeState[];
  addSwitchNode(name: string, nodeIds: string[]): SwitchNodeState;
  selectSwitchOption(switchId: string, optionIndex: number): SwitchNodeState | null;
  setSwitchNodeEnabled(switchId: string, enabled: boolean): SwitchNodeState | null;
  /** Rename a switch node (v31). */
  renameSwitchNode(switchId: string, title: string): SwitchNodeState | null;
  /** Patch a switch node's config — camDistance / updateSceneOnSelectionChange (v31). */
  setSwitchNodeConfig(switchId: string, patch: { camDistance?: number; updateSceneOnSelectionChange?: boolean }): SwitchNodeState | null;
  removeSwitchNode(switchId: string): void;
  /** Library of applyable materials across categories. */
  listMaterialPresets(): MaterialPresetItem[];
  applyMaterialPresetToGroup(id: string, presetId: string): Promise<void>;
  /** The "This scene has N materials…" offer — see `AutoAssignInfo`: a group
   *  count, not a name match, open until `applyDefaultMaterials` runs and
   *  re-opened by every regrouping. Synchronous and side-effect free. */
  getAutoAssignInfo(): AutoAssignInfo;
  applyDefaultMaterials(): Promise<void>;
  /** glTF KHR_materials_variants (read at load). */
  listMaterialVariants(): string[];
  applyMaterialVariant(name: string): Promise<void>;

  // Quality / export
  setQuality(mode: QualityMode): void;
  /** Apply a user-facing quality tier (v38) — pixel ratio + progressive frame
   * budget + SSR/SSAO resolution in one call. Maps onto `QualityMode`:
   * medium→interactive, high→high, ultra→ultra. `high` is the boot default and
   * equals today's desktop settings. */
  setQualityTier(tier: QualityTier): void;
  setPixelRatio(v: number): void;
  snapshot(opts?: { width?: number; height?: number; transparent?: boolean; format?: "png" | "jpeg" | "webp"; quality?: number;
    /** Progressive-accumulation converge frames before reading pixels (v31). */
    frames?: number;
    /** Crop the output to this rect (device pixels) after rendering (v31). */
    crop?: { x: number; y: number; width: number; height: number };
  }): Promise<Blob>;
  /** Tiled high-res still — exceeds the GPU max-renderbuffer size (v15). */
  snapshotTiled(opts: TiledSnapshotOptions): Promise<Blob>;
  /** Turntable video (mp4 via WebCodecs, WebM fallback) (v15). */
  exportTurntableVideo(opts?: VideoExportOptions): Promise<Blob>;
  /** Serialize the scene to our own `of-vjson/1` schema (v15) — the OLDER
   * format, kept for existing documents. Re-importing via loadVjsonUrl/File
   * restores camera, post FX, materials and env, merging over the live scene.
   * It misses much of the scene (no diamond env, no LUTs, no library-metal
   * textures, a file-loaded env as a dead link): new code should use
   * `exportSceneFile` / `exportSceneJSON` (v65). */
  exportVjson(opts?: { pretty?: boolean }): Promise<string>;
  /** GLB export. `compress` Draco-encodes (iJewel-grade sizes). Materials
   * carry `extras.jewelry` metadata so re-imports restore metals/gems. v15 adds
   * Draco quantization controls. */
  exportGLB(opts?: GlbExportOptions): Promise<Blob>;
  /** Apply vjson MaterialConfigurator materials to matching groups (default
   * on). Off keeps the loaded GLB's own materials. */
  setApplySceneMaterials(v: boolean): void;
  getApplySceneMaterials(): boolean;
  /** The OLDER settings snapshot: `SceneState` + the camera pose, no materials
   *  and no asset bytes. Superseded by `exportSceneJSON` (v65). v65 adds the
   *  ring's pose (`modelTransform` = the model root, `modelContainerTransform`,
   *  both from before On Body) with the `modelGroups` ids it belongs to, and the
   *  camera pose from before On Body; `importSettingsJSON` restores the pose
   *  (before the camera) only onto a ring that has every one of those groups. */
  exportSettingsJSON(): string;
  importSettingsJSON(json: string): void;
  exportMetalJSON(id: string): string | null;
  importMetalJSON(id: string, json: string): void;
  exportDiamondJSON(id: string): string | null;
  importDiamondJSON(id: string, json: string): void;

  // ─── Randomized self-shadow (v32) ────────────────────────────────────────
  /** Jitter the key/self-shadow directional light across refine frames so cast
   * model shadows soften progressively. Returns the full effective config. */
  setRandomizedSelfShadow(patch: Partial<RandomizedSelfShadowParams>): RandomizedSelfShadowParams;
  getRandomizedSelfShadow(): RandomizedSelfShadowParams;

  // ─── Gem inclusion material (v32) ────────────────────────────────────────
  /** Set a gem group's inclusion material (webgi GemRefraction). A PATCH:
   * absent fields keep their value. Compiles GEM_INCLUSIONS on the group's
   * stones; the default gem shader is byte-identical until enabled. Resolves
   * once every map in the patch has loaded, and rejects (rolling that map back)
   * when one fails to. */
  setGroupGemInclusions(id: string, params: GemInclusionParams): void;
  /** The inclusion state accumulated on a group so far, or `null` when it has
   * none (v45). The setter takes partial patches and the material rebuilds on
   * every preset change, so this — not the host's own copy — is the truth a
   * panel should render. Since v62 every field is present, defaults filled in,
   * and `scale` mirrors `inclusionScale`. */
  getGroupGemInclusions(id: string): GroupGemInclusionState | null;

  // ─── Variation configurator (v32) ────────────────────────────────────────
  loadVariationConfig(url: string): Promise<VariationConfiguratorState>;
  setVariationConfig(config: VariationConfig | string): VariationConfiguratorState;
  listVariations(): VariationCategoryState[];
  applyVariation(category: string, item: string | number): Promise<VariationConfiguratorState>;
  clearVariations(): void;
  getVariationConfiguratorState(): VariationConfiguratorState;
  setVariationConfiguratorEnabled(enabled: boolean): void;

  // ─── Virtual cameras (v32) ───────────────────────────────────────────────
  addVirtualCamera(name: string, pose?: Partial<VirtualCameraPose>): VirtualCameraState;
  listVirtualCameras(): VirtualCameraState[];
  getVirtualCamera(name: string): VirtualCameraState | null;
  activateVirtualCamera(name: string, opts?: { animate?: boolean; durationMs?: number }): Promise<void>;
  updateVirtualCamera(name: string, patch: Partial<VirtualCameraPose>): VirtualCameraState | null;
  removeVirtualCamera(name: string): void;
  clearVirtualCameras(): void;

  // ─── Scene presets (v32) ─────────────────────────────────────────────────
  saveScenePreset(name: string): ScenePresetState;
  applyScenePreset(name: string): Promise<void>;
  listScenePresets(): ScenePresetState[];
  deleteScenePreset(name: string): void;

  // ─── Scene loop / kiosk playlist (v32) ───────────────────────────────────
  setSceneLoopPlaylist(entries: SceneLoopEntry[], opts?: { intervalMs?: number }): SceneLoopState;
  setSceneLoopInterval(intervalMs: number): SceneLoopState;
  startSceneLoop(): SceneLoopState;
  stopSceneLoop(): SceneLoopState;
  nextSceneLoopItem(): Promise<SceneLoopState>;
  getSceneLoopState(): SceneLoopState;

  // ─── Scrollable camera-views live driver (v32) ───────────────────────────
  /** ScrollableCameraView LIVE: place camera at fraction t (0..1) along the saved-views path. */
  setScrollProgress(t: number): void;
  /** Emit a working page-scroll embed snippet that posts setScrollProgress to the iframe. */
  generateScrollViewsCode(iframeId?: string, targetOrigin?: string): string;

  logDiagnostics(): void;
}

// ─── Band viewer facade ("/band" route) ─────────────────────────────────────
//
// A strict SUPERSET of RingApi: the band route exposes every ring capability
// (scene, environment, lighting, ground, camera, quality, export, material
// editing, ...) PLUS the band-specific configurator methods below. By
// extending RingApi (instead of Pick-ing a subset), any method added to
// RingApi is automatically available on the band route too - the two routes
// can never drift out of sync. Each band setter returns the new BandState so
// the host UI can stay in sync from a single round-trip.

/** @deprecated v37 — superseded by RingBuilderApi (band + engagement in one
 * facade, returning RingState). Kept for back-compat while the hosts migrate. */
export interface BandApi extends RingApi {
  getBandState(): BandState | null;
  /** Route subsequent band edits to ring 0 ("her") or ring 1 ("his"). */
  setActiveRing(index: number): BandState | null;
  setProfile(profileId: string): BandState | null;
  loadProfile3dm(file: File): Promise<BandState | null>;
  setRingSize(usSize: number): BandState | null;
  setRingSizeEU(eu: number): BandState | null;
  setWidth(mm: number): BandState | null;
  setThickness(mm: number): BandState | null;
  setSplitMode(mode: SplitMode): BandState | null;
  setSplitPattern(patternId: string): BandState | null;
  setEdgeStyle(style: EdgeStyle): BandState | null;
  setGrooves(config: Partial<GrooveConfig>): BandState | null;
  setPartitionMaterial(index: number, presetId: string, finishId: string): BandState | null;
  // Per-partition metal overrides + material JSON (matches /3dviewer).
  setPartitionMetalColor(index: number, hex: string): BandState | null;
  setPartitionMetalParam(index: number, name: MetalParamName, value: number): BandState | null;
  exportBandMetalJSON(index: number): string | null;
  importBandMetalJSON(index: number, json: string): BandState | null;
  // Stone cut shape + gem material (preset, advanced uniforms, JSON).
  setStoneShape(shape: StoneShape): BandState | null;
  setGemPreset(presetId: string): BandState | null;
  setGemUniform(name: GemUniformName, value: number): BandState | null;
  setGemVec3Component(field: "boostFactors" | "color", comp: "x" | "y" | "z", value: number): BandState | null;
  setGemOpacity(value: number): BandState | null;
  exportBandDiamondJSON(): string | null;
  importBandDiamondJSON(json: string): BandState | null;
  setAngularSegments(segments: number): BandState | null;
  setDiamond(config: Partial<DiamondConfig>): BandState | null;
  setChannel(config: Partial<ChannelParams>): BandState | null;
  /** v54 — set a channel field AND take it off the style's re-derive list; pass
   *  `null` for a field to hand it back. Gate on caps `ringChannelManualV54`. */
  setChannelManual(patch: ChannelManualPatch): BandState | null;
  setCrossChannel(patch: Partial<CrossChannelParams>): BandState | null;
  setCrossChannelManual(patch: CrossChannelManualPatch): BandState | null;
  setChannelCutShape(shape: ChannelCutShape): BandState | null;
  setEngravingText(text: string): BandState | null;
  setEngraving(config: Partial<EngravingConfig>): BandState | null;
  // Metal karat / purity (drives precise weight in the spec).
  setMetalKarat(id: string): BandState | null;
  // Cross-over / twisted multi-strand shank.
  setBandStyle(style: BandStyle): BandState | null;
  setTwist(config: Partial<TwistConfig>): BandState | null;
  addSecondRing(state?: Partial<BandState>): Promise<BandState | null>;
  removeSecondRing(): void;
  // Two-ring arrangement / viewing-angle preset (visible in pair mode).
  /** v68: optional `pose` — see `RingBuilderApi.setBandLayout`. */
  setBandLayout(preset: BandLayoutPreset, pose?: BandLayoutPose | null): void;
  beginDraft(): void;
  endDraft(): void;
}

// ─── Engagement viewer facade ("/engagement" route) ─────────────────────────
//
// Like BandApi, a strict SUPERSET of RingApi: the engagement route exposes every
// ring capability PLUS the engagement configurator methods below. Setters are
// coarse per-block partial patches — the UI sends e.g. `{ widthMM: 2.1 }` and
// every patch returns the full fresh EngagementState so the host stays in sync
// from a single round-trip (the same `apply()` pattern the band route uses).

/** @deprecated v37 — superseded by RingBuilderApi (band + engagement in one
 * facade, returning RingState). Kept for back-compat while the hosts migrate. */
export interface EngagementApi extends RingApi {
  getEngagementState(): EngagementState | null;
  setCenterStone(partial: Partial<CenterStoneConfig>): EngagementState | null;
  setSideStones(partial: Partial<SideStonesConfig>): EngagementState | null;
  setHead(partial: Partial<HeadConfig>): EngagementState | null;
  setShank(partial: Partial<ShankConfig>): EngagementState | null;
  setEngagementExtras(partial: Partial<LegacyEngagementExtras>): EngagementState | null;
  // Per-part metal (preset/finish), color and material JSON (pmat).
  setHeadMetal(presetId: string, finishId: string): EngagementState | null;
  setShankMetal(presetId: string, finishId: string): EngagementState | null;
  setEngagementMetalColor(part: EngagementMetalPart, colorId: string): EngagementState | null;
  exportEngagementMetalJSON(part: EngagementMetalPart): string | null;
  importEngagementMetalJSON(part: EngagementMetalPart, json: string): EngagementState | null;
  // Center-stone gem material (preset, advanced uniforms, dmat JSON).
  setEngagementGemPreset(presetId: string): EngagementState | null;
  setEngagementGemUniform(name: GemUniformName, value: number): EngagementState | null;
  setEngagementGemVec3Component(field: "boostFactors" | "color", comp: "x" | "y" | "z", value: number): EngagementState | null;
  exportEngagementDiamondJSON(): string | null;
  importEngagementDiamondJSON(json: string): EngagementState | null;
  // Computed cost/manufacturability spec.
  getEngagementSpec(): EngagementSpec | null;
  /** Manufacturing-grade export of the assembled ring. `format` selects the
   * container (`glb` default; `stl` reserved for the CSG-fused solid), and
   * `includeStones` keeps the gems in the output. The binary comes back the way
   * the other export RPCs return theirs — a `Blob` today (matching exportGLB);
   * an STL path may hand back an `ArrayBuffer` or ASCII `string`. v35 STUB
   * delegates to exportGLB; the CSG fuser replaces this next. */
  exportManufacturingGLB(opts?: { format?: "glb" | "stl"; includeStones?: boolean }): Promise<Blob | ArrayBuffer | string>;
  // Slider-drag draft batching (engagement-table implementations, same names as band).
  beginDraft(): void;
  endDraft(): void;
  // Design save/restore (JSON round-trips through the host / Vault).
  serializeEngagementDesign(): EngagementState | null;
  loadEngagementDesign(json: string): EngagementState | null;
  // v36 "complete the set": stack a matching wedding band beside the engagement
  // ring, reusing the band-mode engine. `addCompanionBand` builds + places a
  // band flush against the shank (default: profile A, 2 mm, the shank's ring
  // size + metal; `state` overrides any BandState field). The offset can be
  // slid along the finger axis with `setCompanionBandLayout` (mm, centre-to-
  // centre). `removeCompanionBand` disposes it fully. addCompanionBand returns
  // the (unchanged) EngagementState for host-sync symmetry, or null if
  // engagement mode is not active.
  addCompanionBand(state?: Partial<BandState>): Promise<EngagementState | null>;
  removeCompanionBand(): void;
  setCompanionBandLayout(offsetMM: number): void;
}

// ─── Ring Builder facade (v37, the merged band + engagement route) ───────────
//
// The union of the BandApi and EngagementApi extension blocks over RingApi,
// with every setter returning the merged RingState so the host `apply()` pattern
// is unchanged. RingState extends BandState, so a widened return still satisfies
// every existing BandState call site. beginDraft/endDraft are declared once.
// NOTE: RingApi (the /3dviewer studio surface) is a different thing entirely and
// is untouched — this facade extends it exactly as the two old ones did.

export interface RingBuilderApi extends RingApi {
  getRingState(): RingState | null;
  /** Attach the engagement head assembly (true) or detach + dispose it (false). */
  setEngagementEnabled(on: boolean): RingState | null;
  /** Coarse patch of the engagement-only block (deep-merged, like setHead). */
  setEngagement(patch: Partial<EngagementBlock>): RingState | null;

  // ── Band half (was BandApi) ───────────────────────────────────────────────
  /** Route subsequent band edits to ring `index` of the list (v41: any ring,
   * was ring 0 "her" / ring 1 "his"). Out of range ⇒ ring 0. */
  setActiveRing(index: number): RingState | null;
  setProfile(profileId: string): RingState | null;
  loadProfile3dm(file: File): Promise<RingState | null>;
  setRingSize(usSize: number): RingState | null;
  setRingSizeEU(eu: number): RingState | null;
  setWidth(mm: number): RingState | null;
  setThickness(mm: number): RingState | null;
  setSplitMode(mode: SplitMode): RingState | null;
  setSplitPattern(patternId: string): RingState | null;
  setEdgeStyle(style: EdgeStyle): RingState | null;
  setGrooves(config: Partial<GrooveConfig>): RingState | null;
  setPartitionMaterial(index: number, presetId: string, finishId: string): RingState | null;
  setPartitionMetalColor(index: number, hex: string): RingState | null;
  setPartitionMetalParam(index: number, name: MetalParamName, value: number): RingState | null;
  exportBandMetalJSON(index: number): string | null;
  importBandMetalJSON(index: number, json: string): RingState | null;
  setStoneShape(shape: StoneShape): RingState | null;
  setGemPreset(presetId: string): RingState | null;
  setGemUniform(name: GemUniformName, value: number): RingState | null;
  setGemVec3Component(field: "boostFactors" | "color", comp: "x" | "y" | "z", value: number): RingState | null;
  setGemOpacity(value: number): RingState | null;
  exportBandDiamondJSON(): string | null;
  importBandDiamondJSON(json: string): RingState | null;
  setAngularSegments(segments: number): RingState | null;
  setDiamond(config: Partial<DiamondConfig>): RingState | null;
  setChannel(config: Partial<ChannelParams>): RingState | null;
  /** v54 — set a channel field AND take it off the style's re-derive list; pass
   *  `null` for a field to hand it back. Gate on caps `ringChannelManualV54`. */
  setChannelManual(patch: ChannelManualPatch): RingState | null;
  setCrossChannel(patch: Partial<CrossChannelParams>): RingState | null;
  setCrossChannelManual(patch: CrossChannelManualPatch): RingState | null;
  setChannelCutShape(shape: ChannelCutShape): RingState | null;
  setEngravingText(text: string): RingState | null;
  setEngraving(config: Partial<EngravingConfig>): RingState | null;
  setMetalKarat(id: string): RingState | null;
  setBandStyle(style: BandStyle): RingState | null;
  setTwist(config: Partial<TwistConfig>): RingState | null;
  /** @deprecated v41 — `addRing()`. Kept as a shim: it appends exactly as
   * before and only differs once a third ring exists. */
  addSecondRing(state?: Partial<BandState>): Promise<RingState | null>;
  /** @deprecated v41 — `removeRing(1)`. Removes ring 1, not "the last one". */
  removeSecondRing(): void;
  /** Arrangement preset for the whole ring LIST (v41: N rings, was a pair).
   * Deliberately NOT the same thing as `setCompanionBandLayout` (which slides
   * one band in mm against the shank) — different law, different argument.
   *
   * v68: optional `pose` (`BandLayoutPose`), a per-design override of the
   * "standing" / "catalogue" law — each ring's angles, the partner's place and
   * the layout camera (elevation, lens, fill, centre). It is clamped
   * (`clampBandLayoutPose`), stored, and re-applied on every rebuild, add,
   * remove, move or hide, so editing a stone keeps the photographed pose. A
   * call WITHOUT a pose (an older host, or choosing an Arrangement) clears it:
   * the preset's shared law again, exactly as before v68. A pose on any other
   * preset is dropped. */
  setBandLayout(preset: BandLayoutPreset, pose?: BandLayoutPose | null): void;
  /** v68 (new NAME — gate on caps `ringPoseV68`): the preset in force, its
   * stored pose (null when none) and the layout camera. */
  getBandLayout(): BandLayoutInfo;

  // ── New geometry (v37) ────────────────────────────────────────────────────
  setRingShape(shape: RingShape): RingState | null;
  setDivision(style: DivisionStyle): RingState | null;
  /** Inner-curve bulge in mm; 0 ⇒ flat inside (BandState.comfortFitMM). */
  setComfortFit(mm: number): RingState | null;
  /** Joint-groove cross-section + seam/edge placement (GrooveConfig v37 fields).
   * v38: also carries `seamBoundaries`, and the two milgrain `jointType`s that
   * sweep beads instead of carving. */
  setJointGrooves(config: Partial<GrooveConfig>): RingState | null;

  // ── New geometry (v38) ────────────────────────────────────────────────────
  /** Independent Left/Right edge treatment. `null` drops the override and hands
   * both sides back to the global `setEdgeStyle`. A partial patches one side. */
  setEdgeSides(sides: Partial<EdgeSides> | null): RingState | null;
  /** Polished inner wall regardless of the outer surface finish. */
  setInsidePolished(on: boolean): RingState | null;
  /** Drive thickness from width; applied immediately and on every width edit. */
  setAutoThickness(on: boolean): RingState | null;
  /** Pick a British letter size (`UK_RING_SIZES[].label`). Converts to the
   * inner diameter and drives the existing US/EU size — nothing extra is
   * stored, so the readout is always `spec.innerDiameterMM`. */
  setRingSizeUK(label: string): RingState | null;

  // ── New diamonds (v39) ────────────────────────────────────────────────────
  // Everything else in the v39 wave — placement, sides, side/top setting,
  // spacing, colour, gold beads, the three new styles — rides the existing
  // `setDiamond(Partial<DiamondConfig>)` patch. Only the inner stone needs a
  // method of its own, because it lives on BandState, not DiamondConfig.
  /** The stone set into the INSIDE of the band. `true`/`false` is shorthand for
   * `{ enabled }`; a partial patches size/angle without disturbing the rest. */
  setInnerDiamond(config: Partial<InnerDiamondConfig> | boolean): RingState | null;

  // ── Free placement (v40) ──────────────────────────────────────────────────
  // The unrolled-band editor. Every one of these normalises what it stores
  // (angle wrapped to 0–360, `u` clamped so no stone overhangs the width,
  // repeat ≥ 1, rotate within ±180), so the host cannot express an invalid
  // arrangement. Stones only render under `DiamondStyle` "free-place".
  /** Replace the whole arrangement. The viewer keeps the first 1000 stones (and never places more than 1000 copies however far `setFreeRepeat` multiplies them). */
  setFreeStones(stones: FreeStone[]): RingState | null;
  /** Append one stone. An omitted `id` is assigned deterministically. */
  addFreeStone(stone: Partial<FreeStone>): RingState | null;
  /** Patch one stone by id. The drag path — wrap a drag in beginDraft/endDraft. */
  updateFreeStone(id: string, patch: Partial<FreeStone>): RingState | null;
  removeFreeStone(id: string): RingState | null;
  /** Replace the arrangement with a generated pattern stamp, sized to the
   * current band (`freePatterns` catalog). */
  /** v52 (item 22): `opts.count` re-samples a `resample: "path"` stamp to a
   *  different stone count, `opts.spanDeg` compresses or stretches its angular
   *  extent. Both optional; absent ⇒ the stamp's own values, so an older host
   *  calling this with one argument is unaffected. A count above 1000
   *  (Infinity included) stamps 1000; one that is not a number, the stamp's own. */
  applyFreePattern(patternId: FreePatternId, opts?: { count?: number; spanDeg?: number }): RingState | null;
  /** N-fold repetition around the ring (1 = the arrangement as placed). */
  setFreeRepeat(n: number): RingState | null;
  /** Rotate the whole arrangement around the band, −180 … +180. */
  setFreeRotate(deg: number): RingState | null;

  // ── The long tail (v41) ───────────────────────────────────────────────────
  /** Append a decorative groove. An omitted `id` is assigned deterministically;
   * everything else falls back to a 0.6 × 0.25 mm horizontal groove on the
   * centre line. */
  addDesignGroove(groove?: Partial<DesignGroove>): RingState | null;
  /** Patch one groove by id. The slider path — wrap a drag in
   * beginDraft/endDraft, exactly as for width/thickness. */
  updateDesignGroove(id: string, patch: Partial<DesignGroove>): RingState | null;
  removeDesignGroove(id: string): RingState | null;
  /** Replace the whole Design list (`clampDesignGrooves` normalises it). */
  setDesignGrooves(grooves: DesignGroove[]): RingState | null;
  /** v51 (item 4) — patch the milgrain bead law. NEW METHOD NAME: gate on
   *  `caps.ringMilgrainV51`, because RPC has no timeout and calling it on a
   *  v50 viewer leaves the promise unresolved forever. */
  setMilgrain(config: Partial<MilgrainConfig>): RingState | null;
  /** Custom Balance: arbitrary partition ratios, normalised to the current
   * partition count. `null` restores the split pattern's preset ratios. */
  setCustomRatios(ratios: number[] | null): RingState | null;
  /** Name one partition's metal. `null` (or "") restores "Metal N". */
  setPartitionLabel(index: number, label: string | null): RingState | null;
  /** Append a ring to the list ("+ Add ring"). Ring 0 always exists, so the
   * first call is what `addSecondRing` used to do. v67: a seed `state` is
   * applied like `loadRingState` (its design through `applyRingDesign`, so it
   * is clamped and its channel derived — it used to be copied in verbatim),
   * and the new ring is never mirrored to the others while it is being made. */
  addRing(state?: Partial<BandState>): Promise<RingState | null>;
  /**
   * v67 — apply a whole design (or any part of one) to the ACTIVE ring in ONE
   * rebuild and ONE `ringStateChanged`. NEW METHOD NAME: gate on caps
   * `ringDesignV67`.
   *
   * Runs the existing setters in the host replay's order, so every clamp and
   * derivation still runs (sizes through the dimension derivations,
   * `setDiamond` deriving the channel and cross channel, the channel only
   * through `setChannelManual`, the cross channel as `setCrossChannel` then
   * `setCrossChannelManual`). Wearer fields (`RING_WEARER_FIELDS`) are ignored
   * even when a caller sends them. Mirrored to every ring while linked.
   * Resolves once the stone cut it names has loaded.
   */
  applyRingDesign(patch: RingDesignPatch): Promise<RingState | null>;
  /**
   * v67 — restore the ACTIVE ring to a saved `RingState` (undo/redo,
   * `?design=`, a share link) in ONE rebuild. NEW METHOD NAME: gate on caps
   * `ringDesignV67`.
   *
   * The design half goes through `applyRingDesign` (mirrored while linked);
   * the wearer half — ring size, engraving text, the engagement head (attached,
   * patched or detached to match), ring type and name — lands on the active
   * ring only. Fields absent from `state` are left as they are, except the
   * engagement head, which is detached when the state has none.
   */
  loadRingState(state: RingState): Promise<RingState | null>;
  /** Remove ring `index` — ANY index, including 0 (v43: their manager offers
   * Delete on every row). Removing ring 0 promotes the next ring into its place
   * rather than refusing; removing the active ring hands the edits to ring 0.
   * The last remaining ring is the design itself and cannot be removed. */
  removeRing(index: number): void;
  /** Rings currently in the list, ≥ 1 in band mode and 0 before it starts. */
  getRingCount(): number;

  // ── The ring manager (v43) ────────────────────────────────────────────────
  // Their "Add or remove rings" modal, whose rows carry Hide and Move ←/→
  // alongside the Delete that `removeRing` already serves. Rename is pure
  // `BandState.ringName` and needs no method of its own.
  /** Show/hide one ring without removing it. A hidden ring keeps its state and
   * its slot (indices do not shift) and is skipped by the layout, so the
   * visible rings close up as if it were not there. */
  setRingVisible(index: number, visible: boolean): RingState | null;
  /** Reorder the list — the row's Move ←/→. `to` is the destination index AFTER
   * the ring is lifted out, both clamped into the list; a no-op move is legal.
   * The active ring follows its ring, and the layout is re-applied. */
  moveRing(from: number, to: number): RingState | null;

  // ── N partitions + the live link (v42) ────────────────────────────────────
  /**
   * Split the band into `count` metal partitions (1 … `MAX_PARTITIONS`) — the
   * reference's Single · Duo · Tri · Quad · Multi row, expressed as the number
   * it actually is. Picks the first catalog pattern with that many partitions
   * in the current seam direction, or synthesises an even one
   * (`evenSplitPatternId`) when the catalog has none.
   *
   * `materials`, the seam carve, `grooves.seamBoundaries`, `customRatios` and
   * the milgrain rails all follow the new count. `setSplitMode` /
   * `setSplitPattern` still work and are still the way to pick a specific
   * uneven preset.
   */
  setPartitionCount(count: number): RingState | null;
  /**
   * Link every ring in the set: while on, a band setter applied to the ACTIVE
   * ring is mirrored to all the others, so "her" and "his" stay one design.
   *
   * Mirrored engine-side (not by the host), so it holds for every RPC path.
   * Deliberately NOT mirrored — the fields that belong to a wearer rather than
   * to the design:
   *   · `setRingSize` / `setRingSizeEU` / `setRingSizeUK` — different fingers;
   *   · `setEngravingText` — a personal inscription (the engraving STYLE,
   *     `setEngraving`, is part of the design and does mirror);
   *   · `setEngagementEnabled` / `setEngagement` — the head belongs to the
   *     engagement ring, never to the band stacked next to it.
   * Everything else — profile, width, thickness, splits, materials, gems,
   * grooves, free stones, drafts — mirrors.
   *
   * Linking does not retro-copy: it starts from wherever the rings already are.
   * Use it and then re-apply a setter (or the host's copy-across) to converge.
   */
  setRingsLinked(on: boolean): RingState | null;
  getRingsLinked(): boolean;

  // ── Engagement half (was EngagementApi) ───────────────────────────────────
  setCenterStone(partial: Partial<CenterStoneConfig>): RingState | null;
  setSideStones(partial: Partial<SideStonesConfig>): RingState | null;
  setHead(partial: Partial<HeadConfig>): RingState | null;
  /** Surprise stones. Engraving lives on the band half (`setEngravingText`). */
  setEngagementExtras(partial: Partial<EngagementExtras>): RingState | null;
  setHeadMetal(presetId: string, finishId: string): RingState | null;
  setEngagementMetalColor(part: EngagementMetalPart, colorId: string): RingState | null;
  exportEngagementMetalJSON(part: EngagementMetalPart): string | null;
  importEngagementMetalJSON(part: EngagementMetalPart, json: string): RingState | null;
  /** Center-stone gem material (the band gem setters target the melee run). */
  setEngagementGemPreset(presetId: string): RingState | null;
  setEngagementGemUniform(name: GemUniformName, value: number): RingState | null;
  setEngagementGemVec3Component(field: "boostFactors" | "color", comp: "x" | "y" | "z", value: number): RingState | null;
  exportEngagementDiamondJSON(): string | null;
  importEngagementDiamondJSON(json: string): RingState | null;
  getEngagementSpec(): EngagementSpec | null;
  exportManufacturingGLB(opts?: { format?: "glb" | "stl"; includeStones?: boolean }): Promise<Blob | ArrayBuffer | string>;
  /**
   * v66 — the ACTIVE ring as a 4-axis rotary CNC program (.NC) in the
   * profile's dialect, with its air-cut twin and a setup sheet. Rejects while a
   * draft is open (`beginDraft` without `endDraft`) and while the stone cut's
   * GLB has not loaded — both would plan from geometry the ring does not have.
   * When EITHER the program's gate or its air cut's gate fails, the result
   * comes back with `ok: false` and NEITHER `nc` nor `airCut` text (the
   * `.cam.3dm`, which is not a program, is still issued).
   */
  exportBandCnc(opts: CncExportOptions): Promise<CncExportResult>;
  /**
   * v66 — the active ring's design as one program per ring size. Each size is
   * generated from the CURRENT design without changing the live ring: its
   * state, its scene and any CNC preview stay exactly as they were. `nc` and
   * `airCut` are withheld per item exactly as `exportBandCnc` withholds them,
   * and a size that cannot be generated is an item with `error`, never a
   * rejection of the batch. Rejects in the cases `exportBandCnc` does, and
   * when `sizes` holds fewer than 1 or more than 40 entries.
   */
  exportBandCncBatch(opts: CncBatchOptions): Promise<CncBatchResult>;
  /**
   * v66 — draw the program's toolpaths over the active ring (cutting moves
   * cyan over a dark halo, rapids dashed slate; lifted onto the blank's
   * surface and depth-tested, so the ring hides its far side) and return the
   * gate's report on it. The overlay
   * is outside the model container — never exported, fitted, baked, picked or
   * in the G-buffer — and is cleared by `null`, by another ring becoming
   * active, and by any rebuild of the previewed ring. Resolves null only for
   * `null` (the clear); a design with nothing to cut draws nothing and
   * resolves the report, whose `plan.empty` fail says why.
   */
  previewBandCnc(opts: CncPreviewOptions | null): Promise<CncReport | null>;
  /** v66 — the machine profile seeded from the shop's sample program (a fresh, editable copy). */
  getCncDefaultProfile(): Promise<CncMachineProfile>;
  /** Companion wedding band flush against the shank (mm centre-to-centre). */
  addCompanionBand(state?: Partial<BandState>): Promise<RingState | null>;
  removeCompanionBand(): void;
  setCompanionBandLayout(offsetMM: number): void;

  // ── Draft batching — declared ONCE for the merged facade ──────────────────
  beginDraft(): void;
  endDraft(): void;

  // ── Deprecated back-compat, kept so existing hosts keep compiling ─────────
  /** @deprecated use getRingState. */
  getBandState(): RingState | null;
  /** @deprecated use getRingState. */
  getEngagementState(): RingState | null;
  /** @deprecated the band-grade half is setProfile/setWidth/setRingSize/
   * setEdgeStyle/setGrooves/setChannel/setSplitMode/setSplitPattern/
   * setPartitionMaterial/setDiamond; the rest is setEngagement. */
  setShank(partial: Partial<ShankConfig>): RingState | null;
  /** @deprecated alias of setPartitionMaterial(0, …). */
  setShankMetal(presetId: string, finishId: string): RingState | null;
  /** @deprecated round-trips the whole merged RingState now, not just the
   * engagement half. */
  serializeEngagementDesign(): RingState | null;
  /** @deprecated see serializeEngagementDesign. */
  loadEngagementDesign(json: string): RingState | null;
}

// ─── Transport envelope ──────────────────────────────────────────────────────

export type ViewerMode = "ring" | "band" | "engagement";

export type ViewerEventName =
  | "ready" | "selectionChanged" | "modelsChanged" | "bandStateChanged" | "error"
  | "hoverChanged" | "sceneTreeChanged" | "transformChanged" | "autoAssignAvailable"
  | "animationStateChanged" | "variationProgress" | "sceneLoopStateChanged"
  | "engagementStateChanged" | "ringStateChanged" | "freeStoneSelectionChanged"
  | "viewportLayoutChanged" | "sceneApplied";

export interface ViewerEventPayloads {
  ready: { mode: ViewerMode; contractVersion: number };
  /** `groupId` is the primary (last) selection; `groupIds` the full set (v14). */
  selectionChanged: { groupId: string | null; groupIds: string[] };
  modelsChanged: Record<string, never>;
  /** @deprecated v37 — superseded by ringStateChanged. */
  bandStateChanged: { state: BandState };
  error: { message: string };
  /** Hovered group under the cursor, or null (v14). */
  hoverChanged: { groupId: string | null };
  /**
   * v52 (item 24) — a free-placed stone was clicked in the 3-D view, or the
   * click missed every stone and the selection cleared.
   *
   * An EVENT, not an RPC: the canvas lives in the iframe, so a click never
   * reaches the host to ask about. With `freeRepeat > 1` one FreeStone owns
   * several meshes and they all carry the same id, so clicking any copy selects
   * the arrangement's stone.
   */
  freeStoneSelectionChanged: { id: string | null };
  /** The "apply default materials?" offer is open (v14.3). `count` is the
   *  number of material groups — nothing is matched by name (see `AutoAssignInfo`).
   *  ⚠️ Declared but NEVER EMITTED: no viewer code sends it. A host that wants
   *  the offer polls `getAutoAssignInfo()` after `modelsChanged`. */
  autoAssignAvailable: { count: number };
  /** The scene hierarchy changed (node added/removed/visibility) (v14). */
  sceneTreeChanged: Record<string, never>;
  /** A node was moved by the transform gizmo (v14). */
  transformChanged: { nodeId: string; position: Vec3; rotationDeg: Vec3; scale: Vec3 };
  /** Any discrete animation transition — GLTF clips OR the host timeline (v15).
   * Replaces the host's 200 ms playback poll; time readouts still poll while a
   * monitor is mounted. */
  animationStateChanged: { gltf: GltfPlaybackState; timeline: AnimationPlaybackState };
  /** Per-item progress while a variation loads/applies (v32). */
  variationProgress: VariationProgress;
  /** The kiosk scene-loop playlist state changed (v32). */
  sceneLoopStateChanged: SceneLoopState;
  /** The engagement configurator's state changed (v33).
   * @deprecated v37 — superseded by ringStateChanged. */
  engagementStateChanged: { state: EngagementState };
  /** The Ring Builder state changed (v37) — the one state event for the merged
   * band + engagement builder. */
  ringStateChanged: { state: RingState };
  /**
   * v60 — the split viewport changed, including when the ACTIVE pane changed
   * because the user clicked into a different one.
   *
   * An event rather than a poll for the same reason as
   * `freeStoneSelectionChanged`: the canvas lives in the iframe, so the host
   * never sees the click that moved focus, and its per-pane controls would
   * otherwise highlight the wrong pane until something else happened to ask.
   */
  viewportLayoutChanged: { state: ViewportLayoutState };
  /**
   * v65 — a scene was applied: `origin: "file"` after `importScene` (and the
   * routes that call it), `origin: "model"` when a loaded model's own embedded
   * scene was applied (then `modelId` names it). Fires once the scene's loads
   * have landed, so a host can re-read every panel from here.
   */
  sceneApplied: SceneAppliedEventPayload;
}

export interface RpcRequestMsg { kind: "viewer:rpc"; id: number; method: string; args: unknown[]; }
export interface RpcResultMsg { kind: "viewer:rpc:result"; id: number; result: unknown; }
export interface RpcErrorMsg { kind: "viewer:rpc:error"; id: number; error: string; }
export interface RpcEventMsg { kind: "viewer:event"; name: ViewerEventName; payload: unknown; }
export interface RpcReadyMsg { kind: "viewer:ready"; mode: ViewerMode; contractVersion: number; }
export interface RpcHandshakeMsg { kind: "viewer:hello"; }

export type HostToViewerMsg = RpcRequestMsg | RpcHandshakeMsg;
export type ViewerToHostMsg = RpcResultMsg | RpcErrorMsg | RpcEventMsg | RpcReadyMsg;

export const MSG_KINDS = {
  rpc: "viewer:rpc",
  result: "viewer:rpc:result",
  error: "viewer:rpc:error",
  event: "viewer:event",
  ready: "viewer:ready",
  hello: "viewer:hello",
} as const;

/** Turns a synchronous-or-async facade into an all-async client surface. */
export type Promisify<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer R
    ? (...args: A) => Promise<Awaited<R>>
    : never;
};

export type RingClientApi = Promisify<RingApi>;
export type BandClientApi = Promisify<BandApi>;
export type RingBuilderClientApi = Promisify<RingBuilderApi>;
